// @ts-nocheck
import { useState, useCallback, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { AlertCircle, X } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
// [GUTTED] Old component/page imports removed — UI being rebuilt from Paper designs
import AppLayout from './components/app/AppLayout';
import Sidebar from './components/app/Sidebar';
import type { SidebarUser, HistoryItem } from './components/app/Sidebar';
import TopBar from './components/app/TopBar';
import InputBar from './components/app/InputBar';
import EmptyState from './components/app/EmptyState';
import DocumentColumn from './components/app/DocumentColumn';
import AudioPlayer from './components/app/AudioPlayer';
import ChatPanel from './components/app/ChatPanel';
import type { ChatPanelMessage, ImageResultData, TranscriptionData, AnalysisResultData, MergeResultData, SplitResultData, OcrResultData, QrResultData, WatermarkResultData, PdfUtilityResultData, RateLimitInfo } from './components/app/ChatPanel';
import SigningGuideCard from './components/app/SigningGuideCard';
import ToolPanel from './components/app/ToolPanel';
import VersionTimeline from './components/app/VersionTimeline';
import SettingsPanel, { type BillingInfo } from './components/app/SettingsPanel';
import IntegrationsPanel from './components/app/IntegrationsPanel';
import AppErrorBanner from './components/app/AppErrorBanner';
import type { ExportDestination } from './components/app/ExportBar';
import { FolderBrowserModal, SlackExportModal, GmailExportModal } from './components/app/ExportModals';
import NotionDatabaseEntryModal from './components/app/NotionDatabaseEntryModal';
import LinearCreateIssueModal from './components/app/LinearCreateIssueModal';
import LinearEditIssueModal from './components/app/LinearEditIssueModal';
import ShareDocumentModal from './components/app/ShareDocumentModal';
import { useCollaboration } from './hooks/useCollaboration';
import { getPlaceholderValues, setManyPlaceholderValues, snapshotForBackend } from './state/placeholderValuesStore';
import UnfilledPlaceholderModal from './components/pdfViewer/UnfilledPlaceholderModal';
import PrintedTitleModal from './components/pdfViewer/PrintedTitleModal';
import type { AgentRunSnapshot, CommentPinData, ConversationDocument, EditBatch, HtmlEditAction, HtmlEditApplyResult } from '@corner/shared';
import SignaturesDashboard from './components/app/signatures/SignaturesDashboard';
import ProjectWorkspaceTabs, { type ProjectWorkspaceTab } from './components/app/ProjectWorkspaceTabs';
import IssuesPanel, { CLAUSE_FLAGS_UPDATED_EVENT, type IssuesPanelOnOpenFlag } from './components/app/IssuesPanel';
import ClauseReviewSetupStrip from './components/app/clauseReview/ClauseReviewSetupStrip';
import InlineClauseFlagPopover from './components/app/clauseReview/InlineClauseFlagPopover';
import WorkflowRunPanel from './components/app/workflows/WorkflowRunPanel';
import { useUserWorkflows } from './hooks/useUserWorkflows';
import { useTemplateForFile } from './hooks/useTemplateForFile';
import { generateDefaultMarkers } from './lib/defaultMarkers';
import type { PlacementType } from './components/pdfViewer/AddFieldToolbar';
import { WorkflowDestinationPicker } from './components/app/workflows/WorkflowDestinationPicker';
import { FoldersButton } from './components/app/FoldersButton';
import { useWorkflowDestination, type WorkflowDestination } from './hooks/useWorkflowDestination';
import type { UserWorkflowDTO } from './api/workflowApi';
import { saveContractSendAsWorkflow } from './api/workflowApi';
import { buildWorkflowFromResult, isContractVelocityWorkflow } from './lib/workflowFromResult';
import IntegrationFileBrowserModal from './components/app/IntegrationFileBrowserModal';
import SendForSignatureModal from './components/app/SendForSignatureModal';
import type { SendForSignatureData } from './components/app/SendForSignatureModal';
import { isRecipientPlacementField } from './lib/isRecipientPlacementField';
// PermissionConfirmModal removed — confirmation now inline in CoT panel (CoTConfirmCard)
import SigningStatusPanel from './components/app/SigningStatusPanel';
import VerifySignatureModal from './components/app/VerifySignatureModal';
import FoldersPanel from './components/app/FoldersPanel';
import type { CreateProjectFolderInput, FolderWithCount, ProjectFolderRecentSource } from './components/app/FoldersPanel';
import { AppFolderIcon } from './components/app/AppFolderIcon';
import ArchivedPanel from './components/app/ArchivedPanel';
import type { ArchivedItem } from './components/app/ArchivedPanel';
import RecentPanel from './components/app/RecentPanel';
import PaywallModal from './components/PaywallModal';
import ProUpgradeSuccessModal from './components/app/ProUpgradeSuccessModal';
import type { RightPanelSettings, ToolResult, WorkflowThreadSummary } from './types';
import { useIntent, type EsignContext } from './hooks/useIntent';
import { appendTokenToUrl, downloadUrlWithAuthIfNeeded, fetchUrlAsFile } from './lib/authedDownload';
import useCoTState, { type CoTStatus } from './hooks/useCoTState';
import type { CoTStepData } from './components/app/cot/CoTContainer';
import useQueryRunState from './hooks/useQueryRunState';
import useReviewState from './hooks/useReviewState';
import * as reviewApi from './api/reviewApi';
import * as clauseReviewApi from './api/clauseReviewApi';
import * as projectApi from './api/projectApi';
import { clauseFlagToReviewSuggestion, getCoverageNote } from './lib/clauseFlagMapping';
import { ReviewPanel } from './components/app/review';
import SourcePreviewDrawer from './components/app/review/SourcePreviewDrawer';
import { groundingStatusFromLocateResult, computeGroundingSummary, getGroundingMessage } from './lib/groundingSummary';
import type { ClauseReviewSummary, ClauseFlagLane, ClauseFlagLocateResult, ProjectClauseFlagsResponse } from '@corner/shared';
import { useVersionHistory } from './hooks/useVersionHistory';
import { useConversations } from './hooks/useConversations';
import { useFolders } from './hooks/useFolders';
import { IDLE_WELCOME_PENDING_KEY } from './hooks/useAuth';
import { useAuthContext } from './contexts/AuthContext';
import { useIsMobile } from './hooks/useIsMobile';
import { useViewportWidth } from './hooks/useViewportWidth';
import { Toaster } from './components/ui/toaster';
import { toast } from './hooks/use-toast';
import { toast as sonnerToast } from 'sonner';
import { api } from './lib/api';
import apiClient from './lib/apiClient';
import { getActionError } from './lib/actionErrorPresentation';
import { formatQueryRunBootContext, formatQueryRunContextFromSnapshot } from './lib/formatQueryRunContext';
import { buildMergedV2CotSteps, cotStatusFromV2Snap, v2CotStepsDiffer } from './lib/v2QueryRunCotMerge';
import { documentEventService } from './services/documentEventService';
import { buildEsignChatSummary } from './lib/esignChat';
import { buildMultiFileLoadedSystemMessage } from './lib/multiFileChat';
import { sumPdfPagesInFiles } from './lib/pdfPageCount';
import { parseDraftDuplicateCopyRequest, parseBulkApplyAllDraftsIntent } from './lib/parseDraftCopyRequest';
import { APP_SIGNATURES_PATH, APP_WORKSPACE_PATH, conversationWorkspacePath } from './lib/appRoutes';
import { APP } from './components/app/tokens';
import MobileSidebarDrawer from './components/app/mobile/MobileSidebarDrawer';
import MobileBottomSheet from './components/app/mobile/MobileBottomSheet';
import {
  showAppError,
} from './lib/appErrorBanner';
import {
  isAudioLikeFile,
  formatAudioDurationForChat,
  singleFileLoadedMessageBase,
  singleFileLoadedMessageWithDuration,
  pdfSingleFileLoadedMessage,
} from './lib/audioChat';
import {
  isImageLikeFile,
  formatImageDimensionsForChat,
  singleFileLoadedMessageWithImageMeta,
} from './lib/imageChat';
import { getFileFormatInfo } from './lib/fileFormat';
import { draftHtmlToWorkingPlainText, paragraphsToSimpleDraftHtml } from './lib/draftEditSync';
import { isTransientNetworkError, waitForOnlineOrShortDelay } from './lib/networkRetry';
import {
  deriveExplicitTextOnly,
  shouldSkipHydrationForExplicitTextOnly,
  shouldSkipHydrationForOwnedArtifact,
} from './lib/hydrationDecisions';
import {
  resolveEffectiveVersionId,
  resolveLogicalDocumentId,
  shouldPreserveSelectedVersionAfterProjectRefresh,
  versionOutsideLogicalDocument,
} from './lib/documentVersionSelection';
import { DOC_VERSION_DEBUG, docVersionDebugLog } from './lib/documentVersionDebug';
import { buildVersionTimeline, type VersionTimelineEntry } from './lib/versionTimeline';
import { analyzePdfTextLayer } from './lib/pdfTextLayer';
import type {
  AppMode,
  ChatMessage,
  ParsedIntent,
  ProcessingState,
  SignatureField,
  VersionNode,
  ToolName,
  ConversationListItem,
  WorkspaceSessionDocumentRef,
  WorkspaceSessionSnapshot,
} from './types';
import { isTitleMarkerField, signingFieldSizeForField } from './types';

const DRAFT_LATENCY_LOG_ENABLED =
  import.meta.env.DEV || import.meta.env.VITE_DRAFT_LATENCY_LOGS === '1';
const HYDRATION_DEBUG_LOG_ENABLED =
  import.meta.env.DEV || import.meta.env.VITE_HYDRATION_DEBUG === '1';

function draftLatencyLog(phase: string, data: Record<string, unknown> = {}) {
  if (!DRAFT_LATENCY_LOG_ENABLED) return;
  console.info(`[DraftLatency] FE ${phase}`, data);
}

function hydrationDebugLog(message: string, data: Record<string, unknown> = {}) {
  if (!HYDRATION_DEBUG_LOG_ENABLED) return;
  console.log(message, data);
}

const CLAUSE_FLAGS_LOG_ENABLED =
  import.meta.env.DEV || import.meta.env.VITE_CLAUSE_FLAGS_DEBUG === '1';

function clauseFlagsLog(phase: string, data: Record<string, unknown> = {}) {
  if (!CLAUSE_FLAGS_LOG_ENABLED) return;
  console.info(`[ClauseFlags] ${phase}`, data);
}

type ClausePatchKind = 'replace' | 'insertBefore' | 'insertAfter' | 'delete';

type ClausePatch = {
  suggestionId: string;
  kind: ClausePatchKind;
  originalText?: string;
  proposedText?: string;
  anchorText?: string;
};

const MISSING_CLAUSE_EVIDENCE = '(clause not found in document)';

function buildClausePatchFromSuggestion(suggestion: import('./types/review').ReviewSuggestion): ClausePatch {
  const originalText = suggestion.originalText?.trim();
  const proposedText = suggestion.proposedText?.trim();
  const anchorText = [suggestion.sectionTitle, suggestion.sectionRef]
    .map((part) => part?.trim())
    .find((part) => part && part !== 'N/A' && part !== '-');

  if (suggestion.category === 'deletion') {
    return { suggestionId: suggestion.id, kind: 'delete', originalText };
  }

  if (suggestion.category === 'addition' || originalText === MISSING_CLAUSE_EVIDENCE) {
    return { suggestionId: suggestion.id, kind: 'insertAfter', proposedText, anchorText };
  }

  return { suggestionId: suggestion.id, kind: 'replace', originalText, proposedText };
}

function buildHtmlEditActionFromReviewSuggestion(suggestion: import('./types/review').ReviewSuggestion): HtmlEditAction {
  const patch = buildClausePatchFromSuggestion(suggestion);
  const base = {
    id: suggestion.id,
    label: suggestion.summary || suggestion.sectionTitle || 'Apply clause suggestion',
    explanation: suggestion.rationale,
  };
  if (patch.kind === 'delete') {
    return { ...base, kind: 'delete', originalText: patch.originalText ?? '' };
  }
  if (patch.kind === 'insertBefore' || patch.kind === 'insertAfter') {
    return {
      ...base,
      kind: patch.kind,
      anchorText: patch.anchorText ?? '',
      newText: patch.proposedText ?? '',
    };
  }
  return {
    ...base,
    kind: 'replace',
    originalText: patch.originalText ?? '',
    newText: patch.proposedText ?? '',
  };
}

function applyClausePatch(text: string, patch: ClausePatch): { text: string; applied: boolean; reason?: string } {
  if (patch.kind === 'replace') {
    if (!patch.originalText) return { text, applied: false, reason: 'Missing original text for replacement.' };
    if (!patch.proposedText) return { text, applied: false, reason: 'Missing proposed replacement text.' };
    const index = text.indexOf(patch.originalText);
    if (index < 0) return { text, applied: false, reason: 'Original text was not found in the current document.' };
    return {
      text: `${text.slice(0, index)}${patch.proposedText}${text.slice(index + patch.originalText.length)}`,
      applied: true,
    };
  }

  if (patch.kind === 'delete') {
    if (!patch.originalText) return { text, applied: false, reason: 'Missing original text for deletion.' };
    const index = text.indexOf(patch.originalText);
    if (index < 0) return { text, applied: false, reason: 'Text to delete was not found in the current document.' };
    return {
      text: `${text.slice(0, index)}${text.slice(index + patch.originalText.length)}`,
      applied: true,
    };
  }

  if (patch.kind === 'insertBefore' || patch.kind === 'insertAfter') {
    if (!patch.proposedText) return { text, applied: false, reason: 'Missing proposed insertion text.' };
    if (!patch.anchorText) return { text, applied: false, reason: 'Missing anchor text for insertion.' };
    const index = text.indexOf(patch.anchorText);
    if (index < 0) return { text, applied: false, reason: 'Anchor text was not found in the current document.' };
    const insertAt = patch.kind === 'insertBefore' ? index : index + patch.anchorText.length;
    const spacerBefore = insertAt > 0 && !text.slice(0, insertAt).endsWith('\n\n') ? '\n\n' : '';
    const spacerAfter = !text.slice(insertAt).startsWith('\n\n') ? '\n\n' : '';
    return {
      text: `${text.slice(0, insertAt)}${spacerBefore}${patch.proposedText}${spacerAfter}${text.slice(insertAt)}`,
      applied: true,
    };
  }

  return { text, applied: false, reason: 'Unsupported patch kind.' };
}

function applyClausePatches(
  text: string,
  patches: ClausePatch[],
): { text: string; appliedIds: string[]; failures: Array<{ suggestionId: string; reason: string; patch: ClausePatch }> } {
  let revised = text;
  const appliedIds: string[] = [];
  const failures: Array<{ suggestionId: string; reason: string; patch: ClausePatch }> = [];

  for (const patch of patches) {
    const result = applyClausePatch(revised, patch);
    if (result.applied) {
      revised = result.text;
      appliedIds.push(patch.suggestionId);
    } else {
      failures.push({ suggestionId: patch.suggestionId, reason: result.reason ?? 'Patch did not apply.', patch });
    }
  }

  return { text: revised, appliedIds, failures };
}

const API_FILE_ID_RE = /\/api\/file\/([^/?#]+)/;

function extractApiFileIdFromUrl(url?: string | null): string | null {
  const value = typeof url === 'string' ? url.trim() : '';
  if (!value) return null;
  const match = value.match(API_FILE_ID_RE);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * Synthetic ids do not necessarily map to a fetchable `/api/file/:id`.
 * Use `isFileStorageBackedFileId(id, downloadUrl)` when a URL is available:
 * non-ObjectId `doc-*` ids may map to `/api/file/...`; `doc-{24 hex}` is an
 * in-conversation HTML document (`/api/conversations/:cid/documents/:id`).
 */
function isSyntheticDraftFileId(fileId?: string | null): boolean {
  if (typeof fileId !== 'string') return false;
  return fileId.startsWith('draft-') || fileId.startsWith('doc-');
}

function isLegacyDraftShellFileId(fileId?: string | null): boolean {
  return typeof fileId === 'string' && fileId.startsWith('draft-');
}

function isApiFileUrl(url?: string | null): boolean {
  return Boolean(extractApiFileIdFromUrl(url));
}

function isConversationDocumentUrl(url?: string | null): boolean {
  const value = typeof url === 'string' ? url.trim() : '';
  return /\/api\/conversations\/[^/?#]+\/documents\/[^/?#]+/.test(value);
}

/** Align with BE `conversationSessionService`: subdocument ids from Mongo, not `/api/file/` blobs. */
function isConversationMongoDocumentFileId(fileId?: string | null): boolean {
  return typeof fileId === 'string' && /^doc-[0-9a-f]{24}$/i.test(fileId.trim());
}

function isClauseReviewRunnableSourceId(fileId?: string | null): boolean {
  return typeof fileId === 'string' && fileId.trim().length > 0 && !isLegacyDraftShellFileId(fileId);
}

function clauseReviewSourceKindForId(fileId: string): 'file' | 'document' {
  return isConversationMongoDocumentFileId(fileId) ? 'document' : 'file';
}

function conversationDocumentDownloadPath(
  fileId: string | undefined | null,
  conversationId: string | undefined | null,
): string | null {
  if (!isConversationMongoDocumentFileId(fileId) || !conversationId?.trim()) return null;
  return `/api/conversations/${conversationId.trim()}/documents/${fileId!.trim().slice(4)}`;
}

function isConversationDocumentRef(ref?: {
  fileId?: string | null;
  downloadUrl?: string | null;
  kind?: string | null;
  documentId?: string | null;
} | null): boolean {
  if (!ref) return false;
  return (
    ref.kind === 'document' ||
    Boolean(ref.documentId) ||
    isConversationDocumentUrl(ref.downloadUrl)
  );
}

function isFileStorageBackedFileId(fileId?: string | null, downloadUrl?: string | null): boolean {
  if (typeof fileId !== 'string' || fileId.trim().length === 0) return false;
  if (fileId.startsWith('draft-')) return false;
  if (isConversationMongoDocumentFileId(fileId)) return false;
  if (fileId.startsWith('doc-')) return isApiFileUrl(downloadUrl);
  return true;
}

/** Fingerprint so a background PDF mirror can be validated against live draft HTML. */
function draftHtmlMirrorDigest(html: string): string {
  const t = html.trim();
  if (!t) return '0:0';
  let h = 2166136261;
  for (let i = 0; i < t.length; i += 1) {
    h ^= t.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `${t.length}:${h >>> 0}`;
}

function documentVersionDownloadUrl(version?: { fileId?: string | null; conversationId?: string | null } | null, fallbackConversationId?: string | null): string | null {
  const fileId = version?.fileId?.trim();
  if (!fileId) return null;
  if (isConversationMongoDocumentFileId(fileId)) {
    const conversationId = version?.conversationId || fallbackConversationId;
    const documentId = fileId.slice(4);
    return conversationId && documentId
      ? `/api/conversations/${conversationId}/documents/${documentId}`
      : null;
  }
  return `/api/file/${fileId}`;
}

function fileExtensionForDisplay(fileName?: string | null, mimeType?: string | null): string {
  const fileExt = fileName?.match(/\.([a-z0-9]{2,8})$/i)?.[1]?.toLowerCase();
  if (fileExt) return fileExt === 'doc' ? 'docx' : fileExt;
  const mime = (mimeType ?? '').toLowerCase();
  if (mime.includes('html')) return 'html';
  if (mime.includes('pdf')) return 'pdf';
  if (mime.includes('word') || mime.includes('officedocument')) return 'docx';
  if (mime.includes('text')) return 'txt';
  return 'docx';
}

function isStaleGeneratedDisplayName(fileName?: string | null, source?: string | null): boolean {
  const base = (fileName ?? '')
    .trim()
    .replace(/\u2026/g, '...')
    .replace(/\.[a-z0-9]{2,8}$/i, '')
    .trim()
    .toLowerCase();
  if (!base) return false;
  if (/^generating[\s.]*$/.test(base)) return true;
  return base === 'untitled document'
    && (source === 'generated' || source === 'ai_edit' || source === 'workflow' || source === 'conversion');
}

function documentNameFromProjectTitle(title?: string | null, ext = 'docx'): string {
  const raw = (title ?? '').trim();
  const base = raw
    .replace(/^\s*(draft|write|compose|create|generate|make)\s+(an?\s+|the\s+)?/i, '')
    .replace(/\s+/g, ' ')
    .replace(/[.\u2026]+$/g, '')
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .trim()
    || raw
    || 'Document';

  return /\.[a-z0-9]{2,8}$/i.test(base) ? base : `${base}.${ext}`;
}

function displayFileNameForProjectVersion(projectTitle: string, version?: { fileName?: string | null; mimeType?: string | null; source?: string | null } | null): string | undefined {
  if (!version?.fileName) return undefined;
  if (!isStaleGeneratedDisplayName(version.fileName, version.source)) return version.fileName;
  return documentNameFromProjectTitle(projectTitle, fileExtensionForDisplay(version.fileName, version.mimeType));
}

type DocIntent = 'doc_edit' | 'doc_continue' | 'doc_question' | 'new_draft' | 'plain_chat';

function classifyDocIntent(message: string): DocIntent {
  const raw = message.trim();
  const lower = raw.toLowerCase();
  if (!raw) return 'plain_chat';

  if (/^(?:ok(?:ay)?|thanks?|thank you|got it|sounds good|cool|nice|great|perfect|yes|no)[.!?\s]*$/i.test(raw)) {
    return 'plain_chat';
  }

  if (
    /\?$/.test(raw) ||
    /^(?:what|why|how|where|when|who|which|does|do|did|is|are|can you explain|can you summarize|tell me|show me)\b/i.test(raw) ||
    /\b(?:what is|what's|summarize|explain|review|analyze|compare|is there|does this|do we have)\b/i.test(lower)
  ) {
    return 'doc_question';
  }

  if (
    /^(?:continue|keep going|go on|carry on|resume|finish|complete)(?:\b|[.!?])/i.test(raw) ||
    /\b(?:continue|keep going|finish|complete|resume)\b.{0,80}\b(?:draft|document|agreement|contract|section|clause|paragraph|last section|remaining sections)\b/i.test(lower) ||
    /\bwrite\s+(?:section|clause|paragraph|article)\s+\d+\b/i.test(lower) ||
    /\b(?:finish|complete)\s+(?:the\s+)?(?:indemnification|termination|confidentiality|notices|payment|remaining|last)\s+(?:section|clause|paragraph)\b/i.test(lower)
  ) {
    return 'doc_continue';
  }

  if (
    /\b(?:instead of|change|edit|rewrite|replace|update|revise|modify|insert|remove|delete|set)\b/i.test(lower) ||
    /\badd\s+(?:a|an|the)?\s*(?:clause|section|paragraph|sentence|provision|term|signature|field|notice|confidentiality|indemnification|termination)\b/i.test(lower) ||
    /\bmake\s+(?:the\s+)?(?:seller|buyer|employee|employer|client|contractor|party|parties|name|price|amount|date|address|term|notice period)\b\s+\S+/i.test(lower) ||
    /\b(?:seller|buyer|employee|employer|client|contractor|party|name|price|amount|date|address|term|notice period)\b\s*(?:is|=|:|to|as|with)\s+\S+/i.test(raw) ||
    /[\[{<][^\]}>]{2,120}[\]}>]\s*(?:is|=|:|to|as|with)\s+\S+/i.test(raw)
  ) {
    return 'doc_edit';
  }

  if (
    /^(?:now\s+)?(?:write|draft|compose|create|generate|make)\s+(?:me\s+)?(?:a|an|the)?\s*.+\b(?:agreement|contract|letter|document|memo|proposal|nda|policy|invoice|receipt|template|brief|report)\b/i.test(raw)
  ) {
    return 'new_draft';
  }

  return 'plain_chat';
}

function docIntentAction(intent: DocIntent): 'edit' | 'continue' | 'qa' | 'chat' | 'new-draft' {
  if (intent === 'doc_edit') return 'edit';
  if (intent === 'doc_continue') return 'continue';
  if (intent === 'doc_question') return 'qa';
  if (intent === 'new_draft') return 'new-draft';
  return 'chat';
}

function buildContinueDraftInstruction(message: string): string {
  const trimmed = message.trim();
  if (/^(?:continue|keep going|go on|carry on|resume)[.!?\s]*$/i.test(trimmed)) {
    return 'Continue drafting from where the current document stops. Preserve the existing text and structure, complete any unfinished section, and return the full updated HTML document.';
  }
  return `${trimmed}\n\nTreat this as a continuation request for the current draft, not a tracked edit. Preserve the existing text and return the full updated HTML document.`;
}

function isTransientDocumentUrl(url?: string | null): boolean {
  const value = typeof url === 'string' ? url.trim().toLowerCase() : '';
  return !value || value.startsWith('blob:') || value.startsWith('data:') || value.startsWith('draft://');
}

function projectVersionTreeStorageKey(projectId?: string | null, fallbackTitle?: string | null): string {
  return `corner.versionTree.${projectId || fallbackTitle || 'workspace'}`;
}

function readHiddenProjectVersionIds(projectId?: string | null, fallbackTitle?: string | null): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const raw = window.localStorage.getItem(projectVersionTreeStorageKey(projectId, fallbackTitle));
    if (!raw) return new Set();
    const parsed = JSON.parse(raw) as { hiddenVersionIds?: unknown };
    return new Set(
      Array.isArray(parsed.hiddenVersionIds)
        ? parsed.hiddenVersionIds.filter((id): id is string => typeof id === 'string')
        : [],
    );
  } catch {
    return new Set();
  }
}

/** Map tool name to a short label for history (e.g. "PDF → Word", "Compressed"). */
function toolToFriendlyOperation(tool: ToolName): string {
  const map: Record<string, string> = {
    pdf_to_word: 'PDF → Word',
    pdf_to_excel: 'PDF → Excel',
    pdf_to_pptx: 'PDF → PowerPoint',
    pdf_to_jpg: 'PDF → JPG',
    pdf_to_png: 'PDF → PNG',
    word_to_pdf: 'Word → PDF',
    excel_to_pdf: 'Excel → PDF',
    pptx_to_pdf: 'PowerPoint → PDF',
    jpg_to_pdf: 'Image → PDF',
    png_to_pdf: 'Image → PDF',
    merge_pdf: 'Merged',
    split_pdf: 'Split',
    compress_pdf: 'Compressed',
    rotate_pdf: 'Rotated',
    repair_pdf: 'Repaired',
    add_page_numbers: 'Page numbers',
    password_protect_pdf: 'Password protected',
    remove_pdf_password: 'Password removed',
    add_watermark_pdf: 'Watermarked',
    ocr: 'OCR',
    fill_pdf_form: 'Form filled',
    esign: 'Signed',
    remove_background: 'Background removed',
    resize_image: 'Resized',
    crop_image: 'Cropped',
    flip_rotate_image: 'Flip/rotate',
    add_border_image: 'Border added',
    watermark_image: 'Watermarked',
    image_to_pdf: 'Image → PDF',
    compress_image: 'Compressed',
    convert_image: 'Converted',
    jpg_to_png: 'Converted',
    png_to_jpg: 'Converted',
    webp_to_jpg: 'Converted',
    jpg_to_webp: 'Converted',
    csv_to_excel: 'CSV → Excel',
    excel_to_csv: 'Excel → CSV',
    generate_qr: 'QR code',
    extract_text: 'Text extracted',
    extract_images: 'Images extracted',
    extract_tables: 'Tables extracted',
    summarize_document: 'Summary',
    generate_study_questions: 'Study questions',
    extract_key_terms: 'Key terms',
    generate_citation: 'Citation',
  };
  return map[tool] ?? 'Processed';
}

function isOpenDocumentMutationPrompt(text: string): boolean {
  const lower = text.toLowerCase();
  if (/\b(sign|signature|summarize|explain|review|analyze|compare|what|why|how|where)\b/.test(lower)
    && !/\b(change|edit|rewrite|replace|update|revise|modify|insert|remove|delete|fill|complete|populate|set|make)\b/.test(lower)) {
    return false;
  }

  const mutationVerb = /\b(change|edit|rewrite|replace|update|revise|modify|insert|remove|delete|fill|complete|populate|set|make)\b/.test(lower);
  const documentTarget = /\b(seller|buyer|party|parties|price|purchase price|payment|date|address|name|placeholder|blank|field|bracket|section|clause|paragraph|sentence|wording|text|term|terms|amount|closing)\b/.test(lower);
  const directAssignment = /\b(seller|buyer|purchase price|price|payment|closing date|effective date|address|name)\b\s*(?:name\s*)?(?:is|=|:|to|as|with)\s+\S+/i.test(text);
  const bracketFill = /[\[{<][^\]}>]{2,120}[\]}>]\s*(?:is|=|:|to|as|with)\s+\S+/i.test(text);
  return (mutationVerb && documentTarget) || directAssignment || bracketFill;
}

/** Derive a greeting-style name from the email local-part for signing API `name`. */
function guessNameFromEmail(email: string): string {
  const local = email.split('@')[0] ?? '';
  const cleaned = local.replace(/[._-]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!cleaned) return 'Signer';
  return cleaned
    .split(/\s+/)
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .filter(Boolean)
    .join(' ');
}

/** Tools whose results should render as an ImageResultCard in the chat panel. */
const IMAGE_TOOL_NAMES = new Set<string>([
  'remove_background', 'resize_image', 'crop_image', 'flip_rotate_image',
  'add_border_image', 'watermark_image', 'compress_image', 'convert_image',
  'jpg_to_png', 'png_to_jpg', 'webp_to_jpg', 'jpg_to_webp',
]);

type ESignFlowStatus = 'signed' | 'needs_signature_setup' | 'no_fields_found';
type ESignExecuteResponse = ToolResult & {
  status?: ESignFlowStatus;
  fields?: SignatureField[];
  selfFields?: SignatureField[];
  otherFields?: SignatureField[];
  message?: string;
};

// Drop AI-detected fields whose confidence is too low to render
// without misleading the user. Detections without a confidence value
// are presumed reliable (deterministic regex / anchor matches do
// not carry one). Manually-placed fields go through other code paths
// that bypass this filter entirely.
const AI_DETECTION_MIN_CONFIDENCE = 0.5;
function filterConfidentDetections(fields: SignatureField[]): SignatureField[] {
  return fields.filter((f) =>
    typeof f.confidence !== 'number' || f.confidence >= AI_DETECTION_MIN_CONFIDENCE,
  );
}

function prepareDetectedFieldsForReview(fields: SignatureField[]): SignatureField[] {
  return filterConfidentDetections(fields).map((field) => {
    const isOther = field.party === 'other' || field.signer === 'other';
    const { signedAt: _signedAt, ...rest } = field;
    return {
      ...rest,
      placed: false,
      party: isOther ? 'other' : 'self',
      signer: isOther ? 'other' : 'you',
      state: 'suggested',
    };
  });
}

/** Align viewer markers with persisted signing-request rows (`placed` / `signedAt`). */
function normalizeSigningFieldsFromSigningDetail(fields: SignatureField[]): SignatureField[] {
  return fields.map((f) => {
    const placed = !!(f.placed || f.signedAt);
    return {
      ...f,
      placed,
      state: placed ? ('signed' as const) : (f.state ?? 'suggested'),
    };
  });
}

/**
 * `GET /api/signing/:id` returns recipient-owned rects only — merge into existing
 * workspace fields so owner's self markers stay on screen after Send.
 */
function mergeSigningDetailIntoEsignFields(prev: SignatureField[], serverRows: SignatureField[]): SignatureField[] {
  if (!serverRows.length) return prev;
  if (!prev.length) {
    return normalizeSigningFieldsFromSigningDetail(serverRows);
  }
  const byId = new Map<string, SignatureField>();
  for (const r of serverRows) {
    if (r.id) byId.set(r.id, r);
  }
  return prev.map((f) => {
    const srv = f.id ? byId.get(f.id) : undefined;
    if (!srv) return f;
    const placed = !!(srv.placed || srv.signedAt);
    return {
      ...f,
      ...srv,
      placed,
      state: placed ? ('signed' as const) : (srv.state ?? f.state ?? 'suggested'),
    };
  });
}

const MANUAL_SIGNING_INSTRUCTIONS =
  'Use the signing guide in chat to choose Signature, Date, Name, Initial, or Title, then click the document where each field belongs. When placement looks right, use **Apply changes** in the guide, then add recipients and send.';

const INTERRUPT_CONTEXT_MAX_CHARS = 2800;

function truncateForInterruptContext(body: string, maxChars: number): string {
  const t = body.trim();
  if (t.length <= maxChars) return t;
  return `${t.slice(0, Math.max(0, maxChars - 1))}…`;
}

function v2TaskProgressSummary(snapshot: AgentRunSnapshot): string | null {
  const tasks = snapshot.tasks ?? [];
  if (!tasks.length) return null;
  const lines = tasks.map((task, i) => {
    const st =
      task.status === 'completed' ? 'done'
      : task.status === 'failed' ? 'failed'
      : task.status === 'running' ? 'in progress'
      : task.status === 'retry_pending' ? 'retry pending'
      : task.status === 'skipped' ? 'skipped'
      : task.status === 'cancelled' ? 'cancelled'
      : 'pending';
    return `${i + 1}. ${task.label} — ${st}`;
  });
  return lines.join('\n');
}

function v1StepProgressSummary(steps: CoTStepData[]): string | null {
  if (!steps?.length) return null;
  const lines = steps.map((step, i) => {
    const st =
      step.status === 'done' ? 'done'
      : step.status === 'error' ? 'failed'
      : step.status === 'active' ? 'in progress'
      : step.status === 'pending' ? 'not started'
      : step.status === 'skipped' ? 'skipped'
      : step.status === 'blocked' ? 'blocked'
      : step.status === 'confirm' ? 'awaiting confirmation'
      : step.status === 'clarify' ? 'needs clarification'
      : String(step.status);
    return `${i + 1}. ${step.title} — ${st}`;
  });
  return lines.join('\n');
}

/** Persisted assistant note so the next turn can resume after Stop (V1 orchestrate or V2 query run). */
function buildStoppedContinuationAssistantMessage(input: {
  workLabel?: string | null;
  v2Snapshot: AgentRunSnapshot | null;
  v2BootContext?: string;
  v1UserPrompt: string;
  cotSteps: CoTStepData[];
  cotUnderstanding?: string;
}): string {
  const parts: string[] = [];
  const label = input.workLabel?.trim();
  parts.push(
    label
      ? `Stopped — paused while ${label.toLowerCase()}.`
      : 'Stopped — work was interrupted.',
  );
  parts.push(
    'The document and chat history are unchanged. When you are ready, say **continue** or restate the goal and I will pick up from here.',
  );

  const fromSnap =
    input.v2Snapshot ? formatQueryRunContextFromSnapshot(input.v2Snapshot) : undefined;
  const fromBoot = input.v2BootContext?.trim();
  const v2Header = fromSnap || fromBoot;

  if (v2Header) {
    parts.push(`**Where we were:**\n${truncateForInterruptContext(v2Header, INTERRUPT_CONTEXT_MAX_CHARS)}`);
    if (input.v2Snapshot) {
      const prog = v2TaskProgressSummary(input.v2Snapshot);
      if (prog) parts.push(`**Progress:**\n${prog}`);
    }
  } else {
    const req = input.v1UserPrompt?.trim();
    if (req) {
      parts.push(`**Your last request:**\n${truncateForInterruptContext(req, 1200)}`);
    }
    const und = input.cotUnderstanding?.trim();
    if (und) {
      parts.push(
        `**Corner's working plan:**\n${truncateForInterruptContext(und, INTERRUPT_CONTEXT_MAX_CHARS)}`,
      );
    }
    const prog = v1StepProgressSummary(input.cotSteps);
    if (prog) parts.push(`**Progress:**\n${prog}`);
  }

  return parts.join('\n\n');
}

function buildPersistedCotSteps(steps: CoTStepData[] | undefined | null): CoTStepData[] {
  const allowedStatuses = new Set(['done', 'active', 'pending', 'error', 'skipped', 'blocked', 'confirm', 'clarify']);
  return (Array.isArray(steps) ? steps : [])
    .filter((step) => step && step.editBatch)
    .slice(-20)
    .map((step) => ({
      ...step,
      status: allowedStatuses.has(String(step.status)) ? step.status : 'done',
    }));
}

function editBatchCountFromCotSteps(steps: CoTStepData[] | undefined | null): number {
  return (Array.isArray(steps) ? steps : []).filter((step) => step?.editBatch).length;
}

/** Desktop notification when a draft finishes while the user is elsewhere or the tab is hidden. */
function notifyDraftReadyDesktop(title: string, conversationId: string) {
  if (typeof window === 'undefined' || typeof Notification === 'undefined') return;
  if (Notification.permission === 'denied') return;
  const body = title.trim() || 'Your document finished generating.';
  const fire = () => {
    try {
      new Notification('Draft ready', { body, tag: `corner-draft-${conversationId}` });
    } catch {
      /* ignore */
    }
  };
  if (Notification.permission === 'granted') fire();
  else if (Notification.permission === 'default') {
    void Notification.requestPermission().then((p) => {
      if (p === 'granted') fire();
    });
  }
}

export default function App() {
  // ⚠️  CRITICAL — auth context MUST be the first hook call in this component.
  // Vite's production bundler (Rollup) scope-hoists all `const` declarations into
  // a single flat scope.  If any useCallback/useMemo above this line references
  // `auth` in its dependency array, the production build will crash with a TDZ
  // error ("Cannot access 'X' before initialization") that does NOT appear in dev.
  // See: https://github.com/vitejs/vite/issues/5765
  const auth = useAuthContext();
  const isMobile = useIsMobile();
  const viewportWidth = useViewportWidth();
  const [mode, setMode] = useState<AppMode>('empty');
  const modeRef = useRef(mode);
  useEffect(() => { modeRef.current = mode; }, [mode]);
  const [currentFiles, setCurrentFiles] = useState<File[]>([]);
  const currentFile = currentFiles[0] ?? null;
  const [currentResult, setCurrentResult] = useState<ToolResult | null>(null);
  const [draftContent, setDraftContent] = useState<string>('');
  const draftContentRef = useRef('');
  const draftStreamAbortRef = useRef<AbortController | null>(null);
  const draftStatusRef = useRef<'idle' | 'pending' | 'streaming' | 'complete' | 'failed'>('idle');
  // Conversation id whose draft SSE is in flight (null = none). Scoped so
  // navigating to another chat doesn't show the wrong streaming UI or block
  // autosave for unrelated workspaces.
  const [draftStreamingConversationId, setDraftStreamingConversationId] = useState<string | null>(null);
  const draftStreamingConversationIdRef = useRef<string | null>(draftStreamingConversationId);
  useEffect(() => {
    draftStreamingConversationIdRef.current = draftStreamingConversationId;
  }, [draftStreamingConversationId]);
  type WorkspaceRunKind = 'draft-stream' | 'draft-revise';
  type WorkspaceRunOwner = {
    kind: WorkspaceRunKind;
    ownerConversationId: string | null;
    seq: number;
  };
  const workspaceRunSeqRef = useRef(0);
  const activeWorkspaceRunRef = useRef<WorkspaceRunOwner | null>(null);
  const beginWorkspaceRun = useCallback((kind: WorkspaceRunKind, ownerConversationId: string | null): WorkspaceRunOwner => {
    const run = {
      kind,
      ownerConversationId,
      seq: workspaceRunSeqRef.current + 1,
    };
    workspaceRunSeqRef.current = run.seq;
    activeWorkspaceRunRef.current = run;
    return run;
  }, []);
  const isWorkspaceRunCurrent = useCallback((run: WorkspaceRunOwner): boolean => {
    const active = activeWorkspaceRunRef.current;
    return Boolean(
      active
      && active.seq === run.seq
      && active.kind === run.kind
      && String(active.ownerConversationId ?? '') === String(run.ownerConversationId ?? ''),
    );
  }, []);
  const finishWorkspaceRun = useCallback((run: WorkspaceRunOwner) => {
    if (isWorkspaceRunCurrent(run)) {
      activeWorkspaceRunRef.current = null;
    }
  }, [isWorkspaceRunCurrent]);
  useEffect(() => {
    draftContentRef.current = draftContent;
  }, [draftContent]);
  const [draftTitle, setDraftTitle] = useState<string>('Untitled Document');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationDocuments, setConversationDocuments] = useState<ConversationDocument[]>([]);
  const [audioDurationLabel, setAudioDurationLabel] = useState<string | null>(null);
  /** Paper 5BL — "2048×2560 JPG" patched onto system line when a single image loads */
  const [imageChatMetaLabel, setImageChatMetaLabel] = useState<string | null>(null);
  /** Paper 5ZD — PDF with little extractable text (scanned / image pages) */
  const [likelyScannedPdf, setLikelyScannedPdf] = useState(false);
  /** Paper 667 — Generate QR selected from tools: split view + chat strip without a file */
  const [utilityChatTool, setUtilityChatTool] = useState<'generate_qr' | null>(null);
  const messagesRef = useRef<ChatMessage[]>([]);
  messagesRef.current = messages;
  const [messagesHasMore, setMessagesHasMore] = useState(false);
  const [loadingEarlierMessages, setLoadingEarlierMessages] = useState(false);
  const loadingEarlierRef = useRef(false);
  const [processing, setProcessing] = useState<ProcessingState | null>(null);
  const processingRef = useRef<ProcessingState | null>(processing);
  useEffect(() => { processingRef.current = processing; }, [processing]);
  const cot = useCoTState();
  /** Latest CoT steps for tool-result handlers (avoid stale step indices when attaching edit batches). */
  const cotStepsRef = useRef(cot.state.steps);
  cotStepsRef.current = cot.state.steps;
  const [activeQueryRunId, setActiveQueryRunId] = useState<string | null>(null);
  const activeQueryRunIdRef = useRef<string | null>(activeQueryRunId);
  useEffect(() => { activeQueryRunIdRef.current = activeQueryRunId; }, [activeQueryRunId]);
  // Hoisted from the V2 terminal effect so clearWorkspaceSessionState
  // (defined above the effect) can reset it on conversation switches /
  // workspace clears — otherwise a stale ref blocks the next genuine
  // terminal handoff for the same runId.
  const queryRunTerminalRef = useRef<string | null>(null);
  const queryRun = useQueryRunState({ runId: activeQueryRunId });
  /** V2 placeholder: document + prompt before first snapshot GET returns. */
  const [queryRunBootContext, setQueryRunBootContext] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (queryRun.snapshot) setQueryRunBootContext(undefined);
  }, [queryRun.snapshot]);
  const review = useReviewState();
  // ── Clause Review state ──────────────────────────────────────────
  const [clauseReviewSetupOpen, setClauseReviewSetupOpen] = useState(false);
  const [clauseReviewLane, setClauseReviewLane] = useState<'real_estate' | 'founder_operator' | null>(null);
  const [clauseReviewJurisdiction, setClauseReviewJurisdiction] = useState('');
  const [clauseReviewResult, setClauseReviewResult] = useState<ClauseReviewSummary | null>(null);
  const [clauseReviewLoading, setClauseReviewLoading] = useState(false);
  const [clauseReviewApplyBusy, setClauseReviewApplyBusy] = useState(false);
  /** Set synchronously when opening setup from project Clause flags tab so Run Review sees the right file before React commits currentResult. */
  const clauseReviewPendingSourceRef = useRef<{ fileId: string; title: string; sourceKind: 'file' | 'document' } | null>(null);
  const isClauseReview = clauseReviewResult != null && mode === 'review';
  // ── Source preview drawer state ─────────────────────────────────────
  const [sourcePreview, setSourcePreview] = useState<{
    result: ClauseFlagLocateResult;
    sectionRef: string;
    sectionTitle: string;
  } | null>(null);
  const [sourcePreviewLoading, setSourcePreviewLoading] = useState(false);
  // ── Inline clause flag popover state ────────────────────────────────
  // Lightweight "Open in doc" target for IssuesPanel rows. We do NOT
  // enter global review mode for this entry point — it would swap the
  // chat column for ReviewPanel cards, hide the chat, replace the doc
  // pagination bar with ReviewModeBar, AND open the SourcePreviewDrawer
  // overlay all at once, which destroys the layout the user expects
  // when they're just trying to inspect a single flag.
  //
  // Instead we store the flag + located result here and render a
  // small floating popover anchored to the doc column, with
  // Accept / Dismiss / Locate / Close buttons. The chat panel and
  // doc viewer stay in their normal layout.
  const [inlineClauseFlagPopover, setInlineClauseFlagPopover] = useState<{
    reviewId: string;
    flag: import('@corner/shared').ClauseFlag;
    located: ClauseFlagLocateResult | null;
    versionId: string;
    fileId: string;
  } | null>(null);
  const [inlineClauseFlagBusy, setInlineClauseFlagBusy] = useState(false);
  // Cleanup effects for inlineClauseFlagPopover live further down in
  // the function body, after currentVersionId / projectWorkspaceTab /
  // currentConversationId are declared (the variables they depend on).
  // ── Per-flag grounding status (populated as user clicks "Locate") ───
  const [flagGroundingStatuses, setFlagGroundingStatuses] = useState<Record<string, import('@corner/shared').FlagGroundingStatus>>({});
  // ── Risk summary state (for contract_review analysis badge) ─────────
  const [riskSummary, setRiskSummary] = useState<AnalysisResultData['riskSummary']>(null);
  const riskSummaryFetchedRef = useRef<string | null>(null);

  const dismissClauseReviewSetup = useCallback(() => {
    clauseReviewPendingSourceRef.current = null;
    setClauseReviewSetupOpen(false);
    setClauseReviewLane(null);
    setClauseReviewJurisdiction('');
  }, []);

  const openClauseReviewSetup = useCallback(() => {
    setClauseReviewLane((cur) => (
      cur
      ?? (riskSummary?.lane === 'real_estate' ? 'real_estate'
        : riskSummary?.lane === 'founder_operator' ? 'founder_operator'
        : null)
    ));
    setClauseReviewSetupOpen(true);
  }, [riskSummary?.lane]);
  // ── Batch risk data for sidebar + recent panel items ──────────────────
  const [historyRiskData, setHistoryRiskData] = useState<Record<string, { nudge: 'attention' | 'reconsider'; highCount: number; medCount: number }>>({});
  const historyRiskFetchedRef = useRef<string>('');
  const [rateLimitInfo, setRateLimitInfo] = useState<RateLimitInfo | null>(null);
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [rightOpen, setRightOpen] = useState(false); // default collapsed; opposite of left panel
  const [toolPanelExpanded, setToolPanelExpanded] = useState(false);
  const [activeToolIndex, setActiveToolIndex] = useState(0);
  const [panelSettings, setPanelSettings] = useState<RightPanelSettings>({});
  const [lastTool, setLastTool] = useState<ToolName | null>(null);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [proSuccessOpen, setProSuccessOpen] = useState(false);
  const [linearEditIssueId, setLinearEditIssueId] = useState<string | null>(null);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  // ── Collaboration state ──
  const [collabTab, setCollabTab] = useState<'analysis' | 'comments'>('analysis');
  const [commentPins, setCommentPins] = useState<CommentPinData[]>([]);
  const insertedPinIds = useRef(new Set<string>());
  const [activeCommentPinId, setActiveCommentPinId] = useState<string | null>(null);
  const [commentMode, setCommentMode] = useState(false);
  const [readerView, setReaderView] = useState(false);
  const [editHighlightAnchor, setEditHighlightAnchor] = useState<string | null>(null);
  const [documentText, setDocumentText] = useState<{
    paragraphs: string[];
    loading: boolean;
    error?: string | null;
    blockAnchors?: { page: number; paragraphIndex: number }[];
  } | null>(null);
  const [pendingComment, setPendingComment] = useState<{ xPercent: number; yPercent: number; pageNumber: number; quote?: string } | null>(null);
  const [pendingSelectedSnippet, setPendingSelectedSnippet] = useState<{ text: string; page?: number; paragraphIndex?: number } | null>(null);

  const [fileBrowserSource, setFileBrowserSource] = useState<string | null>(null);
  const [fileBrowserFiles, setFileBrowserFiles] = useState<import('./components/app/IntegrationFileBrowserModal').BrowseFile[]>([]);
  const [fileBrowserBreadcrumb, setFileBrowserBreadcrumb] = useState<string[]>([]);
  const [fileBrowserPath, setFileBrowserPath] = useState<string>('/');
  const [fileBrowserLoading, setFileBrowserLoading] = useState(false);

  // ─── Integration statuses (shared by Sidebar + IntegrationsPanel) ───
  const PROVIDER_NAME_MAP: Record<string, string> = {
    gmail: 'Gmail', dropbox: 'Dropbox', 'google-drive': 'Drive', slack: 'Slack', notion: 'Notion', linear: 'Linear',
  };
  const PROVIDER_IDS = ['gmail', 'dropbox', 'google-drive', 'slack', 'notion', 'linear'] as const;
  const [integrationStatuses, setIntegrationStatuses] = useState<Record<string, boolean>>({});

  const fetchIntegrationStatuses = useCallback(async () => {
    const token = auth.getToken();
    if (!token) return;
    try {
      const results = await Promise.allSettled(
        PROVIDER_IDS.map((pid) =>
          axios.get(`/api/integrations/${pid}/status`, { headers: { Authorization: `Bearer ${token}` } })
        )
      );
      const statuses: Record<string, boolean> = {};
      PROVIDER_IDS.forEach((pid, i) => {
        const displayName = PROVIDER_NAME_MAP[pid] ?? pid;
        const r = results[i];
        statuses[displayName] = r.status === 'fulfilled' && !!(r.value.data?.data?.connected ?? r.value.data?.connected);
      });
      setIntegrationStatuses(statuses);
    } catch {
      // Keep stale data on failure
    }
  }, [auth.getToken]);

  const integrationsFetchedRef = useRef(false);
  useEffect(() => {
    if (auth.user && !integrationsFetchedRef.current) {
      integrationsFetchedRef.current = true;
      fetchIntegrationStatuses();
    }
    if (!auth.user) integrationsFetchedRef.current = false;
  }, [auth.user, fetchIntegrationStatuses]);

  // Fetch files for integration file browser
  const fetchFileBrowserFiles = useCallback(async (source: string, path: string) => {
    const token = auth.getToken();
    if (!token) return;
    setFileBrowserLoading(true);
    try {
      let endpoint = '';
      if (source === 'Dropbox') {
        endpoint = `/api/integrations/dropbox/documents?path=${encodeURIComponent(path)}`;
      } else if (source === 'Drive') {
        endpoint = `/api/integrations/google-drive/files?parentId=${encodeURIComponent(path === '/' ? 'root' : path)}`;
      } else if (source === 'Slack') {
        endpoint = '/api/integrations/slack/documents';
      }
      if (!endpoint) return;
      const res = await axios.get(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      const data = res.data?.data ?? res.data;
      const items = (data.documents ?? data.files ?? data.results ?? []) as Array<{
        id?: string; _id?: string; path?: string; name: string; type?: string; mimeType?: string;
        size?: number; modifiedAt?: string;
      }>;
      const mapped: import('./components/app/IntegrationFileBrowserModal').BrowseFile[] = items.map((item) => {
        const isFolder = item.type === 'folder' || item.mimeType === 'application/vnd.google-apps.folder';
        const ext = item.name.split('.').pop()?.toLowerCase();
        return {
          name: item.name,
          type: isFolder ? 'folder' as const : 'file' as const,
          fileType: isFolder ? undefined : ext,
          meta: item.modifiedAt ? new Date(item.modifiedAt).toLocaleDateString() : undefined,
        };
      });
      setFileBrowserFiles(mapped);
    } catch {
      setFileBrowserFiles([]);
    } finally {
      setFileBrowserLoading(false);
    }
  }, [auth.getToken]);

  useEffect(() => {
    if (!fileBrowserSource || !auth.user) {
      // Reset when closed or not signed in
      if (!fileBrowserSource) {
        setFileBrowserFiles([]);
        setFileBrowserBreadcrumb([]);
        setFileBrowserPath('/');
      }
      return;
    }
    const rootName = fileBrowserSource === 'Dropbox' ? 'Dropbox' : fileBrowserSource === 'Drive' ? 'My Drive' : fileBrowserSource;
    if (fileBrowserBreadcrumb.length === 0) {
      setFileBrowserBreadcrumb([rootName]);
    }
    fetchFileBrowserFiles(fileBrowserSource, fileBrowserPath);
  }, [fileBrowserSource, fileBrowserPath, auth.user, fetchFileBrowserFiles]);

  const [chatPrefill, setChatPrefill] = useState('');

  /** Ephemeral placeholder on main composer after “Retrieve and prompt”; cleared on send */
  const [chatComposerPlaceholderHint, setChatComposerPlaceholderHint] = useState<string | null>(null);
  /** Increment to defer focus + scroll chat composer (e.g. after expanding rail) */
  const [chatComposerFocusNonce, setChatComposerFocusNonce] = useState(0);
  const [inputFocused, setInputFocused] = useState(false);
  const [leftPanelOpen, setLeftPanelOpen] = useState(true); // default expanded; user can collapse
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobileChatOpen, setMobileChatOpen] = useState(false);

  useEffect(() => {
    if (!isMobile) {
      setMobileSidebarOpen(false);
      setMobileChatOpen(false);
    }
  }, [isMobile]);

  const location = useLocation();
  const rawNavigate = useNavigate();
  const navCountRef = useRef(0);
  const navCountResetRef = useRef(0);
  const navigate = useCallback((...args: Parameters<typeof rawNavigate>) => {
    const now = Date.now();
    if (now - navCountResetRef.current > 2000) {
      navCountRef.current = 0;
      navCountResetRef.current = now;
    }
    navCountRef.current++;
    if (navCountRef.current > 5) return; // Prevent navigation storms
    rawNavigate(...args);
  }, [rawNavigate]);

  // ─── URL-driven navigation (replaces old state-based nav) ─────────
  const showSignatures = location.pathname === '/app/signatures' || location.pathname.startsWith('/app/signatures/');
  const showFolders = location.pathname === '/app/folders' || location.pathname.startsWith('/app/folders/');
  const showSettings = location.pathname === '/app/settings';
  const isArchivedRoute = location.pathname === '/app/archived';
  const activeNav: import('./components/app/Sidebar').NavTab = isArchivedRoute ? 'archived' : null;

  useEffect(() => {
    const p = location.pathname;
    const legacyRoots = ['/app/recent', '/app/starred', '/app/trash', '/app/workflows'] as const;
    for (const base of legacyRoots) {
      if (p === base || p.startsWith(`${base}/`)) {
        navigate('/app/signatures', { replace: true });
        return;
      }
    }
  }, [location.pathname, navigate]);

  /** When true the empty/new-flow screen is shown at `/app` regardless of sidebar history. */
  const [newFlowActive, setNewFlowActive] = useState(location.pathname === '/app');
  const [canvasViewMode, setCanvasViewMode] = useState(true); // true = canvas with all frames, false = single doc view
  /** Paper AR-0: full-screen horizontal timeline on top of split workspace */
  const [versionTimelineOverlay, setVersionTimelineOverlay] = useState(false);
  const [focusedDocumentView, setFocusedDocumentView] = useState(false); // true = full-width doc (State 3), no chat column
  const [chatCollapsed, setChatCollapsed] = useState(false); // true = chat sidebar hidden, doc goes full-width
  /** While the first SSE draft (from-scratch) runs, temporarily narrow the chat column for doc focus. */
  const [narrowChatForInitialDraftStream, setNarrowChatForInitialDraftStream] = useState(false);
  const [documentPreviewVariant, setDocumentPreviewVariant] = useState<'original' | 'output'>('original');
  const [chatPanelWidth, setChatPanelWidth] = useState(() => {
    try { const saved = localStorage.getItem('corner.chatPanelWidth'); return saved ? Math.max(320, Math.min(680, parseInt(saved, 10))) : 360; }
    catch { return 360; }
  });
  const chatResizing = useRef(false);
  const maxChatPanelWidth = useMemo(() => {
    if (isMobile || viewportWidth <= 0) return 800;
    const sidebarChrome = leftPanelOpen ? APP.sidebarWidth : 48;
    const toolChrome = 56;
    const availableWorkspace = Math.max(0, viewportWidth - sidebarChrome - toolChrome);
    return Math.max(320, Math.min(680, Math.floor(availableWorkspace * 0.34)));
  }, [isMobile, leftPanelOpen, viewportWidth]);
  // The "Chat" tab and its full-width-chat hack used to live here.
  // Killed when we replaced Chat with the Issues tab — there's no
  // longer a tab that wants to override the chat panel's width. The
  // chat panel uses its normal `effectiveChatPanelWidth` everywhere
  // now, including when the Issues tab is active (Issues replaces the
  // doc column directly; chat stays its normal size on the right).
  const effectiveChatPanelWidth = Math.min(chatPanelWidth, maxChatPanelWidth);
  const desktopChatPanelRenderWidth = useMemo(() => {
    let w = effectiveChatPanelWidth;
    if (narrowChatForInitialDraftStream) {
      w = Math.min(w, 280);
    }
    if (mode === 'esign' || documentPreviewVariant === 'output') {
      w = Math.min(w, 300);
    }
    return w;
  }, [documentPreviewVariant, effectiveChatPanelWidth, mode, narrowChatForInitialDraftStream]);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [guidedContractSendBusy, setGuidedContractSendBusy] = useState(false);
  const guidedContractSendBusyRef = useRef(false);
  const [workflowRunId, setWorkflowRunId] = useState<string | null>(null);
  // Contract velocity HUD: rendered when the user opens a workflow whose graph
  // contains a `dispatch_for_signature` node. Holds the workflow that's
  // currently in HUD mode (form before run, progress after).
  const [hudWorkflow, setHudWorkflow] = useState<UserWorkflowDTO | null>(null);
  const [exportModal, setExportModal] = useState<{
    destination: ExportDestination;
    /** The text content being exported (for analysis results) */
    text?: string;
    /** File name for the export */
    fileName?: string;
    /** Download URL for file-based exports */
    downloadUrl?: string;
    /** Backend file ID for integration exports */
    fileId?: string;
  } | null>(null);
  /**
   * Split-view “original” preview: `URL.createObjectURL(currentFile)` — session-only; lost on refresh.
   * Processed outputs persist as `toolCall.resultFileId` → `/api/file/:id`. User uploads can be stored on
   * message `attachments` in Mongo, but we do not yet rehydrate that into `filePreviewUrl` when reopening a chat.
   */
  const [filePreviewUrl, setFilePreviewUrl] = useState<string | null>(null);
  /** Paper 2A5: file tool output while original upload preview remains (toolbar filename + Original PDF pill) */
  const [lastConversionOutput, setLastConversionOutput] = useState<ToolResult | null>(null);
  /** When set, `lastConversionOutput` is a PDF export of draft HTML matching this digest — enables instant signing until the user edits the draft. */
  const [draftPdfMirrorDigest, setDraftPdfMirrorDigest] = useState<string | null>(null);
  // App error banner is now self-contained in <AppErrorBanner />
  // ─── Review API sync (refs avoid hook-ordering TDZ) ──
  review.onStatusChangeRef.current = useCallback((suggestionId: string, status: string) => {
    // Route status updates to the correct API based on active review type
    if (clauseReviewResult?.clauseReviewId) {
      clauseReviewApi.patchClauseFlag(clauseReviewResult.clauseReviewId, suggestionId, status as any).catch(() => {});
    } else {
      const rid = review.reviewId;
      if (!rid) return;
      reviewApi.patchSuggestion(rid, suggestionId, { status: status as any }).catch(() => {});
    }
  }, [review.reviewId, clauseReviewResult?.clauseReviewId]);
  const userWorkflows = useUserWorkflows({
    isSignedIn: !!auth.user,
  });

  // Require authentication — redirect unauthenticated visitors to login.
  // Guests can only view shared documents via /app/shared/:token (separate route).
  useEffect(() => {
    if (!auth.loading && !auth.user) {
      navigate('/auth/login', { replace: true });
    }
  }, [auth.loading, auth.user, navigate]);

  // Global error banner + 401 auto-logout now handled by apiClient.ts interceptor

  const openFullOnboarding = useCallback(() => {
    try {
      localStorage.removeItem(IDLE_WELCOME_PENDING_KEY);
    } catch {
      /* ignore */
    }
    navigate('/auth/login');
  }, [navigate]);

  const conversationRouteMatch = location.pathname.match(/^\/app\/conversations\/([^/]+)$/);
  const routeConversationId = conversationRouteMatch?.[1] ?? null;

  useLayoutEffect(() => {
    if (!routeConversationId) {
      setWorkspaceRouteBootstrapId(null);
      return;
    }
    const aligned =
      currentConversationIdRef.current != null
      && String(currentConversationIdRef.current) === String(routeConversationId);
    if (!aligned) {
      setWorkspaceRouteBootstrapId(routeConversationId);
    }
  }, [routeConversationId]);

  const folderDetailMatch = location.pathname.match(/^\/app\/folders\/([^/]+)$/);
  const folderIdFromPath = folderDetailMatch?.[1] ?? null;
  const isFolderDetailRoute = folderIdFromPath != null;

  // ── Handle billing redirect from Stripe Checkout ──
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Handle Stripe Checkout success redirect
    if (params.get('billing') === 'success') {
      setProSuccessOpen(true);
      navigate('/app', { replace: true });
      const refresh = async () => {
        for (let i = 0; i < 5; i++) {
          await auth.refreshUser();
          // Check plan via billing status API (avoids stale closure on auth.user)
          try {
            const token = auth.getToken();
            if (token) {
              const res = await axios.get('/api/billing/status', { headers: { Authorization: `Bearer ${token}` } });
              if (res.data?.plan !== 'free') break;
            }
          } catch { /* ignore */ }
          await new Promise((r) => setTimeout(r, 2000));
        }
        // Final refresh to ensure latest state
        await auth.refreshUser();
      };
      refresh();
    }

    // Handle post-signup upgrade redirect (from pricing page)
    // Immediately redirect to Stripe Checkout — don't show the app
    const upgradePlan = params.get('upgrade');
    const upgradeCycle = params.get('cycle') || 'monthly';
    if (upgradePlan && auth.user && (upgradePlan === 'pro' || upgradePlan === 'enterprise')) {
      // Clear URL params immediately so it doesn't re-trigger
      navigate('/app', { replace: true });
      // Show a loading state while redirecting
      toast({ title: 'Redirecting to checkout...' });
      const token = auth.getToken();
      if (token) {
        axios.post<{ url: string }>('/api/billing/checkout', {
          plan: upgradePlan,
          cycle: upgradeCycle,
        }, { headers: { Authorization: `Bearer ${token}` } })
          .then((res) => { window.location.href = res.data.url; })
          .catch(() => { setPaywallOpen(true); });
      }
    }
  }, [location.search, auth.user]);

  const assignConversationIdFromNav =
    (location.state as { assignConversationId?: string } | null)?.assignConversationId ?? null;

  const handleSignOut = useCallback(async () => {
    try {
      await auth.logout();
    } catch {
      /* navigate regardless */
    }
    // Hard navigate to landing page so the entire App tree unmounts cleanly
    window.location.href = '/';
  }, [auth.logout]);

  // E-sign interactive state
  const [esignFields, setEsignFields] = useState<SignatureField[]>([]);
  const esignFieldsRef = useRef<SignatureField[]>(esignFields);
  useEffect(() => {
    esignFieldsRef.current = esignFields;
  }, [esignFields]);
  // v1 placement-redesign: which signer is "active" for new placements via
  // the AddFieldToolbar. Slot 0 (the user themselves) is the default; once
  // a recipient is added (via the SendForSignature flow) the user can
  // switch to them from the signing guide or viewer controls.
  const [esignActiveSignerIndex, setEsignActiveSignerIndex] = useState(0);
  const [esignPlacementMode, setEsignPlacementMode] = useState<PlacementType | null>(null);
  const [esignRecipientDrafts, setEsignRecipientDrafts] = useState<string[]>([]);
  const [esignPdfUrl, setEsignPdfUrl] = useState('');
  const [esignPdfPageCount, setEsignPdfPageCount] = useState(0);
  const [esignDetectingFields, setEsignDetectingFields] = useState(false);
  const esignSessionKeyRef = useRef<string | null>(null);
  /** Prevents double-submit while draft → DOCX → PDF conversion runs for signing. */
  const draftSigningPrepBusyRef = useRef(false);
  /** Context from the most recent esign attempt, passed to follow-up orchestrate calls. */
  const lastEsignContextRef = useRef<EsignContext | null>(null);
  const lastInjectedEsignEntryRef = useRef<string | null>(null);
  // Multi-party signing state
  const [sendForSignatureOpen, setSendForSignatureOpen] = useState(false);
  const [sendForSignatureModalKey, setSendForSignatureModalKey] = useState(0);
  const [sendForSignatureSending, setSendForSignatureSending] = useState(false);
  // CORNER_V2: Permission confirmation now handled inline in CoT panel
  const [activeSigningRequest, setActiveSigningRequest] = useState<import('@corner/shared').SigningRequestSummary | null>(null);
  const [signingRequestsRefreshKey, setSigningRequestsRefreshKey] = useState(0);
  const [esignActiveFieldIndex, setEsignActiveFieldIndex] = useState(-1);
  const [esignVerifyOpen, setEsignVerifyOpen] = useState(false);
  // Pre-sign placeholder warning state. Stores the field-index the user
  // clicked so we can resume `openStoredSignatureVerification` after the user
  // chooses how to handle the unfilled placeholders.
  const [unfilledModalOpen, setUnfilledModalOpen] = useState(false);
  const [unfilledModalEntries, setUnfilledModalEntries] = useState<Array<{ fieldId: string; label: string; page: number; placeholderRaw?: string }>>([]);
  const pendingSignFieldIndexRef = useRef<number | null>(null);
  const [printedTitleModal, setPrintedTitleModal] = useState<{
    fieldId: string;
    initial: string;
    label?: string;
    markPlacedOnSave: boolean;
    /** Placement mode only: update text + orange/green, no apply or sign queue */
    configureOnly?: boolean;
  } | null>(null);
  const esignSignaturePreviewSrc = useMemo(() => {
    const activeField = esignActiveFieldIndex >= 0 ? esignFields[esignActiveFieldIndex] : undefined;
    const useInitials = Boolean(activeField?.label?.toLowerCase().includes('initial') && auth.user?.initialsId);
    const type = useInitials ? 'initials' : 'signature';
    const imageId = useInitials ? auth.user?.initialsId : auth.user?.signatureId;
    if (!imageId) return '';
    return `/api/auth/me/signature-preview?type=${type}&t=${encodeURIComponent(auth.user?.updatedAt ?? imageId)}`;
  }, [auth.user?.initialsId, auth.user?.signatureId, auth.user?.updatedAt, esignActiveFieldIndex, esignFields]);

  const ingestSigningRequestDetail = useCallback((detail: import('@corner/shared').SigningRequestSummary) => {
    setActiveSigningRequest(detail);
    const rows = Array.isArray(detail.fields) ? detail.fields : [];
    if (rows.length > 0) {
      setEsignFields((prev) => mergeSigningDetailIntoEsignFields(prev, rows));
    }
  }, []);

  // ─── Risk snapshot fetch: check for clause review risk summary ──
  // Fetches the risk summary when a document is loaded or after contract_review analysis.
  // The backend handles auto-scan on import; the frontend just reads the result.
  useEffect(() => {
    const fileId = currentResult?.sourceFileId ?? currentResult?.fileId;
    if (!fileId || !auth.getToken()) return;
    if (riskSummaryFetchedRef.current === fileId) return;
    riskSummaryFetchedRef.current = fileId;
    clauseReviewApi.getRiskSummary(fileId)
      .then((summary) => setRiskSummary(summary ?? null))
      .catch(() => { /* silent — risk badge is optional */ });
  }, [currentResult?.sourceFileId, currentResult?.fileId, auth]);

  // When an envelope is open in e-sign workspace, hydrate marker state from Mongo
  // (recipient-completed fields have `signedAt` on the detail payload only).
  useEffect(() => {
    if (mode !== 'esign' || !activeSigningRequest?.requestId) return;
    const tk = auth.getToken();
    if (!tk) return;
    let cancelled = false;
    const rid = activeSigningRequest.requestId;
    axios
      .get(`/api/signing/${rid}`, { headers: { Authorization: `Bearer ${tk}` } })
      .then((res) => {
        if (cancelled) return;
        ingestSigningRequestDetail(res.data);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [mode, activeSigningRequest?.requestId, auth.getToken, ingestSigningRequestDetail]);

  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [restoringConversationId, setRestoringConversationId] = useState<string | null>(null);
  /**
   * Route `/app/conversations/:id` while workspace state still belongs to another id (or null).
   * Set in useLayoutEffect before paint so we never flash EmptyState during hydration.
   * Cleared when hydrate finishes (finally) or route leaves conversation URLs.
   */
  const [workspaceRouteBootstrapId, setWorkspaceRouteBootstrapId] = useState<string | null>(null);
  const currentConversationIdRef = useRef<string | null>(null);
  const orchestrateAssistantMessageIdRef = useRef<string | null>(null);
  const [sessionPrimaryDocument, setSessionPrimaryDocument] = useState<WorkspaceSessionDocumentRef | null>(null);
  const [sessionSourceDocuments, setSessionSourceDocuments] = useState<WorkspaceSessionDocumentRef[]>([]);

  // V5: template-memory link for the actively-displayed PDF.
  // Prefer sourceFileId — the displayed PDF is often a derived
  // version (e.g. an esign preview), and the template's contentHash
  // matched against the source bytes. When the lookup returns a
  // template, the SaveToTemplatePrompt fires after each field drag.
  const rawActiveDocFileId =
    currentResult?.sourceFileId
    ?? currentResult?.fileId
    ?? sessionPrimaryDocument?.fileId
    ?? null;
  const activeDocDownloadUrl =
    currentResult?.sourceFileId
      ? null
      : currentResult?.downloadUrl ?? sessionPrimaryDocument?.downloadUrl ?? null;
  const activeDocFileId = isFileStorageBackedFileId(rawActiveDocFileId, activeDocDownloadUrl)
    ? rawActiveDocFileId
    : null;
  const rawVisibleDocFileId =
    currentResult?.fileId
    ?? sessionPrimaryDocument?.fileId
    ?? null;
  const activeVisibleDocFileId = isFileStorageBackedFileId(
    rawVisibleDocFileId,
    currentResult?.downloadUrl ?? sessionPrimaryDocument?.downloadUrl ?? null,
  )
    ? rawVisibleDocFileId
    : null;
  const displayedDocFileIdRef = useRef<string | null>(null);
  useEffect(() => {
    displayedDocFileIdRef.current = activeVisibleDocFileId ?? activeDocFileId;
  }, [activeDocFileId, activeVisibleDocFileId]);
  const templateLink = useTemplateForFile(activeDocFileId);

  // V6: destination picker state for the post-success "Save to workflow"
  // flow. Holds the messageId so we know which signing-request to save
  // when the user picks.
  const [savePickerOpen, setSavePickerOpen] = useState<{ messageId: string; defaultName: string } | null>(null);
  // V6: pre-emptive destination chosen via the chat composer button.
  // When set, the post-success save handler consumes this silently
  // instead of opening the picker — the user already pre-picked.
  const workflowDestination = useWorkflowDestination();
  const [filePreviewSource, setFilePreviewSource] = useState<'local' | 'restored' | null>(null);
  const filePreviewUrlRef = useRef(filePreviewUrl);
  filePreviewUrlRef.current = filePreviewUrl;
  const filePreviewSourceRef = useRef(filePreviewSource);
  filePreviewSourceRef.current = filePreviewSource;
  const esignPdfUrlRef = useRef(esignPdfUrl);
  esignPdfUrlRef.current = esignPdfUrl;
  const currentResultRef = useRef(currentResult);
  currentResultRef.current = currentResult;
  const sessionPrimaryDocumentRef = useRef(sessionPrimaryDocument);
  sessionPrimaryDocumentRef.current = sessionPrimaryDocument;
  // Window during which we trust the just-applied edit's resulting fileId
  // over anything the conversation hydration tries to load. We previously
  // used 30 seconds as a defensive measure because the backend response
  // didn't always converge quickly with the conversation snapshot. Now
  // that the backend returns the authoritative `EditApplyOutcome`
  // (including resultingProject / resultingVersionId) and we set them
  // synchronously below, 5 seconds is plenty — this exists only to
  // bridge the gap between the apply response and the next hydration
  // tick that includes the new version.
  const PENDING_DOC_EDIT_VISIBLE_TTL_MS = 5000;
  const pendingDocEditVisibleFileRef = useRef<{
    fileId: string;
    batchId: string;
    conversationId: string | null;
    createdAt: number;
  } | null>(null);
  useEffect(() => {
    const visibleFileId = activeVisibleDocFileId ?? activeDocFileId ?? null;
    console.log('[DocSurface] visible-file', {
      fileId: visibleFileId,
      currentResultFileId: currentResult?.fileId ?? null,
      currentResultSourceFileId: currentResult?.sourceFileId ?? null,
      primaryDocumentFileId: sessionPrimaryDocument?.fileId ?? null,
      presentingMode: mode === 'esign' || documentPreviewVariant === 'output',
      wideLayout: mode === 'esign' || documentPreviewVariant === 'output' || Boolean(filePreviewUrl),
      esignFields: mode === 'esign' || esignFields.length > 0 ? esignFields.length : undefined,
    });
    const expected = pendingDocEditVisibleFileRef.current;
    if (!expected) return;
    if (visibleFileId === expected.fileId) {
      console.log('[DocSurface] after-apply', {
        visibleFileId,
        expected: expected.fileId,
        batchId: expected.batchId,
        conversationId: expected.conversationId,
        matched: true,
      });
      pendingDocEditVisibleFileRef.current = null;
      return;
    }
    if (visibleFileId) {
      console.warn('[DocEditApply] visible file mismatch after apply', {
        visibleFileId,
        resultingVersionFileId: expected.fileId,
        batchId: expected.batchId,
        conversationId: expected.conversationId,
      });
      console.warn('[DocSurface] after-apply', {
        visibleFileId,
        expected: expected.fileId,
        batchId: expected.batchId,
        conversationId: expected.conversationId,
        matched: false,
      });
    }
  }, [
    activeDocFileId,
    activeVisibleDocFileId,
    currentResult?.fileId,
    currentResult?.sourceFileId,
    documentPreviewVariant,
    esignFields.length,
    filePreviewUrl,
    mode,
    sessionPrimaryDocument?.fileId,
  ]);
  /** True when preview URL or persisted result/doc refs indicate a loaded document surface. */
  const hasWorkspaceDocArtifact = useCallback(() => {
    const preview = filePreviewUrlRef.current;
    const res = currentResultRef.current;
    const prim = sessionPrimaryDocumentRef.current;
    return !!(
      (typeof preview === 'string' && preview.length > 0)
      || res?.fileId
      || res?.sourceFileId
      || (typeof res?.draftHtml === 'string' && res.draftHtml.trim().length > 0)
      || prim?.fileId
    );
  }, []);
  /**
   * Conversation-scoped artifact ownership check used by hydration guards.
   * Prevents stale document refs from a previous conversation from suppressing
   * hydration of the route conversation after workspace clear.
   */
  const hasWorkspaceDocArtifactForConversation = useCallback((conversationId: string | null | undefined) => {
    if (!conversationId) return false;
    const currentId = currentConversationIdRef.current;
    if (currentId == null || String(currentId) !== String(conversationId)) return false;
    return hasWorkspaceDocArtifact();
  }, [hasWorkspaceDocArtifact]);
  const modeAfterInterruptedWork = useCallback((): AppMode => {
    if (modeRef.current === 'drafting' && draftContent.trim()) return 'drafting';
    if (hasWorkspaceDocArtifact() || currentFiles.length > 0) return 'result';
    return 'empty';
  }, [currentFiles.length, draftContent, hasWorkspaceDocArtifact]);
  const { nodes, addNode, clearHistory } = useVersionHistory(currentConversationId ?? '__draft__');
  /** Last system “file loaded” message id for single audio — patched when duration metadata resolves (Paper 58W). */
  const lastAudioLoadMsgIdRef = useRef<string | null>(null);
  /** Same for single image dimensions (Paper 5BL). */
  const lastImageLoadMsgIdRef = useRef<string | null>(null);
  const conversations = useConversations({
    isSignedIn: true, // App requires authentication; guests use /app/shared/:token
    getToken: auth.getToken,
    authReady: !auth.loading,
  });
  const authGetToken = auth.getToken;
  const conversationList = conversations.list;
  const conversationListRef = useRef(conversationList);
  conversationListRef.current = conversationList;
  const conversationApiBase = conversations.getApiBase();
  const addConversationMessage = conversations.addMessage;
  const createConversation = conversations.createConversation;
  const getConversationMessages = conversations.getMessages;
  const getConversationSession = conversations.getSession;
  const updateConversation = conversations.updateConversation;
  const deleteConversation = conversations.deleteConversation;
  const persistConversationSnapshot = conversations.persistSnapshot;
  const clearStoredConversations = conversations.clearAll;
  const [documentProjects, setDocumentProjects] = useState<import('./types').DocumentProjectSummary[]>([]);
  const [activeDocumentProject, setActiveDocumentProject] = useState<import('./types').DocumentProjectDetail | null>(null);
  const [versionVerifyResult, setVersionVerifyResult] = useState<import('./types').VersionVerificationResult | null>(null);
  /** Bumped when the doc column should briefly expand the version rail (draft stream start, etc.). */
  const [versionRailPeekKey, setVersionRailPeekKey] = useState(0);

  const refreshDocumentProjects = useCallback(async () => {
    if (!auth.user) {
      setDocumentProjects([]);
      return;
    }
    try {
      setDocumentProjects(await projectApi.listProjects(auth.getToken));
    } catch (error) {
      console.warn('[Projects] Failed to list projects', error);
    }
  }, [auth.user, auth.getToken]);

  const conversationProjectFingerprint = useMemo(
    () => conversationList.map((conversation) => `${conversation.id}:${conversation.projectId ?? conversation.sessionSnapshot?.projectId ?? ''}`).join('|'),
    [conversationList],
  );

  useEffect(() => {
    void refreshDocumentProjects();
  }, [refreshDocumentProjects, conversationProjectFingerprint]);

  const currentProjectId = useMemo(() => {
    const fromConversation = conversationList.find((conversation) => conversation.id === currentConversationId)?.projectId;
    const fromSnapshot = conversationList.find((conversation) => conversation.id === currentConversationId)?.sessionSnapshot?.projectId;
    return fromConversation ?? fromSnapshot ?? null;
  }, [conversationList, currentConversationId]);
  const currentConversationFolderId = useMemo(
    () => conversationList.find((conversation) => conversation.id === currentConversationId)?.folderId ?? null,
    [conversationList, currentConversationId],
  );

  useEffect(() => {
    let cancelled = false;
    if (!currentProjectId || !auth.user) {
      setActiveDocumentProject((project) => (
        project?.primaryConversationId === currentConversationId ? project : null
      ));
      return;
    }
    projectApi.getProject(currentProjectId, auth.getToken)
      .then((project) => { if (!cancelled) setActiveDocumentProject(project); })
      .catch((error) => console.warn('[Projects] Failed to load active project', error));
    return () => { cancelled = true; };
  }, [currentProjectId, currentConversationId, auth.user, auth.getToken]);

  const [projectWorkspaceTab, setProjectWorkspaceTab] = useState<ProjectWorkspaceTab>('documents');
  const [projectVersionTreeRevision, setProjectVersionTreeRevision] = useState(0);
  const openClauseReviewSetupScreen = useCallback(() => {
    setProjectWorkspaceTab('issues');
    openClauseReviewSetup();
  }, [openClauseReviewSetup]);
  const handleProjectWorkspaceTabChange = useCallback((tab: ProjectWorkspaceTab) => {
    // Tab clicks just switch the active tab. Setup is opened only via
    // explicit affordances: doc top-bar Risk snapshot button, workflow
    // menu, or the IssuesPanel's "Run new review" header button. Auto-
    // opening setup here used to overlay the IssuesPanel and hide
    // existing pending flags from view (see docs/clause-flagging-audit.md).
    setClauseReviewSetupOpen(false);
    setProjectWorkspaceTab(tab);
  }, []);
  // Version sidebar selection model:
  // - currentLogicalDocumentId: stable outer group (DocumentProject.id)
  // - currentVersionId: selected version row inside that group
  // - compareVersionId: reserved for compare mode so compare selection won't
  //   overload/replace primary selection.
  const [currentLogicalDocumentId, setCurrentLogicalDocumentId] = useState<string | null>(null);
  const [currentVersionId, setCurrentVersionId] = useState<string | null>(null);
  const [compareVersionId, setCompareVersionId] = useState<string | null>(null);
  useEffect(() => {
    setProjectWorkspaceTab('documents');
    setClauseReviewSetupOpen(false);
  }, [currentProjectId]);

  // Live tabs are `documents` and `issues`. `chat`, `reviews`, and
  // `tabular` are legacy values still present in the
  // `ProjectWorkspaceTab` union for back-compat with persisted state
  // from older sessions. If a user reloads with one of those selected
  // (or any other stale tab id) coerce them to `documents` so we
  // don't render nothing — same coercion shape we used when removing
  // `reviews` / `tabular`.
  useEffect(() => {
    if (projectWorkspaceTab !== 'documents' && projectWorkspaceTab !== 'issues') {
      setProjectWorkspaceTab('documents');
    }
  }, [projectWorkspaceTab]);

  // Close inlineClauseFlagPopover when the user navigates away from
  // its anchor — different version, different tab, or different
  // conversation. Without this the popover sticks around showing a
  // flag that no longer matches what's on screen.
  useEffect(() => {
    if (!inlineClauseFlagPopover) return;
    if (
      currentVersionId !== inlineClauseFlagPopover.versionId
      || projectWorkspaceTab !== 'documents'
    ) {
      setInlineClauseFlagPopover(null);
    }
  }, [inlineClauseFlagPopover, currentVersionId, projectWorkspaceTab]);
  useEffect(() => {
    setInlineClauseFlagPopover(null);
  }, [currentConversationId]);

  // ── Issues tab data ─────────────────────────────────────────────
  //
  // The Issues tab badge has to render in the banner even when the
  // user is on the Documents tab, so the fetch lives at the App
  // level — not inside `IssuesPanel`. The panel is presentational.
  //
  // Refresh triggers:
  //   1. Active project changes (different project → different flags).
  //   2. The `corner:clause-flags-updated` window event fires (any
  //      flag-status PATCH, any new clause review created — see
  //      `notifyClauseFlagsUpdated` in `clauseReviewApi`).
  //   3. The user explicitly retries from the panel's error state.
  const [issuesData, setIssuesData] = useState<ProjectClauseFlagsResponse | null>(null);
  const [issuesLoading, setIssuesLoading] = useState(false);
  const [issuesError, setIssuesError] = useState<string | null>(null);
  const [issuesRefreshKey, setIssuesRefreshKey] = useState(0);
  const issuesProjectRef = useRef<string | null>(null);
  const refreshProjectIssues = useCallback(() => {
    setIssuesRefreshKey((k) => k + 1);
  }, []);

  const issuesProjectId = activeDocumentProject?.id
    && !String(activeDocumentProject.id).startsWith('local:')
    ? activeDocumentProject.id
    : null;

  useEffect(() => {
    issuesProjectRef.current = issuesProjectId;
    if (!issuesProjectId) {
      setIssuesData(null);
      setIssuesLoading(false);
      setIssuesError(null);
      return;
    }
    let cancelled = false;
    setIssuesLoading(true);
    setIssuesError(null);
    projectApi.listProjectClauseFlags(issuesProjectId, auth.getToken)
      .then((response) => {
        // Bail if the user navigated away mid-fetch — without this
        // guard a slow response can clobber state for the project the
        // user is now on.
        if (cancelled || issuesProjectRef.current !== issuesProjectId) return;
        setIssuesData(response);
      })
      .catch((err: unknown) => {
        if (cancelled || issuesProjectRef.current !== issuesProjectId) return;
        setIssuesError(err instanceof Error ? err.message : 'Could not load issues');
      })
      .finally(() => {
        if (cancelled) return;
        setIssuesLoading(false);
      });
    return () => { cancelled = true; };
  }, [issuesProjectId, issuesRefreshKey, auth.getToken]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onUpdated = () => refreshProjectIssues();
    window.addEventListener(CLAUSE_FLAGS_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(CLAUSE_FLAGS_UPDATED_EVENT, onUpdated);
  }, [refreshProjectIssues]);

  // The Issues "Open in doc" handler and the empty-state "Run clause
  // review" handler depend on `displayDocumentVersions` and
  // `handleSelectDocumentVersion`, both of which are declared further
  // down. They're defined where those exist (search for
  // `handleOpenIssueFlag`).

  /**
   * Inline rename a project from the workspace banner. Optimistically
   * patches the local copy so the new title shows up immediately, then
   * persists with `PATCH /api/projects/:id`. On failure we revert and
   * surface a toast — no silent drift between the UI and the backend.
   */
  const handleRenameActiveProject = useCallback(
    async (nextTitle: string) => {
      if (!activeDocumentProject) return;
      const trimmed = nextTitle.trim();
      if (!trimmed || trimmed === activeDocumentProject.title) return;
      const previous = activeDocumentProject;
      setActiveDocumentProject({ ...previous, title: trimmed });
      try {
        const fresh = await projectApi.renameProject(previous.id, trimmed, auth.getToken);
        setActiveDocumentProject(fresh);
        void refreshDocumentProjects();
      } catch (error) {
        console.warn('[Projects] Rename failed', error);
        setActiveDocumentProject(previous);
        toast({
          title: 'Could not rename project',
          description: error instanceof Error ? error.message : 'Please try again.',
          variant: 'destructive',
        });
      }
    },
    [activeDocumentProject, auth.getToken, refreshDocumentProjects, toast],
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleTreeUpdate = () => setProjectVersionTreeRevision((revision) => revision + 1);
    window.addEventListener('corner:version-tree-updated', handleTreeUpdate);
    return () => window.removeEventListener('corner:version-tree-updated', handleTreeUpdate);
  }, []);

  const showProjectShell = Boolean(
    activeDocumentProject
      && activeDocumentProject.primaryConversationId === currentConversationId
      && !String(activeDocumentProject.id).startsWith('local:'),
  );
  const hasVisibleDocumentForWorkspaceHeader = Boolean(
    mode !== 'empty' &&
    (
      currentResult ||
      sessionPrimaryDocument ||
      currentFile ||
      filePreviewUrl ||
      draftContent.trim()
    ),
  );
  const showProjectWorkspaceHeader = Boolean(activeDocumentProject || hasVisibleDocumentForWorkspaceHeader);

  const workspaceVersionContextActive = useMemo(
    () =>
      Boolean(
        currentConversationId &&
        mode !== 'empty' &&
        (mode === 'drafting' ||
          mode === 'result' ||
          mode === 'review' ||
          mode === 'esign' ||
          !!currentResult?.fileId ||
          !!sessionPrimaryDocument?.fileId ||
          !!currentProjectId),
      ),
    [
      currentConversationId,
      mode,
      currentResult?.fileId,
      sessionPrimaryDocument?.fileId,
      currentProjectId,
    ],
  );

  const displayDocumentVersions = useMemo((): import('./types').DocumentVersionSummary[] => {
    const fromProject = activeDocumentProject?.versions ?? [];
    if (fromProject.length > 0) {
      return fromProject.map((version) => ({
        ...version,
        fileName: displayFileNameForProjectVersion(activeDocumentProject?.title ?? 'Document', version) ?? version.fileName,
      }));
    }
    if (!workspaceVersionContextActive || !currentConversationId) {
      return [];
    }

    if (conversationDocuments.length > 0) {
      const optimisticProjectId = resolveLogicalDocumentId({
        activeProjectId: activeDocumentProject?.id ?? null,
        currentProjectId,
        currentConversationId,
      }) ?? `conversation:${currentConversationId}`;
      const createdBy = auth.user?.id ?? 'local';
      return [...conversationDocuments]
        .sort((a, b) => {
          const versionDelta = Number(a.version) - Number(b.version);
          if (Number.isFinite(versionDelta) && versionDelta !== 0) return versionDelta;
          return String(a.createdAt ?? '').localeCompare(String(b.createdAt ?? ''));
        })
        .map((doc) => ({
          id: `workspace:file:doc-${doc.id}:v${doc.version}`,
          projectId: optimisticProjectId,
          versionNumber: doc.version,
          fileId: `doc-${doc.id}`,
          fileName: `${(doc.title || draftTitle || 'Document').replace(/\.(docx|doc|html?)$/i, '') || 'Document'}.html`,
          mimeType: 'text/html',
          source: doc.originPrompt ? 'generated' : 'ai_edit',
          lineageKind: 'version',
          createdBy,
          createdFromVersionId: null,
          changeSummary: doc.originPrompt || (doc.version === 1 ? 'Initial draft.' : `Version ${doc.version}.`),
          conversationId: currentConversationId,
          createdAt: doc.createdAt,
        }));
    }

    const rawFileId = sessionPrimaryDocument?.fileId || currentResult?.fileId || '';
    const fileId = isLegacyDraftShellFileId(rawFileId) ? '' : rawFileId;
    const fileName =
      sessionPrimaryDocument?.fileName ||
      currentResult?.fileName ||
      draftTitle ||
      'Document';
    const mimeType =
      sessionPrimaryDocument?.mimeType ||
      currentResult?.mimeType ||
      (mode === 'drafting' ? 'text/html' : 'application/octet-stream');
    const optimisticProjectId = resolveLogicalDocumentId({
      activeProjectId: activeDocumentProject?.id ?? null,
      currentProjectId,
      currentConversationId,
    }) ?? `conversation:${currentConversationId}`;
    const now = new Date().toISOString();
    const createdBy = auth.user?.id ?? 'local';
    const isDraftShell = mode === 'drafting' && !fileId;
    const changeSummary = isDraftShell
      ? (processing?.label ?? 'Draft in progress')
      : mode === 'drafting'
        ? 'Live draft'
        : null;
    const src: import('./types').DocumentVersionSummary['source'] =
      mode === 'drafting'
        ? 'generated'
        : sessionPrimaryDocument?.source === 'generated'
          ? 'generated'
          : 'upload';


    return [
      {
        id: fileId
          ? `workspace:file:${fileId}:v${currentResult?.documentVersion ?? 1}`
          : `workspace:draft:${currentConversationId}`,
        projectId: optimisticProjectId,
        versionNumber: currentResult?.documentVersion ?? 1,
        fileId,
        fileName,
        mimeType,
        source: src,
        lineageKind: 'version',
        createdBy,
        createdFromVersionId: null,
        changeSummary,
        conversationId: currentConversationId,
        createdAt: now,
      },
    ];
  }, [
    activeDocumentProject?.id,
    activeDocumentProject?.title,
    activeDocumentProject?.versions,
    auth.user?.id,
    conversationDocuments,
    currentConversationId,
    currentProjectId,
    currentResult?.documentVersion,
    currentResult?.fileId,
    currentResult?.fileName,
    currentResult?.mimeType,
    draftTitle,
    mode,
    processing?.label,
    sessionPrimaryDocument?.fileId,
    sessionPrimaryDocument?.fileName,
    sessionPrimaryDocument?.mimeType,
    sessionPrimaryDocument?.source,
    workspaceVersionContextActive,
  ]);

  const projectContextDocuments = useMemo((): WorkspaceSessionDocumentRef[] => {
    if (!activeDocumentProject || activeDocumentProject.primaryConversationId !== currentConversationId) {
      return [];
    }

    const seen = new Set<string>();
    const hiddenVersionIds = readHiddenProjectVersionIds(activeDocumentProject.id, activeDocumentProject.title);
    return (activeDocumentProject.versions ?? [])
      .filter((version) => {
        const fileId = version?.fileId;
        if (!fileId || isLegacyDraftShellFileId(fileId) || hiddenVersionIds.has(version.id) || seen.has(fileId)) return false;
        seen.add(fileId);
        return true;
      })
      .map((version) => ({
        fileId: version.fileId,
        fileName: displayFileNameForProjectVersion(activeDocumentProject.title, version) || `Version ${version.versionNumber}`,
        mimeType: version.mimeType || 'application/octet-stream',
        sizeBytes: 0,
        downloadUrl: documentVersionDownloadUrl(version, activeDocumentProject.primaryConversationId) ?? `/api/file/${version.fileId}`,
        source:
          version.source === 'upload' || version.source === 'original'
            ? 'upload'
            : version.source === 'generated' || version.source === 'ai_edit'
              ? 'generated'
              : 'result',
      }));
  }, [activeDocumentProject, currentConversationId, projectVersionTreeRevision]);

  const versionTimelineDocuments = useMemo((): ConversationDocument[] => {
    const docs = [...conversationDocuments];
    for (const version of displayDocumentVersions) {
      const docId = isConversationMongoDocumentFileId(version.fileId) ? version.fileId.slice(4) : null;
      if (!docId) continue;
      if (docs.some((doc) => doc.id === docId || doc.version === version.versionNumber)) continue;
      docs.push({
        id: docId,
        title: version.fileName?.replace(/\.(docx|doc|html?)$/i, '') || version.changeSummary || 'Document',
        html: '',
        version: version.versionNumber,
        createdAt: version.createdAt,
        updatedAt: version.createdAt,
        originPrompt: version.source === 'generated' ? 'Generated document' : null,
      });
    }
    const currentDocumentId =
      currentResult?.documentId ??
      (isConversationMongoDocumentFileId(currentResult?.fileId) ? currentResult.fileId.slice(4) : null);
    const currentVersion =
      typeof currentResult?.documentVersion === 'number'
        ? currentResult.documentVersion
        : null;
    const html = typeof currentResult?.draftHtml === 'string' ? currentResult.draftHtml : draftContent;
    if (currentDocumentId && currentVersion != null && html?.trim() && !docs.some((doc) => doc.id === currentDocumentId || doc.version === currentVersion)) {
      const now = new Date().toISOString();
      docs.push({
        id: currentDocumentId,
        title: currentResult?.draftTitle ?? draftTitle ?? currentResult?.fileName ?? 'Document',
        html,
        version: currentVersion,
        createdAt: now,
        updatedAt: now,
        originPrompt: null,
      });
    }
    return docs;
  }, [
    conversationDocuments,
    currentResult?.documentId,
    currentResult?.documentVersion,
    currentResult?.draftHtml,
    currentResult?.draftTitle,
    currentResult?.fileId,
    currentResult?.fileName,
    displayDocumentVersions,
    draftContent,
    draftTitle,
  ]);

  const versionTimelineEntries = useMemo(
    () => buildVersionTimeline({
      conversationId: currentConversationId ?? '',
      documents: versionTimelineDocuments,
      cotSteps: cot.state.steps,
    }),
    [currentConversationId, cot.state.steps, versionTimelineDocuments],
  );

  const activeDocumentVersionId = useMemo(() => {
    const runnable = isClauseReviewRunnableSourceId;

    const fid = currentResult?.fileId ?? sessionPrimaryDocument?.fileId ?? '';
    if (runnable(fid)) {
      const byFile = displayDocumentVersions.find((v) => v.fileId === fid);
      if (byFile && runnable(byFile.fileId)) return byFile.id;
    }
    if (displayDocumentVersions.length === 1) {
      const only = displayDocumentVersions[0];
      if (runnable(only.fileId)) return only.id;
    }
    const cvId = activeDocumentProject?.currentVersionId ?? null;
    if (cvId) {
      const head = displayDocumentVersions.find((v) => v.id === cvId);
      if (head && runnable(head.fileId)) return cvId;
    }
    const firstRunnable = displayDocumentVersions.find((v) => runnable(v.fileId));
    return firstRunnable?.id ?? null;
  }, [
    activeDocumentProject?.currentVersionId,
    currentResult?.fileId,
    displayDocumentVersions,
    sessionPrimaryDocument?.fileId,
  ]);

  useEffect(() => {
    const logicalId = resolveLogicalDocumentId({
      activeProjectId: activeDocumentProject?.id ?? null,
      currentProjectId,
      currentConversationId,
    });
    setCurrentLogicalDocumentId(logicalId);
  }, [activeDocumentProject?.id, currentConversationId, currentProjectId]);

  useEffect(() => {
    const hasCurrent = currentVersionId
      ? displayDocumentVersions.some((v) => v.id === currentVersionId)
      : false;
    if (hasCurrent) return;
    setCurrentVersionId(activeDocumentVersionId);
  }, [activeDocumentVersionId, currentVersionId, displayDocumentVersions]);

  const effectiveActiveVersionId = resolveEffectiveVersionId({
    currentVersionId,
    displayVersions: displayDocumentVersions,
    fallbackVersionId: activeDocumentVersionId,
  });

  const selectedConversationDocument = useMemo(() => {
    const selectedVersion = displayDocumentVersions.find((version) => version.id === effectiveActiveVersionId)
      ?? displayDocumentVersions.find((version) => version.id === currentVersionId)
      ?? null;
    const selectedDocId = isConversationMongoDocumentFileId(selectedVersion?.fileId)
      ? selectedVersion.fileId.slice(4)
      : null;
    if (selectedDocId) {
      const selectedDoc = conversationDocuments.find((doc) => doc.id === selectedDocId);
      if (selectedDoc) return selectedDoc;
    }
    const currentDocId =
      currentResult?.documentId ??
      (isConversationMongoDocumentFileId(currentResult?.fileId) ? currentResult.fileId.slice(4) : null);
    if (currentDocId) {
      const currentDoc = conversationDocuments.find((doc) => doc.id === currentDocId);
      if (currentDoc) return currentDoc;
    }
    return conversationDocuments.at(-1) ?? null;
  }, [
    conversationDocuments,
    currentResult?.documentId,
    currentResult?.fileId,
    currentVersionId,
    displayDocumentVersions,
    effectiveActiveVersionId,
  ]);

  const activeDocumentSurface = useMemo(() => {
    const currentDraftHtml = typeof currentResult?.draftHtml === 'string' && currentResult.draftHtml.trim()
      ? currentResult.draftHtml
      : draftContent?.trim()
        ? draftContent
        : null;
    const currentResultIsVisibleDraft =
      Boolean(currentDraftHtml) &&
      (
        isLegacyDraftShellFileId(currentResult?.fileId) ||
        currentResult?.downloadUrl?.startsWith('draft://') ||
        mode === 'drafting'
      );
    if (currentResult && currentDraftHtml && currentResultIsVisibleDraft) {
      const documentId =
        currentResult.documentId ??
        (isConversationMongoDocumentFileId(currentResult.fileId) ? currentResult.fileId.slice(4) : null);
      const fileId = documentId ? `doc-${documentId}` : currentResult.fileId;
      const title = currentResult.draftTitle || draftTitle || currentResult.fileName || 'Document';
      const ref: WorkspaceSessionDocumentRef = {
        fileId,
        fileName: currentResult.fileName || `${title.replace(/\.(docx|doc|html?)$/i, '') || 'Document'}.html`,
        mimeType: currentResult.mimeType || 'text/html',
        sizeBytes: currentDraftHtml.length,
        downloadUrl: documentId && currentConversationId
          ? `/api/conversations/${currentConversationId}/documents/${documentId}`
          : currentResult.downloadUrl,
        source: 'generated',
      };
      return {
        ref,
        currentDocumentId: documentId ?? (currentConversationId ? `draft:${currentConversationId}` : currentResult.fileId),
        currentDocumentVersion: currentResult.documentVersion ?? null,
        source: 'syntheticCurrentResult' as const,
        draftHtml: currentDraftHtml,
      };
    }
    const primaryFileId = sessionPrimaryDocument?.fileId ?? null;
    const primaryConversationDocumentId = isConversationMongoDocumentFileId(primaryFileId)
      ? primaryFileId!.slice(4)
      : null;
    if (sessionPrimaryDocument?.fileId && !isLegacyDraftShellFileId(primaryFileId) && !primaryConversationDocumentId) {
      return {
        ref: sessionPrimaryDocument,
        currentDocumentId: sessionPrimaryDocument.fileId,
        currentDocumentVersion: null,
        source: 'primaryDocument' as const,
        draftHtml: null as string | null,
      };
    }
    if (selectedConversationDocument && currentConversationId) {
      const title = selectedConversationDocument.title?.trim() || draftTitle || 'Document';
      const ref: WorkspaceSessionDocumentRef = {
        fileId: `doc-${selectedConversationDocument.id}`,
        fileName: `${title.replace(/\.(docx|doc|html?)$/i, '') || 'Document'}.html`,
        mimeType: 'text/html',
        sizeBytes: selectedConversationDocument.html.length,
        downloadUrl: `/api/conversations/${currentConversationId}/documents/${selectedConversationDocument.id}`,
        source: selectedConversationDocument.originPrompt ? 'generated' : 'result',
      };
      return {
        ref,
        currentDocumentId: selectedConversationDocument.id,
        currentDocumentVersion: selectedConversationDocument.version,
        source: 'conversationDocument' as const,
        draftHtml: selectedConversationDocument.html,
      };
    }
    if (primaryConversationDocumentId && currentConversationId) {
      const primaryConversationDocument = conversationDocuments.find((doc) => doc.id === primaryConversationDocumentId);
      if (primaryConversationDocument) {
        const title = primaryConversationDocument.title?.trim() || draftTitle || sessionPrimaryDocument?.fileName || 'Document';
        const ref: WorkspaceSessionDocumentRef = {
          fileId: `doc-${primaryConversationDocument.id}`,
          fileName: `${title.replace(/\.(docx|doc|html?)$/i, '') || 'Document'}.html`,
          mimeType: 'text/html',
          sizeBytes: primaryConversationDocument.html.length,
          downloadUrl: `/api/conversations/${currentConversationId}/documents/${primaryConversationDocument.id}`,
          source: primaryConversationDocument.originPrompt ? 'generated' : 'result',
        };
        return {
          ref,
          currentDocumentId: primaryConversationDocument.id,
          currentDocumentVersion: primaryConversationDocument.version,
          source: 'conversationDocument' as const,
          draftHtml: primaryConversationDocument.html,
        };
      }
    }
    if (currentResult && currentDraftHtml) {
      const documentId =
        currentResult.documentId ??
        (isConversationMongoDocumentFileId(currentResult.fileId) ? currentResult.fileId.slice(4) : null);
      const fileId = documentId ? `doc-${documentId}` : currentResult.fileId;
      const title = currentResult.draftTitle || draftTitle || currentResult.fileName || 'Document';
      const ref: WorkspaceSessionDocumentRef = {
        fileId,
        fileName: `${title.replace(/\.(docx|doc|html?)$/i, '') || 'Document'}.html`,
        mimeType: 'text/html',
        sizeBytes: currentDraftHtml.length,
        downloadUrl: documentId && currentConversationId
          ? `/api/conversations/${currentConversationId}/documents/${documentId}`
          : currentResult.downloadUrl,
        source: 'generated',
      };
      return {
        ref,
        currentDocumentId: documentId ?? (currentConversationId ? `draft:${currentConversationId}` : currentResult.fileId),
        currentDocumentVersion: currentResult.documentVersion ?? null,
        source: 'syntheticCurrentResult' as const,
        draftHtml: currentDraftHtml,
      };
    }
    return null;
  }, [
    currentConversationId,
    currentResult,
    draftContent,
    draftTitle,
    conversationDocuments,
    selectedConversationDocument,
    sessionPrimaryDocument,
    mode,
  ]);

  const handleSelectDocumentVersion = useCallback((version: import('./types').DocumentVersionSummary) => {
    const selectedLogicalId = version.projectId ?? activeDocumentProject?.id ?? currentLogicalDocumentId;
    const versionLabel = `v${version.versionNumber ?? '?'}`;
    docVersionDebugLog('selected', {
      documentId: selectedLogicalId ?? null,
      versionId: version.id,
      fileId: version.fileId ?? null,
      type: version.source ?? null,
      versionLabel,
      mimeType: version.mimeType ?? null,
      fileName: version.fileName ?? null,
    });
    // Clicking the version that's already showing should be a true
    // no-op. Without this guard, in drafting mode with the live draft
    // sitting as v1, a click ran the full file-preview path: it
    // flipped `mode` from 'drafting' → 'result', set `filePreviewUrl`
    // to a synthetic `doc-*` URL the iframe couldn't render, and
    // replaced the live editor with a broken viewer — exactly the
    // "doc view crash" the user reported when clicking v1 of an
    // active draft.
    if (version.id === effectiveActiveVersionId) {
      docVersionDebugLog('ignored', {
        reason: 'already-active',
        documentId: selectedLogicalId ?? null,
        versionId: version.id,
      });
      return;
    }
    if (!version.fileId) {
      docVersionDebugLog('ignored', {
        reason: 'missing-file-id',
        documentId: selectedLogicalId ?? null,
        versionId: version.id,
      });
      return;
    }
    // Synthetic ids (`draft-*`, `doc-*`) don't correspond to real
    // entries in file storage. The version is legitimately in the
    // tree (it represents the in-conversation draft) but routing it
    // through the file-preview path yields a 400 because there's no
    // matching artifact. Until a dedicated "switch to a different
    // draft version" path that hydrates from `Conversation.documents[]`
    // exists, treat selecting these as a no-op rather than crashing
    // the doc view.
    const downloadUrl = documentVersionDownloadUrl(version, activeDocumentProject?.primaryConversationId ?? currentConversationId);
    const directLoadSupported =
      isConversationMongoDocumentFileId(version.fileId)
      || isFileStorageBackedFileId(version.fileId, downloadUrl);

    if (!directLoadSupported) {
      console.warn('[VersionSelect] Version has no resolvable content path', {
        documentId: selectedLogicalId ?? null,
        versionId: version.id,
        fileId: version.fileId,
        downloadUrl,
      });
      docVersionDebugLog('load_decision', {
        documentId: selectedLogicalId ?? null,
        versionId: version.id,
        contentSource: 'mismatch',
        fileId: version.fileId,
        downloadUrl: downloadUrl ?? null,
        reason: 'unresolvable-file-path',
      });
      setCurrentVersionId(version.id);
      if (selectedLogicalId) setCurrentLogicalDocumentId(selectedLogicalId);
      if (compareVersionId === version.id) setCompareVersionId(null);
      return;
    }

    if (versionOutsideLogicalDocument({ logicalDocumentId: currentLogicalDocumentId, version })) {
      console.warn('[VersionSelect] Selected version belongs to a different logical document', {
        selectedLogicalId,
        currentLogicalDocumentId,
        versionId: version.id,
        projectId: version.projectId ?? null,
      });
    }
    if (!downloadUrl) {
      docVersionDebugLog('load_decision', {
        documentId: selectedLogicalId ?? null,
        versionId: version.id,
        contentSource: 'missing',
        fileId: version.fileId,
        reason: 'missing-download-url',
      });
      return;
    }
    const conversationDocumentId = isConversationMongoDocumentFileId(version.fileId)
      ? version.fileId.slice(4)
      : null;
    if (conversationDocumentId) {
      const versionConversationId = version.conversationId ?? activeDocumentProject?.primaryConversationId ?? currentConversationId;
      if (!versionConversationId) return;
      void (async () => {
        try {
          const cachedDoc = conversationDocuments.find((doc) => doc.id === conversationDocumentId);
          const doc = cachedDoc ?? (await axios.get<{ document: ConversationDocument }>(
            `/api/conversations/${versionConversationId}/documents/${conversationDocumentId}`,
            { headers: auth.getToken() ? { Authorization: `Bearer ${auth.getToken()}` } : {} },
          )).data.document;
          if (DOC_VERSION_DEBUG) {
            console.log('[VersionLoad] select', {
              logicalDocumentId: selectedLogicalId ?? `conversation:${versionConversationId}`,
              documentVersion: doc.version,
              htmlLength: doc.html.length,
            });
          }
          const title = doc.title || version.fileName || draftTitle || 'Document';
          const htmlResult: ToolResult = {
            fileId: version.fileId,
            downloadUrl,
            fileName: `${title.replace(/\.(docx|doc|html?)$/i, '') || 'Document'}.html`,
            mimeType: 'text/html',
            sizeBytes: doc.html.length,
            kind: 'document',
            documentId: doc.id,
            documentVersion: doc.version,
            draftHtml: doc.html,
            draftTitle: title,
          };
          setConversationDocuments((prev) => (
            prev.some((candidate) => candidate.id === doc.id)
              ? prev.map((candidate) => candidate.id === doc.id ? doc : candidate)
              : [...prev, doc].sort((a, b) => Number(a.version) - Number(b.version))
          ));
          setLastConversionOutput(null);
          setDraftContent(doc.html);
          setDraftTitle(title);
          setCurrentResult(htmlResult);
          setSessionPrimaryDocument({
            fileId: version.fileId,
            fileName: htmlResult.fileName,
            mimeType: 'text/html',
            sizeBytes: htmlResult.sizeBytes,
            downloadUrl,
            source: version.source === 'generated' ? 'generated' : 'result',
          });
          setDocumentPreviewVariant('original');
          setFilePreviewUrl(null);
          setFilePreviewSource(null);
          setMode('drafting');
          if (selectedLogicalId) setCurrentLogicalDocumentId(selectedLogicalId);
          setCurrentVersionId(version.id);
          if (compareVersionId === version.id) setCompareVersionId(null);
          setVersionVerifyResult(null);
        } catch (error) {
          console.warn('[VersionSelect] Failed to load conversation document version', {
            conversationId: versionConversationId,
            documentId: conversationDocumentId,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      })();
      return;
    }
    const contentSource =
      isConversationMongoDocumentFileId(version.fileId) || /html/i.test(version.mimeType ?? '')
        ? 'htmlDraft'
        : /pdf/i.test(version.mimeType ?? '')
          ? 'pdfFile'
          : 'mismatch';
    docVersionDebugLog('load_decision', {
      documentId: selectedLogicalId ?? null,
      versionId: version.id,
      contentSource,
      fileId: version.fileId,
      downloadUrl,
    });
    console.info('[VersionSelect] User switched document version', {
      documentId: selectedLogicalId ?? null,
      prevVersionId: effectiveActiveVersionId ?? null,
      nextVersionId: version.id,
    });
    const authedUrl = appendTokenToUrl(downloadUrl, auth.getToken);
    setLastConversionOutput(null);
    setDocumentPreviewVariant('original');
    setFilePreviewUrl(authedUrl);
    setFilePreviewSource('restored');
    setMode('result');
    const displayFileName = displayFileNameForProjectVersion(activeDocumentProject?.title ?? 'Document', version) ?? version.fileName;
    setCurrentResult({
      fileId: version.fileId,
      downloadUrl,
      fileName: displayFileName,
      mimeType: version.mimeType,
      sizeBytes: 0,
    });
    setSessionPrimaryDocument({
      fileId: version.fileId,
      fileName: displayFileName,
      mimeType: version.mimeType,
      sizeBytes: 0,
      downloadUrl,
      source: version.source === 'generated' ? 'generated' : 'result',
    });
    if (selectedLogicalId) setCurrentLogicalDocumentId(selectedLogicalId);
    setCurrentVersionId(version.id);
    if (compareVersionId === version.id) setCompareVersionId(null);
    setVersionVerifyResult(null);
  }, [
    activeDocumentProject?.id,
    activeDocumentProject?.primaryConversationId,
    auth.getToken,
    compareVersionId,
    conversationDocuments,
    currentConversationId,
    currentLogicalDocumentId,
    currentVersionId,
    draftTitle,
    effectiveActiveVersionId,
  ]);

  const switchToProjectCurrentVersion = useCallback((project: import('./types').DocumentProjectDetail) => {
    const currentVersion = project.currentVersion
      ?? project.versions.find((version) => version.id === project.currentVersionId)
      ?? project.versions.at(-1)
      ?? null;
    if (!currentVersion) return;
    handleSelectDocumentVersion(currentVersion);
  }, [handleSelectDocumentVersion]);

  const handleSelectVersionTimelineEntry = useCallback((entry: VersionTimelineEntry) => {
    const matchingVersion = displayDocumentVersions.find((version) => {
      const fileIdMatches = version.fileId === `doc-${entry.documentId}`;
      const versionMatches = String(version.versionNumber) === String(entry.version);
      return fileIdMatches || versionMatches;
    });
    if (matchingVersion) {
      handleSelectDocumentVersion(matchingVersion);
    }
    setVersionTimelineOverlay(false);
    if (entry.kind === 'edit_batch') {
      window.setTimeout(() => {
        window.dispatchEvent(new CustomEvent('corner:cot-focus-edit-batch', {
          detail: { batchId: entry.batchId },
        }));
      }, 0);
    }
  }, [displayDocumentVersions, handleSelectDocumentVersion]);

  const reconcileProjectVersionSelection = useCallback((project: import('./types').DocumentProjectDetail) => {
    setActiveDocumentProject(project);
    const selectedVersionId = currentVersionId ?? effectiveActiveVersionId;
    if (shouldPreserveSelectedVersionAfterProjectRefresh({
      selectedVersionId,
      projectVersions: project.versions,
    })) {
      setCurrentLogicalDocumentId(project.id);
      docVersionDebugLog('selection_preserved_after_project_refresh', {
        documentId: project.id,
        selectedVersionId,
      });
      return;
    }
    switchToProjectCurrentVersion(project);
  }, [currentVersionId, effectiveActiveVersionId, switchToProjectCurrentVersion]);

  // ─── SSE: Real-time document signing events ─────────────────────────
  // Subscribe while editing the doc in esign mode, OR while the user has an
  // in-flight envelope in `activeSigningRequest` (e.g. Signatures hub / chat /
  // another tab of the shell) — otherwise emit has zero subscribers.
  //
  // Must run AFTER `currentProjectId`, `refreshDocumentProjects`, and
  // `switchToProjectCurrentVersion` exist — their ids appear in the dependency
  // array; placing this effect earlier causes TDZ ("Cannot access before
  // initialization") in production bundles.
  useEffect(() => {
    const esignDocId = mode === 'esign' ? currentResult?.fileId : undefined;
    const pend = activeSigningRequest?.status === 'pending' || activeSigningRequest?.status === 'partially_signed';
    const trackedDocId = pend && activeSigningRequest?.documentFileId ? activeSigningRequest.documentFileId : undefined;
    const docId = esignDocId ?? trackedDocId;
    if (!docId) {
      documentEventService.disconnect();
      return;
    }

    const token = auth.getToken();
    documentEventService.connect(docId, token);

    const handleFieldSigned = (event: import('./services/documentEventService').DocumentEvent) => {
      if (!event.fieldId) return;
      // Update local esign fields to reflect the signed state
      setEsignFields((prev) =>
        prev.map((f) =>
          f.id === event.fieldId
            ? { ...f, placed: true, signedAt: Date.now() }
            : f
        )
      );
    };

    const handleAllSigned = (event: import('./services/documentEventService').DocumentEvent) => {
      setSigningRequestsRefreshKey((k) => k + 1);
      const docName = currentResult?.fileName ?? 'your document';
      const versionLabel = event.versionNumber ? `v${event.versionNumber}` : 'a new signed version';
      toast({
        title: 'All parties have signed',
        description: `${docName} is fully signed. ${versionLabel} was added to the document versions.`,
      });

      if (event.projectId) {
        void projectApi.getProject(event.projectId, auth.getToken)
          .then((project) => {
            if (project.id === currentProjectId || project.id === activeDocumentProject?.id) {
              reconcileProjectVersionSelection(project);
              setVersionRailPeekKey((k) => k + 1);
            }
            void refreshDocumentProjects();
          })
          .catch(() => {
            void refreshDocumentProjects();
          });
      } else {
        void refreshDocumentProjects();
      }

      // Refresh the signing request so SigningStatusPanel shows the download button.
      // Read activeSigningRequest from state at call time (avoid stale closure).
      setActiveSigningRequest((prev) => {
        if (prev?.requestId) {
          const tk = auth.getToken();
          if (tk) {
            axios.get(`/api/signing/${prev.requestId}`, {
              headers: { Authorization: `Bearer ${tk}` },
            }).then((res) => {
              ingestSigningRequestDetail(res.data);
            }).catch(() => { /* best-effort refresh */ });
          }
        }
        return prev;
      });

      // Add a chat message so the completion is persistent
      setMessages((prev) => [...prev, {
        id: crypto.randomUUID(),
        role: 'corner' as const,
        content: `All signatures have been collected for ${docName}. The signed copy is now saved as ${versionLabel} in this document's versions.`,
        timestamp: Date.now(),
      }]);
    };

    documentEventService.on('FIELD_SIGNED', handleFieldSigned);
    documentEventService.on('ALL_SIGNED', handleAllSigned);

    return () => {
      documentEventService.off('FIELD_SIGNED', handleFieldSigned);
      documentEventService.off('ALL_SIGNED', handleAllSigned);
      documentEventService.disconnect();
    };
  }, [
    mode,
    currentResult?.fileId,
    activeSigningRequest?.status,
    activeSigningRequest?.documentFileId,
    auth.getToken,
    currentProjectId,
    activeDocumentProject?.id,
    currentResult?.fileName,
    ingestSigningRequestDetail,
    refreshDocumentProjects,
    reconcileProjectVersionSelection,
  ]);

  const handleVerifyDocumentVersion = useCallback(async (baseVersionId: string) => {
    if (!activeDocumentProject) return;
    const targetVersionId = activeDocumentProject.currentVersionId ?? activeDocumentProject.versions.at(-1)?.id ?? null;
    try {
      const result = await projectApi.verifyVersion(activeDocumentProject.id, baseVersionId, targetVersionId, auth.getToken);
      setVersionVerifyResult(result);
    } catch (error) {
      const e = getActionError('verify versions', error);
      toast({ title: e.title, description: e.description, variant: 'destructive' });
    }
  }, [activeDocumentProject, auth.getToken]);

  /**
   * Click handler for "Open in doc" in the Issues panel. Switches
   * back to the Documents tab, selects the flag's source version, and
   * fires a best-effort flag-locate so the doc viewer can scroll the
   * right clause into view. The locate failure is intentionally
   * silent: the user still gets the right document open even if the
   * highlight doesn't land.
   */
  const handleOpenIssueFlag = useCallback<IssuesPanelOnOpenFlag>((target) => {
    // Switch to the Documents tab and select the right version so the
    // user is looking at the document the flag belongs to.
    setProjectWorkspaceTab('documents');
    setClauseReviewSetupOpen(false);
    const targetVersion = displayDocumentVersions.find((version) => version.id === target.versionId);
    if (targetVersion) {
      handleSelectDocumentVersion(targetVersion);
    }
    // Show a busy popover scaffold immediately so the click feels
    // responsive while we fetch the full review + locate result.
    setInlineClauseFlagBusy(true);
    void Promise.all([
      clauseReviewApi.getClauseReview(target.reviewId),
      clauseReviewApi.locateClauseFlag(target.reviewId, target.flagId),
    ])
      .then(([reviewSummary, located]) => {
        const flag = reviewSummary.flags.find((f) => f.flagId === target.flagId);
        if (!flag) return;
        // Open the inline floating popover. Crucially we do NOT set
        // `mode = 'review'`, do NOT call `setSourcePreview` (drawer),
        // and do NOT load the full suggestion list into the review
        // panel — that whole cascade is what was destroying the
        // layout (chat -> ReviewPanel cards, doc gets ReviewModeBar,
        // drawer overlays everything). The lightweight popover stays
        // anchored top-right and the rest of the workspace renders
        // exactly as it did before the click.
        setInlineClauseFlagPopover({
          reviewId: target.reviewId,
          flag,
          located,
          versionId: target.versionId,
          fileId: target.fileId,
        });
        setFlagGroundingStatuses((prev) => ({
          ...prev,
          [target.flagId]: groundingStatusFromLocateResult(located),
        }));
        // Jump the PDF iframe to the page where the clause lives so
        // the user can see the clause behind the popover. Prefer the
        // located page (matched against text); fall back to pageHint.
        const targetPage = located.page ?? flag.pageHint ?? null;
        if (targetPage != null) {
          const iframe = document.querySelector('iframe[title="Document preview"]') as HTMLIFrameElement | null;
          if (iframe?.src) {
            const base = iframe.src.replace(/#.*$/, '');
            iframe.src = `${base}#page=${targetPage}`;
          }
        }
      })
      .catch(() => { /* best-effort navigation hint; no toast */ })
      .finally(() => setInlineClauseFlagBusy(false));
  }, [displayDocumentVersions, handleSelectDocumentVersion]);

  /**
   * Re-jump the PDF to the popover flag's page. Useful if the user has
   * scrolled the iframe away from the clause and wants to find it
   * again without re-clicking "Open in doc" from the Issues tab.
   */
  const handleInlinePopoverLocate = useCallback(() => {
    const popover = inlineClauseFlagPopover;
    if (!popover) return;
    const targetPage = popover.located?.page ?? popover.flag.pageHint ?? null;
    if (targetPage == null) return;
    const iframe = document.querySelector('iframe[title="Document preview"]') as HTMLIFrameElement | null;
    if (iframe?.src) {
      const base = iframe.src.replace(/#.*$/, '');
      iframe.src = `${base}#page=${targetPage}`;
    }
  }, [inlineClauseFlagPopover]);

  /**
   * Accept or dismiss the popover's flag. Routes through the same
   * PATCH endpoint the in-chat ReviewPanel uses, so the project Issues
   * count refreshes via `notifyClauseFlagsUpdated` and the bubble +
   * panel update without further action from the user.
   */
  const handleInlinePopoverPatch = useCallback(
    (status: 'accepted' | 'dismissed') => {
      const popover = inlineClauseFlagPopover;
      if (!popover || inlineClauseFlagBusy) return;
      setInlineClauseFlagBusy(true);
      void clauseReviewApi
        .patchClauseFlag(popover.reviewId, popover.flag.flagId, status)
        .then(() => {
          setInlineClauseFlagPopover(null);
        })
        .catch(() => { /* leave popover open so user can retry */ })
        .finally(() => setInlineClauseFlagBusy(false));
    },
    [inlineClauseFlagPopover, inlineClauseFlagBusy],
  );

  const handleInlinePopoverClose = useCallback(() => {
    setInlineClauseFlagPopover(null);
  }, []);

  /**
   * Empty-state CTA: launch the clause-review setup dialog targeting
   * the version the user passed in. We switch to Documents, select
   * the version, and pop the existing dialog — the user picks lane /
   * jurisdiction and the existing `handleLaunchClauseReview` flow
   * runs unchanged.
   */
  const handleRunClauseReviewFromIssues = useCallback((versionId: string) => {
    const targetVersion = displayDocumentVersions.find((version) => version.id === versionId);
    if (
      targetVersion?.fileId
      && isClauseReviewRunnableSourceId(targetVersion.fileId)
    ) {
      const title =
        displayFileNameForProjectVersion(activeDocumentProject?.title ?? 'Document', targetVersion)
        ?? targetVersion.fileName
        ?? 'Document';
      clauseReviewPendingSourceRef.current = {
        fileId: targetVersion.fileId,
        title,
        sourceKind: clauseReviewSourceKindForId(targetVersion.fileId),
      };
    } else {
      clauseReviewPendingSourceRef.current = null;
    }
    if (targetVersion) {
      handleSelectDocumentVersion(targetVersion);
    }
    openClauseReviewSetupScreen();
  }, [activeDocumentProject?.title, displayDocumentVersions, handleSelectDocumentVersion, openClauseReviewSetupScreen]);

  const handleEditBatchVersionReady = useCallback((batch: EditBatch, htmlRevision?: HtmlEditApplyResult) => {
    if (batch.applyMode === 'conversation_html_revision' && htmlRevision) {
      const conversationId = batch.conversationId ?? currentConversationId;
      if (!conversationId) return;
      const title = htmlRevision.draftTitle?.trim() || batch.draftTitle || 'Document';
      const result: ToolResult = {
        fileId: `doc-${htmlRevision.documentId}`,
        downloadUrl: `/api/conversations/${conversationId}/documents/${htmlRevision.documentId}`,
        fileName: `${title.replace(/\.(docx|doc|html?)$/i, '') || 'Document'}.html`,
        mimeType: 'text/html',
        sizeBytes: htmlRevision.draftHtml.length,
        kind: 'document',
        documentId: htmlRevision.documentId,
        documentVersion: htmlRevision.documentVersion,
        draftHtml: htmlRevision.draftHtml,
        draftTitle: title,
      };
      const now = new Date().toISOString();
      const appliedDocument: ConversationDocument = {
        id: htmlRevision.documentId,
        title,
        html: htmlRevision.draftHtml,
        version: htmlRevision.documentVersion,
        createdAt: now,
        updatedAt: now,
        originPrompt: null,
      };
      setConversationDocuments((prev) => {
        const existingIndex = prev.findIndex((doc) => doc.id === appliedDocument.id);
        const next = existingIndex >= 0
          ? prev.map((doc, index) => (
              index === existingIndex
                ? {
                    ...doc,
                    ...appliedDocument,
                    createdAt: doc.createdAt || appliedDocument.createdAt,
                  }
                : doc
            ))
          : [...prev, appliedDocument];
        return next.sort((a, b) => {
          const versionDelta = Number(a.version) - Number(b.version);
          if (Number.isFinite(versionDelta) && versionDelta !== 0) return versionDelta;
          return String(a.createdAt ?? '').localeCompare(String(b.createdAt ?? ''));
        });
      });
      setDraftContent(htmlRevision.draftHtml);
      setDraftTitle(title);
      setCurrentResult(result);
      setSessionPrimaryDocument({
        fileId: result.fileId,
        fileName: result.fileName,
        mimeType: result.mimeType,
        sizeBytes: result.sizeBytes,
        downloadUrl: result.downloadUrl,
        source: 'generated',
      });
      setMode('drafting');
      const logicalDocumentId = resolveLogicalDocumentId({
        activeProjectId: activeDocumentProject?.id ?? null,
        currentProjectId,
        currentConversationId: conversationId,
      }) ?? `conversation:${conversationId}`;
      setCurrentLogicalDocumentId(logicalDocumentId);
      const appliedVersionId = `workspace:file:${result.fileId}:v${htmlRevision.documentVersion}`;
      setCurrentVersionId(appliedVersionId);
      const createdBy = auth.user?.id ?? 'local';
      const acceptedEditCount = htmlRevision.appliedIds?.length ?? batch.edits.filter((edit) => edit.status === 'accepted').length;
      const documentsForVersions = [
        ...conversationDocuments.filter((doc) => doc.id !== appliedDocument.id),
        appliedDocument,
      ].sort((a, b) => {
        const versionDelta = Number(a.version) - Number(b.version);
        if (Number.isFinite(versionDelta) && versionDelta !== 0) return versionDelta;
        return String(a.createdAt ?? '').localeCompare(String(b.createdAt ?? ''));
      });
      const fallbackVersions = documentsForVersions.map((doc): import('./types').DocumentVersionSummary => ({
        id: `workspace:file:doc-${doc.id}:v${doc.version}`,
        projectId: logicalDocumentId,
        versionNumber: doc.version,
        fileId: `doc-${doc.id}`,
        fileName: `${(doc.title || title || 'Document').replace(/\.(docx|doc|html?)$/i, '') || 'Document'}.html`,
        mimeType: 'text/html',
        source: doc.id === appliedDocument.id ? 'ai_edit' : 'generated',
        lineageKind: 'version',
        createdBy,
        createdFromVersionId: null,
        changeSummary: doc.id === appliedDocument.id
          ? `Applied ${acceptedEditCount} accepted edit${acceptedEditCount === 1 ? '' : 's'}.`
          : 'Draft version.',
        conversationId,
        createdAt: doc.createdAt ?? now,
      }));
      const existingVersions = activeDocumentProject?.versions?.length
        ? activeDocumentProject.versions
        : fallbackVersions;
      const existingVersion = existingVersions.find((version) => version.fileId === result.fileId);
      const appliedVersion: import('./types').DocumentVersionSummary = {
        id: existingVersion?.id ?? appliedVersionId,
        projectId: logicalDocumentId,
        versionNumber: htmlRevision.documentVersion,
        fileId: result.fileId,
        fileName: result.fileName,
        mimeType: result.mimeType,
        source: 'ai_edit',
        lineageKind: 'version',
        createdBy,
        createdFromVersionId: activeDocumentProject?.currentVersionId ?? null,
        changeSummary: `Applied ${acceptedEditCount} accepted edit${acceptedEditCount === 1 ? '' : 's'}.`,
        conversationId,
        createdAt: now,
      };
      const versions = existingVersion
        ? existingVersions.map((version) => version.fileId === result.fileId ? appliedVersion : version)
        : [...existingVersions, appliedVersion];
      setActiveDocumentProject({
        id: logicalDocumentId,
        title: activeDocumentProject?.title ?? title,
        folderId: activeDocumentProject?.folderId ?? currentConversationFolderId ?? null,
        primaryConversationId: conversationId,
        currentVersionId: appliedVersion.id,
        currentVersion: appliedVersion,
        versionCount: versions.length,
        createdFrom: activeDocumentProject?.createdFrom ?? 'conversation',
        lastActivityAt: now,
        createdAt: activeDocumentProject?.createdAt ?? now,
        updatedAt: now,
        versions,
        conversations: activeDocumentProject?.conversations ?? [],
      });
      const nextCotSteps = cotStepsRef.current.map((step) =>
        step.editBatch?.id === batch.id ? { ...step, editBatch: batch } : step,
      );
      const snapshot = buildWorkspaceSessionSnapshot({
        draftContent: htmlRevision.draftHtml,
        draftTitle: title,
        mode: 'drafting',
        currentResult: result,
        cotSteps: nextCotSteps,
      });
      persistConversationSnapshot(conversationId, snapshot).catch((persistErr) => {
        console.error('[HtmlEdit] persist applied revision snapshot failed', persistErr);
      });
      void warmDraftPdfMirrorIfFresh({ conversationId, html: htmlRevision.draftHtml, title });
      console.log('[CoT] edit-batch-applied', {
        conversationId,
        batchId: batch.id,
        documentId: htmlRevision.documentId,
        documentVersion: htmlRevision.documentVersion,
      });
      console.log('[HtmlEditApply] workspace-updated', {
        conversationId,
        logicalDocumentId,
        documentId: htmlRevision.documentId,
        documentVersion: htmlRevision.documentVersion,
      });
      return;
    }

    const version = batch.resultingVersion;
    if (!version.fileId) return;

    const downloadUrl = version.downloadUrl ?? `/api/file/${version.fileId}`;
    const fileName = version.filename ?? batch.sourceFilename ?? currentResult?.fileName ?? sessionPrimaryDocument?.fileName ?? 'Document.pdf';
    const mimeType = version.mimeType ?? batch.sourceMimeType ?? currentResult?.mimeType ?? sessionPrimaryDocument?.mimeType ?? 'application/pdf';
    const isPdfVersion = mimeType === 'application/pdf' || fileName.toLowerCase().endsWith('.pdf');
    const acceptedCount = batch.edits.filter((edit) => edit.status === 'accepted').length;
    const baseDoc = sessionPrimaryDocument?.fileId
      ? sessionPrimaryDocument
      : currentResult?.fileId
        ? {
            fileId: currentResult.fileId,
            fileName: currentResult.fileName,
            mimeType: currentResult.mimeType,
          }
        : batch.docId && batch.docId !== version.fileId
          ? {
              fileId: batch.docId,
              fileName: batch.sourceFilename ?? 'Original document.pdf',
              mimeType: batch.sourceMimeType ?? 'application/pdf',
            }
          : null;
    const result: ToolResult = {
      fileId: version.fileId,
      downloadUrl,
      fileName,
      mimeType,
      sizeBytes: currentResult?.sizeBytes ?? sessionPrimaryDocument?.sizeBytes ?? 0,
      sourceFileId: batch.docId,
    };
    pendingDocEditVisibleFileRef.current = {
      fileId: version.fileId,
      batchId: batch.id,
      conversationId: currentConversationId,
      createdAt: Date.now(),
    };
    console.log('[DocEditAcceptAll] response', {
      batchId: batch.id,
      resultingFileId: version.fileId,
      currentDocumentId: currentResult?.fileId ?? sessionPrimaryDocument?.fileId ?? activeVisibleDocFileId ?? activeDocFileId ?? null,
    });
    console.log('[DocEditPipeline] visible-file-post-apply', {
      batchId: batch.id,
      documentId: batch.docId,
      visibleFileId: currentResult?.fileId ?? sessionPrimaryDocument?.fileId ?? null,
      resultingFileId: version.fileId,
      modalState: 'applied',
    });

    setCurrentResult(result);
    setLastConversionOutput(result);
    setMode('result');
    setDocumentPreviewVariant(isPdfVersion ? 'output' : 'original');
    if (isPdfVersion) {
      setFilePreviewUrl(appendTokenToUrl(downloadUrl, auth.getToken));
      setFilePreviewSource('restored');
    }
    setSessionPrimaryDocument({
      fileId: version.fileId,
      fileName,
      mimeType,
      sizeBytes: result.sizeBytes,
      downloadUrl,
      source: 'result',
    });
    addNode(fileName, undefined, downloadUrl, `AI edit v${version.version}`);
    setVersionVerifyResult(null);
    if (acceptedCount > 0 && currentConversationId) {
      if (batch.resultingProject) {
        const canonicalVersionId = batch.resultingVersionId
          ?? batch.resultingProject.currentVersionId
          ?? batch.resultingProject.versions.find((candidate) => candidate.fileId === version.fileId)?.id
          ?? null;
        setActiveDocumentProject(batch.resultingProject);
        setCurrentLogicalDocumentId(batch.resultingProject.id);
        if (canonicalVersionId) setCurrentVersionId(canonicalVersionId);
        console.info('[DocVersionSync] canonical-edit-version', {
          batchId: batch.id,
          lineageId: batch.resultingProject.id,
          resultingVersionId: canonicalVersionId,
          resultingFileId: version.fileId,
        });
      } else {
      const now = new Date().toISOString();
      const optimisticProjectId = resolveLogicalDocumentId({
        activeProjectId: activeDocumentProject?.id ?? null,
        currentProjectId,
        currentConversationId,
      }) ?? `conversation:${currentConversationId}`;
      const optimisticCreatedBy = auth.user?.id ?? 'local';
      const originalVersion: import('./types').DocumentVersionSummary | null = baseDoc?.fileId && baseDoc.fileId !== version.fileId
        ? {
            id: activeDocumentProject?.versions.find((candidate) => candidate.fileId === baseDoc.fileId)?.id ?? `local:${baseDoc.fileId}`,
            projectId: optimisticProjectId,
            versionNumber: 1,
            fileId: baseDoc.fileId,
            fileName: baseDoc.fileName ?? batch.sourceFilename ?? 'Original document.pdf',
            mimeType: baseDoc.mimeType ?? batch.sourceMimeType ?? 'application/pdf',
            source: 'source' in baseDoc && baseDoc.source === 'generated' ? 'generated' : 'original',
            lineageKind: 'version',
            createdBy: optimisticCreatedBy,
            createdFromVersionId: null,
            changeSummary: 'Original document.',
            conversationId: currentConversationId,
            createdAt: now,
          }
        : null;
      const existingVersions = activeDocumentProject?.versions ?? [];
      const withOriginal = originalVersion && !existingVersions.some((candidate) => candidate.fileId === originalVersion.fileId)
        ? [originalVersion, ...existingVersions]
        : existingVersions;
      const existingEdited = withOriginal.find((candidate) => candidate.fileId === version.fileId);
      const editedVersion: import('./types').DocumentVersionSummary = {
        id: existingEdited?.id ?? `local:${version.fileId}`,
        projectId: optimisticProjectId,
        versionNumber: existingEdited?.versionNumber ?? Math.max(1, withOriginal.length + 1),
        fileId: version.fileId,
        fileName,
        mimeType,
        source: 'ai_edit',
        lineageKind: 'version',
        createdBy: optimisticCreatedBy,
        createdFromVersionId: activeDocumentProject?.currentVersionId ?? originalVersion?.id ?? null,
        changeSummary: `Accepted ${acceptedCount} staged edit${acceptedCount === 1 ? '' : 's'}.`,
        conversationId: currentConversationId,
        createdAt: now,
      };
      const versions = withOriginal.some((candidate) => candidate.fileId === editedVersion.fileId)
        ? withOriginal.map((candidate) => candidate.fileId === editedVersion.fileId ? editedVersion : candidate)
        : [...withOriginal, editedVersion];
      setActiveDocumentProject({
        id: optimisticProjectId,
        title: activeDocumentProject?.title ?? batch.sourceFilename ?? fileName,
        folderId: activeDocumentProject?.folderId ?? currentConversationFolderId ?? null,
        primaryConversationId: currentConversationId,
        currentVersionId: editedVersion.id,
        currentVersion: editedVersion,
        versionCount: versions.length,
        createdFrom: activeDocumentProject?.createdFrom ?? 'conversation',
        lastActivityAt: now,
        createdAt: activeDocumentProject?.createdAt ?? now,
        updatedAt: now,
        versions,
        conversations: activeDocumentProject?.conversations ?? [],
      });
      }
    }

    void (async () => {
      if (!auth.user || !currentConversationId) return;
      if (batch.resultingProject) {
        void refreshDocumentProjects();
        return;
      }
      try {
        let project = activeDocumentProject?.id.startsWith('local:') ? null : activeDocumentProject;

        if (!project) {
          const projectId = currentProjectId;
          if (projectId) {
            project = await projectApi.getProject(projectId, auth.getToken);
          } else if (baseDoc?.fileId && baseDoc.fileId !== version.fileId) {
            const summary = await projectApi.ensureProject({
              conversationId: currentConversationId,
              title: batch.sourceFilename ?? baseDoc.fileName ?? fileName,
              folderId: currentConversationFolderId ?? null,
              file: {
                fileId: baseDoc.fileId,
                fileName: baseDoc.fileName,
                mimeType: baseDoc.mimeType,
              },
              source: sessionPrimaryDocument?.source === 'generated' ? 'generated' : 'original',
              changeSummary: 'Original document.',
            }, auth.getToken);
            project = await projectApi.getProject(summary.id, auth.getToken);
          } else {
            const summary = await projectApi.ensureProject({
              conversationId: currentConversationId,
              title: batch.sourceFilename ?? fileName,
              folderId: currentConversationFolderId ?? null,
              file: {
                fileId: version.fileId,
                fileName,
                mimeType,
              },
              source: 'ai_edit',
              changeSummary: acceptedCount > 0
                ? `Accepted ${acceptedCount} staged edit${acceptedCount === 1 ? '' : 's'}.`
                : 'AI edit artifact.',
            }, auth.getToken);
            project = await projectApi.getProject(summary.id, auth.getToken);
          }
        }

        if (!project) return;

        const nextProject = await projectApi.createProjectVersion(project.id, {
          fileId: version.fileId,
          fileName,
          mimeType,
          source: 'ai_edit',
          changeSummary: acceptedCount > 0
            ? `Accepted ${acceptedCount} staged edit${acceptedCount === 1 ? '' : 's'}.`
            : 'AI edit artifact.',
          conversationId: currentConversationId,
          createdFromVersionId: project.currentVersionId ?? null,
        }, auth.getToken);
        setActiveDocumentProject(nextProject);
        void refreshDocumentProjects();
      } catch (error) {
        console.warn('[Projects] Failed to register accepted edit version', error);
      }
    })();
  }, [
    activeDocumentProject,
    activeDocFileId,
    activeVisibleDocFileId,
    addNode,
    auth.getToken,
    auth.user,
    conversationDocuments,
    currentConversationFolderId,
    currentConversationId,
    currentProjectId,
    currentResult,
    persistConversationSnapshot,
    refreshDocumentProjects,
    sessionPrimaryDocument,
  ]);

  const handleEditBatchRecoveryVersionReady = useCallback((result: import('@corner/shared').DocEditRecoveryResult) => {
    const project = result.project ?? null;
    const version = project?.versions.find((candidate) => (
      candidate.id === result.versionId ||
      candidate.id === result.recovery.selectedVersionId ||
      candidate.fileId === result.fileId
    )) ?? null;
    if (!version) {
      console.warn('[DocEditRecovery] missing-recovery-version', {
        fileId: result.fileId,
        versionId: result.versionId,
        projectId: project?.id ?? null,
      });
      return;
    }
    const downloadUrl = documentVersionDownloadUrl(version, project?.primaryConversationId ?? currentConversationId);
    if (!downloadUrl) {
      console.warn('[DocEditRecovery] missing-recovery-download-url', {
        fileId: result.fileId,
        versionId: version.id,
      });
      return;
    }
    const toolResult: ToolResult = {
      fileId: version.fileId,
      downloadUrl,
      fileName: version.fileName,
      mimeType: version.mimeType,
      sizeBytes: 0,
      sourceFileId: result.recovery.sourceFinalFileId,
    };
    if (project) {
      setActiveDocumentProject(project);
      setCurrentLogicalDocumentId(project.id);
    }
    setCurrentVersionId(version.id);
    setCurrentResult(toolResult);
    setLastConversionOutput(toolResult);
    setDocumentPreviewVariant('original');
    setSessionPrimaryDocument({
      fileId: version.fileId,
      fileName: version.fileName,
      mimeType: version.mimeType,
      sizeBytes: 0,
      downloadUrl,
      source: 'conversion',
    });
    pendingDocEditVisibleFileRef.current = {
      fileId: version.fileId,
      batchId: result.editBatch.id,
      conversationId: currentConversationId,
      createdAt: Date.now(),
    };
    console.log('[DocEditRecovery] editable-draft-selected', {
      batchId: result.editBatch.id,
      sourceFinalFileId: result.recovery.sourceFinalFileId,
      fileId: version.fileId,
      versionId: version.id,
      projectId: project?.id ?? null,
    });
    void (async () => {
      if (!result.hasEditableDoc) {
        setMode('result');
        setFilePreviewUrl(appendTokenToUrl(downloadUrl, auth.getToken));
        setFilePreviewSource('restored');
        return;
      }
      const token = auth.getToken();
      if (!token) {
        setMode('result');
        setFilePreviewUrl(appendTokenToUrl(downloadUrl, auth.getToken));
        setFilePreviewSource('restored');
        return;
      }
      try {
        const res = await axios.get<{
          paragraphs: string[];
          blockAnchors?: { page: number; paragraphIndex: number }[];
        }>(`/api/orchestrate/working-document/${encodeURIComponent(version.fileId)}/paragraphs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const draftHtml = paragraphsToSimpleDraftHtml(res.data.paragraphs);
        const draftTitle = version.fileName.replace(/\.(docx?|pdf|html?)$/i, '').trim() || 'Editable copy from signed PDF';
        const editableResult: ToolResult = {
          ...toolResult,
          draftHtml,
          draftTitle,
        };
        setDraftContent(draftHtml);
        setDraftTitle(draftTitle);
        setCurrentResult(editableResult);
        setLastConversionOutput(editableResult);
        setDocumentText({
          paragraphs: res.data.paragraphs,
          blockAnchors: res.data.blockAnchors,
          loading: false,
        });
        setReaderView(false);
        setFilePreviewUrl(null);
        setFilePreviewSource('restored');
        setMode('drafting');
        console.log('[DocEditRecovery] editable-doc-opened', {
          batchId: result.editBatch.id,
          fileId: version.fileId,
          versionId: version.id,
          paragraphCount: res.data.paragraphs.length,
        });
      } catch (err) {
        console.warn('[DocEditRecovery] editable-doc-open-failed', {
          batchId: result.editBatch.id,
          fileId: version.fileId,
          message: err instanceof Error ? err.message : String(err),
        });
        setMode('result');
        setFilePreviewUrl(appendTokenToUrl(downloadUrl, auth.getToken));
        setFilePreviewSource('restored');
      }
    })();
    void refreshDocumentProjects();
  }, [auth.getToken, currentConversationId, refreshDocumentProjects]);

  // ── Real-time collaboration ──
  const collab = useCollaboration({
    conversationId: currentConversationId,
    getToken: auth.getToken,
    enabled: !!auth.user && !!currentConversationId,
  });

  // Fetch comment pins when conversation changes
  useEffect(() => {
    const commentToken = auth.getToken();
    if (!currentConversationId || !commentToken) { setCommentPins([]); return; }
    axios.get<{ results: CommentPinData[] }>(`/api/comments/${currentConversationId}`, {
      headers: { Authorization: `Bearer ${commentToken}` },
    }).then((res) => {
      const pins = res.data.results ?? [];
      insertedPinIds.current = new Set(pins.map((p: CommentPinData) => p.pinId));
      setCommentPins(pins);
    })
      .catch(() => { insertedPinIds.current.clear(); setCommentPins([]); });
  }, [currentConversationId, auth.getToken]);

  // Fetch document text when reader view is toggled on
  useEffect(() => {
    if (!readerView) return;
    const workingDocId = activeDocFileId;
    const fileId = currentResult?.fileId ?? sessionPrimaryDocument?.fileId ?? activeDocFileId;
    if (!workingDocId && !fileId) { setDocumentText({ paragraphs: [], loading: false, error: 'No document loaded' }); return; }
    const token = auth.getToken();
    if (!token) return;
    let cancelled = false;
    setDocumentText({ paragraphs: [], loading: true });
    (async () => {
      try {
        if (workingDocId) {
          const res = await axios.get<{
            paragraphs: string[];
            blockAnchors?: { page: number; paragraphIndex: number }[];
          }>(`/api/orchestrate/working-document/${encodeURIComponent(workingDocId)}/paragraphs`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (cancelled) return;
          setDocumentText({
            paragraphs: res.data.paragraphs,
            loading: false,
            blockAnchors: res.data.blockAnchors,
          });
          return;
        }
      } catch {
        // Some legacy/session-restored files only have persisted bytes,
        // not a live editable cache. Fall through to text extraction.
      }

      if (!fileId) {
        if (!cancelled) setDocumentText({ paragraphs: [], loading: false, error: 'No document loaded' });
        return;
      }
      try {
        const res = await axios.get<{ text: string; paragraphs: string[]; blockAnchors?: { page: number; paragraphIndex: number }[] }>(`/api/documents/${fileId}/text`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setDocumentText({
          paragraphs: res.data.paragraphs,
          loading: false,
          blockAnchors: res.data.blockAnchors,
        });
      } catch {
        if (!cancelled) setDocumentText({ paragraphs: [], loading: false, error: 'Could not extract text from this document' });
      }
    })();
    return () => { cancelled = true; };
  }, [readerView, activeDocFileId, currentResult?.fileId, sessionPrimaryDocument?.fileId, auth.getToken]);

  useEffect(() => {
    const handleViewEditAnchor = (event: Event) => {
      const detail = (event as CustomEvent<{ anchor?: string; docId?: string }>).detail;
      if (!detail?.anchor) return;
      if (detail.docId?.startsWith('draft:') && modeRef.current === 'drafting') {
        return;
      }
      setReaderView(true);
      setEditHighlightAnchor(detail.anchor);
    };
    window.addEventListener('corner:view-edit-anchor', handleViewEditAnchor);
    return () => window.removeEventListener('corner:view-edit-anchor', handleViewEditAnchor);
  }, []);

  useEffect(() => {
    const onWorkingDoc = async (ev: Event) => {
      const docId = (ev as CustomEvent<{ docId?: string }>).detail?.docId;
      if (!docId) return;
      const token = auth.getToken();
      if (!token) return;
      try {
        const res = await axios.get<{
          paragraphs: string[];
          blockAnchors?: { page: number; paragraphIndex: number }[];
        }>(`/api/orchestrate/working-document/${encodeURIComponent(docId)}/paragraphs`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const { paragraphs, blockAnchors } = res.data;
        if (modeRef.current === 'drafting' && docId.startsWith('draft:')) {
          setDraftContent(paragraphsToSimpleDraftHtml(paragraphs));
          return;
        }
        if (docId === displayedDocFileIdRef.current && readerView) {
          setDocumentText({ paragraphs, loading: false, blockAnchors });
        }
      } catch {
        // No in-memory working copy (e.g. server restart) — ignore
      }
    };
    window.addEventListener('corner:working-doc-updated', onWorkingDoc);
    return () => window.removeEventListener('corner:working-doc-updated', onWorkingDoc);
  }, [auth.getToken, readerView]);

  // Reset reader view state on conversation change
  useEffect(() => {
    setReaderView(false);
    setDocumentText(null);
    setPendingComment(null);
  }, [currentConversationId]);

  // Listen for real-time comment events
  useEffect(() => {
    collab.onCommentEvent((event) => {
      switch (event.type) {
        case 'created':
          if (!insertedPinIds.current.has(event.pin.pinId)) {
            insertedPinIds.current.add(event.pin.pinId);
            setCommentPins((prev) => [event.pin, ...prev]);
          }
          break;
        case 'reply':
          setCommentPins((prev) => prev.map((p) =>
            p.pinId === event.pinId
              ? { ...p, comments: [...p.comments, event.comment] }
              : p
          ));
          break;
        case 'resolved':
          setCommentPins((prev) => prev.map((p) =>
            p.pinId === event.pinId ? { ...p, status: 'resolved' } : p
          ));
          break;
        case 'moved':
          setCommentPins((prev) => prev.map((p) =>
            p.pinId === event.pinId ? { ...p, xPercent: event.xPercent, yPercent: event.yPercent } : p
          ));
          break;
        case 'deleted':
          insertedPinIds.current.delete(event.pinId);
          setCommentPins((prev) => prev.filter((p) => p.pinId !== event.pinId));
          break;
      }
    });
  }, [collab.onCommentEvent]);

  useEffect(() => {
    collab.onProjectEvent((event) => {
      if (event.type !== 'version_updated') return;
      const project = event.project;
      if (currentProjectId && project.id !== currentProjectId) return;
      reconcileProjectVersionSelection(project);
      void refreshDocumentProjects();
    });
  }, [collab.onProjectEvent, currentProjectId, reconcileProjectVersionSelection, refreshDocumentProjects]);

  // ── Comment mode keyboard shortcuts (C to enter, Esc to exit) ──
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      const isEditable = (e.target as HTMLElement).isContentEditable;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || isEditable) return;

      if (e.key === 'c' && !e.metaKey && !e.ctrlKey && !e.altKey && auth.user) {
        e.preventDefault();
        setCommentMode(true);
      }
      if (e.key === 'Escape' && commentMode) {
        e.preventDefault();
        setCommentMode(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [commentMode, auth.user]);

  // Exit comment mode when switching conversations
  useEffect(() => {
    setCommentMode(false);
  }, [currentConversationId]);

  // ── Fetch billing status (rateLimitInfo for free, billingInfo for all) ──
  useEffect(() => {
    if (!auth.user) return;
    const token = auth.getToken();
    if (!token) return;
    axios.get<{
      plan: string;
      subscription: { status: string; billingCycle: string; currentPeriodEnd: string; trialEnd: string | null; cancelAtPeriodEnd: boolean } | null;
      usage: { aiConversations: number };
      limits: { aiConversations: number | null };
    }>('/api/billing/status', {
      headers: { Authorization: `Bearer ${token}` },
    }).then((res) => {
      // Populate billingInfo for settings panel
      const sub = res.data.subscription;
      if (sub) {
        setBillingInfo({
          status: sub.status as BillingInfo['status'],
          billingCycle: sub.billingCycle as BillingInfo['billingCycle'],
          currentPeriodEnd: sub.currentPeriodEnd,
          trialEnd: sub.trialEnd,
          cancelAtPeriodEnd: sub.cancelAtPeriodEnd,
        });
      } else {
        setBillingInfo(null);
      }

      // Populate rateLimitInfo for free users
      if (auth.user?.plan === 'free') {
        const used = res.data.usage?.aiConversations ?? 0;
        const limit = res.data.limits?.aiConversations;
        if (limit != null && used >= limit) {
          const nextMonth = new Date();
          nextMonth.setUTCMonth(nextMonth.getUTCMonth() + 1, 1);
          setRateLimitInfo({
            used,
            limit,
            resetsLabel: nextMonth.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
            onUpgrade: () => setPaywallOpen(true),
            onViewUsage: () => navigate('/app/settings'),
          });
        } else {
          setRateLimitInfo(null);
        }
      } else {
        setRateLimitInfo(null);
      }
    }).catch(() => { /* silent */ });
  }, [auth.user, auth.getToken, currentConversationId]);

  // ─── Batch risk nudges for sidebar history items ──────────────────
  // Fetches risk summaries for all history items that have a sourceFileId.
  // NOTE: Must be after conversationList is declared to avoid TDZ error in production bundle.
  useEffect(() => {
    if (!auth.getToken()) return;
    const sourceIds = conversationList
      .filter((c: any) => !c.inTrash && !c.archived && c.sessionSnapshot?.primaryDocument?.fileId)
      .map((c: any) => c.sessionSnapshot!.primaryDocument!.fileId!)
      .filter(Boolean);
    if (sourceIds.length === 0) return;
    const key = sourceIds.sort().join(',');
    if (historyRiskFetchedRef.current === key) return;
    historyRiskFetchedRef.current = key;
    clauseReviewApi.getRiskSummariesBatch(sourceIds)
      .then((summaries) => {
        const data: Record<string, { nudge: 'attention' | 'reconsider'; highCount: number; medCount: number }> = {};
        for (const [sid, s] of Object.entries(summaries)) {
          if (s.nudgeLevel === 'attention' || s.nudgeLevel === 'reconsider') {
            data[sid] = { nudge: s.nudgeLevel, highCount: s.highRiskCount, medCount: s.mediumRiskCount };
          }
        }
        setHistoryRiskData(data);
      })
      .catch(() => { /* silent */ });
  }, [conversationList, auth]);

  const conversationIdsInFolder = useCallback(
    (folderId: string) =>
      conversationList.filter((c) => c.folderId === folderId).map((c) => c.id),
    [conversationList]
  );
  const foldersApi = useFolders({
    updateConversation,
    conversationIdsInFolder,
    isSignedIn: !!auth.user,
    getToken: auth.getToken,
  });
  const foldersWithCounts: FolderWithCount[] = useMemo(
    () =>
      foldersApi.folders.map((f) => ({
        ...f,
        conversationCount: f.conversationCount
          ?? Math.max(
            conversationList.filter((c) => c.folderId === f.id).length,
            documentProjects.filter((project) => project.folderId === f.id).length,
          ),
      })),
    [foldersApi.folders, conversationList, documentProjects],
  );
  const projectFolderRecentSources: ProjectFolderRecentSource[] = useMemo(() => {
    const byFileId = new Map<string, ProjectFolderRecentSource>();
    for (const project of documentProjects) {
      const version = project.currentVersion;
      if (!version?.fileId || isSyntheticDraftFileId(version.fileId)) continue;
      byFileId.set(version.fileId, {
        fileId: version.fileId,
        fileName: displayFileNameForProjectVersion(project.title, version) ?? version.fileName,
        mimeType: version.mimeType,
        title: project.title,
        updatedAt: project.lastActivityAt,
      });
    }
    for (const conversation of conversationList) {
      const doc = conversation.sessionSnapshot?.primaryDocument;
      const fileId = doc?.fileId ?? conversation.latestResultFileId;
      const fileName = doc?.fileName ?? conversation.latestResultFileName;
      const mimeType = doc?.mimeType ?? conversation.latestResultMimeType ?? 'application/octet-stream';
      if (!fileId || isSyntheticDraftFileId(fileId) || !fileName || byFileId.has(fileId)) continue;
      byFileId.set(fileId, {
        fileId,
        fileName,
        mimeType,
        title: conversation.title,
        updatedAt: conversation.lastMessageAt,
      });
    }
    return [...byFileId.values()].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
  }, [conversationList, documentProjects]);

  const handleCreateProjectFolder = useCallback(async (input: CreateProjectFolderInput) => {
    if (!auth.user) {
      navigate('/auth/login');
      return;
    }

    const folderName = input.name.trim().slice(0, 100) || 'New project';
    const folderId = await foldersApi.createFolder(folderName);
    const conversationId = await createConversation(folderName);
    await updateConversation(conversationId, { folderId });

    type ProjectSeedFile = {
      fileId: string;
      fileName: string;
      mimeType: string;
      sizeBytes: number;
      downloadUrl: string;
      source: 'upload' | 'manual';
    };
    const seedFiles: ProjectSeedFile[] = [];
    const recentSeeds = (input.recentSources?.length ? input.recentSources : input.recentSource ? [input.recentSource] : [])
      .filter((source) => source.fileId && !isSyntheticDraftFileId(source.fileId));
    for (const source of recentSeeds) {
      seedFiles.push({
        fileId: source.fileId,
        fileName: source.fileName,
        mimeType: source.mimeType,
        sizeBytes: 0,
        downloadUrl: `/api/file/${source.fileId}`,
        source: 'manual',
      });
    }
    const localSeeds = input.localFiles?.length ? input.localFiles : input.localFile ? [input.localFile] : [];
    for (const file of localSeeds) {
      const uploaded = await projectApi.uploadProjectSeedFile(file, auth.getToken);
      seedFiles.push({ ...uploaded, source: 'upload' });
    }
    const seedFile = seedFiles[0] ?? null;

    const summary = await projectApi.ensureProject({
      conversationId,
      title: folderName,
      folderId,
      file: seedFile
        ? { fileId: seedFile.fileId, fileName: seedFile.fileName, mimeType: seedFile.mimeType }
        : null,
      source: seedFile ? seedFile.source : 'manual',
      changeSummary: seedFiles.length > 0
        ? `Started project with ${seedFiles.length} initial document${seedFiles.length === 1 ? '' : 's'}.`
        : 'Started an empty project.',
    }, auth.getToken);
    let project = await projectApi.getProject(summary.id, auth.getToken);
    for (const extraSeed of seedFiles.slice(1)) {
      project = await projectApi.createProjectVersion(project.id, {
        fileId: extraSeed.fileId,
        fileName: extraSeed.fileName,
        mimeType: extraSeed.mimeType,
        source: extraSeed.source,
        changeSummary: 'Added during project setup.',
        conversationId,
        lineageKind: 'copy',
      }, auth.getToken);
    }

    setCurrentConversationId(conversationId);
    currentConversationIdRef.current = conversationId;
    setActiveDocumentProject(project);
    setVersionVerifyResult(null);
    setMessages([]);
    setCurrentFiles([]);
    setLastConversionOutput(null);
    setDocumentPreviewVariant('original');
    setProjectWorkspaceTab('documents');
    setRightOpen(true);
    setChatCollapsed(false);
    setFocusedDocumentView(false);
    setCanvasViewMode(false);
    setProcessing(null);
    cot.reset();

    if (seedFile) {
      const documentRef = {
        fileId: seedFile.fileId,
        fileName: seedFile.fileName,
        mimeType: seedFile.mimeType,
        sizeBytes: seedFile.sizeBytes,
        downloadUrl: seedFile.downloadUrl,
        source: seedFile.source === 'upload' ? 'upload' as const : 'result' as const,
      };
      const result = {
        fileId: seedFile.fileId,
        downloadUrl: seedFile.downloadUrl,
        fileName: seedFile.fileName,
        mimeType: seedFile.mimeType,
        sizeBytes: seedFile.sizeBytes,
      };
      setSessionPrimaryDocument(documentRef);
      setSessionSourceDocuments(seedFiles.map((file) => ({
        fileId: file.fileId,
        fileName: file.fileName,
        mimeType: file.mimeType,
        sizeBytes: file.sizeBytes,
        downloadUrl: file.downloadUrl,
        source: file.source === 'upload' ? 'upload' as const : 'result' as const,
      })));
      setCurrentResult(result);
      setFilePreviewUrl(appendTokenToUrl(seedFile.downloadUrl, auth.getToken));
      setFilePreviewSource('restored');
      setMode('result');
      addNode(seedFile.fileName, undefined, seedFile.downloadUrl, 'Project start');
      await persistConversationSnapshot(conversationId, {
        title: folderName,
        projectId: project.id,
        activeVersionId: project.currentVersionId ?? null,
        mode: 'result',
        primaryDocument: documentRef,
        sourceDocuments: seedFiles.map((file) => ({
          fileId: file.fileId,
          fileName: file.fileName,
          mimeType: file.mimeType,
          sizeBytes: file.sizeBytes,
          downloadUrl: file.downloadUrl,
          source: file.source === 'upload' ? 'upload' as const : 'result' as const,
        })),
        currentResult: result,
        lastConversionOutput: null,
        panelState: {
          chatCollapsed: false,
          focusedDocumentView: false,
          canvasViewMode: false,
          rightPanelOpen: true,
          toolPanelExpanded: false,
          documentPreviewVariant: 'original',
        },
        taskContext: { lastToolName: null, modeLabel: null },
      });
    } else {
      setSessionPrimaryDocument(null);
      setSessionSourceDocuments([]);
      setCurrentResult(null);
      setFilePreviewUrl(null);
      setFilePreviewSource(null);
      setMode('empty');
      const prompt = 'Upload a document or tell Corner what to do.';
      setMessages([{
        id: crypto.randomUUID(),
        role: 'corner' as const,
        content: prompt,
        timestamp: Date.now(),
      }]);
      await addConversationMessage(conversationId, { role: 'corner', content: prompt }).catch(() => {});
      await persistConversationSnapshot(conversationId, {
        title: folderName,
        projectId: project.id,
        activeVersionId: null,
        mode: 'empty',
        primaryDocument: null,
        sourceDocuments: [],
        currentResult: null,
        lastConversionOutput: null,
        panelState: {
          chatCollapsed: false,
          focusedDocumentView: false,
          canvasViewMode: false,
          rightPanelOpen: true,
          toolPanelExpanded: false,
          documentPreviewVariant: 'original',
        },
        taskContext: { lastToolName: null, modeLabel: null },
      });
    }

    for (const email of [...new Set(input.inviteEmails)]) {
      if (!/.+@.+\..+/.test(email)) continue;
      axios.post(
        `/api/sharing/${conversationId}/invite`,
        { email, displayName: email.split('@')[0], permission: 'edit' },
        { headers: auth.getToken() ? { Authorization: `Bearer ${auth.getToken()}` } : {} },
      ).catch((error) => console.warn('[ProjectFolder] Invite failed:', email, error));
    }

    await refreshDocumentProjects();
    navigate(conversationWorkspacePath(conversationId), { replace: true });
    toast({
      title: 'Project created',
      description: seedFiles.length > 0
        ? `${seedFiles.length} document${seedFiles.length === 1 ? '' : 's'} ready with Corner context.`
        : 'You can upload a document or start with a prompt.',
    });
  }, [
    addConversationMessage,
    addNode,
    auth,
    cot,
    createConversation,
    foldersApi,
    navigate,
    persistConversationSnapshot,
    refreshDocumentProjects,
    toast,
    updateConversation,
  ]);

  const handleDownloadFolderZip = useCallback((folderId: string) => {
    const url = appendTokenToUrl(projectApi.folderDownloadUrl(folderId), auth.getToken);
    window.open(url, '_blank');
  }, [auth.getToken]);

  const handleMoveProjectToFolder = useCallback(async (projectId: string, folderId: string | null) => {
    if (!auth.user) return;
    try {
      await projectApi.moveProjectToFolder(projectId, folderId, auth.getToken);
      await refreshDocumentProjects();
      toast({ title: 'Document moved' });
    } catch (err) {
      showAppError('Could not move document', err instanceof Error ? err.message : undefined);
    }
  }, [auth.getToken, auth.user, refreshDocumentProjects, showAppError, toast]);

  const handleDropFilesIntoFolder = useCallback(async (files: FileList | File[], folderId: string) => {
    if (!auth.user) {
      navigate('/auth/login');
      return;
    }
    const list = Array.from(files).filter((file) => file.size > 0);
    if (list.length === 0) return;

    try {
      for (const file of list) {
        const uploaded = await projectApi.uploadProjectSeedFile(file, auth.getToken);
        const title = file.name.replace(/\.[^.]+$/, '') || file.name || 'Uploaded document';
        const conversationId = await createConversation(title);
        await updateConversation(conversationId, { folderId });
        const summary = await projectApi.ensureProject({
          conversationId,
          title,
          folderId,
          file: {
            fileId: uploaded.fileId,
            fileName: uploaded.fileName,
            mimeType: uploaded.mimeType,
          },
          source: 'upload',
          changeSummary: 'Added to project by drag and drop.',
        }, auth.getToken);
        const result = {
          fileId: uploaded.fileId,
          downloadUrl: uploaded.downloadUrl,
          fileName: uploaded.fileName,
          mimeType: uploaded.mimeType,
          sizeBytes: uploaded.sizeBytes,
        };
        const documentRef = {
          ...result,
          source: 'upload' as const,
        };
        await persistConversationSnapshot(conversationId, {
          title,
          projectId: summary.id,
          activeVersionId: summary.currentVersionId ?? null,
          mode: 'result',
          primaryDocument: documentRef,
          sourceDocuments: [documentRef],
          currentResult: result,
          lastConversionOutput: null,
          panelState: {
            chatCollapsed: false,
            focusedDocumentView: false,
            canvasViewMode: false,
            rightPanelOpen: true,
            toolPanelExpanded: false,
            documentPreviewVariant: 'original',
          },
          taskContext: { lastToolName: null, modeLabel: null },
        });
      }
      await refreshDocumentProjects();
      toast({ title: list.length === 1 ? 'Document added' : 'Documents added' });
    } catch (err) {
      showAppError('Could not add document to project', err instanceof Error ? err.message : undefined);
    }
  }, [
    auth,
    createConversation,
    navigate,
    persistConversationSnapshot,
    refreshDocumentProjects,
    showAppError,
    toast,
    updateConversation,
  ]);
  const archivedItems: ArchivedItem[] = useMemo(
    () =>
      conversationList
        .filter((c) => c.archived && !c.inTrash)
        .sort((a, b) => new Date(b.lastMessageAt).getTime() - new Date(a.lastMessageAt).getTime())
        .map((c) => ({
          id: c.id,
          title: c.title,
          lastMessageAt: c.lastMessageAt,
          fileName: c.sessionSnapshot?.primaryDocument?.fileName ?? c.latestResultFileName ?? undefined,
          operation: c.toolsUsed?.length
            ? toolToFriendlyOperation(c.toolsUsed[c.toolsUsed.length - 1] as ToolName)
            : undefined,
        })),
    [conversationList],
  );
  useEffect(() => {
    currentConversationIdRef.current = currentConversationId;
  }, [currentConversationId]);

  const clearWorkspaceSessionState = useCallback(() => {
    setCurrentFiles([]);
    setCurrentResult(null);
    setConversationDocuments([]);
    setMessages([]);
    setMessagesHasMore(false);
    setFocusedDocumentView(false);
    setCanvasViewMode(false);
    setChatCollapsed(false);
    setRightOpen(false);
    setToolPanelExpanded(false);
    setPanelSettings({});
    setLastConversionOutput(null);
    setDraftPdfMirrorDigest(null);
    setDocumentPreviewVariant('original');
    setSessionPrimaryDocument(null);
    setSessionSourceDocuments([]);
    setUtilityChatTool(null);
    setLikelyScannedPdf(false);
    setLastTool(null);
    setChatPrefill('');
    setChatComposerPlaceholderHint(null);
    setProcessing(null);
    // V2 query runs hold their own gate on the InputBar disabled state
    // (`disabled={!!processing || !!activeQueryRunId}`). Without
    // clearing it here, a run that never reached a terminal status
    // (network drop, conversation switch mid-stream, error path that
    // didn't hit the terminal effect) leaves the chat send button
    // silently disabled forever — InputBar's handleSubmit short-
    // circuits with no log, no fetch, no toast.
    setActiveQueryRunId(null);
    queryRunTerminalRef.current = null;
    setLoadingEarlierMessages(false);
    loadingEarlierRef.current = false;
    setCollabTab('analysis');
    if (filePreviewSourceRef.current === 'local' && filePreviewUrlRef.current) {
      URL.revokeObjectURL(filePreviewUrlRef.current);
    }
    setFilePreviewUrl(null);
    setFilePreviewSource(null);
    if (esignPdfUrlRef.current) {
      URL.revokeObjectURL(esignPdfUrlRef.current);
    }
    setEsignPdfUrl('');
    setEsignFields([]);
    setEsignRecipientDrafts([]);
    setEsignActiveSignerIndex(0);
    setEsignPlacementMode(null);
    setEsignDetectingFields(false);
    setEsignPdfPageCount(0);
    esignSessionKeyRef.current = null;
    setDraftContent('');
    setDraftTitle('Untitled Document');
  }, []);

  const filesToSessionDocuments = useCallback(
    (files: File[], sourceFileId?: string | null): WorkspaceSessionDocumentRef[] =>
      files.map((file, index) => ({
        ...(index === 0 && sourceFileId ? { fileId: sourceFileId } : {}),
        fileName: file.name,
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        ...(index === 0 && sourceFileId ? { downloadUrl: `/api/file/${sourceFileId}` } : {}),
        source: sourceFileId && index === 0 ? 'source_file' : 'upload',
      })),
    [],
  );

  useEffect(() => {
    if (messages.length === 0) {
      lastAudioLoadMsgIdRef.current = null;
      lastImageLoadMsgIdRef.current = null;
    }
  }, [messages.length]);

  useEffect(() => {
    const f = currentFiles[0];
    if (!f || currentFiles.length !== 1 || !isAudioLikeFile(f)) {
      setAudioDurationLabel(null);
      return;
    }
    const url = URL.createObjectURL(f);
    const el = new Audio();
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      URL.revokeObjectURL(url);
      el.removeAttribute('src');
    };
    el.preload = 'metadata';
    el.src = url;
    const onMeta = () => {
      const d = el.duration;
      if (Number.isFinite(d) && d > 0 && d < 86400) {
        setAudioDurationLabel(formatAudioDurationForChat(d));
      } else {
        setAudioDurationLabel(null);
      }
      cleanup();
    };
    const onErr = () => {
      setAudioDurationLabel(null);
      cleanup();
    };
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('error', onErr);
    return () => {
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('error', onErr);
      cleanup();
    };
  }, [currentFiles]);

  useEffect(() => {
    if (!audioDurationLabel) return;
    const id = lastAudioLoadMsgIdRef.current;
    if (!id) return;
    const name = currentFiles[0]?.name;
    if (!name || currentFiles.length !== 1) return;
    const nextContent = singleFileLoadedMessageWithDuration(name, audioDurationLabel);
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== id) return m;
        if (m.content === nextContent) return m;
        return { ...m, content: nextContent };
      })
    );
  }, [audioDurationLabel, currentFiles[0]?.name, currentFiles.length]);

  useEffect(() => {
    const f = currentFiles[0];
    if (!f || currentFiles.length !== 1 || !isImageLikeFile(f)) {
      setImageChatMetaLabel(null);
      return;
    }
    const url = URL.createObjectURL(f);
    const img = new Image();
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      URL.revokeObjectURL(url);
    };
    img.onload = () => {
      const label = formatImageDimensionsForChat(f, img.naturalWidth, img.naturalHeight);
      setImageChatMetaLabel(label || null);
      cleanup();
    };
    img.onerror = () => {
      setImageChatMetaLabel(null);
      cleanup();
    };
    img.src = url;
    return () => {
      img.onload = null;
      img.onerror = null;
      cleanup();
    };
  }, [currentFiles]);

  useEffect(() => {
    if (!imageChatMetaLabel) return;
    const id = lastImageLoadMsgIdRef.current;
    if (!id) return;
    const name = currentFiles[0]?.name;
    if (!name || currentFiles.length !== 1) return;
    const nextContent = singleFileLoadedMessageWithImageMeta(name, imageChatMetaLabel);
    setMessages((prev) =>
      prev.map((m) => {
        if (m.id !== id) return m;
        if (m.content === nextContent) return m;
        return { ...m, content: nextContent };
      })
    );
  }, [imageChatMetaLabel, currentFiles[0]?.name, currentFiles.length]);

  useEffect(() => {
    const f = currentFiles[0];
    if (!f || currentFiles.length !== 1) {
      setLikelyScannedPdf(false);
      return;
    }
    const info = getFileFormatInfo(f.name);
    if (!info.isPDF && f.type !== 'application/pdf') {
      setLikelyScannedPdf(false);
      return;
    }
    let cancelled = false;
    analyzePdfTextLayer(f).then(({ likelyScanned }) => {
      if (!cancelled) setLikelyScannedPdf(likelyScanned);
    });
    return () => {
      cancelled = true;
    };
  }, [currentFiles]);

  // Paper 5XP — patch PDF loaded message when scanned PDF is detected
  useEffect(() => {
    if (!likelyScannedPdf) return;
    const name = currentFiles[0]?.name;
    if (!name || currentFiles.length !== 1) return;
    const scannedMsg = `${name} loaded. Looks like a scanned image — OCR available.`;
    setMessages((prev) =>
      prev.map((m) => {
        if (m.systemKind !== 'pdf_single_loaded' || m.role !== 'corner') return m;
        if (m.content === scannedMsg) return m;
        return { ...m, content: scannedMsg };
      })
    );
  }, [likelyScannedPdf, currentFiles[0]?.name, currentFiles.length]);

  useEffect(() => {
    if (currentFiles.length > 0) setUtilityChatTool(null);
  }, [currentFiles.length]);

  // Paper 64J-0 — QR generator: add intro message + chips when entering QR mode
  useEffect(() => {
    if (utilityChatTool !== 'generate_qr') return;
    setMessages((prev) => {
      // Don't add if already present
      if (prev.some((m) => m.systemKind === 'qr_generator_intro')) return prev;
      return [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'corner' as const,
          content: 'What would you like to generate a QR code for?',
          timestamp: Date.now(),
          systemKind: 'qr_generator_intro',
        },
      ];
    });
  }, [utilityChatTool]);

  // Object URL for currentFile so left column can preview before processing
  // Create Object URL for currentFile so left column can preview before processing.
  // IMPORTANT: deps must NOT include filePreviewUrl or filePreviewSource — they are
  // outputs of this effect. Including them caused an infinite loop where each URL
  // creation triggered re-execution, causing the preview to blink/blank repeatedly.
  useEffect(() => {
    if (!currentFile) {
      if (filePreviewSourceRef.current === 'local' && filePreviewUrlRef.current) {
        URL.revokeObjectURL(filePreviewUrlRef.current);
        setFilePreviewUrl(null);
        setFilePreviewSource(null);
      }
      return;
    }
    setLastConversionOutput(null);
    setDocumentPreviewVariant('original');
    const url = URL.createObjectURL(currentFile);
    setFilePreviewUrl(url);
    setFilePreviewSource('local');
    return () => URL.revokeObjectURL(url);
  }, [currentFile]);

  const showSplit =
    !!(currentFile || currentResult || filePreviewUrl || lastConversionOutput || utilityChatTool === 'generate_qr') && !focusedDocumentView;

  useEffect(() => {
    if (!showSplit) setVersionTimelineOverlay(false);
  }, [showSplit]);

  useEffect(() => {
    if (mode === 'esign') setVersionTimelineOverlay(false);
  }, [mode]);

  useEffect(() => {
    if (!versionTimelineOverlay) return;
    if (import.meta.env.VITE_VERSION_TIMELINE_DEBUG === '1') {
      console.log('[VersionTimeline] entries', versionTimelineEntries);
    }
  }, [versionTimelineEntries, versionTimelineOverlay]);

  const handleEsignPdfReady = useCallback(({ pageCount }: { pageCount: number }) => {
    setEsignPdfPageCount(pageCount);
  }, []);

  useEffect(() => {
    if (mode !== 'esign') {
      setEsignPdfPageCount(0);
      setEsignPlacementMode(null);
    }
  }, [mode]);

  /** Paper 5EV — "Sign now" stays in-product instead of re-parsing intent. */
  const handleSignFlowSegment = useCallback(
    (segmentId: 'sign_now' | 'review_first' | 'add_initials') => {
      if (segmentId !== 'sign_now') return false;
      setMessages((prev) => [
        ...prev,
        {
          id: crypto.randomUUID(),
          role: 'user',
          content: 'Sign the document',
          timestamp: Date.now(),
        },
        {
          id: crypto.randomUUID(),
          role: 'corner',
          content:
            'I will help you place signing fields. Start with your own signature fields in the guide below, then click the document where each field belongs.',
          timestamp: Date.now(),
        },
      ]);
      return true;
    },
    []
  );

  /** Paper 5G8 — sign-thread "+ Ask" matches ESignCanvas */
  const handleEsignThreadAsk = useCallback(() => {
    setChatPrefill('');
    setChatComposerFocusNonce((n) => n + 1);
  }, []);

  const handleEsignStatusResult = useCallback((data: ToolResult, fallbackFields: SignatureField[] = []) => {
    const status = data.status;
    if (!status) return;

    const detected = data.fields?.length ? data.fields : fallbackFields;

    // When auto-signing completes via orchestrator, set esignFields so green markers show.
    // Don't change mode here — the orchestrator result handler handles mode transition.
    if (status === 'signed') {
      if (detected.length) {
        console.log('[handleEsignStatusResult] Setting signed fields:', detected.length, 'placed:', detected.filter((f: SignatureField) => f.placed).length);
        setEsignFields(detected);
      }
      return;
    }

    // Store context so follow-up questions can reference this attempt
    lastEsignContextRef.current = {
      status,
      fileName: currentFile?.name,
      fileType: currentFile?.type,
      fieldCount: detected.length,
      timestamp: Date.now(),
      signedFields: detected
        .filter((f: SignatureField) => f.placed && f.party === 'self')
        .map((f: SignatureField) => ({ label: f.label, type: f.type ?? 'signature', party: 'self', page: f.page })),
      awaitingFields: detected
        .filter((f: SignatureField) => !f.placed || f.party === 'other')
        .map((f: SignatureField) => ({ label: f.label, type: f.type ?? 'signature', party: f.party ?? 'other', page: f.page })),
    };

    if (status === 'role_selection_needed') {
      setProcessing(null);
      const messageText = (data as { message?: string }).message ?? '';
      // Prefer structured availableRoles array; fall back to parsing from message
      const availableRoles: string[] = (data as { availableRoles?: string[] }).availableRoles ?? [];
      let roleNames = availableRoles;
      if (!roleNames.length) {
        const roleMatch = messageText.match(/for (.+?)\./);
        roleNames = roleMatch
          ? roleMatch[1].split(/\s+and\s+|\s*,\s*/).map((r: string) => r.trim()).filter(Boolean)
          : [];
      }
      const roleChips = roleNames.map((role: string) => ({
        label: `I'm the ${role}`,
        variant: 'followup' as const,
        sendAs: `sign as the ${role.toLowerCase()}`,
      }));
      setMessages((prev) => [...prev, {
        id: crypto.randomUUID(),
        role: 'corner' as const,
        content: messageText || 'I found signature lines for multiple parties. Who are you signing as?',
        timestamp: Date.now(),
        chips: [
          ...roleChips,
          { label: 'Cancel', variant: 'followup' as const },
        ],
      }]);
      return;
    }

    if (status === 'needs_signature_setup') {
      setEsignFields(prepareDetectedFieldsForReview(detected));
      setMode('esign');
      setProcessing(null);
      setMessages((prev) => [...prev, {
        id: crypto.randomUUID(),
        role: 'corner' as const,
        content: `I found possible signing spots, but you haven't set up your signature yet. ${MANUAL_SIGNING_INSTRUCTIONS}`,
        timestamp: Date.now(),
        chips: [
          { label: 'Set up signature', variant: 'followup' as const },
          { label: 'Cancel', variant: 'followup' as const },
        ],
      }]);
      return;
    }

    if (status === 'no_fields_found') {
      setEsignFields([]);
      setMode('esign');
      setProcessing(null);
      setMessages((prev) => [...prev, {
        id: crypto.randomUUID(),
        role: 'corner' as const,
        content: `No signature placements are saved yet for this document. ${MANUAL_SIGNING_INSTRUCTIONS}`,
        timestamp: Date.now(),
        chips: [
          { label: 'Add recipients', variant: 'followup' as const },
          { label: 'Cancel', variant: 'followup' as const },
        ],
      }]);
      return;
    }

    // Backward-compatible handling for older backend responses.
    if (status === 'detection_unavailable') {
      setEsignFields([]);
      setMode('esign');
      setProcessing(null);
      const messageText = (data as { message?: string }).message
        ?? `I couldn't load saved placements for this document. ${MANUAL_SIGNING_INSTRUCTIONS}`;
      setMessages((prev) => [...prev, {
        id: crypto.randomUUID(),
        role: 'corner' as const,
        content: messageText,
        timestamp: Date.now(),
        chips: [
          { label: 'Add recipients', variant: 'followup' as const },
          { label: 'Cancel', variant: 'followup' as const },
        ],
      }]);
    }
  }, []);

  const openSendForSignatureModal = useCallback(() => {
    setSendForSignatureModalKey((prev) => prev + 1);
    setSendForSignatureOpen(true);
  }, []);

  const handleGuidedContractSend = useCallback(async () => {
    if (!auth.user) {
      setPaywallOpen(true);
      return;
    }
    if (guidedContractSendBusyRef.current) return;
    guidedContractSendBusyRef.current = true;
    setGuidedContractSendBusy(true);
    try {
      const created = await userWorkflows.create({ forkPresetId: 'contract_send' });
      if (!created) return;
      if (isContractVelocityWorkflow(created.definition)) {
        setHudWorkflow(created);
      }
    } catch (err) {
      showAppError('Could not start guided send', err instanceof Error ? err.message : undefined);
    } finally {
      guidedContractSendBusyRef.current = false;
      setGuidedContractSendBusy(false);
    }
  }, [auth.user, userWorkflows]);

  /** Same entry as Guided send: ContractRunWizard (recipients → doc source → preview), not the workspace-only modal. */
  const handleSignaturesDashboardSendForSignature = useCallback(() => {
    void handleGuidedContractSend();
  }, [handleGuidedContractSend]);

  const handleOpenSigningRequestFromDashboard = useCallback(async (r: import('@corner/shared').SigningRequestSummary) => {
    setEsignFields([]);
    setEsignRecipientDrafts([]);
    setEsignActiveSignerIndex(0);
    setEsignPlacementMode(null);
    setCurrentFiles([]);
    setLastConversionOutput(null);
    setDocumentPreviewVariant('original');
    let request = r;
    const token = auth.getToken();
    if (token) {
      try {
        const res = await axios.get(`/api/signing/${r.requestId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        request = res.data;
        ingestSigningRequestDetail(request);
      } catch (err) {
        console.warn('[SignaturesDashboard] Could not load signing request detail', err);
      }
    }
    const previewFileId =
      request.status === 'completed' && request.signedDocumentFileId
        ? request.signedDocumentFileId
        : request.documentFileId ?? request.signedDocumentFileId ?? null;
    if (previewFileId) {
      const downloadUrl = `/api/file/${previewFileId}`;
      const fileName = request.status === 'completed'
        ? `signed_${request.documentName}`
        : request.documentName;
      const result: ToolResult = {
        fileId: previewFileId,
        downloadUrl,
        fileName,
        mimeType: 'application/pdf',
        sizeBytes: 0,
        ...(request.signedDocumentFileId && previewFileId === request.signedDocumentFileId && request.documentFileId
          ? { sourceFileId: request.documentFileId }
          : {}),
      };
      setCurrentResult(result);
      setSessionPrimaryDocument({
        fileId: previewFileId,
        fileName,
        mimeType: 'application/pdf',
        sizeBytes: 0,
        downloadUrl,
        source: request.status === 'completed' ? 'result' : 'source_file',
      });
      setFilePreviewUrl(appendTokenToUrl(downloadUrl, auth.getToken));
      setFilePreviewSource('restored');
    } else {
      toast({
        title: 'Document unavailable',
        description: 'This signing request does not have a document file attached.',
        variant: 'destructive',
      });
    }
    setActiveSigningRequest(request);
    setMode('esign');
    navigate(APP_WORKSPACE_PATH);
  }, [auth.getToken, ingestSigningRequestDetail, navigate, toast]);

  // ── Signing entry point (top-bar "Send for signature" button) ──
  //
  // Background: prior to PR #signing-restore-v1 the top bar carried a
  // row of placement chips (Signature / Date / Title …). Users found
  // those confusing — they didn't realize they were the *entry point*
  // to signing. We removed them in a prior pass and accidentally left
  // no visible way to start signing a doc unless you already happened
  // to be in mode='esign'. This callback wires up a single, clear
  // top-bar entry that:
  //
  //   • For an already-uploaded/processed PDF (currentResult is a PDF
  //     with a fileId, or filePreviewUrl points at a PDF) — flips
  //     mode→'esign', so the right legend (RecipientLegend) and the
  //     SigningGuideCard appear. Users place/confirm fields first, then
  //     open the recipient modal from the signing rail.
  //
  //   • When the user is viewing a converted PDF (Original / Output
  //     toggle on Output) while drafting HTML — treat as available and
  //     align currentResult to that PDF before entering esign.
  //
  //   • For an HTML draft (mode='drafting' with body text) — server-export
  //     PDF from HTML, then enter esign placement mode. Markers still require
  //     PDF today; Word signing is not supported in-viewer.
  //
  //   • Otherwise — toast that signing requires a PDF.
  type SigningButtonState = 'hidden' | 'available' | 'draft' | 'convertible' | 'unsupported' | 'in-flight';
  const visibleConversationDraftHtmlForSigning = (
    selectedConversationDocument?.html?.trim()
    || (currentResult?.kind === 'document' || isConversationMongoDocumentFileId(currentResult?.fileId)
      ? conversationDocuments.find((doc) =>
          doc.id === (currentResult?.documentId ?? currentResult?.fileId?.slice(4))
        )?.html?.trim()
      : '')
    || ''
  );
  const effectiveDraftHtmlForSigning =
    draftContent.trim()
    || activeDocumentSurface?.draftHtml?.trim()
    || visibleConversationDraftHtmlForSigning
    || (typeof currentResult?.draftHtml === 'string' ? currentResult.draftHtml.trim() : '');

  const signingButtonState = useMemo<SigningButtonState>(() => {
    if (mode === 'esign') return 'in-flight';

    // Prefer a concrete PDF artifact over drafting UI shell — chats often keep
    // mode='drafting' while a PDF version is already loaded for preview/send.
    const fileName = (currentResult?.fileName ?? currentFile?.name ?? '').toLowerCase();
    const mimeType = currentResult?.mimeType ?? currentFile?.type ?? '';
    const isPdf = mimeType === 'application/pdf' || fileName.endsWith('.pdf');
    if (isPdf && (currentResult?.fileId || filePreviewUrl)) {
      return 'available';
    }
    const isConvertibleUpload =
      /\.(docx?|rtf|txt)$/i.test(fileName) ||
      mimeType === 'text/plain' ||
      mimeType === 'application/msword' ||
      mimeType === 'application/rtf' ||
      mimeType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

    const out = lastConversionOutput;
    const outIsPdf =
      Boolean(out?.fileId) &&
      (out.mimeType === 'application/pdf' ||
        (out.fileName ?? '').toLowerCase().endsWith('.pdf'));

    const draftingPdfSynced =
      effectiveDraftHtmlForSigning.length > 0
      && draftPdfMirrorDigest != null
      && draftHtmlMirrorDigest(effectiveDraftHtmlForSigning) === draftPdfMirrorDigest;

    const draftingPdfStale =
      effectiveDraftHtmlForSigning.length > 0
      && draftPdfMirrorDigest != null
      && !draftingPdfSynced;

    if (outIsPdf && out) {
      if (draftingPdfStale) {
        /* fall through — edited since mirror; user needs fresh PDF */
      } else if (draftingPdfSynced) {
        return 'available';
      } else if (documentPreviewVariant === 'output') {
        const draftingWithHtml = effectiveDraftHtmlForSigning.length > 0;
        if (!draftingWithHtml) return 'available';
        /* Drafting HTML without a digest match — don't treat unrelated PDF output as sign-ready */
      }
    }

    if (effectiveDraftHtmlForSigning) return 'draft';
    if (isConvertibleUpload && (currentResult?.fileId || currentResult?.downloadUrl || currentFile || filePreviewUrl)) {
      return 'convertible';
    }

    if (currentResult?.fileId || currentFile || filePreviewUrl) return 'unsupported';
    return 'hidden';
  }, [
    mode,
    effectiveDraftHtmlForSigning,
    currentResult?.fileName,
    currentResult?.mimeType,
    currentResult?.fileId,
    currentResult?.downloadUrl,
    currentFile,
    filePreviewUrl,
    lastConversionOutput,
    documentPreviewVariant,
    draftPdfMirrorDigest,
  ]);

  const openPdfResultForSigning = useCallback((result: ToolResult, label = 'Converted to PDF') => {
    const downloadUrl = result.downloadUrl ?? `/api/file/${result.fileId}`;
    const pdfName = result.fileName ?? 'Document.pdf';
    const pdfResult: ToolResult = {
      ...result,
      fileId: result.fileId,
      downloadUrl,
      fileName: pdfName,
      mimeType: result.mimeType ?? 'application/pdf',
      sizeBytes: result.sizeBytes ?? 0,
    };
    addNode(pdfName, undefined, downloadUrl, label);
    setCurrentResult(pdfResult);
    setSessionPrimaryDocument({
      fileId: pdfResult.fileId,
      fileName: pdfName,
      mimeType: pdfResult.mimeType,
      sizeBytes: pdfResult.sizeBytes,
      downloadUrl,
      source: 'result',
    });
    setLastConversionOutput(pdfResult);
    setDocumentPreviewVariant('output');
    setFilePreviewUrl(appendTokenToUrl(downloadUrl, auth.getToken));
    setFilePreviewSource('restored');
    setMode('esign');
  }, [addNode, auth.getToken]);

  const handleLaunchSigning = useCallback(() => {
    const state = signingButtonState;
    if (state === 'hidden' || state === 'in-flight') return;

    if (state === 'draft') {
      const html = effectiveDraftHtmlForSigning.trim();
      if (!html) return;
      if (draftSigningPrepBusyRef.current) return;
      draftSigningPrepBusyRef.current = true;
      void (async () => {
        try {
          const token = auth.getToken();
          if (!token) {
            toast({ variant: 'destructive', title: 'Sign in required', description: 'Sign in to send documents for signature.' });
            return;
          }
          setProcessing({ progress: 15, label: 'Converting draft to PDF…' });
          const baseName = (draftTitle || 'Document').replace(/\.(html?|docx|pdf)$/i, '') || 'Document';
          const exportRes = await axios.post(
            '/api/export-text',
            { text: html, format: 'pdf', fileName: baseName },
            { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } },
          );
          const pdfFile = new File(
            [exportRes.data],
            `${baseName}.pdf`,
            { type: 'application/pdf' },
          );
          setProcessing({ progress: 55, label: 'Preparing signing workspace…' });
          const uploaded = await projectApi.uploadProjectSeedFile(pdfFile, auth.getToken);
          setProcessing(null);
          openPdfResultForSigning({
            fileId: uploaded.fileId,
            downloadUrl: uploaded.downloadUrl ?? `/api/file/${uploaded.fileId}`,
            fileName: uploaded.fileName ?? `${baseName}.pdf`,
            mimeType: uploaded.mimeType ?? 'application/pdf',
            sizeBytes: uploaded.sizeBytes ?? 0,
          }, 'Draft → PDF');
          toast({
            title: 'Ready for signing fields',
            description: 'Place or confirm the signature markers first, then send to recipients.',
          });
        } catch (err) {
          setProcessing(null);
          const e = getActionError('convert draft for signing', err);
          toast({
            variant: 'destructive',
            title: e.title,
            description: `${e.description} Try asking Corner to convert this draft to PDF, or use Print → Save as PDF.`,
          });
        } finally {
          draftSigningPrepBusyRef.current = false;
        }
      })();
      return;
    }

    if (state === 'convertible') {
      if (draftSigningPrepBusyRef.current) return;
      draftSigningPrepBusyRef.current = true;
      void (async () => {
        try {
          const token = auth.getToken();
          if (!token) {
            toast({ variant: 'destructive', title: 'Sign in required', description: 'Sign in to send documents for signature.' });
            return;
          }
          setProcessing({ progress: 20, label: 'Converting document to PDF…' });
          let fileToConvert: File | null = currentFile ?? null;
          const source = currentResult?.downloadUrl
            ? currentResult
            : lastConversionOutput?.downloadUrl
              ? lastConversionOutput
              : null;
          const inferredName =
            source?.fileName
            ?? sessionPrimaryDocument?.fileName
            ?? currentFile?.name
            ?? 'document.docx';
          const inferredMime =
            source?.mimeType
            ?? sessionPrimaryDocument?.mimeType
            ?? currentFile?.type
            ?? 'application/octet-stream';
          if (!fileToConvert && source?.downloadUrl) {
            fileToConvert = await fetchUrlAsFile(source.downloadUrl, inferredName, inferredMime, auth.getToken);
          } else if (!fileToConvert && filePreviewUrl && !filePreviewUrl.startsWith('data:')) {
            fileToConvert = await fetchUrlAsFile(filePreviewUrl, inferredName, inferredMime, auth.getToken);
          }
          if (!fileToConvert) {
            toast({
              variant: 'destructive',
              title: 'No document to convert',
              description: 'Open the uploaded document from the chat result, then try Send for signature again.',
            });
            return;
          }
          const fd = new FormData();
          fd.append('files', fileToConvert);
          fd.append('tool', 'word_to_pdf');
          const currentDocumentId = currentResult?.sourceFileId ?? currentResult?.fileId ?? sessionPrimaryDocument?.fileId ?? '';
          fd.append('params', JSON.stringify(currentDocumentId ? { currentDocumentId } : {}));
          const res = await axios.post<ToolResult>('/api/execute', fd, {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`,
            },
          });
          setProcessing(null);
          openPdfResultForSigning(res.data, 'Converted to PDF');
          toast({
            title: 'Ready for signing fields',
            description: 'Your document is now a PDF. Place or confirm the signature markers, then send to recipients.',
          });
        } catch (err) {
          setProcessing(null);
          const e = getActionError('convert document for signing', err);
          toast({
            variant: 'destructive',
            title: e.title,
            description: `${e.description} Try converting the document to PDF in chat, then send it for signature.`,
          });
        } finally {
          draftSigningPrepBusyRef.current = false;
        }
      })();
      return;
    }

    if (state === 'unsupported') {
      toast({
        title: 'Convert to PDF first',
        description: 'Signing requires a PDF. Use “Convert to PDF” in chat, then try again.',
      });
      return;
    }

    const out = lastConversionOutput;
    const outIsPdf =
      Boolean(out?.fileId) &&
      (out.mimeType === 'application/pdf' ||
        (out.fileName ?? '').toLowerCase().endsWith('.pdf'));
    const draftingPdfSynced =
      effectiveDraftHtmlForSigning.length > 0
      && draftPdfMirrorDigest != null
      && draftHtmlMirrorDigest(effectiveDraftHtmlForSigning) === draftPdfMirrorDigest;

    const draftingPdfStale =
      effectiveDraftHtmlForSigning.length > 0
      && draftPdfMirrorDigest != null
      && !draftingPdfSynced;

    const draftingWithHtml = effectiveDraftHtmlForSigning.length > 0;

    const shouldAlignPdfFromMirror =
      outIsPdf && out && !draftingPdfStale && (
        draftingPdfSynced
        || (documentPreviewVariant === 'output' && !draftingWithHtml)
      );

    // #region agent log
    fetch('http://127.0.0.1:7675/ingest/4eb987b4-fa77-42e3-95cf-87b64e13ddeb', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Debug-Session-Id': 'fbf77c' },
      body: JSON.stringify({
        sessionId: 'fbf77c',
        runId: 'launch-sign',
        hypothesisId: 'H-align',
        location: 'App.tsx:handleLaunchSigning',
        message: 'before_align_pdf',
        data: {
          shouldAlignPdfFromMirror,
          draftingPdfSynced,
          draftingPdfStale,
          draftingWithHtml,
          outIsPdf,
          previewVariant: documentPreviewVariant,
        },
        timestamp: Date.now(),
      }),
    }).catch(() => {});
    // #endregion

    if (shouldAlignPdfFromMirror) {
      const downloadUrl = out.downloadUrl ?? `/api/file/${out.fileId}`;
      setCurrentResult({
        fileId: out.fileId,
        downloadUrl,
        fileName: out.fileName ?? 'Document.pdf',
        mimeType: out.mimeType ?? 'application/pdf',
        sizeBytes: out.sizeBytes ?? 0,
        ...(typeof out.sourceFileId === 'string' ? { sourceFileId: out.sourceFileId } : {}),
      });
      setFilePreviewUrl(appendTokenToUrl(downloadUrl, auth.getToken));
      setFilePreviewSource('restored');
    }

    setMode('esign');
  }, [
    addNode,
    auth.getToken,
    draftPdfMirrorDigest,
    draftTitle,
    documentPreviewVariant,
    effectiveDraftHtmlForSigning,
    currentFile,
    currentResult,
    filePreviewUrl,
    lastConversionOutput,
    mode,
    openPdfResultForSigning,
    sessionPrimaryDocument?.fileId,
    sessionPrimaryDocument?.fileName,
    sessionPrimaryDocument?.mimeType,
    signingButtonState,
    toast,
  ]);

  const executeESignFields = useCallback(
    async (
      fields: SignatureField[],
      options: { fileOverride?: File; sessionKey?: string } = {}
    ): Promise<ESignExecuteResponse | null> => {
      let fileToSign: File | null | undefined = options.fileOverride ?? currentFile;
      if (!fileToSign) {
        const toolSource =
          currentResult?.downloadUrl
            ? currentResult
            : lastConversionOutput?.downloadUrl
              ? lastConversionOutput
              : null;
        const inferredName =
          toolSource?.fileName
          ?? sessionPrimaryDocument?.fileName
          ?? (mode === 'drafting' && draftTitle
            ? (draftTitle.toLowerCase().endsWith('.pdf') ? draftTitle : `${draftTitle}.pdf`)
            : undefined)
          ?? 'document.pdf';
        const inferredMime = toolSource?.mimeType ?? 'application/pdf';
        if (toolSource?.downloadUrl) {
          try {
            fileToSign = await fetchUrlAsFile(
              toolSource.downloadUrl,
              inferredName,
              inferredMime,
              auth.getToken,
            );
          } catch (e) {
            const msg = e instanceof Error ? e.message : 'Could not load the PDF.';
            toast({ variant: 'destructive', title: 'Cannot apply changes', description: msg });
            return null;
          }
        } else if (filePreviewUrl && !filePreviewUrl.startsWith('data:')) {
          try {
            fileToSign = await fetchUrlAsFile(
              filePreviewUrl,
              inferredName,
              'application/pdf',
              auth.getToken,
            );
          } catch (e) {
            const msg = e instanceof Error ? e.message : 'Could not read the open document.';
            toast({ variant: 'destructive', title: 'Cannot apply changes', description: msg });
            return null;
          }
        }
      }
      if (!fileToSign) {
        toast({
          variant: 'destructive',
          title: 'No document to sign',
          description:
            'Your PDF isn\'t attached as a file in this session. Open the PDF from the chat result or download and upload it, then try **Apply changes** again.',
        });
        return null;
      }

      setMode('processing');
      setProcessing({ progress: 50, label: 'Applying your changes...' });

      try {
        const token = auth.getToken();
        if (!token) {
          throw new Error('Sign in to use your saved signature.');
        }

        const fd = new FormData();
        fd.append('files', fileToSign);
        fd.append('tool', 'esign');
        const currentDocumentId = currentResult?.sourceFileId ?? currentResult?.fileId ?? sessionPrimaryDocument?.fileId ?? '';
        fd.append('params', JSON.stringify({
          fields,
          ...(currentDocumentId ? { currentDocumentId } : {}),
        }));

        const res = await axios.post<ESignExecuteResponse>('/api/execute', fd, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        });

        if (options.sessionKey && esignSessionKeyRef.current !== options.sessionKey) return null;

        const data = res.data;
        const status = data.status ?? 'signed';

        if (status === 'needs_signature_setup') {
          handleEsignStatusResult(data, fields);
          return data;
        }

        if (status === 'no_fields_found') {
          handleEsignStatusResult(data, fields);
          return data;
        }

        const rawSignedFields = data.fields?.length
          ? data.fields
          : fields.map((field) => {
              const isOther = field.party === 'other' || field.signer === 'other';
              // Backend response is missing data.fields — defensive
              // fallback. Don't assume self fields are placed; the
              // backend now reports `placed:false` for fields it
              // couldn't render, and the safe default if we have NO
              // fields response at all is to leave them unplaced so
              // they show as 'suggested' (orange) in the viewer.
              // Empty green markers are the failure mode here.
              return {
                ...field,
                placed: !isOther && !!field.placed,
                signer: isOther ? ('other' as const) : ('you' as const),
                party: isOther ? ('other' as const) : ('self' as const),
                signedAt: isOther ? field.signedAt : (field.placed ? Date.now() : field.signedAt),
              };
            });
        const signedFields = rawSignedFields.map((field) => {
          const isOther = field.party === 'other' || field.signer === 'other';
          const isEmbedded = !isOther && (field.placed || field.signedAt);
          return isEmbedded ? { ...field, state: 'signed' as const } : field;
        });
        const selfFields = data.selfFields?.length ? data.selfFields : signedFields.filter((field) => field.signer !== 'other');
        const otherFields = data.otherFields?.length ? data.otherFields : signedFields.filter((field) => field.signer === 'other');
        const firstSelf = selfFields[0];
        const location = firstSelf ? ` on page ${firstSelf.page}` : '';

        console.log('[esign:completion] Setting esignFields:', signedFields.length, 'fields, placed:', signedFields.filter((f) => f.placed).length);
        setEsignFields(signedFields);
        setProcessing({ progress: 100, label: 'Signed' });
        await new Promise((r) => setTimeout(r, 400));
        setProcessing(null);
        if (esignPdfUrlRef.current) URL.revokeObjectURL(esignPdfUrlRef.current);
        setEsignPdfUrl('');
        setCurrentResult(data);
        setLastConversionOutput(data);
        setDocumentPreviewVariant('output');
        setLastTool('esign');
        setMode('result');
        addNode(data.fileName, undefined, data.downloadUrl, 'Signed');

        const esignSummary = buildEsignChatSummary(signedFields, data);
        const total = signedFields.length;
        const placedCount = signedFields.filter((f) => f.placed).length;
        const selfCopy = placedCount > 0
          ? `I've applied your changes to ${placedCount} field${placedCount === 1 ? '' : 's'}${location}.`
          : selfFields.length > 0
            ? `I found ${selfFields.length} field${selfFields.length === 1 ? '' : 's'} for you but couldn't apply the signature — you may need to specify your signing role.`
            : "I didn't find a field assigned to you, so I left the document ready for the other signer.";
        const otherCopy = otherFields.length
          ? ` The other party's ${otherFields.length === 1 ? 'signature field is' : 'signature fields are'} highlighted — click Send for Signature to invite them.`
          : '';
        const esignCornerBody = `I analyzed your document and found ${total} signature field${total === 1 ? '' : 's'}. ${selfCopy}${otherCopy}`;

        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: 'corner',
            content: esignCornerBody,
            timestamp: Date.now(),
            result: data,
            toolName: 'esign' as ToolName,
            esignSummary,
            hideInlineResultCard: true,
          },
        ]);

        let cid = currentConversationIdRef.current;
        if (!cid) {
          cid = await createConversation(fileToSign.name?.trim()?.slice(0, 200) || 'Signed document');
          setCurrentConversationId(cid);
          currentConversationIdRef.current = cid;
        }
        await addConversationMessage(cid, {
          role: 'corner',
          content: esignCornerBody,
          toolCall: {
            toolName: 'esign',
            resultFileId: data.fileId,
            resultFileName: data.fileName,
            resultMimeType: data.mimeType,
            resultSizeBytes: data.sizeBytes,
            params: { esignSummary },
          },
        }).catch(() => {});

        return data;
      } catch (err) {
        setMode('esign');
        setProcessing(null);
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        const responseData = axios.isAxiosError(err) ? err.response?.data : undefined;
        const responseMessage =
          typeof responseData?.message === 'string'
            ? responseData.message
            : typeof responseData?.error === 'string'
              ? responseData.error
              : undefined;
        const msg = status === 422
          ? "This file type isn't supported for signing yet. Please convert your document to PDF and try again."
          : responseMessage ?? (err instanceof Error ? err.message : 'E-sign failed');
        setMessages((prev) => [...prev, {
          id: crypto.randomUUID(),
          role: 'corner' as const,
          content: msg,
          timestamp: Date.now(),
        }]);
        toast({ variant: 'destructive', title: status === 422 ? 'Unsupported format' : 'Signing failed', description: msg });
        return null;
      }
    },
    [
      addConversationMessage,
      addNode,
      auth,
      createConversation,
      currentFile,
      currentResult?.sourceFileId,
      currentResult?.fileId,
      currentResult?.downloadUrl,
      currentResult?.fileName,
      currentResult?.mimeType,
      lastConversionOutput?.downloadUrl,
      lastConversionOutput?.fileName,
      lastConversionOutput?.mimeType,
      filePreviewUrl,
      sessionPrimaryDocument?.fileId,
      sessionPrimaryDocument?.fileName,
      mode,
      draftTitle,
      handleEsignStatusResult,
      toast,
    ]
  );

  /** First self title marker missing `printedText` — opens modal; returns true if gated. */
  const tryOpenSelfTitleModal = useCallback((fields: SignatureField[]): boolean => {
    const missing = fields.filter(
      (f) =>
        f.signer !== 'other'
        && f.party !== 'other'
        && isTitleMarkerField(f)
        && !String(f.printedText ?? '').trim(),
    );
    if (missing.length === 0) return false;
    const m = missing[0];
    setPrintedTitleModal({
      fieldId: m.id ?? '',
      initial: m.printedText ?? '',
      label: m.label,
      markPlacedOnSave: false,
    });
    return true;
  }, []);

  /** Shared by toolbar "Apply", title modal Save, and chat phrases — after `executeESignFields`. */
  const flushTitlesThenApply = useCallback((fields: SignatureField[]) => {
    if (tryOpenSelfTitleModal(fields)) return;
    void executeESignFields(fields);
  }, [tryOpenSelfTitleModal, executeESignFields]);

  useEffect(() => {
    if (mode !== 'esign' || !currentFile || esignPdfPageCount < 1 || esignDetectingFields) return;
    const session = esignSessionKeyRef.current;
    if (!session || lastInjectedEsignEntryRef.current === session) return;
    lastInjectedEsignEntryRef.current = session;
    const name = currentFile.name;
    const pages = esignPdfPageCount;
    const nf = esignFields.length;
    setMessages((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        role: 'corner',
        systemKind: 'esign_entry' as const,
        content: `${name} loaded — ${pages} page${pages === 1 ? '' : 's'}. ${nf} saved signing field${nf === 1 ? '' : 's'} loaded.`,
        timestamp: Date.now(),
      },
    ]);
  }, [mode, currentFile, esignPdfPageCount, esignFields.length, esignDetectingFields]);

  // ─── v1 placement-redesign: working-set mutators for the new viewer UX
  // The viewer drives add/delete/confirm/move via these. esignFields IS
  // the working set — no separate state needed; we just mutate it in
  // place. On send (handleSendForSignature) the post-edit array is what
  // gets bundled into the SigningRequest.
  // (Hoisted above the default-marker effect to avoid a temporal
  // dead zone — the effect's dep array references esignSigners.)
  const handleAddField = useCallback((field: SignatureField) => {
    setEsignFields((prev) => [...prev, field]);
  }, []);

  const addManualField = useCallback((type: 'signature' | 'initials' | 'date' | 'text', label: string) => {
    const suffix = type === 'initials' ? 'initials' : type === 'text' ? 'name' : type;
    const size = signingFieldSizeForField({ type, label, role: `self_${suffix}` });
    const manualField: SignatureField = {
      id: `manual_${suffix}_${Date.now()}`,
      type,
      party: 'self',
      page: esignPdfPageCount || 1,
      x: 10,
      y: 82,
      width: size.width,
      height: size.height,
      label,
      role: `self_${suffix}`,
      placed: false,
      signer: 'you',
      signedByEmail: auth.user?.email ?? 'self',
      assignedSignerIndex: 0,
      state: 'confirmed',
    };
    setEsignFields((prev) => [...prev, manualField]);
  }, [auth.user?.email, esignPdfPageCount]);

  const handleDeleteField = useCallback((fieldId: string) => {
    const deletedField = esignFields.find((field) => field.id === fieldId);
    const wasEmbeddedSignature = !!deletedField && deletedField.signer !== 'other' && deletedField.party !== 'other' && !!(deletedField.placed || deletedField.signedAt);
    setEsignFields((prev) => prev.filter((f) => f.id !== fieldId));
    if (wasEmbeddedSignature && mode === 'result' && lastTool === 'esign') {
      setDocumentPreviewVariant('original');
      setMode('esign');
      toast({
        title: 'Signature marker removed',
        description: 'I restored the unsigned preview so you can place the marker again and apply changes once.',
      });
    }
  }, [esignFields, lastTool, mode]);

  const handleConfirmField = useCallback((fieldId: string) => {
    setEsignFields((prev) =>
      prev.map((f) => {
        if (f.id !== fieldId) return f;
        if (isTitleMarkerField(f)) {
          const forRecipient = f.party === 'other' || f.signer === 'other';
          if (forRecipient) {
            return { ...f, state: 'confirmed' as const };
          }
          return {
            ...f,
            state: f.printedText?.trim() ? ('confirmed' as const) : ('suggested' as const),
          };
        }
        return { ...f, state: 'confirmed' as const };
      }),
    );
  }, []);

  const handleMoveField = useCallback((fieldId: string, rect: { x: number; y: number; width: number; height: number }) => {
    setEsignFields((prev) =>
      prev.map((f) => {
        if (f.id !== fieldId) return f;
        const moved = { ...f, x: rect.x, y: rect.y, width: rect.width, height: rect.height };
        if (isTitleMarkerField(f)) {
          const forRecipient = f.party === 'other' || f.signer === 'other';
          if (forRecipient) {
            return { ...moved, state: 'confirmed' as const };
          }
          return {
            ...moved,
            state: f.printedText?.trim() ? ('confirmed' as const) : ('suggested' as const),
          };
        }
        return { ...moved, state: 'confirmed' as const };
      }),
    );
  }, []);

  // Derive the signer list shown in the guide/viewer. Always include a
  // generic recipient row so the sender can switch into blue recipient-marker
  // placement before opening the send modal. The modal remains the source of
  // truth for real recipient emails/names.
  const esignSigners = useMemo(() => {
    const list: Array<{ email: string; name: string; role: string }> = [];
    list.push({
      email: auth.user?.email ?? 'self',
      name: auth.user?.displayName ?? 'You',
      role: 'self',
    });
    if (esignRecipientDrafts.length === 0) {
      list.push({ email: 'recipient', name: 'Recipient', role: 'other' });
    } else {
      esignRecipientDrafts.forEach((email) => {
        list.push({ email, name: guessNameFromEmail(email), role: 'other' });
      });
    }
    return list;
  }, [auth.user?.email, auth.user?.displayName, esignRecipientDrafts]);

  // v1 placement-redesign: when AI detection finishes and returns 0
  // fields, drop default markers on the last page so the user has a
  // starting point. They can drag, delete, or add via the toolbar — the
  // cost of being wrong is just a few seconds of fixup.
  const lastDefaultDropSessionRef = useRef<string | null>(null);
  useEffect(() => {
    if (mode !== 'esign' || esignPdfPageCount < 1 || esignDetectingFields) return;
    if (esignFields.length > 0) return; // AI / regex found something — leave it alone
    const session = esignSessionKeyRef.current;
    if (!session || lastDefaultDropSessionRef.current === session) return;
    lastDefaultDropSessionRef.current = session;
    const defaults = generateDefaultMarkers(esignSigners, esignPdfPageCount);
    if (defaults.length > 0) setEsignFields(defaults);
  }, [mode, esignPdfPageCount, esignDetectingFields, esignFields.length, esignSigners]);

  const handleEsignInteractive = useCallback(
    async (_parsed: ParsedIntent, file: File | undefined) => {
      if (!file) {
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: 'corner',
            content: 'Please attach a PDF first, then ask me to sign it.',
            timestamp: Date.now(),
          },
        ]);
        setMode('empty');
        return;
      }

      const sessionKey = `${file.name}:${file.size}:${Date.now()}`;
      esignSessionKeyRef.current = sessionKey;
      const url = URL.createObjectURL(file);
      setEsignPdfUrl(url);
      setEsignFields([]);
      setEsignRecipientDrafts([]);
      setEsignActiveSignerIndex(0);
      setEsignPlacementMode(null);
      setEsignDetectingFields(true);
      setMode('esign');

      const pageCount = await sumPdfPagesInFiles([file]);
      if (esignSessionKeyRef.current === sessionKey) {
        setEsignPdfPageCount(pageCount || 1);
      }

      try {
        setProcessing({ progress: 30, label: 'Loading saved signing placements...' });
        const fd = new FormData();
        fd.append('files', file);
        const currentDocumentId = currentResult?.sourceFileId ?? currentResult?.fileId ?? sessionPrimaryDocument?.fileId ?? '';
        if (currentDocumentId) fd.append('currentDocumentId', currentDocumentId);
        const token = auth.getToken();
        const res = await axios.post<{ fields?: SignatureField[] }>('/api/signing/detect-fields', fd, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });

        if (esignSessionKeyRef.current !== sessionKey) return;

        const detectedFields = Array.isArray(res.data?.fields) ? res.data.fields : [];
        const reviewFields = prepareDetectedFieldsForReview(detectedFields);
        setEsignFields(reviewFields);
        setEsignDetectingFields(false);
        setProcessing(null);

        if (!reviewFields.length) {
          setMessages((prev) => [...prev, {
            id: crypto.randomUUID(),
            role: 'corner' as const,
            content: `No saved signing placements were found for this document yet. ${MANUAL_SIGNING_INSTRUCTIONS}`,
            timestamp: Date.now(),
            chips: [
              { label: 'Add recipients', variant: 'followup' as const },
              { label: 'Cancel', variant: 'followup' as const },
            ],
          }]);
          return;
        }

        const selfFields = reviewFields.filter(f => f.party === 'self' || f.signer === 'you');
        const otherFields = reviewFields.filter(f => f.party === 'other' || f.signer === 'other');
        setMessages((prev) => [...prev, {
          id: crypto.randomUUID(),
          role: 'corner' as const,
          content: `I restored ${reviewFields.length} saved signing field${reviewFields.length !== 1 ? 's' : ''}${selfFields.length ? ` — ${selfFields.length} for you` : ''}${otherFields.length ? ` and ${otherFields.length} for the other party` : ''}. ${MANUAL_SIGNING_INSTRUCTIONS}`,
          timestamp: Date.now(),
          chips: [
            { label: 'Add recipients', variant: 'followup' as const },
            { label: 'Cancel', variant: 'followup' as const },
          ],
        }]);
      } catch (err) {
        if (esignSessionKeyRef.current !== sessionKey) return;
        setEsignFields([]);
        setEsignDetectingFields(false);
        setProcessing(null);
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        const responseData = axios.isAxiosError(err) ? err.response?.data : undefined;
        const msg = status === 422
          ? "This file type isn't supported for signing yet. Please convert your document to PDF and try again."
          : typeof responseData?.message === 'string'
            ? responseData.message
            : typeof responseData?.error === 'string'
              ? responseData.error
              : `I couldn't load saved signing placements right now. ${MANUAL_SIGNING_INSTRUCTIONS}`;
        setMessages((prev) => [...prev, {
          id: crypto.randomUUID(),
          role: 'corner' as const,
          content: msg,
          timestamp: Date.now(),
          chips: status === 422 ? undefined : [
            { label: 'Add recipients', variant: 'followup' as const },
            { label: 'Cancel', variant: 'followup' as const },
          ],
        }]);
        toast({ variant: 'destructive', title: status === 422 ? 'Unsupported format' : 'Could not load placements', description: msg });
      }
    },
    [
      auth,
      currentResult?.sourceFileId,
      currentResult?.fileId,
      sessionPrimaryDocument?.fileId,
    ]
  );

  const handleToolResult = useCallback(
    (result: ToolResult, tool: ToolName) => {
      if (result.editBatch) {
        const ph = orchestrateAssistantMessageIdRef?.current;
        if (ph && orchestrateAssistantMessageIdRef) {
          orchestrateAssistantMessageIdRef.current = null;
          setMessages((prev) => prev.filter((m) => m.id !== ph));
        }
        const stepsSnap = cotStepsRef.current;
        const batchId = result.editBatch.id;
        const existingEditStepIndex = stepsSnap.findIndex((step) => step.editBatch?.id === batchId);
        let nextStepIndex = existingEditStepIndex;
        if (nextStepIndex < 0) {
          const indices = stepsSnap.map((_, i) => i);
          const lastEditRow = [...indices].reverse().find((i) => stepsSnap[i].editBatch || stepsSnap[i].category === 'edit');
          const lastActive = [...indices].reverse().find((i) => stepsSnap[i].status === 'active');
          nextStepIndex = lastEditRow ?? lastActive ?? Math.max(0, stepsSnap.length - 1);
        }
        cot.onStepEditBatch(nextStepIndex, result.editBatch);
        setLastTool(tool);
        return;
      }

      // ── E-sign signed result: store context + build esign summary card ──
      if (tool === 'esign' && result.status === 'signed' && result.fields) {
        lastEsignContextRef.current = {
          status: 'signed',
          fileName: result.fileName,
          fileType: result.mimeType,
          fieldCount: result.fields.length,
          timestamp: Date.now(),
          signedFields: result.fields
            .filter((f: import('@corner/shared').SignatureField) => f.placed && f.party === 'self')
            .map((f: import('@corner/shared').SignatureField) => ({ label: f.label, type: f.type ?? 'signature', party: 'self', page: f.page })),
          awaitingFields: result.fields
            .filter((f: import('@corner/shared').SignatureField) => !f.placed || f.party === 'other')
            .map((f: import('@corner/shared').SignatureField) => ({ label: f.label, type: f.type ?? 'signature', party: f.party ?? 'other', page: f.page })),
        };
        // Set signed fields so green markers render on the signed PDF
        console.log('[handleToolResult:esign] Setting signed fields:', result.fields.length, 'placed:', result.fields.filter((f: any) => f.placed).length);
        setEsignFields(result.fields);

        // Show signed PDF in document viewer
        if (result.downloadUrl && result.fileName) {
          addNode(result.fileName, undefined, result.downloadUrl, 'Signed');
          setMode('result');
          setLastConversionOutput(result);
          setDocumentPreviewVariant('output');
        }
        // Build esign chat summary card
        const summary = buildEsignChatSummary(result.fields, result);
        const prefE = orchestrateAssistantMessageIdRef?.current;
        const msgId = prefE ?? crypto.randomUUID();
        if (prefE && orchestrateAssistantMessageIdRef) orchestrateAssistantMessageIdRef.current = null;
        setMessages((prev) => {
          const row = {
            id: msgId,
            role: 'corner' as const,
            content: summary.headerTitle,
            timestamp: Date.now(),
            result,
            toolName: tool,
            esignSummary: summary,
            streaming: false as const,
          };
          const idx = prev.findIndex((m) => m.id === msgId);
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = { ...prev[idx], ...row };
            return next;
          }
          return [...prev, row];
        });
        cot.setAnswerMessageId(msgId);
        setLastTool(tool);
        // Persist to conversation
        const cid = currentConversationIdRef.current;
        if (cid) {
          const token = auth.getToken();
          if (token) {
            axios.post(`/api/conversations/${cid}/messages`, {
              role: 'assistant',
              content: summary.headerTitle,
              visibility: 'shared',
              result,
            }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
          }
        }
        return;
      }

      // ── fill_pdf_form: mirror server-applied values into the live overlay
      // store so the viewer reflects the AI fill instantly (no re-download). ──
      if (tool === 'fill_pdf_form' && Array.isArray(result.filledPlaceholders) && result.filledPlaceholders.length > 0) {
        // Use the original document fileId as the store key — same key the
        // viewer subscribes with via PdfSigningViewer's documentId prop.
        const docKey =
          (result as ToolResult & { sourceFileId?: string }).sourceFileId
          ?? sessionPrimaryDocument?.fileId
          ?? result.fileId;
        if (docKey) {
          const valuesMap: Record<string, string> = {};
          for (const fp of result.filledPlaceholders) {
            valuesMap[fp.fieldId] = fp.value;
          }
          setManyPlaceholderValues(docKey, valuesMap, 'ai');
        }
      }

      const STUDY_TOOL_NAMES = ['summarize_document', 'generate_study_questions', 'extract_key_terms',
        'summarize', 'study_questions', 'key_terms', 'citation_generator', 'contract_review',
        'action_items', 'email_draft', 'sensitive_data'];
      const isStudyTool = STUDY_TOOL_NAMES.includes(tool);
      const isOcrTool = tool === 'ocr';
      const isQrTool = tool === 'generate_qr';
      const isWatermarkTool = tool === 'add_watermark_pdf' && Boolean(result.watermarkMeta);
      const isPageNumbersTool = tool === 'add_page_numbers' && Boolean(result.pageNumbersMeta);
      const isTextResult = Boolean(result.textContent?.trim()) && !isOcrTool;
      const isAudioTool =
        tool === 'transcribe_audio' ||
        tool === 'extract_audio' ||
        tool === 'remove_silence' ||
        tool === 'convert_audio' ||
        tool === 'audio_to_pdf';
      const keepSourceDocOnLeft = !isAudioTool;
      const completionMessage = (result as ToolResult & { message?: string }).message ?? "Here's your processed document.";
      let bodyContent = isOcrTool || isQrTool || isWatermarkTool || isPageNumbersTool ? '' : (result.textContent?.trim() ?? '');

      const conversionFileOutput =
        keepSourceDocOnLeft &&
        !isStudyTool &&
        !isTextResult &&
        !isOcrTool &&
        !isQrTool &&
        !isWatermarkTool &&
        !isPageNumbersTool &&
        Boolean(result.fileName && result.downloadUrl);

      const hasMergeManifest =
        tool === 'merge_pdf' && Boolean(result.mergeManifest && result.mergeManifest.length > 0);

      const hasSplitZipOutput =
        tool === 'split_pdf' &&
        typeof result.splitPageCount === 'number' &&
        result.splitPageCount > 0;

      if (isStudyTool || isTextResult || isOcrTool || isQrTool) {
        setLastConversionOutput(null);
        setDocumentPreviewVariant('original');
      }

      if (conversionFileOutput && !bodyContent) {
        const fn = result.fileName.toLowerCase();
        if (hasMergeManifest) {
          bodyContent = 'Your merged PDF is ready — each source file is listed below.';
        } else if (hasSplitZipOutput) {
          bodyContent =
            'Each page is a separate PDF inside the zip — use the strip below to see how the split maps to pages, then download.';
        } else if (fn.endsWith('.docx') || fn.endsWith('.doc')) {
          bodyContent =
            'Done! Your Word document is ready. Use the toolbar above to download — or open “Original PDF” for Retrieve original / Retrieve and prompt.';
        } else if (fn.endsWith('.pdf')) {
          bodyContent =
            'Done! Your PDF is ready. Use the toolbar above to download — or open “Original PDF” for Retrieve original / Retrieve and prompt.';
        } else {
          bodyContent = completionMessage;
        }
      } else if (!bodyContent && !isQrTool && !isOcrTool && !isWatermarkTool && !isPageNumbersTool) {
        bodyContent = completionMessage;
      }

      const hideInlineResultCard =
        hasMergeManifest || hasSplitZipOutput
          ? true
          : Boolean(conversionFileOutput) || isOcrTool || isQrTool || isWatermarkTool || isPageNumbersTool;

      if (!keepSourceDocOnLeft) {
        setLastConversionOutput(null);
        setDocumentPreviewVariant('original');
        setCurrentResult(result);
        setMode('result');
        addNode(result.fileName, undefined, result.downloadUrl, toolToFriendlyOperation(tool));
        setCanvasViewMode(true);
      } else if (isQrTool) {
        setCurrentResult(result);
        setLastConversionOutput(null);
        setDocumentPreviewVariant('original');
        setMode('result');
        addNode(result.fileName, undefined, result.downloadUrl, toolToFriendlyOperation(tool));
      } else if (isWatermarkTool || isPageNumbersTool) {
        // Watermark / Page numbers: show output PDF in document viewer, card in chat
        addNode(result.fileName, undefined, result.downloadUrl, toolToFriendlyOperation(tool));
        setCurrentResult(result);
        setMode('result');
        setLastConversionOutput(result);
        setDocumentPreviewVariant('output');
      } else if (isOcrTool) {
        // OCR: keep source doc on left, store result for overlay badge
        setCurrentResult(result);
        setLastConversionOutput(null);
      } else if (!isStudyTool && !isTextResult) {
        addNode(result.fileName, undefined, result.downloadUrl, toolToFriendlyOperation(tool));
        setCurrentResult(result);
        if (result.fileId) {
          setSessionPrimaryDocument({
            fileId: result.fileId,
            fileName: result.fileName,
            mimeType: result.mimeType,
            sizeBytes: result.sizeBytes ?? 0,
            downloadUrl: result.downloadUrl,
            source: tool === 'generate_document' ? 'generated' : 'result',
          });
        }
        setMode('result');
        setLastConversionOutput(result);
        setDocumentPreviewVariant('output');
      }
      setLastTool(tool);

      // Document draft: switch to drafting mode with editable rich text
      if (result.draftHtml) {
        setCurrentResult(result);
        setDraftContent(result.draftHtml);
        setDraftTitle(result.draftTitle ?? result.fileName ?? 'Untitled Document');
        setMode('drafting');
      }

      // If the result came from an analysis/study tool, mark it as an analysis message
      // so the chatMessages enrichment renders an AnalysisCard with full ExportBar.
      // Use result.studyTool when available, otherwise fall back to the tool name itself.
      const analysisStudyTool = result.studyTool ?? (isStudyTool ? tool : undefined);
      const isAnalysisResult = isStudyTool && Boolean(result.textContent || bodyContent);

      const prefId = orchestrateAssistantMessageIdRef?.current;
      const msgId = prefId ?? crypto.randomUUID();
      if (prefId && orchestrateAssistantMessageIdRef) orchestrateAssistantMessageIdRef.current = null;

      const cornerRow = {
        id: msgId,
        role: 'corner' as const,
        content: bodyContent,
        timestamp: Date.now(),
        result,
        toolName: tool,
        hideInlineResultCard,
        streaming: false as const,
        ...(isAnalysisResult && {
          type: 'analysis' as const,
          analysisType: analysisStudyTool as import('./types').AnalysisType,
          exportable: true,
        }),
      };

      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.id === msgId);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...prev[idx], ...cornerRow };
          return next;
        }
        return [...prev, cornerRow];
      });

      // Link this message to the CoT answer so ChatPanel can hide the bubble
      // when the CoTContainer is rendering the same content.
      cot.setAnswerMessageId(msgId);
      console.log('[CoT DEBUG] handleToolResult', { msgId: msgId.slice(0, 8), tool, cotStatus: cot.state.status, cotHasAnswer: !!cot.state.answer });
      const cid = currentConversationIdRef.current;
      if (cid) {
        const toolParams: Record<string, unknown> = {};
        if (hasMergeManifest && result.mergeManifest) {
          toolParams.mergeManifest = result.mergeManifest;
          if (result.mergeDurationMs != null) toolParams.mergeDurationMs = result.mergeDurationMs;
        }
        if (hasSplitZipOutput && result.splitPageCount != null) {
          toolParams.splitPageCount = result.splitPageCount;
          if (result.splitFiles) toolParams.splitFiles = result.splitFiles;
          if (result.splitDurationMs != null) toolParams.splitDurationMs = result.splitDurationMs;
        }
        if (isOcrTool && result.ocrMeta) {
          toolParams.ocrMeta = result.ocrMeta;
          if (result.textContent) toolParams.ocrTextContent = result.textContent;
        }
        if (isQrTool && result.qrMeta) {
          toolParams.qrMeta = result.qrMeta;
        }
        if (isWatermarkTool && result.watermarkMeta) {
          toolParams.watermarkMeta = result.watermarkMeta;
        }
        if (isPageNumbersTool && result.pageNumbersMeta) {
          toolParams.pageNumbersMeta = result.pageNumbersMeta;
        }
        if (isAnalysisResult && analysisStudyTool) {
          toolParams.analysisType = analysisStudyTool;
          toolParams.exportable = true;
        }
        addConversationMessage(cid, {
          role: 'corner',
          content: bodyContent,
          toolCall: {
            toolName: isAnalysisResult ? '__analysis__' : tool,
            resultFileId: result.fileId,
            resultFileName: result.fileName,
            resultMimeType: result.mimeType,
            resultSizeBytes: result.sizeBytes,
            ...(Object.keys(toolParams).length > 0 ? { params: toolParams } : {}),
          },
        }).catch((err) => {
          console.error('[handleToolResult] Failed to persist AI message:', err);
          // Retry once after a short delay — this is the most common cause of missing AI messages on refresh
          setTimeout(() => {
            addConversationMessage(cid, {
              role: 'corner',
              content: bodyContent,
              toolCall: {
                toolName: isAnalysisResult ? '__analysis__' : tool,
                resultFileId: result.fileId,
                resultFileName: result.fileName,
                resultMimeType: result.mimeType,
                resultSizeBytes: result.sizeBytes,
                ...(Object.keys(toolParams).length > 0 ? { params: toolParams } : {}),
              },
            }).catch((retryErr) => console.error('[handleToolResult] Retry also failed:', retryErr));
          }, 2000);
        });
      }
    },
    [addConversationMessage, addNode, cot, orchestrateAssistantMessageIdRef]
  );

  const runQrAlternateFormat = useCallback(
    async (format: 'png' | 'svg', payload: string) => {
      try {
        setProcessing({ progress: 30, label: 'Generating…' });
        const formData = new FormData();
        formData.append('tool', 'generate_qr');
        formData.append(
          'params',
          JSON.stringify({
            text: payload,
            format,
            qrSize: panelSettings.qrSize ?? 512,
          })
        );
        const token = auth.getToken();
        const { data } = await axios.post<ToolResult>('/api/execute', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        setProcessing({ progress: 100, label: 'Done' });
        await new Promise((r) => setTimeout(r, 280));
        setProcessing(null);
        handleToolResult(data, 'generate_qr');
      } catch (err) {
        setProcessing(null);
        const e = getActionError('generate QR code', err);
        toast({ variant: 'destructive', title: e.title, description: e.description });
      }
    },
    [auth.getToken, panelSettings.qrSize, handleToolResult]
  );

  /** Latest user message for orchestrator CoT “How I’m approaching this” header. */
  const lastOrchestratorUserPromptRef = useRef('');

  const getCurrentDocumentContext = useCallback(() => {
    const resolved = activeDocumentSurface;
    const draftDocId = !resolved && mode === 'drafting' && currentConversationId
      ? `draft:${currentConversationId}`
      : undefined;
    const baseName = (draftTitle || 'Draft').replace(/\.(docx|doc|pdf)$/i, '');
    const documentId = resolved?.currentDocumentId ?? draftDocId ?? (currentResult?.sourceFileId ?? currentResult?.fileId ?? sessionPrimaryDocument?.fileId ?? undefined);
    console.log('[PrimaryDocument] orchestrate-base', {
      currentDocumentId: documentId ?? null,
      currentDocumentVersion: resolved?.currentDocumentVersion ?? currentResult?.documentVersion ?? null,
      source: resolved?.source ?? (draftDocId ? 'syntheticCurrentResult' : sessionPrimaryDocument?.fileId ? 'primaryDocument' : 'syntheticCurrentResult'),
    });
    return {
      documentId,
      fileName: draftDocId
        ? `${baseName || 'Draft'}.docx`
        : (resolved?.ref.fileName ?? currentResult?.fileName ?? currentFile?.name ?? sessionPrimaryDocument?.fileName ?? undefined),
      mimeType: draftDocId
        ? 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        : (resolved?.ref.mimeType ?? currentResult?.mimeType ?? currentFile?.type ?? sessionPrimaryDocument?.mimeType ?? undefined),
    };
  }, [
    activeDocumentSurface,
    mode,
    currentConversationId,
    draftTitle,
    currentResult?.sourceFileId,
    currentResult?.fileId,
    currentResult?.fileName,
    currentResult?.mimeType,
    currentFile?.name,
    currentFile?.type,
    sessionPrimaryDocument?.fileId,
    sessionPrimaryDocument?.fileName,
    sessionPrimaryDocument?.mimeType,
  ]);

  // Intent hook wires parse → execute → result
  const { execute: executeIntent, executeWithOrchestrator, stop: stopProcessing } = useIntent({
    getToken: auth.getToken,
    getCurrentDocumentContext,
    onProcessingChange: setProcessing,
    orchestrateAssistantMessageIdRef,
    onResult: handleToolResult,
    onEsignStatusResult: handleEsignStatusResult,
    onMessages: setMessages,
    onClarify: () => setMode('clarifying'),
    onEsignInteractive: handleEsignInteractive,
    onCoTPlanning: cot.onPlanning,
    onCoTPlanReady: (plan) => {
      const ctx = getCurrentDocumentContext();
      const headerParts: string[] = [];
      if (ctx?.fileName?.trim()) headerParts.push(`Document: ${ctx.fileName.trim()}`);
      const req = lastOrchestratorUserPromptRef.current?.trim();
      if (req) headerParts.push(`Your request:\n${req}`);
      const header = headerParts.join('\n\n');
      const planU = plan.understanding?.trim();
      const merged =
        header && planU ? `${header}\n\n${planU}`
        : header || planU || plan.understanding;
      cot.onPlanReady({ ...plan, understanding: merged || undefined });
    },
    onCoTStepStart: cot.onStepStart,
    onCoTStepComplete: cot.onStepComplete,
    onCoTStepError: cot.onStepError,
    onCoTEditBatch: cot.onStepEditBatch,
    onCoTThinkingChunk: cot.onThinkingChunk,
    onCoTClarificationStep: cot.onClarificationStep,
    onCoTDone: (answerText?: string, partialCompletion?: boolean) => {
      // Persist text-only orchestrator answers so they appear on refresh
      if (answerText && !partialCompletion) {
        const cid = currentConversationIdRef.current;
        if (cid && auth.user) {
          addConversationMessage(cid, { role: 'corner', content: answerText }).catch((err) => {
            console.error('[onCoTDone] Failed to persist answer text:', err);
          });
        }
      }
      cot.onDone(answerText ? {
        content: answerText,
        actions: [
          {
            label: 'Draft redlines',
            icon: 'edit' as const,
            primary: true,
            onClick: async () => {
              setMode('review');
              const token = auth.getToken();
              const fileId = currentResult?.sourceFileId ?? currentResult?.fileId ?? sessionPrimaryDocument?.fileId;
              if (!token) {
                review.startGenerating([], () => {
                  review.triggerGeneratingError('Sign in to generate review suggestions.', 'parse');
                });
                return;
              }
              if (!fileId) {
                review.startGenerating([], () => {
                  review.triggerGeneratingError('No document loaded. Upload a document first.', 'parse');
                });
                return;
              }
              // Start the generating animation immediately
              review.startGenerating([], async () => {
                // Animation finished — suggestions should already be loaded
              });
              try {
                const created = await reviewApi.createReview({
                  sourceKind: 'file',
                  sourceId: fileId,
                  title: topbarFileName ?? currentFile?.name ?? 'Document Review',
                });
                review.setReviewId(created.reviewId);
                const generated = await reviewApi.generateReview(created.reviewId);
                const suggestions = generated.suggestions.map((s) => ({
                  id: s.id,
                  sectionRef: s.sectionRef,
                  sectionTitle: s.sectionTitle,
                  priority: s.priority,
                  category: s.category,
                  summary: s.summary,
                  originalText: s.originalText,
                  proposedText: s.proposedText,
                  rationale: s.rationale,
                  tags: s.tags,
                  shortLabel: s.shortLabel,
                  status: s.status,
                }));
                review.loadSuggestions(suggestions);
              } catch (err) {
                review.triggerGeneratingError(
                  err instanceof Error ? err.message : 'Failed to generate review. Please try again.',
                  'draft',
                );
              }
            },
          },
          {
            label: 'Clause review',
            primary: false,
            onClick: () => {
              clauseReviewPendingSourceRef.current = null;
              openClauseReviewSetup();
            },
          },
          {
            label: 'Save as PDF',
            primary: false,
            onClick: () => {
              if (answerText) {
                const fileName = topbarFileName || 'Summary';
                api.exportText(answerText, 'pdf', fileName).then(() => {
                  toast({ title: 'PDF saved' });
                }).catch((err) => {
                  const e = getActionError('export document', err);
                  toast({ title: e.title, description: e.description, variant: 'destructive' });
                });
              }
            },
          },
        ],
      } : undefined, partialCompletion);
    },
    onCoTError: cot.onError,
    onCoTReset: cot.reset,
    // CORNER_V2: Permission confirmation — show inline in CoT panel
    onPermissionConfirmNeeded: (request) => {
      cot.onStepConfirmNeeded(
        request.stepIndex,
        request.toolName,
        request.description,
        request.permissionTier,
      );
    },
  });

  // CORNER_V2: Inline CoT confirmation handlers
  const handleConfirmStep = useCallback((stepIndex: number) => {
    cot.onStepConfirmed(stepIndex);
    const docId = sessionPrimaryDocument?.fileId ?? currentResult?.fileId;
    const token = auth.getToken();
    if (token) {
      fetch('/api/orchestrate/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ docId }),
      }).catch(() => {});
    }
  }, [cot, sessionPrimaryDocument, currentResult, auth]);

  const handleCancelStep = useCallback((_stepIndex: number) => {
    // Tell backend to cancel the pending confirmation gate
    const token = auth.getToken();
    if (token) {
      fetch('/api/orchestrate/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      }).catch(() => {});
    }
    cot.reset();
    stopProcessing();
  }, [cot, stopProcessing, auth]);

  const handleClarifyAnswer = useCallback((stepIndex: number, answer: string) => {
    cot.onClarificationAnswered(stepIndex);
    // TODO: send answer back to backend when clarification_step pause-resume is implemented
  }, [cot]);

  const handleClarifyCancel = useCallback((stepIndex: number) => {
    cot.onClarificationAnswered(stepIndex);
  }, [cot]);

  const handleRightToolSelect = useCallback(
    (tool: ToolName, settings?: Record<string, unknown>) => {
      if (tool === 'generate_qr') {
        setUtilityChatTool('generate_qr');
        setCurrentResult(null);
        setLastConversionOutput(null);
        setDocumentPreviewVariant('original');
        setRightOpen(true);
        setMode('empty');
        return;
      }
      setMode('processing');
      const prompt =
        settings && Object.keys(settings).length > 0
          ? `use the ${tool} tool with settings: ${JSON.stringify(settings)}`
          : `use the ${tool} tool`;
      executeIntent(prompt, currentFiles[0] ?? undefined);
    },
    [executeIntent, currentFiles]
  );

  // Shared file-intake handler — used by both dropzone and InputBar attach
  const handleFilesReceived = useCallback(async (files: File[]) => {
    if (!files.length) return;

    // Client-side file validation — reject before uploading
    // Must match backend: guests 50MB, authenticated users 500MB (any plan)
    const MAX_FILE_SIZE = auth.user ? 500 * 1024 * 1024 : 50 * 1024 * 1024;
    const oversized = files.filter((f) => f.size > MAX_FILE_SIZE);
    if (oversized.length > 0) {
      const limitMb = Math.round(MAX_FILE_SIZE / (1024 * 1024));
      toast({
        variant: 'destructive',
        title: 'File too large',
        description: `${oversized[0].name} exceeds the ${limitMb} MB limit.${!auth.user ? ' Sign in for 500 MB uploads.' : ''}`,
      });
      return;
    }

    // Navigate to workspace only if not already there (avoid redundant history entries)
    if (window.location.pathname !== APP_WORKSPACE_PATH && !window.location.pathname.includes('/conversations/')) {
      navigate(APP_WORKSPACE_PATH, { replace: true });
    }
    if (!auth.user) {
      openFullOnboarding();
      return;
    }
    setCurrentFiles(files);
    const sourceDocuments = filesToSessionDocuments(files);
    setSessionSourceDocuments(sourceDocuments);
    setSessionPrimaryDocument(sourceDocuments[0] ?? null);
    const loadId = crypto.randomUUID();
    if (files.length === 1 && isAudioLikeFile(files[0])) {
      lastAudioLoadMsgIdRef.current = loadId;
      lastImageLoadMsgIdRef.current = null;
    } else if (files.length === 1 && isImageLikeFile(files[0])) {
      lastImageLoadMsgIdRef.current = loadId;
      lastAudioLoadMsgIdRef.current = null;
    } else {
      lastAudioLoadMsgIdRef.current = null;
      lastImageLoadMsgIdRef.current = null;
    }
    if (files.length === 1) {
      const isSinglePdf = getFileFormatInfo(files[0].name).isPDF;
      if (isSinglePdf) {
        const pages = await sumPdfPagesInFiles([files[0]]);
        const label = pdfSingleFileLoadedMessage(files[0].name, pages > 0 ? pages : undefined);
        setMessages((prev) => [
          ...prev,
          {
            id: loadId,
            role: 'corner' as const,
            content: label,
            timestamp: Date.now(),
            systemKind: 'pdf_single_loaded' as const,
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            id: loadId,
            role: 'system' as const,
            content: singleFileLoadedMessageBase(files[0].name),
            timestamp: Date.now(),
          },
        ]);
      }
      return;
    }
    const multi = await buildMultiFileLoadedSystemMessage(Array.from(files));
    setMessages((prev) => [
      ...prev,
      {
        id: loadId,
        role: 'system',
        content: multi.content,
        systemKind: multi.systemKind,
        timestamp: Date.now(),
      },
    ]);
  }, [auth.user, openFullOnboarding, filesToSessionDocuments]);

  // Canvas-wide drop zone — accept multiple files
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleFilesReceived,
    noClick: true,
    multiple: true,
  });

  const handleRemoveQueuedFile = useCallback((index: number) => {
    setCurrentFiles((prev) => {
      const nextFiles = prev.filter((_, i) => i !== index);
      const nextDocuments = filesToSessionDocuments(nextFiles);
      setSessionSourceDocuments(nextDocuments);
      setSessionPrimaryDocument(nextDocuments[0] ?? null);
      return nextFiles;
    });
  }, [filesToSessionDocuments]);

  const handleAttachProjectFileToChat = useCallback(async (input: {
    fileId: string;
    fileName: string;
    mimeType?: string;
  }) => {
    if (!input.fileId || isSyntheticDraftFileId(input.fileId)) return;
    try {
      const response = await fetch(appendTokenToUrl(`/api/file/${input.fileId}`, auth.getToken));
      if (!response.ok) throw new Error(`Could not fetch file (${response.status})`);
      const blob = await response.blob();
      const file = new File([blob], input.fileName || 'Project document', {
        type: input.mimeType || blob.type || 'application/octet-stream',
      });
      await handleFilesReceived([...currentFiles, file]);
      setChatCollapsed(false);
      setCollabTab('analysis');
      toast({ title: 'Attached to chat', description: input.fileName });
    } catch (err) {
      showAppError('Could not attach project file', err instanceof Error ? err.message : undefined);
    }
  }, [auth.getToken, currentFiles, handleFilesReceived, showAppError, toast]);

  const handleChatProjectFileDrop = useCallback((event: React.DragEvent) => {
    const raw = event.dataTransfer.getData('application/x-corner-file');
    if (!raw) return;
    event.preventDefault();
    event.stopPropagation();
    try {
      const parsed = JSON.parse(raw) as { fileId?: string; fileName?: string; mimeType?: string };
      if (!parsed.fileId || !parsed.fileName) return;
      void handleAttachProjectFileToChat({
        fileId: parsed.fileId,
        fileName: parsed.fileName,
        mimeType: parsed.mimeType,
      });
    } catch {
      toast({ title: 'Could not attach file', description: 'Try opening the project and attaching it again.', variant: 'destructive' });
    }
  }, [handleAttachProjectFileToChat, toast]);

  const handleClearCurrentFiles = useCallback(() => {
    setCurrentFiles([]);
    setSessionSourceDocuments([]);
    setSessionPrimaryDocument(null);
  }, []);

  /** Collect fileIds from session documents that have been indexed (have fileId). */
  const getV2EligibleFileIds = useCallback((): string[] => {
    const ids: string[] = [];
    const addFileId = (fileId?: string | null) => {
      if (!fileId || isSyntheticDraftFileId(fileId) || ids.includes(fileId)) return;
      ids.push(fileId);
    };

    // Primary document from workspace load or prior tool execution
    addFileId(sessionPrimaryDocument?.fileId);
    // Additional session source documents
    for (const doc of sessionSourceDocuments) {
      addFileId(doc.fileId);
    }
    // Files already living in the active project should be usable as context
    // without forcing the user to drag them into the chat composer.
    for (const doc of projectContextDocuments) {
      addFileId(doc.fileId);
    }
    // Current result's source file (from prior orchestration)
    const resultFileId = currentResult?.sourceFileId ?? currentResult?.fileId;
    addFileId(resultFileId);
    return ids;
  }, [sessionPrimaryDocument, sessionSourceDocuments, projectContextDocuments, currentResult]);

  const buildDraftActivityPhases = useCallback((kind: 'draft' | 'revise') => [{
    id: `draft-activity-${Date.now()}`,
    collapsed: false,
    steps: [
      {
        id: 'draft-activity-read',
        kind: 'thinking',
        label: 'Thinking through the draft',
        body: kind === 'revise'
          ? "I'm reading the current draft and your requested change before rewriting the document."
          : "I'm reading your prompt as a drafting brief and deciding the right document structure.",
        status: 'success',
        expanded: true,
      },
      {
        id: 'draft-activity-layout',
        kind: 'tool_call',
        icon: 'draft',
        label: kind === 'revise' ? 'Revise the document' : 'Draft the document',
        status: 'active',
      },
    ],
  }], []);

  /** Export draft HTML → uploaded PDF in the background so Send for signature skips the blocking spinner when HTML hasn’t changed. */
  const warmDraftPdfMirrorIfFresh = useCallback(
    async (args: { conversationId: string; html: string; title: string }) => {
      const { conversationId, html, title } = args;
      const trimmed = html.trim();
      if (!trimmed) return;
      const digest = draftHtmlMirrorDigest(trimmed);
      const token = auth.getToken();
      if (!token || !auth.user) return;
      const cid = conversationId;
      try {
        const baseName = (title || 'Document').replace(/\.(html?|docx|pdf)$/i, '') || 'Document';
        const exportRes = await axios.post(
          '/api/export-text',
          { text: trimmed, format: 'pdf', fileName: baseName },
          { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } },
        );
        const pdfFile = new File(
          [exportRes.data],
          `${baseName}.pdf`,
          { type: 'application/pdf' },
        );
        const uploaded = await projectApi.uploadProjectSeedFile(pdfFile, auth.getToken);
        if (String(currentConversationIdRef.current) !== String(cid)) return;
        if (draftHtmlMirrorDigest(draftContentRef.current) !== digest) return;
        const downloadUrl = uploaded.downloadUrl ?? `/api/file/${uploaded.fileId}`;
        const pdfName = uploaded.fileName ?? `${baseName}.pdf`;
        const pdfResult: ToolResult = {
          fileId: uploaded.fileId,
          downloadUrl,
          fileName: pdfName,
          mimeType: uploaded.mimeType ?? 'application/pdf',
          sizeBytes: uploaded.sizeBytes ?? 0,
        };
        setLastConversionOutput(pdfResult);
        setDraftPdfMirrorDigest(digest);
      } catch {
        /* non-fatal — Send for signature still converts on demand */
      }
    },
    [auth.getToken, auth.user],
  );

  const handleSend = useCallback(
    async (text: string, files: File[] = []) => {
      if (!auth.user) {
        navigate('/auth/login');
        return;
      }
      setChatComposerPlaceholderHint(null);
      const selectedSnippetForSend = pendingSelectedSnippet;
      text = text.trim() || (selectedSnippetForSend ? 'Explain this selection.' : '');
      if (selectedSnippetForSend) setPendingSelectedSnippet(null);
      const allFiles = files.length > 0 ? files : currentFiles;
      const isFirstFile = files.length > 0 && currentFiles.length === 0;
      if (files.length > 0) {
        setCurrentFiles(files);
        const sourceDocuments = filesToSessionDocuments(files);
        setSessionSourceDocuments(sourceDocuments);
        setSessionPrimaryDocument(sourceDocuments[0] ?? null);
      }

      // Typed "apply changes" / "apply my signature" should run the same local `/api/execute` path as
      // the PDF toolbar — not the orchestrator/V2 query run (which often no-ops).
      const applySignatureChatPhrase =
        /\bapply\s+(?:my\s+)?changes\b|\b(apply|stamp|embed|place)\s+(my\s+)?(saved\s+)?signature\b|\bapply\s+my\s+signature\b|\b(sign|drop)\s+my\s+signature\b|\bsign\s+it\s+now\b|\bput\s+my\s+signature\s+on\b/i;
      const wantsApplySignatureFromChat =
        files.length === 0
        && applySignatureChatPhrase.test(text.trim())
        && esignFields.length > 0;

      const signingGuideChatPhrase =
        /\b(sign\s+(this|the|it|for\s+me)|prepare\s+.*sign|add\s+.*signature|signature\s+field|apply\s+my\s+signature|send\s+for\s+signature)\b/i;
      const wantsSigningGuideFromChat =
        files.length === 0
        && signingGuideChatPhrase.test(text.trim())
        && Boolean(currentResult || currentFile || filePreviewUrl || esignFields.length > 0);

      if (wantsApplySignatureFromChat) {
        const hasSelf = esignFields.some(
          (f) => f.party !== 'other' && f.signer !== 'other',
        );

        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: 'user' as const,
            content: text,
            timestamp: Date.now(),
            attachmentName: allFiles.length > 1 ? `${allFiles.length} files` : allFiles[0]?.name,
          },
        ]);

        if (auth.user) {
          (async () => {
            let cid = currentConversationIdRef.current;
            if (!cid) {
              const fileName = (files[0] ?? currentFile)?.name?.trim();
              const title = fileName || text.trim().slice(0, 80) || 'New Conversation';
              cid = await createConversation(title.slice(0, 200));
              setCurrentConversationId(cid);
              currentConversationIdRef.current = cid;
              if (!window.location.pathname.includes('/conversations/')) {
                navigate(conversationWorkspacePath(cid), { replace: true });
              }
            }
            await addConversationMessage(cid, { role: 'user', content: text });
          })().catch((err) => {
            console.error('[handleSend] Failed to persist conversation:', err);
            toast({ variant: 'destructive', title: 'Couldn\u2019t save message', description: 'Your message may not be saved. Check your connection.' });
          });
        }

        if (!hasSelf) {
          const guideMessage = 'I will help you place it first. Click the document where your signature belongs.';
          setMode('esign');
          setProcessing(null);
          cot.reset();
          setEsignActiveSignerIndex(0);
          setEsignPlacementMode('signature');
          setMessages((prev) => [...prev, {
            id: crypto.randomUUID(),
            role: 'corner' as const,
            content: guideMessage,
            timestamp: Date.now(),
          }]);
          const persistCid = currentConversationIdRef.current;
          if (persistCid) {
            addConversationMessage(persistCid, { role: 'corner', content: guideMessage })
              .catch((err) => console.error('[signing-guide] persist apply-marker msg failed', err));
          }
          return;
        }

        const nextFields = esignFields.map((f) => {
          if (f.party === 'other' || f.signer === 'other') return f;
          if (f.state === 'suggested') return { ...f, state: 'confirmed' as const };
          return f;
        });
        setEsignFields(nextFields);
        flushTitlesThenApply(nextFields);
        return;
      }

      const draftKeywords =
        /\b(write|draft|compose|create|generate|make)\b.*\b(contract|agreement|letter|document|memo|proposal|email|report|nda|terms|policy|invoice|receipt|template|brief|plan|outline|summary)\b/i;
      const willDraftRevise = modeRef.current === 'drafting' && !!draftContent;
      const willDraftStream =
        allFiles.length === 0 && projectContextDocuments.length === 0 && draftKeywords.test(text) && !willDraftRevise;
      const draftLatencyRun = willDraftStream
        ? {
            id: typeof crypto !== 'undefined' && 'randomUUID' in crypto
              ? crypto.randomUUID()
              : `draft-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            submitAt: performance.now(),
          }
        : null;

      if (draftLatencyRun) {
        draftLatencyLog('submitted', {
          runId: draftLatencyRun.id,
          promptLength: text.length,
          conversationId: currentConversationIdRef.current ?? null,
        });
      }

      if (willDraftStream) {
        setDraftPdfMirrorDigest(null);
        setDraftContent('');
        setDraftTitle('Generating...');
        setMode('drafting');
        setProcessing({ progress: 8, label: 'Drafting...' });
        setNarrowChatForInitialDraftStream(true);

        setVersionRailPeekKey((k) => k + 1);
        cot.reset();
        cot.applySnapshot({
          status: 'working',
          steps: [
            { title: 'Analyzing your request', status: 'done' },
            { title: 'Drafting document', status: 'active', description: 'Streaming text…' },
          ],
          activityPhases: buildDraftActivityPhases('draft'),
        });
        if (draftLatencyRun) {
          draftLatencyLog('submit->ui-ready', {
            runId: draftLatencyRun.id,
            ms: Math.round(performance.now() - draftLatencyRun.submitAt),
          });
        }
      } else if (!willDraftRevise && !wantsSigningGuideFromChat) {
        setMode('processing');
        // Keep `processing` non-null until the orchestrator / query-run pipeline
        // emits real progress — otherwise the effect below sees mode=processing +
        // processing=null and flips back to empty while awaiting network I/O.
        setProcessing({ progress: 0, label: 'Starting…' });
        cot.reset();
      }

      let multiSystem: { content: string; systemKind: 'multi_files_loaded' } | null = null;
      if (isFirstFile && allFiles.length > 1) {
        multiSystem = await buildMultiFileLoadedSystemMessage(allFiles);
      }

      // Pre-compute pdf page count outside the synchronous setMessages updater
      let pdfPages = 0;
      if (isFirstFile && allFiles.length === 1 && getFileFormatInfo(allFiles[0].name).isPDF) {
        pdfPages = await sumPdfPagesInFiles([allFiles[0]]);
      }

      setMessages((prev) => {
        const next = [...prev];
        if (isFirstFile && allFiles.length > 0) {
          const loadId = crypto.randomUUID();
          if (allFiles.length === 1 && isAudioLikeFile(allFiles[0])) {
            lastAudioLoadMsgIdRef.current = loadId;
            lastImageLoadMsgIdRef.current = null;
          } else if (allFiles.length === 1 && isImageLikeFile(allFiles[0])) {
            lastImageLoadMsgIdRef.current = loadId;
            lastAudioLoadMsgIdRef.current = null;
          } else {
            lastAudioLoadMsgIdRef.current = null;
            lastImageLoadMsgIdRef.current = null;
          }
          if (allFiles.length === 1) {
            const isSinglePdf = getFileFormatInfo(allFiles[0].name).isPDF;
            next.push({
              id: loadId,
              role: isSinglePdf ? 'corner' as const : 'system' as const,
              content: isSinglePdf
                ? pdfSingleFileLoadedMessage(allFiles[0].name, pdfPages > 0 ? pdfPages : undefined)
                : singleFileLoadedMessageBase(allFiles[0].name),
              timestamp: Date.now(),
              ...(isSinglePdf && { systemKind: 'pdf_single_loaded' as const }),
            });
          } else if (multiSystem) {
            next.push({
              id: loadId,
              role: 'corner',
              content: multiSystem.content,
              systemKind: multiSystem.systemKind,
              timestamp: Date.now(),
            });
          }
        }
        next.push({
          id: crypto.randomUUID(),
          role: 'user',
          content: text,
          timestamp: Date.now(),
          attachmentName: allFiles.length > 1 ? `${allFiles.length} files` : allFiles[0]?.name,
        });
        return next;
      });

      const persistUserTurnPromise: Promise<string | null> = !auth.user
        ? Promise.resolve(currentConversationIdRef.current)
        : (async () => {
          let cid = currentConversationIdRef.current;
          if (!cid) {
            const fileName = (files[0] ?? currentFile)?.name?.trim();
            const title = fileName || text.trim().slice(0, 80) || 'New Conversation';
            cid = await createConversation(title.slice(0, 200));
            setCurrentConversationId(cid);
            currentConversationIdRef.current = cid;
            // Only navigate if still on a generic /app path (avoid clobbering active mode navigations)
            if (!window.location.pathname.includes('/conversations/')) {
              navigate(conversationWorkspacePath(cid), { replace: true });
            }
          }
          await addConversationMessage(cid, { role: 'user', content: text });
          return cid;
        })().then((cid) => cid, (err) => {
          console.error('[handleSend] Failed to persist conversation:', err);
          toast({ variant: 'destructive', title: 'Couldn\u2019t save message', description: 'Your message may not be saved. Check your connection.' });
          throw err;
        });

      if (wantsSigningGuideFromChat) {
        const guideMessage = esignFields.length > 0
          ? 'I will help you finish signing this document. Use the guide below to place any missing fields, then apply changes when the markers look right.'
          : 'I will help you place signing fields. Use the Signature chip below, then click the document where your signature belongs.';
        setMode('esign');
        setProcessing(null);
        cot.reset();
        setEsignPlacementMode(null);
        if (applySignatureChatPhrase.test(text.trim())) {
          setEsignActiveSignerIndex(0);
          setEsignPlacementMode('signature');
        }
        setMessages((prev) => [...prev, {
          id: crypto.randomUUID(),
          role: 'corner' as const,
          content: guideMessage,
          timestamp: Date.now(),
        }]);
        const persistCid = currentConversationIdRef.current;
        if (persistCid) {
          addConversationMessage(persistCid, { role: 'corner', content: guideMessage })
            .catch((err) => console.error('[signing-guide] persist AI msg failed', err));
        }
        return;
      }

      // Signing intent while drafting — route through the same launch
      // handler the doc-top-bar "Send for signature" button uses, so
      // chat and the button stay symmetric. Without this branch, a
      // message like "sign for me" / "send for signature" / "let's
      // sign this" falls through to the draft-revision branch below
      // and POSTs to /api/analyze/draft as if it were a revision
      // prompt. That endpoint hands the text to the doc-revision LLM,
      // which has no signing capability and 502s — the exact failure
      // the user hit. Our launch handler already knows that drafts
      // can't be signed directly yet (no draft→PDF infra), so it
      // emits a Save-as-PDF nudge + window.print(); we just add a
      // chat ack so the response isn't a silent print dialog.
      //
      // The earlier `wantsSigningGuideFromChat` block above does NOT
      // fire here because its gate requires currentResult / currentFile
      // / filePreviewUrl / esignFields — none of which exist for a
      // pure HTML draft.
      if (
        modeRef.current === 'drafting'
        && draftContent
        && signingGuideChatPhrase.test(text.trim())
      ) {
        const ackMessage =
          'Converting your draft to PDF for signing — you\'ll get the recipient panel next.';
        setMessages((prev) => [...prev, {
          id: crypto.randomUUID(),
          role: 'corner' as const,
          content: ackMessage,
          timestamp: Date.now(),
        }]);
        const persistCid = currentConversationIdRef.current;
        if (persistCid) {
          addConversationMessage(persistCid, { role: 'corner', content: ackMessage })
            .catch((err) => console.error('[signing-from-draft] persist ack failed', err));
        }
        handleLaunchSigning();
        return;
      }

      const duplicateCopyCount =
        modeRef.current === 'drafting' && draftContent?.trim()
          ? parseDraftDuplicateCopyRequest(text)
          : null;

      if (duplicateCopyCount && auth.user && currentConversationId) {
        setProcessing({ progress: 30, label: `Saving ${duplicateCopyCount} cop${duplicateCopyCount === 1 ? 'y' : 'ies'}…` });
        cot.reset();
        cot.applySnapshot({
          status: 'working',
          steps: [
            { title: 'Preparing duplicates', status: 'done' },
            { title: 'Writing copies to project', status: 'active', description: `${duplicateCopyCount} files` },
          ],
        });
        try {
          let projectId = currentProjectId;
          if (!projectId) {
            const summary = await projectApi.ensureProject({
              conversationId: currentConversationId,
              title: draftTitle,
              folderId: currentConversationFolderId ?? null,
            }, auth.getToken);
            projectId = summary.id;
          }
          const detail = await projectApi.createDraftHtmlCopies(
            projectId,
            {
              draftHtml: draftContent,
              baseTitle: draftTitle || 'Document',
              count: duplicateCopyCount,
              conversationId: currentConversationId,
            },
            auth.getToken,
          );
          setActiveDocumentProject(detail);
          void refreshDocumentProjects();
          const msg = `Saved ${duplicateCopyCount} duplicate cop${duplicateCopyCount === 1 ? 'y' : 'ies'} for this project. Open the version list on the left — they appear under Copies.`;
          setMessages((prev) => [...prev, {
            id: crypto.randomUUID(),
            role: 'corner' as const,
            content: msg,
            timestamp: Date.now(),
          }]);
          const persistCid = currentConversationIdRef.current;
          if (persistCid) {
            addConversationMessage(persistCid, { role: 'corner', content: msg })
              .catch((err) => console.error('[draft-copies] persist AI msg failed', err));
          }
          cot.applySnapshot({
            status: 'done',
            steps: [
              { title: 'Preparing duplicates', status: 'done' },
              { title: 'Writing copies to project', status: 'done' },
            ],
          });
          toast({ title: 'Copies saved', description: 'Each copy is a separate file in the project.' });
        } catch (err: unknown) {
          const e = getActionError('save draft copies', err);
          setMessages((prev) => [...prev, {
            id: crypto.randomUUID(),
            role: 'corner' as const,
            content: e.description,
            timestamp: Date.now(),
          }]);
          const persistCid = currentConversationIdRef.current;
          if (persistCid) {
            addConversationMessage(persistCid, { role: 'corner', content: e.description })
              .catch((persistErr) => console.error('[draft-copies] persist error msg failed', persistErr));
          }
          cot.applySnapshot({
            status: 'error',
            steps: [
              { title: 'Preparing duplicates', status: 'done' },
              { title: 'Writing copies to project', status: 'error', errorMessage: e.description },
            ],
          });
          toast({ variant: 'destructive', title: e.title, description: e.description });
        } finally {
          setProcessing(null);
        }
        return;
      }

      const htmlVersionsForBulk = (activeDocumentProject?.versions ?? []).filter((v) => /html/i.test(v.mimeType));
      const wantsBulkProjectDraft =
        parseBulkApplyAllDraftsIntent(text)
        && modeRef.current === 'drafting'
        && draftContent?.trim()
        && htmlVersionsForBulk.length >= 2
        && auth.user
        && currentConversationId;

      if (wantsBulkProjectDraft) {
        const priorProject = activeDocumentProject;
        const activeFid = sessionPrimaryDocument?.fileId ?? currentResult?.fileId ?? null;
        const priorVersionMeta = activeFid && priorProject
          ? priorProject.versions.find((v) => v.fileId === activeFid)
          : null;

        setProcessing({ progress: 15, label: `Updating ${htmlVersionsForBulk.length} documents…` });
        cot.reset();
        cot.applySnapshot({
          status: 'working',
          steps: [
            { title: 'Analyzing your request', status: 'done' },
            {
              title: 'Revising all project documents',
              status: 'active',
              description: `${htmlVersionsForBulk.length} HTML files`,
            },
          ],
        });
        try {
          const conversationHistory = messages
            .filter((m) => m.role === 'user' || m.role === 'corner')
            .map((m) => ({ role: (m.role === 'user' ? 'user' : 'assistant') as const, content: m.content }))
            .slice(-20);
          let projectId = currentProjectId;
          if (!projectId) {
            const summary = await projectApi.ensureProject({
              conversationId: currentConversationId,
              title: draftTitle,
              folderId: currentConversationFolderId ?? null,
            }, auth.getToken);
            projectId = summary.id;
          }
          const detail = await projectApi.bulkReviseProjectHtml(
            projectId,
            { prompt: text, conversationHistory, conversationId: currentConversationId },
            auth.getToken,
          );
          setActiveDocumentProject(detail);
          void refreshDocumentProjects();

          const match = priorVersionMeta
            ? detail.versions.find((v) => v.id === priorVersionMeta.id)
            : detail.versions.find((v) => /html/i.test(v.mimeType));

          if (match) {
            const downloadUrl = `/api/file/${match.fileId}`;
            const token = auth.getToken();
            if (priorVersionMeta && sessionPrimaryDocument?.fileId === priorVersionMeta.fileId) {
              setSessionPrimaryDocument({
                ...sessionPrimaryDocument,
                fileId: match.fileId,
                fileName: match.fileName,
                mimeType: match.mimeType,
                downloadUrl,
              });
            }
            if (priorVersionMeta && currentResult?.fileId === priorVersionMeta.fileId) {
              setCurrentResult({
                ...currentResult,
                fileId: match.fileId,
                fileName: match.fileName,
                mimeType: match.mimeType,
                downloadUrl,
              });
            }
            if (/html/i.test(match.mimeType) && token) {
              const r = await fetch(appendTokenToUrl(`/api/file/${match.fileId}`, () => token));
              if (r.ok) {
                setDraftContent(await r.text());
              }
            }
            if (match.fileName) {
              setDraftTitle(match.fileName.replace(/\.(html?|pdf)$/i, '') || draftTitle);
            }
          }

          const msg = `Updated all ${htmlVersionsForBulk.length} HTML documents in this project to match your request.`;
          setMessages((prev) => [...prev, {
            id: crypto.randomUUID(),
            role: 'corner' as const,
            content: msg,
            timestamp: Date.now(),
          }]);
          const persistCid = currentConversationIdRef.current;
          if (persistCid) {
            addConversationMessage(persistCid, { role: 'corner', content: msg })
              .catch((err) => console.error('[bulk-revise] persist AI msg failed', err));
          }
          cot.applySnapshot({
            status: 'done',
            steps: [
              { title: 'Analyzing your request', status: 'done' },
              { title: 'Revising all project documents', status: 'done' },
            ],
          });
          toast({ title: 'All documents updated', description: msg });
        } catch (err: unknown) {
          const e = getActionError('update all project documents', err);
          setMessages((prev) => [...prev, {
            id: crypto.randomUUID(),
            role: 'corner' as const,
            content: e.description,
            timestamp: Date.now(),
          }]);
          const persistCid = currentConversationIdRef.current;
          if (persistCid) {
            addConversationMessage(persistCid, { role: 'corner', content: e.description })
              .catch((persistErr) => console.error('[bulk-revise] persist error msg failed', persistErr));
          }
          cot.applySnapshot({
            status: 'error',
            steps: [
              { title: 'Analyzing your request', status: 'done' },
              { title: 'Revising all project documents', status: 'error', errorMessage: e.description },
            ],
          });
          toast({ variant: 'destructive', title: e.title, description: e.description });
        } finally {
          setProcessing(null);
        }
        return;
      }

      const activeDraftDocIntent =
        modeRef.current === 'drafting' && draftContent?.trim()
          ? classifyDocIntent(text)
          : null;
      if (activeDraftDocIntent) {
        console.log('[DocIntent] classified', {
          conversationId: currentConversationIdRef.current,
          documentId: currentResult?.documentId ?? currentResult?.fileId ?? sessionPrimaryDocument?.fileId ?? null,
          messagePreview: text.slice(0, 80),
          intent: activeDraftDocIntent,
        });
      }

      if (
        activeDraftDocIntent
        && activeDraftDocIntent !== 'doc_edit'
        && activeDraftDocIntent !== 'doc_continue'
      ) {
        console.log('[DocIntent] routed', {
          intent: activeDraftDocIntent,
          action: docIntentAction(activeDraftDocIntent),
        });
      }

      // Draft mode: only true edits enter the structured edit-review planner.
      if (
        modeRef.current === 'drafting'
        && draftContent
        && (activeDraftDocIntent === 'doc_edit' || activeDraftDocIntent === 'doc_continue')
      ) {
        const reviseCid = currentConversationIdRef.current;
        const reviseRun = beginWorkspaceRun('draft-revise', reviseCid);
        const isReviseUiTarget = () =>
          isWorkspaceRunCurrent(reviseRun)
          && (
            reviseCid == null
            || String(currentConversationIdRef.current) === String(reviseCid)
          );
        // Draft revisions should not round-trip a saved signature image
        // back through the LLM. If an older draft already contains one,
        // preserve the signing area as a visible blank line and let the
        // signing workflow place the actual signature later.
        const cleanDraftHtml = draftContent.replace(
          /<img\s[^>]*alt="Signature"[^>]*>/gi,
          '<span>____________________________</span>',
        );
        const baseDocumentId =
          currentResult?.documentId ??
          (isConversationMongoDocumentFileId(currentResult?.fileId) ? currentResult.fileId.slice(4) : null) ??
          (isConversationMongoDocumentFileId(sessionPrimaryDocument?.fileId) ? sessionPrimaryDocument?.fileId?.slice(4) ?? null : null);
        const revisionConversationHistory = messages
          .filter((m) => m.role === 'user' || m.role === 'corner')
          .map((m) => ({ role: (m.role === 'user' ? 'user' : 'assistant') as const, content: m.content }))
          .slice(-20);
        const applyDirectRevisionFallback = async (reason: string) => {
          const token = auth.getToken();
          if (!reviseCid) {
            throw new Error('This draft is still connecting to the conversation. Wait a moment, then ask for the edit again.');
          }
          const revisionPrompt = reason === 'continue'
            ? buildContinueDraftInstruction(text)
            : text;
          const revisionRes = await axios.post<{
            draftHtml?: string;
            draftTitle?: string;
            documentId?: string;
            documentVersion?: number;
          }>(
            '/api/analyze/draft',
            {
              prompt: revisionPrompt,
              currentDraftHtml: cleanDraftHtml,
              conversationHistory: revisionConversationHistory,
              conversationId: reviseCid,
              ...(baseDocumentId ? { documentId: baseDocumentId } : {}),
              ...(currentResult?.documentVersion != null ? { previousVersionId: currentResult.documentVersion } : {}),
            },
            { headers: token ? { Authorization: `Bearer ${token}` } : {} },
          );
          if (!isReviseUiTarget()) return;
          const revisedHtml = revisionRes.data.draftHtml ?? '';
          if (!revisedHtml.trim()) throw new Error('The direct revision fallback returned an empty document.');
          const title = revisionRes.data.draftTitle?.trim() || draftTitle || currentResult?.draftTitle || 'Document';
          const documentId = revisionRes.data.documentId ?? baseDocumentId;
          if (!documentId) {
            throw new Error('Corner revised the draft but could not persist the document.');
          }
          const documentVersion = revisionRes.data.documentVersion ?? currentResult?.documentVersion;
          const result: ToolResult = {
            fileId: `doc-${documentId}`,
            downloadUrl: `/api/conversations/${reviseCid}/documents/${documentId}`,
            fileName: `${title.replace(/\.(docx|doc|html?)$/i, '') || 'Document'}.html`,
            mimeType: 'text/html',
            sizeBytes: revisedHtml.length,
            kind: 'document',
            documentId,
            ...(documentVersion != null ? { documentVersion } : {}),
            draftHtml: revisedHtml,
            draftTitle: title,
          };
          const now = new Date().toISOString();
          if (documentVersion != null) {
            const revisedDocument: ConversationDocument = {
              id: documentId,
              title,
              html: revisedHtml,
              version: documentVersion,
              createdAt: now,
              updatedAt: now,
              originPrompt: reason === 'continue' ? 'Continued document' : 'Revised document',
            };
            setConversationDocuments((prev) => {
              const next = prev.some((doc) => doc.id === revisedDocument.id)
                ? prev.map((doc) => doc.id === revisedDocument.id ? revisedDocument : doc)
                : [...prev, revisedDocument];
              return next.sort((a, b) => {
                const versionDelta = Number(a.version) - Number(b.version);
                if (Number.isFinite(versionDelta) && versionDelta !== 0) return versionDelta;
                return String(a.createdAt ?? '').localeCompare(String(b.createdAt ?? ''));
              });
            });
          }
          setDraftContent(revisedHtml);
          setDraftTitle(title);
          setCurrentResult(result);
          setSessionPrimaryDocument({
            fileId: result.fileId,
            fileName: result.fileName,
            mimeType: result.mimeType,
            sizeBytes: result.sizeBytes,
            downloadUrl: result.downloadUrl,
            source: 'generated',
          });
          const logicalDocumentId = resolveLogicalDocumentId({
            activeProjectId: activeDocumentProject?.id ?? null,
            currentProjectId,
            currentConversationId: reviseCid,
          }) ?? `conversation:${reviseCid}`;
          setCurrentLogicalDocumentId(logicalDocumentId);
          setCurrentVersionId(documentVersion != null ? `workspace:file:doc-${documentId}:v${documentVersion}` : null);
          if (documentVersion != null) {
            const versionSummary: import('./types').DocumentVersionSummary = {
              id: `workspace:file:doc-${documentId}:v${documentVersion}`,
              projectId: logicalDocumentId,
              versionNumber: documentVersion,
              fileId: `doc-${documentId}`,
              fileName: result.fileName,
              mimeType: result.mimeType,
              source: reason === 'continue' ? 'generated' : 'ai_edit',
              lineageKind: 'version',
              createdBy: auth.user?.id ?? 'local',
              createdFromVersionId: activeDocumentProject?.currentVersionId ?? null,
              changeSummary: reason === 'continue' ? 'Continued document.' : 'Revised document.',
              conversationId: reviseCid,
              createdAt: now,
            };
            const revisedDocumentsForVersions = [
              ...conversationDocuments.filter((doc) => doc.id !== documentId),
              {
                id: documentId,
                title,
                html: revisedHtml,
                version: documentVersion,
                createdAt: now,
                updatedAt: now,
                originPrompt: reason === 'continue' ? 'Continued document' : 'Revised document',
              },
            ].sort((a, b) => Number(a.version) - Number(b.version));
            const fallbackVersions = revisedDocumentsForVersions.map((doc): import('./types').DocumentVersionSummary => ({
              id: `workspace:file:doc-${doc.id}:v${doc.version}`,
              projectId: logicalDocumentId,
              versionNumber: doc.version,
              fileId: `doc-${doc.id}`,
              fileName: `${(doc.title || title || 'Document').replace(/\.(docx|doc|html?)$/i, '') || 'Document'}.html`,
              mimeType: 'text/html',
              source: doc.id === documentId ? versionSummary.source : 'generated',
              lineageKind: 'version',
              createdBy: auth.user?.id ?? 'local',
              createdFromVersionId: null,
              changeSummary: doc.id === documentId ? versionSummary.changeSummary : 'Draft version.',
              conversationId: reviseCid,
              createdAt: doc.createdAt ?? now,
            }));
            const existingVersions = activeDocumentProject?.versions?.length ? activeDocumentProject.versions : fallbackVersions;
            const versions = existingVersions.some((version) => version.fileId === versionSummary.fileId)
              ? existingVersions.map((version) => version.fileId === versionSummary.fileId ? versionSummary : version)
              : [...existingVersions, versionSummary];
            setActiveDocumentProject({
              id: logicalDocumentId,
              title: activeDocumentProject?.title ?? title,
              folderId: activeDocumentProject?.folderId ?? currentConversationFolderId ?? null,
              primaryConversationId: reviseCid,
              currentVersionId: versionSummary.id,
              currentVersion: versionSummary,
              versionCount: versions.length,
              createdFrom: activeDocumentProject?.createdFrom ?? 'conversation',
              lastActivityAt: now,
              createdAt: activeDocumentProject?.createdAt ?? now,
              updatedAt: now,
              versions,
              conversations: activeDocumentProject?.conversations ?? [],
            });
          }
          const snapshot = buildWorkspaceSessionSnapshot({
            draftContent: revisedHtml,
            draftTitle: title,
            mode: 'drafting',
            currentResult: result,
          });
          persistConversationSnapshot(reviseCid, snapshot).catch((persistErr) => {
            console.error('[DraftRevision] persist fallback snapshot failed', persistErr);
          });
          void warmDraftPdfMirrorIfFresh({ conversationId: reviseCid, html: revisedHtml, title });
          cot.applySnapshot({
            status: 'done',
            steps: [
              { title: 'Analyzing your request', status: 'done' },
              {
                title: reason === 'continue' ? 'Continuing document' : 'Revising document',
                status: 'done',
                description: reason === 'continue'
                  ? 'Saved a continued draft directly.'
                  : 'Saved a revised version directly.',
              },
            ],
          });
          const fallbackMsg = reason === 'continue'
            ? 'I continued the draft and saved a new version.'
            : reason === 'no-edits'
            ? 'I could not turn that into exact reviewable edits, so I saved a revised version directly.'
            : 'The edit review planner had trouble, so I saved a revised version directly.';
          setMessages((prev) => [...prev, {
            id: crypto.randomUUID(),
            role: 'corner' as const,
            content: fallbackMsg,
            timestamp: Date.now(),
          }]);
          addConversationMessage(reviseCid, { role: 'corner', content: fallbackMsg })
            .catch((err) => console.error('[draft-revise] persist fallback msg failed', err));
          console.log('[DraftRevision] applied', {
            conversationId: reviseCid,
            documentId,
            documentVersion,
            fallbackReason: reason,
          });
        };
        if (activeDraftDocIntent === 'doc_continue') {
          console.log('[DocIntent] routed', { intent: activeDraftDocIntent, action: 'continue' });
          setDraftPdfMirrorDigest(null);
          setProcessing({ progress: 30, label: 'Continuing document...' });
          cot.reset();
          cot.applySnapshot({
            status: 'working',
            steps: [
              { title: 'Analyzing your request', status: 'done' },
              { title: 'Continuing document', status: 'active', description: 'Extending the draft…' },
            ],
            activityPhases: buildDraftActivityPhases('revise'),
          });
          try {
            await applyDirectRevisionFallback('continue');
          } catch (err) {
            if (!isReviseUiTarget()) return;
            const e = getActionError('continue the document', err);
            setMessages((prev) => [...prev, {
              id: crypto.randomUUID(),
              role: 'corner' as const,
              content: e.description,
              timestamp: Date.now(),
            }]);
            const persistCid = reviseCid ?? currentConversationIdRef.current;
            if (persistCid) {
              addConversationMessage(persistCid, { role: 'corner', content: e.description })
                .catch((persistErr) => console.error('[draft-continue] persist error msg failed', persistErr));
            }
            cot.applySnapshot({
              status: 'error',
              steps: [
                { title: 'Analyzing your request', status: 'done' },
                { title: 'Continuing document', status: 'error', errorMessage: e.description },
              ],
            });
          } finally {
            if (isReviseUiTarget()) {
              setProcessing(null);
            }
            finishWorkspaceRun(reviseRun);
          }
          return;
        }

        console.log('[DocIntent] routed', { intent: activeDraftDocIntent, action: 'edit' });
        setDraftPdfMirrorDigest(null);
        setProcessing({ progress: 30, label: 'Revising document...' });
        // Synthetic CoT timeline — the draft endpoints don't emit
        // step events, so the timeline used to stay invisible for
        // these flows even though the AI was visibly working. We
        // reset and seed two synthetic steps so the chat panel
        // mirrors what the doc panel is doing.
        cot.reset();
        cot.applySnapshot({
          status: 'working',
          steps: [
            { title: 'Analyzing your request', status: 'done' },
            { title: 'Revising document', status: 'active', description: 'Generating revisions…' },
          ],
          activityPhases: buildDraftActivityPhases('revise'),
        });
        try {
          const token = auth.getToken();
          if (!reviseCid || !baseDocumentId) {
            await applyDirectRevisionFallback('draft-not-persisted');
            return;
          }
          const res = await axios.post<{ editBatch?: EditBatch; clarification?: string }>(
            '/api/document-edits/html/plan',
            {
              conversationId: reviseCid,
              documentId: baseDocumentId,
              ...(currentResult?.documentVersion != null ? { documentVersion: currentResult.documentVersion } : {}),
              currentDraftHtml: cleanDraftHtml,
              draftTitle: draftTitle || currentResult?.draftTitle || 'Document',
              instruction: text,
            },
            { headers: token ? { Authorization: `Bearer ${token}` } : {} },
          );
          if (!isReviseUiTarget()) return;
          const editBatch = res.data.editBatch;
          const clarification = typeof res.data.clarification === 'string' && res.data.clarification.trim()
            ? res.data.clarification.trim()
            : '';
          if (!editBatch || editBatch.edits.length === 0) {
            if (clarification) {
              const clarificationSteps: CoTStepData[] = [
                { title: 'Analyzing your request', status: 'done' },
                { title: 'Needs clarification', status: 'done', description: clarification },
              ];
              cot.applySnapshot({
                status: 'done',
                steps: clarificationSteps,
              });
              setMessages((prev) => [...prev, {
                id: crypto.randomUUID(),
                role: 'corner' as const,
                content: clarification,
                timestamp: Date.now(),
              }]);
              const persistCid = reviseCid ?? currentConversationIdRef.current;
              if (persistCid) {
                addConversationMessage(persistCid, { role: 'corner', content: clarification })
                  .catch((err) => console.error('[html-edit] persist clarification msg failed', err));
                const cotSnapshot = buildWorkspaceSessionSnapshot({ cotSteps: clarificationSteps });
                persistConversationSnapshot(persistCid, cotSnapshot).catch((persistErr) => {
                  console.error('[CoT] persist clarification snapshot failed', persistErr);
                });
              }
              return;
            }
            await applyDirectRevisionFallback('no-edits');
            return;
          }
          const plannedCotSteps: CoTStepData[] = [
            { title: 'Analyzing your request', status: 'done' },
            {
              title: 'Review proposed edits',
              status: 'done',
              description: `${editBatch.edits.length} proposed edit${editBatch.edits.length === 1 ? '' : 's'}`,
              editBatch,
            },
          ];
          cot.applySnapshot({
            status: 'done',
            steps: plannedCotSteps,
          });
          console.log('[CoT] edit-batch-created', {
            conversationId: reviseCid,
            batchId: editBatch.id,
            editCount: editBatch.edits.length,
          });
          const truncMsg = `I found ${editBatch.edits.length} proposed edit${editBatch.edits.length === 1 ? '' : 's'}. Review them below, then apply the accepted changes to create a new version.`;
          setMessages((prev) => [...prev, {
            id: crypto.randomUUID(),
            role: 'corner' as const,
            content: truncMsg,
            timestamp: Date.now(),
          }]);
          // Persist the AI reply too — without this the response is
          // local-only and disappears when the conversation reloads
          // from history (only the user's prompt was being saved).
          const persistCid = reviseCid ?? currentConversationIdRef.current;
          if (persistCid) {
            addConversationMessage(persistCid, { role: 'corner', content: truncMsg })
              .catch((err) => console.error('[html-edit] persist AI msg failed', err));
            const cotSnapshot = buildWorkspaceSessionSnapshot({ cotSteps: plannedCotSteps });
            persistConversationSnapshot(persistCid, cotSnapshot).catch((persistErr) => {
              console.error('[CoT] persist edit batch snapshot failed', persistErr);
            });
          }
        } catch (err) {
          if (!isReviseUiTarget()) return;
          try {
            await applyDirectRevisionFallback('planner-failed');
            return;
          } catch (fallbackErr) {
            console.warn('[html-edit] direct revision fallback failed', fallbackErr);
          }
          const e = getActionError('update the document', err);
          setMessages((prev) => [...prev, {
            id: crypto.randomUUID(),
            role: 'corner' as const,
            content: e.description,
            timestamp: Date.now(),
          }]);
          const persistCid = reviseCid ?? currentConversationIdRef.current;
          if (persistCid) {
            addConversationMessage(persistCid, { role: 'corner', content: e.description })
              .catch((persistErr) => console.error('[draft-revise] persist error msg failed', persistErr));
          }
          cot.applySnapshot({
            status: 'error',
            steps: [
              { title: 'Analyzing your request', status: 'done' },
              { title: 'Revising document', status: 'error', errorMessage: e.description },
            ],
          });
        } finally {
          if (isReviseUiTarget()) {
            setProcessing(null);
          }
          finishWorkspaceRun(reviseRun);
        }
        return;
      }

      if (willDraftStream) {
        const knownConversationId = currentConversationIdRef.current;
        let streamCid: string | null = knownConversationId ?? null;
        if (streamCid) {
          // Existing workspaces do not need to block draft generation on
          // message persistence. Let the chat write finish in parallel while
          // the backend starts the LLM stream for the same conversation id.
          persistUserTurnPromise
            .then(() => {
              if (draftLatencyRun) {
                draftLatencyLog('message-persist-finished', {
                  runId: draftLatencyRun.id,
                  msSinceSubmit: Math.round(performance.now() - draftLatencyRun.submitAt),
                });
              }
            })
            .catch(() => {});
          if (draftLatencyRun) {
            draftLatencyLog('message-persist-parallelized', {
              runId: draftLatencyRun.id,
              conversationId: streamCid,
            });
          }
        } else {
          const beforeConversationReady = performance.now();
          streamCid = await persistUserTurnPromise;
          if (draftLatencyRun) {
            draftLatencyLog('conversation-ready', {
              runId: draftLatencyRun.id,
              conversationId: streamCid,
              ms: Math.round(performance.now() - beforeConversationReady),
              msSinceSubmit: Math.round(performance.now() - draftLatencyRun.submitAt),
            });
          }
        }
        const draftRun = beginWorkspaceRun('draft-stream', streamCid);
        let draftStreamSawDone = false;
        let draftStreamErrored = false;
        let draftStreamAborted = false;
        /** Conversation owning this SSE run (snapshot at send time). */
        const isDraftUiTarget = () =>
          isWorkspaceRunCurrent(draftRun)
          && (
            streamCid == null
              ? routeConversationIdRef.current == null && currentConversationIdRef.current == null
              : String(currentConversationIdRef.current) === String(streamCid)
          );
        const draftController = new AbortController();
        draftStreamAbortRef.current?.abort();
        draftStreamAbortRef.current = draftController;
        draftStatusRef.current = 'pending';
        draftStreamingConversationIdRef.current = streamCid ?? null;
        setDraftStreamingConversationId(streamCid ?? null);
        // UI transition + CoT seeded synchronously above (before PDF/multi awaits).
        // Stream the draft via SSE so content appears progressively. We retry
        // up to MAX_DRAFT_NETWORK_RETRIES on transient network failures
        // (Wi-Fi flap / VPN kick / ERR_NETWORK_CHANGED). Each retry starts a
        // fresh request — backend doesn't currently support resume tokens —
        // and replaces the partial draft as the new stream produces output.
        //
        // CRITICAL: both `fetch()` AND the SSE read loop must live inside the
        // same inner try/catch. Earlier versions only wrapped the read loop,
        // which meant a network drop *at fetch time* (which is exactly what
        // ERR_NETWORK_CHANGED does) skipped retry entirely.
        const MAX_DRAFT_NETWORK_RETRIES = 8;
        const RETRY_BACKOFFS_MS = [1000, 2000, 4000, 6000, 8000, 10000, 12000, 15000];
        const MIN_PARTIAL_DRAFT_CHARS_TO_KEEP = 120;
        let draftAttempt = 0;
        let firstDraftDeltaAt = 0;
        let activeRequestSentAt = 0;
        // accumulatedText must be reachable in the outer catch so the failure
        // message can mention partial progress; declared here, reset per try.
        let accumulatedText = '';
        let pendingDraftContent = '';
        let draftContentFlushTimer: ReturnType<typeof window.setTimeout> | null = null;
        let lastDraftContentFlushAt = 0;
        const DRAFT_CONTENT_FLUSH_MS = 50;
        let lastDraftProgress = 8;
        let lastDraftProgressUpdateAt = 0;
        const setDraftStreamProgress = (progress: number, label: string, options?: { force?: boolean }) => {
          if (!isDraftUiTarget()) return;
          const nextProgress = Math.max(lastDraftProgress, Math.min(90, Math.round(progress)));
          const now = performance.now();
          if (
            !options?.force &&
            nextProgress - lastDraftProgress < 2 &&
            now - lastDraftProgressUpdateAt < 250
          ) {
            return;
          }
          lastDraftProgress = nextProgress;
          lastDraftProgressUpdateAt = now;
          setProcessing({ progress: nextProgress, label });
        };
        const setDraftProgressFromStreamLength = (chars: number) => {
          const progress = 18 + (1 - Math.exp(-Math.max(chars, 0) / 6000)) * 72;
          const label =
            chars < 600
              ? 'Drafting structure...'
              : chars < 4000
                ? 'Filling in sections...'
                : 'Polishing draft...';
          setDraftStreamProgress(progress, label);
        };
        const flushDraftContent = () => {
          if (draftContentFlushTimer) {
            window.clearTimeout(draftContentFlushTimer);
            draftContentFlushTimer = null;
          }
          if (!pendingDraftContent) return;
          lastDraftContentFlushAt = Date.now();
          if (!isDraftUiTarget()) return;
          setDraftContent(pendingDraftContent);
        };
        const scheduleDraftContentFlush = (html: string) => {
          pendingDraftContent = html;
          const now = Date.now();
          if (lastDraftContentFlushAt === 0 || now - lastDraftContentFlushAt >= DRAFT_CONTENT_FLUSH_MS) {
            flushDraftContent();
            return;
          }
          if (!draftContentFlushTimer) {
            draftContentFlushTimer = window.setTimeout(flushDraftContent, DRAFT_CONTENT_FLUSH_MS - (now - lastDraftContentFlushAt));
          }
        };
        try {
          const token = auth.getToken();
          const fetchHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
          if (token) fetchHeaders['Authorization'] = `Bearer ${token}`;
          if (draftLatencyRun) fetchHeaders['X-Draft-Latency-Run-Id'] = draftLatencyRun.id;

          const persistDraftCompletionMessage = (
            conversationId: string,
            title: string,
            html: string,
            documentId?: string | null,
            documentVersion?: number | null,
            truncated?: boolean,
          ) => {
            const draftMsg = truncated
              ? `I've drafted "${title}", but it was truncated due to length. Ask me to "continue the document" to generate the rest. You can also edit it directly.`
              : `I've drafted "${title}". You can edit it directly in the document view, or tell me what to change.`;
            const isVisibleConversation =
              String(currentConversationIdRef.current ?? '') === String(conversationId) ||
              String(routeConversationIdRef.current ?? '') === String(conversationId);
            if (isVisibleConversation) {
              setMessages((prev) => {
                const alreadyRendered = prev.some(
                  (msg) =>
                    msg.role === 'corner' &&
                    msg.content === draftMsg &&
                    msg.toolName === 'generate_document',
                );
                if (alreadyRendered) return prev;
                return [...prev, {
                  id: crypto.randomUUID(),
                  role: 'corner' as const,
                  content: draftMsg,
                  timestamp: Date.now(),
                  toolName: 'generate_document' as const,
                }];
              });
            }
            addConversationMessage(conversationId, {
              role: 'corner',
              content: draftMsg,
              toolCall: {
                toolName: 'generate_document',
                params: {
                  draftHtml: html,
                  draftTitle: title,
                  ...(truncated ? { truncated: true } : {}),
                  ...(documentId ? { documentId } : {}),
                  ...(documentVersion != null ? { documentVersion } : {}),
                },
              },
            }).catch((err) => console.error('[draft-stream] persist AI msg failed', err));
          };

          const buildDocumentBackedDraftResult = (
            conversationId: string,
            title: string,
            html: string,
            documentId: string,
            documentVersion?: number | null,
          ): ToolResult => ({
              fileId:          `doc-${documentId}`,
              downloadUrl:     `/api/conversations/${conversationId}/documents/${documentId}`,
              fileName:        `${(title ?? 'Document').replace(/\.(docx|doc|html?)$/i, '') || 'Document'}.html`,
              mimeType:        'text/html',
              sizeBytes:       html.length,
              kind:            'document',
              documentId,
              ...(documentVersion != null ? { documentVersion } : {}),
              draftHtml:       html,
              draftTitle:      title,
          });

          const applyDocumentBackedDraft = (
            conversationId: string,
            title: string,
            html: string,
            documentId: string,
            documentVersion?: number | null,
          ): ToolResult => {
            const result = buildDocumentBackedDraftResult(
              conversationId,
              title,
              html,
              documentId,
              documentVersion,
            );
            if (isDraftUiTarget()) {
              setDraftContent(html);
              setDraftTitle(title);
              setCurrentResult(result);
            }
            return result;
          };

          const titleFromDraftHtml = (html: string, fallback = 'Recovered draft') => {
            const titleMatch = html.match(/<h1[^>]*>(.*?)<\/h1>/i);
            const title = titleMatch?.[1]?.replace(/<[^>]+>/g, '').trim();
            return title || fallback;
          };

          const buildRecoverableDraftResult = (
            conversationId: string,
            title: string,
            html: string,
          ): ToolResult => ({
            fileId: `draft-${conversationId}`,
            downloadUrl: `draft://${conversationId}`,
            fileName: `${(title || 'Document').replace(/\.(docx|doc|html?)$/i, '') || 'Document'}.docx`,
            mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            sizeBytes: html.length,
            draftHtml: html,
            draftTitle: title,
          });

          const applyRecoverableDraft = (
            reason: string,
            html: string,
            title = titleFromDraftHtml(html),
          ) => {
            const cid = streamCid ?? currentConversationIdRef.current;
            if (!html.trim()) return false;
            draftStreamSawDone = true;
            draftStreamErrored = false;
            draftStatusRef.current = 'complete';
            if (draftStreamAbortRef.current === draftController) {
              draftStreamAbortRef.current = null;
            }
            if (cid && String(draftStreamingConversationIdRef.current ?? '') === String(cid)) {
              draftStreamingConversationIdRef.current = null;
              setDraftStreamingConversationId((current) =>
                String(current ?? '') === String(cid) ? null : current,
              );
            }
            flushDraftContent();
            if (isDraftUiTarget()) {
              setDraftContent(html);
              setDraftTitle(title);
              const result = cid ? buildRecoverableDraftResult(cid, title, html) : null;
              if (result) setCurrentResult(result);
              const doneCotSteps: CoTStepData[] = [
                { title: 'Analyzing your request', status: 'done' },
                {
                  title: 'Drafting document',
                  status: 'done',
                  description: 'Recovered latest saved draft text',
                },
              ];
              cot.applySnapshot({
                status: 'done',
                steps: doneCotSteps,
              });
              if (cid && result) {
                const snapshot = buildWorkspaceSessionSnapshot({
                  draftContent: html,
                  draftTitle: title,
                  mode: 'drafting',
                  currentResult: result,
                  cotSteps: doneCotSteps,
                });
                persistConversationSnapshot(cid, snapshot).catch((persistErr) => {
                  console.error('[draft-stream] persist recoverable draft snapshot failed', persistErr);
                });
              }
            }
            console.warn('[draft-stream] kept recoverable draft after interrupted stream', {
              reason,
              conversationId: cid ?? null,
              chars: html.trim().length,
            });
            return true;
          };

          const recoverCompletedDraftFromSession = async (reason: string): Promise<boolean> => {
            const cid = streamCid ?? currentConversationIdRef.current;
            if (!cid) return false;
            const promptTrim = text.trim();
            const recoveryDelaysMs = [0, 900, 1400, 2200, 3200, 4500, 6500, 9000, 12000];
            let latestPendingHtml = '';
            for (const delayMs of recoveryDelaysMs) {
              try {
                if (isDraftUiTarget()) {
                  cot.applySnapshot({
                    status: 'working',
                    steps: [
                      { title: 'Analyzing your request', status: 'done' },
                      {
                        title: 'Drafting document',
                        status: 'working',
                        description: 'Finalizing saved draft…',
                      },
                    ],
                  });
                }
                await waitForOnlineOrShortDelay(delayMs, draftController.signal);
                const loaded = await getConversationSession(cid, { nocache: true });
                const docs = loaded.documents ?? [];
                const latestDoc = docs.length > 0 ? docs[docs.length - 1] : null;
                const matchedByPrompt = [...docs].reverse().find(
                  (d) =>
                    !!d.html?.trim()
                    && typeof d.originPrompt === 'string'
                    && d.originPrompt.trim() === promptTrim,
                );
                let resolved = matchedByPrompt ?? null;
                if (
                  !resolved
                  && loaded.draftStatus === 'complete'
                  && !loaded.pendingDraft
                  && latestDoc?.html?.trim()
                ) {
                  resolved = latestDoc;
                }
                if (!resolved?.html?.trim()) {
                  const pendingHtml = loaded.pendingDraft?.currentDraftHtml?.trim();
                  if (pendingHtml && pendingHtml !== latestPendingHtml) {
                    latestPendingHtml = pendingHtml;
                    if (isDraftUiTarget()) {
                      const pendingTitle = titleFromDraftHtml(pendingHtml, draftTitle || 'Recovered draft');
                      const pendingResult = buildRecoverableDraftResult(cid, pendingTitle, pendingHtml);
                      setMode('drafting');
                      setDraftContent(pendingHtml);
                      setDraftTitle(pendingTitle);
                      setCurrentResult(pendingResult);
                      setProcessing({ progress: Math.max(lastDraftProgress, 90), label: 'Finalizing saved draft...' });
                    }
                    console.warn('[draft-stream] showing pending draft while waiting for completion', {
                      reason,
                      conversationId: cid,
                      chars: pendingHtml.length,
                    });
                  }
                  console.warn('[draft-stream] completed document not visible yet after interrupted stream', {
                    reason,
                    docs: docs.length,
                    matchedByPrompt: !!matchedByPrompt,
                    draftStatus: loaded.draftStatus,
                    hasPendingDraft: !!loaded.pendingDraft,
                  });
                  continue;
                }
                draftStreamSawDone = true;
                draftStatusRef.current = 'complete';
                const title = resolved.title?.trim() || 'Untitled Document';
                const recoveredResult = applyDocumentBackedDraft(cid, title, resolved.html, resolved.id, resolved.version);
                if (isDraftUiTarget()) {
                  setMode('drafting');
                  setProcessing(null);
                  setActiveDocumentProject((project) =>
                    project?.primaryConversationId === cid ? project : null,
                  );
                  setCurrentLogicalDocumentId(resolveLogicalDocumentId({
                    activeProjectId: null,
                    currentProjectId: null,
                    currentConversationId: cid,
                  }) ?? `conversation:${cid}`);
                  setCurrentVersionId(null);
                  setSessionPrimaryDocument({
                    fileId: recoveredResult.fileId,
                    fileName: recoveredResult.fileName,
                    mimeType: recoveredResult.mimeType,
                    sizeBytes: recoveredResult.sizeBytes,
                    downloadUrl: recoveredResult.downloadUrl,
                    source: 'generated',
                  });
                }
                persistDraftCompletionMessage(cid, title, resolved.html, resolved.id, resolved.version);
                draftStreamingConversationIdRef.current = null;
                setDraftStreamingConversationId((current) =>
                  String(current ?? '') === String(cid) ? null : current,
                );
                const snapshotForRecovery = buildWorkspaceSessionSnapshot({
                  draftContent: resolved.html,
                  draftTitle: title,
                  mode: 'drafting',
                  currentResult: recoveredResult,
                });
                await persistConversationSnapshot(cid, snapshotForRecovery).catch((persistErr) => {
                  console.error('[draft-stream] persist recovered completion snapshot failed', persistErr);
                });
                patchRouteHydrationState(cid, {
                  attempted: true,
                  succeeded: true,
                  explicitTextOnly: false,
                  lastFailure: null,
                });
                try {
                  const recoveredSession = await getConversationSession(cid, { nocache: true });
                  const recoveredProjectId =
                    recoveredSession.conversation?.projectId ??
                    recoveredSession.sessionSnapshot?.projectId ??
                    null;
                  if (recoveredProjectId) {
                    const project = await projectApi.getProject(recoveredProjectId, auth.getToken);
                    const currentVersion =
                      project.currentVersion ??
                      project.versions.find((version) => version.id === project.currentVersionId) ??
                      project.versions.at(-1) ??
                      null;
                    setActiveDocumentProject(project);
                    setCurrentLogicalDocumentId(project.id);
                    setCurrentVersionId(currentVersion?.id ?? project.currentVersionId ?? null);
                    void refreshDocumentProjects();
                  }
                } catch (projectRecoverErr) {
                  console.warn('[draft-stream] recovered document project sync failed', projectRecoverErr);
                }
                if (isDraftUiTarget()) {
                  cot.applySnapshot({
                    status: 'done',
                    steps: [
                      { title: 'Analyzing your request', status: 'done' },
                      { title: 'Drafting document', status: 'done', description: title },
                    ],
                  });
                }
                console.warn('[draft-stream] recovered completed document after interrupted stream', {
                  reason,
                  conversationId: cid,
                  documentId: resolved.id,
                  version: resolved.version,
                });
                return true;
              } catch (recoverErr) {
                if (recoverErr instanceof DOMException && recoverErr.name === 'AbortError') {
                  return false;
                }
                console.warn('[draft-stream] completion recovery lookup failed', recoverErr);
              }
            }
            if (latestPendingHtml) {
              return applyRecoverableDraft(reason, latestPendingHtml, titleFromDraftHtml(latestPendingHtml));
            }
            return false;
          };

          const stopAutoRetryAndKeepPartialDraft = (reason: string) => {
            const html = pendingDraftContent.trim() || accumulatedText.trim();
            if (!html) return;
            applyRecoverableDraft(reason, html, titleFromDraftHtml(html));
          };

          // Outer retry loop. Each iteration is one fresh fetch+stream attempt.
          // Successful completion (`done` event) sets draftStreamSawDone and we
          // break. Transient network errors break out of the inner loop, sleep
          // briefly, then re-enter for another attempt.
          // eslint-disable-next-line no-constant-condition
          while (true) {
            let attemptNetworkErrored = false;
            accumulatedText = '';
            pendingDraftContent = '';
            if (draftContentFlushTimer) {
              window.clearTimeout(draftContentFlushTimer);
              draftContentFlushTimer = null;
            }
            lastDraftContentFlushAt = 0;
            let buffer = '';

            const handleDraftSseEvent = (eventName: string, payloadRaw: string) => {
              if (draftController.signal.aborted) {
                draftStreamAborted = true;
                return;
              }
              const data = JSON.parse(payloadRaw.trim()) as {
                text?: string;
                draftHtml?: string;
                draftTitle?: string;
                truncated?: boolean;
                message?: string;
                /** When the BE persists the draft to documents[], the new
                 * subdocument's id flows through here so the FE can stamp
                 * it into the snapshot's `currentResult.documentId` and
                 * skip the legacy synthetic `draft-${cid}` fileId path. */
                documentId?: string;
                documentVersion?: number;
              };
              if (eventName === 'delta' && data.text) {
                draftStatusRef.current = 'streaming';
                if (draftLatencyRun && firstDraftDeltaAt === 0) {
                  firstDraftDeltaAt = performance.now();
                  draftLatencyLog('first-visible-delta', {
                    runId: draftLatencyRun.id,
                    attempt: draftAttempt + 1,
                    msSinceSubmit: Math.round(firstDraftDeltaAt - draftLatencyRun.submitAt),
                    msSinceRequest: activeRequestSentAt
                      ? Math.round(firstDraftDeltaAt - activeRequestSentAt)
                      : null,
                    chars: data.text.length,
                  });
                }
                accumulatedText += data.text;
                setDraftProgressFromStreamLength(accumulatedText.length);
                // TipTap pagination walks direct children of .tiptap — one pre-wrap wrapper made
                // the whole stream a single block. Strip optional ```html fence so semantic tags
                // become top-level blocks while the draft streams.
                let html = accumulatedText.replace(/^\uFEFF/, '');
                if (/^```(?:html)?\s*/i.test(html)) html = html.replace(/^```(?:html)?\s*/i, '');
                html = html.replace(/\n?```\s*$/, '');
                const next = html.trim() ? html : '<p></p>';
                scheduleDraftContentFlush(next);
                return;
              }
              if (eventName === 'done') {
                draftStreamSawDone = true;
                draftStatusRef.current = 'complete';
                flushDraftContent();
                if (isDraftUiTarget()) {
                  lastDraftProgress = 100;
                  setProcessing({ progress: 100, label: 'Draft ready' });
                }
                if (draftLatencyRun) {
                  draftLatencyLog('done-event', {
                    runId: draftLatencyRun.id,
                    attempt: draftAttempt + 1,
                    msSinceSubmit: Math.round(performance.now() - draftLatencyRun.submitAt),
                    totalStreamChars: accumulatedText.length,
                    hasDocumentId: typeof data.documentId === 'string' && !!data.documentId,
                  });
                }
                const html = typeof data.draftHtml === 'string' ? data.draftHtml : '';
                const title = typeof data.draftTitle === 'string' ? data.draftTitle : 'Untitled Document';

                // Detect a successful-but-empty completion. Without this guard,
                // the user sees a blank document and no indication that anything
                // went wrong — the UI sits on `mode === 'drafting'` with nothing
                // to edit. Treat it as a soft error: keep streamed content if we
                // got any (the server's final html being empty is then ignored
                // in favour of accumulatedText), otherwise surface a clear retry
                // message and flip CoT into the error state.
                const accumulatedTrimmed = accumulatedText.trim();
                const htmlTrimmed = html.trim();
                const looksEmpty = (s: string) =>
                  !s
                  || s === '<p></p>'
                  || s === '<p><br></p>'
                  || s === '<p><br /></p>'
                  || s === '<p>&nbsp;</p>';
                if (accumulatedTrimmed.length === 0 && looksEmpty(htmlTrimmed)) {
                  draftStreamErrored = true;
                  draftStatusRef.current = 'failed';
                  const errorMsg =
                    "I couldn't generate any content for that request. Try rewording or adding more detail, then send again.";
                  if (isDraftUiTarget()) {
                    setMessages((prev) => [...prev, {
                      id: crypto.randomUUID(),
                      role: 'corner' as const,
                      content: errorMsg,
                      timestamp: Date.now(),
                    }]);
                  }
                  const persistEmptyCid = streamCid ?? currentConversationIdRef.current;
                  if (persistEmptyCid) {
                    addConversationMessage(persistEmptyCid, { role: 'corner', content: errorMsg })
                      .catch((err) => console.error('[draft-stream] persist empty-content msg failed', err));
                  }
                  if (isDraftUiTarget()) {
                    cot.applySnapshot({
                      status: 'error',
                      steps: [
                        { title: 'Analyzing your request', status: 'done' },
                        {
                          title: 'Drafting document',
                          status: 'error',
                          errorMessage: 'No content was generated.',
                        },
                      ],
                    });
                  } else {
                    sonnerToast.error('Draft failed', {
                      description: errorMsg,
                      duration: 12_000,
                    });
                  }
                  return;
                }

                const persistCid = streamCid ?? currentConversationIdRef.current;
                const persistedDocumentId = typeof data.documentId === 'string' && data.documentId
                  ? data.documentId
                  : null;
                const persistedDocumentVersion = typeof data.documentVersion === 'number'
                  ? data.documentVersion
                  : null;
                let documentBackedResultForDone: ToolResult | null = null;

                const finalHtml = html.length > 0 ? html : accumulatedText;

                if (persistedDocumentId && persistCid) {
                  documentBackedResultForDone = applyDocumentBackedDraft(
                    persistCid,
                    title,
                    finalHtml,
                    persistedDocumentId,
                    persistedDocumentVersion,
                  );
                } else if (isDraftUiTarget()) {
                  if (finalHtml.trim()) setDraftContent(finalHtml);
                  setDraftTitle(title);
                }

                if (persistCid) {
                  persistDraftCompletionMessage(
                    persistCid,
                    title,
                    finalHtml,
                    persistedDocumentId,
                    persistedDocumentVersion,
                    data.truncated,
                  );
                }
                if (isDraftUiTarget()) {
                  const doneCotSteps: CoTStepData[] = [
                    { title: 'Analyzing your request', status: 'done' },
                    { title: 'Drafting document', status: 'done', description: title },
                  ];
                  cot.applySnapshot({
                    status: 'done',
                    steps: doneCotSteps,
                  });
                  if (persistCid) {
                    const snapshotWithDoneTimeline = buildWorkspaceSessionSnapshot({
                      draftContent: finalHtml,
                      draftTitle: title,
                      mode: 'drafting',
                      currentResult: documentBackedResultForDone,
                      cotSteps: doneCotSteps,
                    });
                    persistConversationSnapshot(persistCid, snapshotWithDoneTimeline).catch((persistErr) => {
                      console.error('[draft-stream] persist done timeline snapshot failed', persistErr);
                    });
                  }
                }

                // Avoid overwriting another workspace's panel/doc chrome while the user
                // browses elsewhere — BE already persisted the draft body; message +
                // documents hydrate completes the story when they return.
                if (persistCid && isDraftUiTarget()) {
                  const snapshotForDone = buildWorkspaceSessionSnapshot({
                    draftContent: finalHtml,
                    draftTitle: title,
                    mode: 'drafting',
                    currentResult: documentBackedResultForDone,
                  });
                  persistConversationSnapshot(persistCid, snapshotForDone).catch((persistErr) => {
                    console.error('[draft-stream] force-persist snapshot failed', persistErr);
                    toast({
                      title: 'Draft not saved',
                      description: 'We couldn\'t save this draft to the server. It\'s here for now, but may not survive a reload. Try again or check your connection.',
                      variant: 'destructive',
                    });
                  });
                }

                if (persistCid) {
                  const viewing =
                    currentConversationIdRef.current != null
                    && String(currentConversationIdRef.current) === String(persistCid);
                  const tabVisible = typeof document !== 'undefined' && document.visibilityState === 'visible';
                  if (!viewing || !tabVisible) {
                    sonnerToast.success('Draft ready', {
                      description: viewing && !tabVisible
                        ? `"${title}" is ready.`
                        : `"${title}" finished while you were elsewhere.`,
                      action: {
                        label: 'Open',
                        onClick: () => navigate(conversationWorkspacePath(persistCid)),
                      },
                      duration: 14_000,
                    });
                    notifyDraftReadyDesktop(title, persistCid);
                  }
                }
                if (persistCid && finalHtml.trim()) {
                  void warmDraftPdfMirrorIfFresh({
                    conversationId: persistCid,
                    html: finalHtml,
                    title,
                  });
                }
                return;
            }
            if (eventName === 'error') {
              throw new Error(data.message ?? 'Draft generation failed');
            }
          };

          const processSseLineBlock = (lineBlock: string[]) => {
            let eventName = '';
            for (const line of lineBlock) {
              if (line.startsWith('event: ')) {
                eventName = line.slice(7).trim();
              } else if (line.startsWith('data: ') && eventName) {
                try {
                  handleDraftSseEvent(eventName, line.slice(6));
                } catch (parseErr) {
                  if (parseErr instanceof SyntaxError) continue;
                  throw parseErr;
                }
                eventName = '';
              }
            }
          };

          try {
            // fetch() is INSIDE this try so connect-time failures
            // (ERR_NETWORK_CHANGED at handshake time, DNS fail, etc.) get
            // the same retry treatment as mid-stream drops. Earlier this
            // sat outside and a fetch-time failure skipped retry entirely.
            activeRequestSentAt = performance.now();
            if (draftLatencyRun) {
              draftLatencyLog('request-sent', {
                runId: draftLatencyRun.id,
                attempt: draftAttempt + 1,
                conversationId: streamCid ?? null,
                msSinceSubmit: Math.round(activeRequestSentAt - draftLatencyRun.submitAt),
              });
            }
            setDraftStreamProgress(12, 'Connecting to drafting model...', { force: true });
            const response = await fetch('/api/analyze/draft-stream', {
              method: 'POST',
              headers: fetchHeaders,
              signal: draftController.signal,
              // conversationId opts the request into server-side
              // persistence: the BE marks `draftStatus='pending'`,
              // streams, then writes to `documents[]` on done. The
              // SSE `done` event will include `documentId` so the FE
              // can reference the canonical body. When omitted (no
              // conversation yet) the server falls back to legacy
              // stream-only behaviour.
              body: JSON.stringify({
                prompt: text,
                ...(streamCid ? { conversationId: streamCid } : {}),
              }),
            });
            if (!response.ok || !response.body) {
              throw new Error(`Draft request failed: ${response.status}`);
            }
            if (draftLatencyRun) {
              draftLatencyLog('headers-received', {
                runId: draftLatencyRun.id,
                attempt: draftAttempt + 1,
                status: response.status,
                msSinceSubmit: Math.round(performance.now() - draftLatencyRun.submitAt),
                msSinceRequest: Math.round(performance.now() - activeRequestSentAt),
              });
            }
            setDraftStreamProgress(14, 'Waiting for first words...', { force: true });
            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            // eslint-disable-next-line no-constant-condition
            while (true) {
              const { done, value } = await reader.read();
              if (draftController.signal.aborted) {
                draftStreamAborted = true;
                break;
              }
              if (value) {
                buffer += decoder.decode(value, { stream: true });
              }
              const lines = buffer.split('\n');
              buffer = lines.pop() ?? '';
              processSseLineBlock(lines);
              if (done) {
                buffer += decoder.decode();
                if (buffer.trim()) {
                  processSseLineBlock(buffer.split('\n'));
                }
                break;
              }
            }
          } catch (readErr) {
            // A network failure — at connect time OR during the stream —
            // surfaces here. Retry the whole request: backend has no
            // resume token, so the partial draft is replaced when the
            // next stream produces output.
            const hasMeaningfulPartial =
              accumulatedText.trim().length >= MIN_PARTIAL_DRAFT_CHARS_TO_KEEP;
            if (
              !draftController.signal.aborted
              && !draftStreamSawDone
              && draftAttempt < MAX_DRAFT_NETWORK_RETRIES
              && isTransientNetworkError(readErr)
            ) {
              flushDraftContent();
              if (hasMeaningfulPartial && await recoverCompletedDraftFromSession('transient-network-error')) {
                attemptNetworkErrored = false;
              } else if (hasMeaningfulPartial) {
                stopAutoRetryAndKeepPartialDraft('transient-network-error');
              } else {
                attemptNetworkErrored = true;
              }
            } else if (
              !draftController.signal.aborted
              && !draftStreamSawDone
              && isTransientNetworkError(readErr)
            ) {
              // Retries exhausted on a transient network error. Replace the
              // generic "couldn't generate" message with a clear, actionable
              // one so the user knows it's a connection issue, not a model
              // failure, and can just resend.
              throw new Error(
                "Connection lost while drafting. Your network dropped and we couldn't reconnect. Send your prompt again to retry.",
              );
            } else {
              throw readErr;
            }
          }

          if (draftStreamSawDone || draftStreamAborted) break;
          if (draftStreamErrored) break;

          if (attemptNetworkErrored) {
            draftAttempt += 1;
            const backoffMs = RETRY_BACKOFFS_MS[draftAttempt - 1] ?? 6000;
            if (isDraftUiTarget()) {
              cot.applySnapshot({
                status: 'working',
                steps: [
                  { title: 'Analyzing your request', status: 'done' },
                  {
                    title: 'Drafting document',
                    status: 'working',
                    description: `Connection dropped — reconnecting (attempt ${draftAttempt} of ${MAX_DRAFT_NETWORK_RETRIES})…`,
                  },
                ],
              });
            }
            // Wait for the network to recover, then retry. Bounded by the
            // retry budget so we never sit forever; the inner `online` event
            // short-circuits the wait when the connection comes back early
            // (typical Wi-Fi flap returns in ~1-2s).
            await waitForOnlineOrShortDelay(backoffMs, draftController.signal);
            continue;
          }

          if (!draftStreamSawDone && !draftStreamAborted) {
            const hasMeaningfulPartial =
              accumulatedText.trim().length >= MIN_PARTIAL_DRAFT_CHARS_TO_KEEP;
            if (hasMeaningfulPartial && await recoverCompletedDraftFromSession('stream-ended-before-done')) {
              break;
            }
            if (hasMeaningfulPartial) {
              stopAutoRetryAndKeepPartialDraft('stream-ended-before-done');
              break;
            }
            if (draftAttempt < MAX_DRAFT_NETWORK_RETRIES) {
              attemptNetworkErrored = true;
              draftAttempt += 1;
              const backoffMs = RETRY_BACKOFFS_MS[draftAttempt - 1] ?? 15000;
              if (isDraftUiTarget()) {
                cot.applySnapshot({
                  status: 'working',
                  steps: [
                    { title: 'Analyzing your request', status: 'done' },
                    {
                      title: 'Drafting document',
                      status: 'working',
                      description: `Stream paused before completion — reconnecting (attempt ${draftAttempt} of ${MAX_DRAFT_NETWORK_RETRIES})…`,
                    },
                  ],
                });
              }
              await waitForOnlineOrShortDelay(backoffMs, draftController.signal);
              continue;
            }
            throw new Error(
              "Connection lost while drafting. Your network dropped and we couldn't reconnect. Send your prompt again to retry.",
            );
          }
          break;
          } // close outer retry while
        } catch (err) {
          if (err instanceof Error && err.name === 'AbortError') {
            draftStreamAborted = true;
          } else {
            draftStreamErrored = true;
            draftStatusRef.current = 'failed';
            const e = getActionError('generate the document', err);
            if (isDraftUiTarget()) {
              setMessages((prev) => [...prev, {
                id: crypto.randomUUID(),
                role: 'corner' as const,
                content: e.description,
                timestamp: Date.now(),
              }]);
            } else {
              sonnerToast.error(e.title || 'Draft failed', {
                description: e.description,
                duration: 12_000,
              });
            }
            const persistErrCid = streamCid ?? currentConversationIdRef.current;
            if (persistErrCid) {
              addConversationMessage(persistErrCid, { role: 'corner', content: e.description })
                .catch((persistErr) => console.error('[draft-stream] persist error msg failed', persistErr));
            }
            if (isDraftUiTarget()) {
              cot.applySnapshot({
                status: 'error',
                steps: [
                  { title: 'Analyzing your request', status: 'done' },
                  { title: 'Drafting document', status: 'error', errorMessage: e.description },
                ],
              });
            }
          }
        } finally {
          flushDraftContent();
          if (draftLatencyRun) {
            draftLatencyLog('finished', {
              runId: draftLatencyRun.id,
              msSinceSubmit: Math.round(performance.now() - draftLatencyRun.submitAt),
              sawDone: draftStreamSawDone,
              aborted: draftStreamAborted,
              errored: draftStreamErrored,
              attempts: draftAttempt + 1,
            });
          }
          if (draftStreamAbortRef.current === draftController) {
            draftStreamAbortRef.current = null;
          }
          if (draftStreamAborted && !draftStreamSawDone && !draftStreamErrored) {
            draftStatusRef.current = 'idle';
          }
          if (!draftStreamAborted && !draftStreamSawDone && !draftStreamErrored && isDraftUiTarget()) {
            cot.applySnapshot({
              status: 'done',
              steps: [
                { title: 'Analyzing your request', status: 'done' },
                { title: 'Drafting document', status: 'done' },
              ],
            });
          }
          if (isDraftUiTarget()) {
            setNarrowChatForInitialDraftStream(false);
            setProcessing(null);
          }
          setDraftStreamingConversationId((current) =>
            String(current ?? '') === String(streamCid ?? '') ? null : current,
          );
          if (String(draftStreamingConversationIdRef.current ?? '') === String(streamCid ?? '')) {
            draftStreamingConversationIdRef.current = null;
          }
          finishWorkspaceRun(draftRun);
        }
        return;
      }

      // V2 eligibility: only when we have indexed documents (with fileIds) and no utility tool
      const v2FileIds = getV2EligibleFileIds();
      const isOpenDocumentEditPrompt =
        v2FileIds.length > 0
        && isOpenDocumentMutationPrompt(text);
      const useV2 = v2FileIds.length > 0 && !utilityChatTool && allFiles.length === 0 && !selectedSnippetForSend && !isOpenDocumentEditPrompt;
      if (useV2) {
        const v2DocLabelById = new Map<string, string>();
        if (sessionPrimaryDocument?.fileId) {
          v2DocLabelById.set(sessionPrimaryDocument.fileId, sessionPrimaryDocument.fileName || 'Document');
        }
        for (const d of sessionSourceDocuments) {
          if (d.fileId) v2DocLabelById.set(d.fileId, d.fileName || 'Document');
        }
        for (const d of projectContextDocuments) {
          if (d.fileId) v2DocLabelById.set(d.fileId, d.fileName || 'Project document');
        }
        const resultFileId = currentResult?.sourceFileId ?? currentResult?.fileId;
        if (resultFileId) {
          v2DocLabelById.set(resultFileId, currentResult?.fileName || currentFile?.name || 'Document');
        }
        const v2DocNames = v2FileIds.map((id) => v2DocLabelById.get(id)).filter((n): n is string => Boolean(n));
        setQueryRunBootContext(formatQueryRunBootContext(v2DocNames, text));
        try {
          const res = await apiClient.post<{ runId: string }>('/api/query-runs', {
            query: text,
            fileIds: v2FileIds,
            conversationId: currentConversationId || undefined,
          });
          setActiveQueryRunId(res.data.runId);
        } catch (err) {
          setQueryRunBootContext(undefined);
          setProcessing(null);
          const actionErr = getActionError('run query', err);
          toast({ title: actionErr.title, description: actionErr.description, variant: 'destructive' });
          setMode((currentFile || currentResult) ? 'result' : 'empty');
        }
      } else {
        lastOrchestratorUserPromptRef.current = text;
        const activeDraftHtml = activeDocumentSurface?.draftHtml?.trim()
          ? activeDocumentSurface.draftHtml
          : draftContent;
        if (selectedSnippetForSend) {
          const visibleFileId =
            activeDocumentSurface?.ref.fileId ??
            activeVisibleDocFileId ??
            activeDocFileId ??
            currentResult?.fileId ??
            sessionPrimaryDocument?.fileId ??
            null;
          console.log('[SelectionAction] trigger', {
            visibleFileId,
            visibleFileName:
              activeDocumentSurface?.ref.fileName ??
              currentResult?.fileName ??
              sessionPrimaryDocument?.fileName ??
              null,
            isDraft: Boolean(
              visibleFileId?.startsWith('draft-') ||
              activeDocumentSurface?.currentDocumentId?.startsWith('draft:'),
            ),
            conversationId: currentConversationId,
          });
        }
        executeWithOrchestrator(text, allFiles, {
          esignContext: lastEsignContextRef.current,
          selectedSnippet: selectedSnippetForSend ?? undefined,
          workingDocumentPlainText:
            activeDraftHtml?.trim()
              ? draftHtmlToWorkingPlainText(activeDraftHtml)
              : undefined,
        });
        // Clear the snippet after sending
        setPendingSelectedSnippet(null);
      }
    },
    [addConversationMessage, auth.user, beginWorkspaceRun, buildDraftActivityPhases, createConversation, currentFiles, currentFile, filePreviewUrl, esignFields, executeWithOrchestrator, filesToSessionDocuments, finishWorkspaceRun, flushTitlesThenApply, isWorkspaceRunCurrent, navigate, cot, draftContent, draftTitle, auth.getToken, utilityChatTool, currentConversationId, currentConversationFolderId, currentProjectId, currentResult, sessionPrimaryDocument, sessionSourceDocuments, projectContextDocuments, pendingSelectedSnippet, toast, refreshDocumentProjects, activeDocumentProject, messages, getConversationSession, handleLaunchSigning, persistConversationSnapshot, warmDraftPdfMirrorIfFresh, activeDocumentSurface, activeVisibleDocFileId, activeDocFileId]
  );

  const handleChatPrefillConsumed = useCallback(() => {
    setChatPrefill('');
  }, []);

  // When user stops processing, leave "processing" mode so UI returns to idle
  useEffect(() => {
    if (processing === null && mode === 'processing' && !activeQueryRunId) {
      // Keep the workspace in doc view whenever a restored/generated document is visible.
      setMode(modeAfterInterruptedWork());
    }
  }, [processing, mode, activeQueryRunId, modeAfterInterruptedWork]);

  const { applySnapshot: applyCotSnapshot } = cot;
  const queryRunRefresh = queryRun.refresh;

  useEffect(() => {
    if (!activeQueryRunId || !queryRun.snapshot) return;
    const status = queryRun.snapshot.status;
    if (status === 'completed' || status === 'failed' || status === 'partial' || status === 'cancelled') return;

    const updatedAt = Date.parse(queryRun.snapshot.updatedAt ?? queryRun.snapshot.createdAt);
    if (!Number.isFinite(updatedAt)) return;

    const timer = window.setTimeout(() => {
      const latest = queryRun.snapshot;
      if (!activeQueryRunId || !latest) return;
      const latestUpdatedAt = Date.parse(latest.updatedAt ?? latest.createdAt);
      const staleForMs = Number.isFinite(latestUpdatedAt) ? Date.now() - latestUpdatedAt : 0;
      const isStillRunning = !['completed', 'failed', 'partial', 'cancelled'].includes(latest.status);
      if (!isStillRunning || staleForMs < 90_000) return;

      queryRunRefresh().catch(() => {});
      setActiveQueryRunId(null);
      queryRunTerminalRef.current = null;
      setProcessing(null);
      setMode(modeAfterInterruptedWork());
      applyCotSnapshot({
        status: 'error',
        steps: latest.tasks.map((task) => ({
          title: task.label,
          status: task.status === 'completed' ? 'done' : task.status === 'failed' ? 'error' : 'skipped',
          description: task.result ?? task.visibleSteps?.at(-1),
          errorMessage: task.error ?? undefined,
        })),
        answer: 'Corner finished the document work, but the final response stream stalled. The workspace is unlocked so you can keep going.',
      });
      toast({
        variant: 'destructive',
        title: 'Final response stalled',
        description: 'I unlocked the chat so you can keep working. Please retry if you still need the final summary.',
      });
    }, 95_000);

    return () => window.clearTimeout(timer);
  }, [activeQueryRunId, queryRun.snapshot, queryRunRefresh, modeAfterInterruptedWork, applyCotSnapshot, toast]);

  // V2 query run completion → mode transition + conversation refresh
  const queryRunStatus = queryRun.snapshot?.status;
  useEffect(() => {
    if (!activeQueryRunId || !queryRunStatus) return;
    const isTerminal = queryRunStatus === 'completed' || queryRunStatus === 'failed' || queryRunStatus === 'partial' || queryRunStatus === 'cancelled';
    if (!isTerminal) return;
    // Guard: only fire once per run (prevent double-fire on re-renders)
    if (queryRunTerminalRef.current === activeQueryRunId) {
      return;
    }
    queryRunTerminalRef.current = activeQueryRunId;

    // Transition mode
    setProcessing(null);
    setMode(modeAfterInterruptedWork());

    // Reload conversation messages from backend (single source of truth).
    // The backend's finalizeRun already wrote the answer via appendConversationAssistantMessage.
    const convId = currentConversationId;
    if (convId) {
      getConversationMessages(convId)
        .then(({ messages }) => {
          setMessages(messages);
        })
        .catch(() => {});
    }

    // Clear active run ID so next send starts fresh
    setActiveQueryRunId(null);
  }, [queryRunStatus, activeQueryRunId, currentConversationId, getConversationMessages, modeAfterInterruptedWork]);

  // ── V2 → CoT timeline bridge ─────────────────────────────────────
  // The V1 orchestrate path streams plan_ready/step_start/step_complete/done
  // events that useIntent already wires into the CoT state. The V2
  // query-runs path uses a totally different shape (AgentRunSnapshot with
  // tasks). Without this bridge, the CoT timeline never appeared for the
  // V2 path even though the AgentProgressTimeline component was ready to
  // render — `cotState.steps.length === 0` kept the container hidden.
  //
  // Project the snapshot.tasks into CoTStepData[] on every snapshot
  // change. Cheap (just a map + status enum translation), and makes the
  // same timeline UI light up regardless of which backend path served
  // the chat turn.
  useEffect(() => {
    const snap = queryRun.snapshot;
    if (!activeQueryRunId || !snap) return;

    const prevSteps = cot.state.steps;
    const merged = buildMergedV2CotSteps(snap, prevSteps);
    if (!v2CotStepsDiffer(prevSteps, merged)) return;

    applyCotSnapshot({
      status:            cotStatusFromV2Snap(snap, merged) as CoTStatus,
      steps:             merged,
      partialCompletion: snap.status === 'partial',
      understanding:     formatQueryRunContextFromSnapshot(snap) ?? '',
    });
  }, [queryRun.snapshot, activeQueryRunId, cot.state.steps, applyCotSnapshot]);

  // Cancel V2 run: wire into the same stop mechanism as V1
  const handleStopProcessing = useCallback(() => {
    const activeDraftStream = draftStreamAbortRef.current;
    if (activeDraftStream) {
      activeWorkspaceRunRef.current = null;
      activeDraftStream.abort();
      draftStreamAbortRef.current = null;
    }
    const stoppingV2 = !!activeQueryRunId;
    const v2Snap = stoppingV2 ? queryRun.snapshot : null;
    const runningTaskLabel = queryRun.snapshot?.tasks.find((task) =>
      ['running', 'replanning', 'retry_pending'].includes(task.status),
    )?.label;
    const workLabel = runningTaskLabel ?? processing?.label;
    const stoppedCotSteps = cot.state.steps.map((step) =>
      step.status === 'active'
        ? { ...step, status: 'skipped' as const, description: 'Paused by user.' }
        : step,
    );
    const stoppedMessage = buildStoppedContinuationAssistantMessage({
      workLabel,
      v2Snapshot: v2Snap,
      v2BootContext: stoppingV2 ? queryRunBootContext : undefined,
      v1UserPrompt: lastOrchestratorUserPromptRef.current,
      cotSteps: stoppedCotSteps,
      cotUnderstanding: cot.state.understanding,
    });

    if (activeQueryRunId) {
      queryRun.cancel().catch(() => {});
      setActiveQueryRunId(null);
      setProcessing(null);
      setMode(modeAfterInterruptedWork());
    } else {
      stopProcessing();
      setProcessing(null);
      setMode(modeAfterInterruptedWork());
    }

    setMessages((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        role: 'corner' as const,
        content: stoppedMessage,
        timestamp: Date.now(),
      },
    ]);
    if (currentConversationIdRef.current && auth.user) {
      addConversationMessage(currentConversationIdRef.current, {
        role: 'corner',
        content: stoppedMessage,
      }).catch((err) => console.error('[handleStopProcessing] Failed to persist stop message:', err));
    }
    if (cot.state.status !== 'idle' && cot.state.status !== 'done' && cot.state.status !== 'error') {
      cot.onDone({ content: stoppedMessage }, true);
    }
  }, [
    activeQueryRunId,
    addConversationMessage,
    auth.user,
    cot,
    modeAfterInterruptedWork,
    processing?.label,
    queryRun,
    queryRunBootContext,
    stopProcessing,
  ]);

  /** Quick action: send intent directly to /analyze (bypass parse). Adds intent + result messages. */
  const handleQuickAction = useCallback(
    async (intent: string) => {
      if (!auth.user) {
        navigate('/auth/login');
        return;
      }
      const file = currentFiles[0] ?? null;
      if (!file) return;
      const labels: Record<string, string> = {
        summarize: 'Summarize',
        study_questions: 'Study questions',
        key_terms: 'Key terms',
        citation_generator: 'Get citation',
        contract_review: 'Review contract',
        action_items: 'Action items',
        email_draft: 'Draft email',
        sensitive_data: 'Scan for sensitive data',
      };
      const label = labels[intent] ?? intent;
      setProcessing({ progress: 50, label: 'Analyzing...' });
      setMessages((prev) => [
        ...prev,
        { id: crypto.randomUUID(), role: 'corner', content: label, timestamp: Date.now() },
      ]);
      try {
        const data = await api.analyzeDocument(file, intent);
        setProcessing({ progress: 100, label: 'Done' });
        await new Promise((r) => setTimeout(r, 300));
        setMessages((prev) => [
          ...prev,
          {
            id: crypto.randomUUID(),
            role: 'corner',
            type: 'analysis' as const,
            content: data.result,
            analysisType: data.analysisType as import('./types').AnalysisType,
            exportable: data.exportable,
            timestamp: Date.now(),
          },
        ]);
        // Persist analysis result so it reloads with the conversation
        const cid = currentConversationIdRef.current;
        if (cid && auth.user) {
          addConversationMessage(cid, {
            role: 'corner',
            content: data.result,
            toolCall: {
              toolName: '__analysis__',
              params: { analysisType: data.analysisType, exportable: data.exportable },
            },
          }).catch((err) => {
            console.error('[analyzeFromBody] Failed to persist analysis result:', err);
            // Retry once
            setTimeout(() => {
              addConversationMessage(cid, {
                role: 'corner',
                content: data.result,
                toolCall: { toolName: '__analysis__', params: { analysisType: data.analysisType, exportable: data.exportable } },
              }).catch((retryErr) => console.error('[analyzeFromBody] Retry also failed:', retryErr));
            }, 2000);
          });
        }
      } catch (err) {
        if (!axios.isAxiosError(err)) {
          const e = getActionError('analyze document', err);
          toast({ variant: 'destructive', title: e.title, description: e.description });
        }
      } finally {
        setProcessing(null);
      }
    },
    [addConversationMessage, auth.user, currentFiles, navigate]
  );

  const handleRetrieveOriginalAndPrompt = useCallback(() => {
    setDocumentPreviewVariant('original');
    setFocusedDocumentView(false);
    setChatCollapsed(false);
    setChatComposerPlaceholderHint('What should we do differently with your original file?');
    setChatPrefill("The last result wasn't right because ");
    setChatComposerFocusNonce((n) => n + 1);
  }, []);

  const handleVersionRestore = useCallback((v: VersionNode) => {
    setLastConversionOutput(null);
    setDocumentPreviewVariant('original');
    setCurrentResult({
      fileId: v.id,
      fileName: v.label,
      mimeType: v.downloadUrl?.endsWith('.pdf') ? 'application/pdf' : 'image/png',
      sizeBytes: 0,
      downloadUrl: v.downloadUrl ?? '',
    });
    setMode('result');
  }, []);

  function inferFileTypeFromFileName(fileName: string): LeftPanelVersionNode['fileType'] {
    const s = fileName.toLowerCase();
    if (s.endsWith('.pdf')) return 'pdf';
    if (/\.(png|jpe?g|gif|webp|bmp)$/.test(s)) return 'image';
    if (/\.(docx?|doc)$/.test(s)) return 'word';
    return 'other';
  }

  const resolveSessionDocumentUrl = useCallback(
    (document: WorkspaceSessionDocumentRef | null): string | null => {
      if (!document) return null;
      const fid = typeof document.fileId === 'string' ? document.fileId.trim() : '';
      // Conversation HTML docs are not iframe-streamable file URLs (GET returns JSON).
      if (isConversationMongoDocumentFileId(fid)) return null;
      const downloadUrl = typeof document.downloadUrl === 'string' ? document.downloadUrl.trim() : '';
      const mongoFromDownload = extractApiFileIdFromUrl(downloadUrl);
      if (isConversationMongoDocumentFileId(mongoFromDownload)) return null;
      if (isConversationDocumentRef(document) && downloadUrl) {
        return downloadUrl.startsWith('http')
          ? downloadUrl
          : `${conversationApiBase}${downloadUrl.startsWith('/') ? downloadUrl : `/${downloadUrl}`}`;
      }
      const apiFileId = extractApiFileIdFromUrl(downloadUrl);
      const fileId = document.fileId && isFileStorageBackedFileId(document.fileId, downloadUrl)
        ? document.fileId
        : (apiFileId && isFileStorageBackedFileId(apiFileId, downloadUrl) ? apiFileId : null);
      // Draft documents have no real server-side file — skip them
      if (!fileId && (isTransientDocumentUrl(downloadUrl) || isSyntheticDraftFileId(document.fileId) || isSyntheticDraftFileId(apiFileId))) {
        return null;
      }
      // Historical snapshots can contain stale absolute URLs, missing tokens,
      // or blob URLs from an earlier tab. Rebuild API file URLs from fileId so
      // hydration always fetches the current authenticated server artifact.
      if (fileId) {
        return `${conversationApiBase}/api/file/${fileId}`;
      }
      if (downloadUrl) {
        return downloadUrl.startsWith('http')
          ? downloadUrl
          : `${conversationApiBase}${downloadUrl.startsWith('/') ? downloadUrl : `/${downloadUrl}`}`;
      }
      return null;
    },
    [conversationApiBase],
  );

  const buildWorkspaceSessionSnapshot = useCallback((
    /**
     * Optional overrides used by the draft-stream `done` handler — at the
     * exact moment `done` fires we just called `setDraftContent(html)` and
     * `setDraftTitle(title)`, but React state hasn't propagated yet, so the
     * default closure would still snapshot the *previous* values. Passing
     * overrides lets us persist with the just-completed values without
     * waiting for a re-render.
     */
    overrides?: {
      draftContent?: string;
      draftTitle?: string;
      mode?: AppMode;
      currentResult?: ToolResult | null;
      cotSteps?: CoTStepData[];
    },
  ): Omit<WorkspaceSessionSnapshot, 'conversationId' | 'updatedAt'> => {
    const effectiveDraftContent = overrides?.draftContent ?? draftContent;
    const effectiveDraftTitle = overrides?.draftTitle ?? draftTitle;
    const effectiveMode = overrides?.mode ?? mode;
    const sourceFileId =
      currentResult?.sourceFileId ??
      lastConversionOutput?.sourceFileId ??
      sessionPrimaryDocument?.fileId ??
      null;
    const sourceDocuments =
      currentFiles.length > 0
        ? filesToSessionDocuments(currentFiles, sourceFileId)
        : sessionSourceDocuments;
    const primaryDocument =
      sourceDocuments[0] ??
      sessionPrimaryDocument ??
      activeDocumentSurface?.ref ??
      null;

    let snapshotCurrentResult: ToolResult | null = Object.prototype.hasOwnProperty.call(overrides ?? {}, 'currentResult')
      ? overrides?.currentResult ?? null
      : currentResult;
    if (effectiveMode === 'drafting' && currentConversationId) {
      const draftScopeId = currentConversationId;
      const synthetic: ToolResult = {
        fileId: `draft-${draftScopeId}`,
        downloadUrl: `draft://${draftScopeId}`,
        fileName: `${(effectiveDraftTitle || 'Document').replace(/\.(docx|doc|pdf)$/i, '') || 'Document'}.docx`,
        mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        sizeBytes: snapshotCurrentResult?.sizeBytes ?? 0,
      };
      const base = snapshotCurrentResult ? { ...snapshotCurrentResult } : synthetic;
      const hasRealArtifact =
        typeof base.fileId === 'string' && base.fileId.length > 0 && !base.fileId.startsWith('draft-');
      snapshotCurrentResult = {
        ...base,
        ...(hasRealArtifact
          ? {}
          : {
              fileId: synthetic.fileId,
              downloadUrl: synthetic.downloadUrl,
              fileName: synthetic.fileName,
              mimeType: synthetic.mimeType,
            }),
        draftHtml: effectiveDraftContent,
        draftTitle:
          effectiveDraftTitle.trim() ? effectiveDraftTitle : (base.draftTitle ?? (base.fileName?.replace(/\.(docx|doc)$/i, '') || 'Document')),
      };
    }

    return {
      title: conversationListRef.current.find((conversation) => conversation.id === currentConversationId)?.title ?? null,
      projectId: activeDocumentProject?.id ?? currentProjectId ?? null,
      activeVersionId: activeDocumentProject?.currentVersionId ?? null,
      mode: effectiveMode,
      primaryDocument,
      sourceDocuments,
      currentResult: snapshotCurrentResult,
      lastConversionOutput,
      panelState: {
        chatCollapsed,
        focusedDocumentView,
        canvasViewMode,
        rightPanelOpen: rightOpen,
        toolPanelExpanded,
        previewMode: panelSettings.previewMode,
        zoom: panelSettings.zoom,
        documentPreviewVariant,
      },
      taskContext: {
        lastToolName: lastTool ?? null,
        modeLabel:
          effectiveMode === 'esign'
            ? 'Signing'
            : effectiveMode === 'clarifying'
              ? 'Clarifying'
              : effectiveMode === 'processing'
                ? 'Processing'
              : null,
      },
      cotSteps: buildPersistedCotSteps(overrides?.cotSteps ?? cotStepsRef.current),
    };
  }, [
    canvasViewMode,
    chatCollapsed,
    activeDocumentSurface,
    currentConversationId,
    currentProjectId,
    currentFiles,
    currentResult,
    activeDocumentProject?.id,
    activeDocumentProject?.currentVersionId,
    documentPreviewVariant,
    filesToSessionDocuments,
    focusedDocumentView,
    lastConversionOutput,
    lastTool,
    mode,
    panelSettings.previewMode,
    panelSettings.zoom,
    rightOpen,
    sessionPrimaryDocument,
    sessionSourceDocuments,
    draftContent,
    draftTitle,
  ]);

  const handleEditBatchChange = useCallback((batch: EditBatch) => {
    const nextCotSteps = cotStepsRef.current.map((step) =>
      step.editBatch?.id === batch.id ? { ...step, editBatch: batch } : step,
    );
    cot.updateEditBatch(batch);
    const conversationId = batch.conversationId ?? currentConversationIdRef.current;
    if (!conversationId) return;
    const snapshot = buildWorkspaceSessionSnapshot({ cotSteps: nextCotSteps });
    persistConversationSnapshot(conversationId, snapshot).catch((persistErr) => {
      console.error('[CoT] persist edit batch update failed', persistErr);
    });
  }, [buildWorkspaceSessionSnapshot, cot, persistConversationSnapshot]);

  const hydratingConversationIdRef = useRef<string | null>(null);
  /** When hydrate was skipped because the same conversation had an in-flight run — retried when idle. */
  const hydrateDeferredForRouteRef = useRef<string | null>(null);
  /** Legacy marker kept for diagnostics; route-specific status lives in hydrationRouteStateRef. */
  const mainHydrateAttemptedForRouteRef = useRef<string | null>(null);
  type RouteHydrationState = {
    attempted: boolean;
    succeeded: boolean;
    explicitTextOnly: boolean;
    lastFailure: string | null;
    nocacheAttempts: number;
    lastNocacheAt: number | null;
  };
  const hydrationRouteStateRef = useRef<Record<string, RouteHydrationState>>({});
  const getRouteHydrationState = useCallback((conversationId: string): RouteHydrationState => {
    const existing = hydrationRouteStateRef.current[conversationId];
    if (existing) return existing;
    const init: RouteHydrationState = {
      attempted: false,
      succeeded: false,
      explicitTextOnly: false,
      lastFailure: null,
      nocacheAttempts: 0,
      lastNocacheAt: null,
    };
    hydrationRouteStateRef.current[conversationId] = init;
    return init;
  }, []);
  const patchRouteHydrationState = useCallback((
    conversationId: string,
    patch: Partial<RouteHydrationState>,
  ): RouteHydrationState => {
    const next: RouteHydrationState = {
      ...getRouteHydrationState(conversationId),
      ...patch,
    };
    hydrationRouteStateRef.current[conversationId] = next;
    return next;
  }, [getRouteHydrationState]);
  const hydrationRequestSeqRef = useRef(0);
  /** Latest `/app/conversations/:id` id from the URL — async hydrate must not apply after user navigates away. */
  const routeConversationIdRef = useRef<string | null>(routeConversationId);
  /** Hydration applies server state locally; the autosave effect must not immediately PATCH that derived state back. */
  const skipNextWorkspaceSnapshotPersistRef = useRef(false);

  useEffect(() => {
    routeConversationIdRef.current = routeConversationId;
    if (routeConversationId != null) return;
    // User left conversation URLs (/app, Signatures, Settings…); invalidate any in-flight hydrate for `/app/conversations/:id`.
    hydrationRequestSeqRef.current += 1;
    hydratingConversationIdRef.current = null;
    hydrateDeferredForRouteRef.current = null;
    mainHydrateAttemptedForRouteRef.current = null;
    hydrationRouteStateRef.current = {};
  }, [routeConversationId]);

  /**
   * Auto-resume an interrupted draft generation. Borrowed from the
   * Boltzman pattern: the BE marks `draftStatus='pending'|'streaming'`
   * + stashes the prompt in `pendingDraft` the moment the user submits.
   * On hydrate, if those flags are set, we fire a fresh draft-stream
   * with the same prompt — the user does NOT need to re-submit, and
   * the result lands in `Conversation.documents[]` exactly as if the
   * original had completed normally.
   *
   * Critically, this does NOT push a duplicate user message: the
   * original submission already wrote it to the messages array, and
   * the BE handles document persistence on `done`.
   */
  const resumeDraftStream = useCallback(
    async (args: {
      conversationId: string;
      prompt: string;
      currentDraftHtml?: string | null;
    }): Promise<void> => {
      const { conversationId, prompt, currentDraftHtml } = args;
      const resumeCid = conversationId;
      const resumeUiTarget = () =>
        String(currentConversationIdRef.current) === String(resumeCid);
      // Cancel any prior in-flight draft from this tab. After a tab
      // close + reopen there shouldn't be one, but defensive.
      const draftController = new AbortController();
      draftStreamAbortRef.current?.abort();
      draftStreamAbortRef.current = draftController;
      draftStatusRef.current = 'pending';
      draftStreamingConversationIdRef.current = resumeCid;
      setDraftStreamingConversationId(resumeCid);
      setMode('drafting');

      if (resumeUiTarget()) {
        cot.applySnapshot({
          status: 'in_progress',
          steps: [
            { title: 'Resuming draft', status: 'done', description: 'Reconnecting to the previous generation' },
            { title: 'Drafting document', status: 'in_progress' },
          ],
        });
      }

      const MAX_RESUME_RETRIES = 4;
      const RESUME_BACKOFFS_MS = [1000, 2000, 4000, 6000];
      let attempt = 0;
      let accumulatedText = '';
      let sawDone = false;
      let aborted = false;

      try {
        const token = auth.getToken();
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;

        for (;;) {
          let attemptErrored = false;
          accumulatedText = '';
          let buffer = '';

          try {
            const response = await fetch('/api/analyze/draft-stream', {
              method: 'POST',
              headers,
              signal: draftController.signal,
              body: JSON.stringify({
                prompt,
                ...(currentDraftHtml ? { currentDraftHtml } : {}),
                conversationId,
              }),
            });
            if (!response.ok || !response.body) {
              throw new Error(`Draft resume failed: ${response.status}`);
            }
            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            const handle = (eventName: string, payloadRaw: string) => {
              if (draftController.signal.aborted) {
                aborted = true;
                return;
              }
              const data = JSON.parse(payloadRaw.trim()) as {
                text?: string;
                draftHtml?: string;
                draftTitle?: string;
                truncated?: boolean;
                message?: string;
                documentId?: string;
                documentVersion?: number;
              };
              if (eventName === 'delta' && data.text) {
                draftStatusRef.current = 'streaming';
                accumulatedText += data.text;
                let html = accumulatedText.replace(/^\uFEFF/, '');
                if (/^```(?:html)?\s*/i.test(html)) html = html.replace(/^```(?:html)?\s*/i, '');
                html = html.replace(/\n?```\s*$/, '');
                if (resumeUiTarget()) setDraftContent(html.trim() ? html : '<p></p>');
                return;
              }
              if (eventName === 'done') {
                sawDone = true;
                draftStatusRef.current = 'complete';
                const html = typeof data.draftHtml === 'string' ? data.draftHtml : '';
                const title = typeof data.draftTitle === 'string' ? data.draftTitle : 'Untitled Document';
                if (resumeUiTarget()) {
                  if (html.length > 0) setDraftContent(html);
                  setDraftTitle(title);
                  if (data.documentId) {
                    setCurrentResult({
                      fileId:          `doc-${data.documentId}`,
                      downloadUrl:     `/api/conversations/${conversationId}/documents/${data.documentId}`,
                      fileName:        `${(title ?? 'Document').replace(/\.(docx|doc|html?)$/i, '') || 'Document'}.html`,
                      mimeType:        'text/html',
                      sizeBytes:       html.length,
                      kind:            'document',
                      documentId:      data.documentId,
                      ...(data.documentVersion != null ? { documentVersion: data.documentVersion } : {}),
                      draftHtml:       html,
                      draftTitle:      title,
                    });
                  }
                  cot.applySnapshot({
                    status: 'done',
                    steps: [
                      { title: 'Resuming draft', status: 'done' },
                      { title: 'Drafting document', status: 'done', description: title },
                    ],
                  });
                }
                const viewing =
                  currentConversationIdRef.current != null
                  && String(currentConversationIdRef.current) === String(resumeCid);
                const tabVisible = typeof document !== 'undefined' && document.visibilityState === 'visible';
                if (!viewing || !tabVisible) {
                  sonnerToast.success('Draft ready', {
                    description: viewing && !tabVisible
                      ? `"${title}" is ready.`
                      : `"${title}" finished while you were elsewhere.`,
                    action: {
                      label: 'Open',
                      onClick: () => navigate(conversationWorkspacePath(resumeCid)),
                    },
                    duration: 14_000,
                  });
                  notifyDraftReadyDesktop(title, resumeCid);
                }
                if (resumeCid && html.trim()) {
                  void warmDraftPdfMirrorIfFresh({
                    conversationId: resumeCid,
                    html,
                    title,
                  });
                }
                return;
              }
              if (eventName === 'error') {
                throw new Error(data.message ?? 'Draft generation failed');
              }
            };

            const processBlock = (block: string[]) => {
              let evt = '';
              for (const line of block) {
                if (line.startsWith('event: ')) evt = line.slice(7).trim();
                else if (line.startsWith('data: ') && evt) handle(evt, line.slice(6));
              }
            };

            for (;;) {
              const { done, value } = await reader.read();
              if (draftController.signal.aborted) {
                aborted = true;
                break;
              }
              if (done) break;
              buffer += decoder.decode(value, { stream: true });
              let idx;
              while ((idx = buffer.indexOf('\n\n')) !== -1) {
                const block = buffer.slice(0, idx).split('\n');
                buffer = buffer.slice(idx + 2);
                processBlock(block);
                if (sawDone) break;
              }
              if (sawDone) break;
            }
          } catch (readErr) {
            if (
              !draftController.signal.aborted
              && !sawDone
              && attempt < MAX_RESUME_RETRIES
              && isTransientNetworkError(readErr)
            ) {
              attemptErrored = true;
            } else if (
              !draftController.signal.aborted
              && !sawDone
              && isTransientNetworkError(readErr)
            ) {
              throw new Error("Connection lost while resuming. Send your prompt again to retry.");
            } else {
              throw readErr;
            }
          }

          if (sawDone || aborted) break;
          if (attemptErrored && attempt < MAX_RESUME_RETRIES) {
            attempt += 1;
            const backoffMs = RESUME_BACKOFFS_MS[attempt - 1] ?? 6000;
            await waitForOnlineOrShortDelay(backoffMs);
            continue;
          }
          break;
        }
      } catch (err) {
        draftStatusRef.current = 'failed';
        console.error('[draft-resume] failed:', err);
        if (resumeUiTarget()) {
          cot.applySnapshot({
            status: 'error',
            steps: [
              { title: 'Resuming draft', status: 'done' },
              {
                title: 'Drafting document',
                status: 'error',
                errorMessage: err instanceof Error ? err.message : 'Could not resume draft',
              },
            ],
          });
        }
        toast({
          title: 'Could not resume draft',
          description: 'Send your prompt again to regenerate.',
          variant: 'destructive',
        });
      } finally {
        if (aborted && !sawDone) {
          draftStatusRef.current = 'idle';
        }
        if (String(draftStreamingConversationIdRef.current ?? '') === String(resumeCid)) {
          draftStreamingConversationIdRef.current = null;
        }
        setDraftStreamingConversationId(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [auth, cot, toast, navigate, warmDraftPdfMirrorIfFresh],
  );
  const hydrateFromSessionSnapshot = useCallback(
    async (id: string, options?: { forceNocache?: boolean }) => {
      hydrationDebugLog('[Hydrate] Start', {
        id,
        alreadyHydrating: hydratingConversationIdRef.current,
        mode: modeRef.current,
      });
      if (hydratingConversationIdRef.current === id) {
        hydrationDebugLog('[Hydrate] Skip: already hydrating this id', { id });
        return;
      }
      if (
        routeConversationIdRef.current == null
        || String(routeConversationIdRef.current) !== String(id)
      ) {
        hydrationDebugLog('[Hydrate] Skip: URL not on this conversation', {
          targetId: id,
          routeConversationId: routeConversationIdRef.current,
        });
        return;
      }
      const switchingToAnotherConversation =
        currentConversationIdRef.current != null
        && String(currentConversationIdRef.current) !== String(id);
      const pendingDocEditVisible = pendingDocEditVisibleFileRef.current;
      if (
        !switchingToAnotherConversation &&
        pendingDocEditVisible &&
        (!pendingDocEditVisible.conversationId || String(pendingDocEditVisible.conversationId) === String(id)) &&
        Date.now() - pendingDocEditVisible.createdAt < PENDING_DOC_EDIT_VISIBLE_TTL_MS
      ) {
        hydrationDebugLog('[Hydrate] Skip: pending doc edit visible file', {
          targetId: id,
          expectedFileId: pendingDocEditVisible.fileId,
          currentResultFileId: currentResultRef.current?.fileId ?? null,
          primaryDocumentFileId: sessionPrimaryDocumentRef.current?.fileId ?? null,
        });
        patchRouteHydrationState(id, {
          attempted: true,
          succeeded: true,
          explicitTextOnly: false,
          lastFailure: null,
        });
        return;
      }
      // `switchingToAnotherConversation` means the URL target (`id`) differs
      // from the workspace currently loaded in-memory. In that case, this call
      // is a route transition hydrate (history click / sidebar nav), not an
      // in-place refresh of the active workspace.
      const draftStreamingOwner = draftStreamingConversationIdRef.current;
      const hasActiveDraftController =
        draftStreamAbortRef.current != null &&
        !draftStreamAbortRef.current.signal.aborted;
      const hasActiveDraftStreamForRoute =
        hasActiveDraftController &&
        draftStatusRef.current !== 'idle' &&
        draftStatusRef.current !== 'failed' &&
        draftStatusRef.current !== 'complete' &&
        (
          draftStreamingOwner != null
            ? String(draftStreamingOwner) === String(id)
            : !switchingToAnotherConversation
        );
      const protectDraftHydrateSkip =
        modeRef.current === 'drafting'
        && hasActiveDraftStreamForRoute;
      const isEsign = modeRef.current === 'esign';
      const isProcessingMode = modeRef.current === 'processing';
      const hasActiveOps = processingRef.current != null || activeQueryRunIdRef.current != null;
      // Historically e-sign used a hard "never hydrate" guard to avoid blowing
      // away an in-flight signing screen. We now soften it: protect only in-place
      // e-sign sessions, but allow route-transition hydration into e-sign threads.
      const shouldProtectEsign =
        isEsign && !switchingToAnotherConversation && !hasActiveOps;
      const pendingRequestSeq = hydrationRequestSeqRef.current + 1;
      const logHydrateGuardSkip = (reason: string, extra: Record<string, unknown> = {}) => {
        hydrationDebugLog('[Hydrate] Skip: guard', {
          reason,
          routeId: routeConversationIdRef.current,
          targetId: id,
          switchingToAnotherConversation,
          mode: modeRef.current,
          processingRef: processingRef.current,
          activeQueryRunIdRef: activeQueryRunIdRef.current,
          requestSeq: pendingRequestSeq,
          ...extra,
        });
      };
      if (
        !switchingToAnotherConversation
        && (protectDraftHydrateSkip || isProcessingMode || shouldProtectEsign)
      ) {
        logHydrateGuardSkip('mode-protection', {
          protectDraftHydrateSkip,
          isProcessingMode,
          shouldProtectEsign,
          hasActiveOps,
        });
        if (protectDraftHydrateSkip) {
          hydrationDebugLog('[Hydrate] Skip: drafting guard', {
            protectDraftHydrateSkip,
            mode: modeRef.current,
            draftStatusRef: draftStatusRef.current,
            draftStreamingConversationIdRef: draftStreamingConversationIdRef.current,
            targetId: id,
          });
        }
        // If this route already owns an artifact, treat this as an intentional
        // settled state so HydrationEffect doesn't repeatedly call hydrate while
        // we're deliberately protecting an active in-place e-sign workspace.
        if (shouldProtectEsign && hasWorkspaceDocArtifactForConversation(id)) {
          patchRouteHydrationState(id, {
            attempted: true,
            succeeded: true,
            explicitTextOnly: false,
            lastFailure: null,
          });
        }
        return;
      }
      if (switchingToAnotherConversation && isEsign && hasActiveOps) {
        logHydrateGuardSkip('esign-route-switch-active-ops', { hasActiveOps });
        hydrateDeferredForRouteRef.current = id;
        return;
      }
      if (
        !switchingToAnotherConversation
        && hasActiveOps
      ) {
        hydrationDebugLog('[Hydrate] Skip: in-flight processing or query run');
        logHydrateGuardSkip('active-ops', {});
        hydrateDeferredForRouteRef.current = id;
        return;
      }
      const requestSeq = hydrationRequestSeqRef.current + 1;
      hydrationRequestSeqRef.current = requestSeq;
      hydratingConversationIdRef.current = id;
      patchRouteHydrationState(id, {
        attempted: true,
        ...(options?.forceNocache
          ? { nocacheAttempts: getRouteHydrationState(id).nocacheAttempts + 1, lastNocacheAt: Date.now() }
          : {}),
      });
      const isStaleHydration = () =>
        hydrationRequestSeqRef.current !== requestSeq
        || hydratingConversationIdRef.current !== id
        || routeConversationIdRef.current == null
        || String(routeConversationIdRef.current) !== String(id);
      try {
        let loaded = await getConversationSession(id, options?.forceNocache ? { nocache: true } : undefined);
        if (isStaleHydration()) {
          hydrationDebugLog('[Hydrate] Stale session response ignored', { id });
          return;
        }
        let snapshot = loaded.sessionSnapshot;
        hydrationDebugLog('[Hydrate] Loaded session', {
          snapshot: !!snapshot,
          snapshotMode: (snapshot as any)?.mode ?? 'none',
          primaryDoc: snapshot?.primaryDocument
            ? {
                fileId: (snapshot.primaryDocument as any).fileId,
                downloadUrl: (snapshot.primaryDocument as any).downloadUrl,
              }
            : null,
          currentResult: snapshot?.currentResult
            ? {
                fileId: (snapshot.currentResult as any).fileId,
                downloadUrl: (snapshot.currentResult as any).downloadUrl,
              }
            : null,
          messages: loaded.messages?.length ?? 0,
          latestResult: loaded.latestResult
            ? { fileId: loaded.latestResult.fileId, downloadUrl: loaded.latestResult.downloadUrl }
            : null,
          documents: loaded.documents?.length ?? 0,
          draftStatus: loaded.draftStatus ?? 'idle',
          pendingDraft: !!loaded.pendingDraft,
        });

        // ─── New canonical hydration path ──────────────────────────
        // 1. If the BE marked a draft as still pending/streaming, the
        //    user closed the tab or hit a network drop mid-generation.
        //    Auto-resume by re-launching the SSE with the same prompt
        //    — survives full reloads, network changes, anything that
        //    kills the original live connection. This is Boltzman's
        //    "status: 'initial' → re-launch" pattern, generalized.
        // 2. Otherwise, if the snapshot's currentResult points at a
        //    document by id, resolve the body from `loaded.documents[]`
        //    (returned in the same payload — no extra round-trip) and
        //    set up a normal drafting view. This replaces the legacy
        //    fallback-walk-through-messages logic for new drafts and
        //    cannot produce orphans by construction.
        let draftStatus = loaded.draftStatus ?? 'idle';
        let pendingDraft = loaded.pendingDraft ?? null;
        let allDocs = loaded.documents ?? [];
        draftStatusRef.current = draftStatus;

        if (
          (draftStatus === 'pending' || draftStatus === 'streaming')
          && pendingDraft?.prompt
        ) {
          hydrationDebugLog('[Hydrate] Branch A (resume interrupted draft)', {
            reason: 'draftStatus pending/streaming with pendingDraft.prompt',
            draftStatus,
          });
          // Apply minimal local state (mode, messages) so the chat
          // history is visible while resume streams new content.
          clearWorkspaceSessionState();
          skipNextWorkspaceSnapshotPersistRef.current = true;
          currentConversationIdRef.current = id;
          setCurrentConversationId(id);
          setMessages(Array.isArray(loaded.messages) ? loaded.messages : []);
          setMessagesHasMore(loaded.hasMore);
          setConversationDocuments(allDocs);
          // If a previous version exists, render it as the starting
          // point so the user sees something while the resume streams.
          const lastDoc = allDocs.length > 0 ? allDocs[allDocs.length - 1] : null;
          if (lastDoc) {
            setDraftContent(lastDoc.html);
            setDraftTitle(lastDoc.title);
          } else {
            setDraftContent('<p></p>');
          }
          // Fire and forget — resumeDraftStream owns its own lifecycle.
          void resumeDraftStream({
            conversationId: id,
            prompt: pendingDraft.prompt,
            currentDraftHtml: pendingDraft.currentDraftHtml ?? null,
          });
          patchRouteHydrationState(id, {
            succeeded: true,
            explicitTextOnly: false,
            lastFailure: null,
          });
          return;
        }

        // Document-backed currentResult? Resolve the body now so the
        // legacy hydration code (which renders displayResult.draftHtml)
        // sees the canonical content instead of an empty draft-* shell.
        let snapshotResultEarly = snapshot?.currentResult ?? null;
        let docIdRef =
          snapshotResultEarly?.kind === 'document' && typeof snapshotResultEarly.documentId === 'string'
            ? snapshotResultEarly.documentId
            : undefined;
        if (
          !docIdRef &&
          typeof snapshotResultEarly?.fileId === 'string' &&
          isConversationMongoDocumentFileId(snapshotResultEarly.fileId)
        ) {
          docIdRef = snapshotResultEarly.fileId.slice(4);
        }
        if (docIdRef) {
          const matched = allDocs.find((d) => d.id === docIdRef);
          if (matched && snapshot) {
            hydrationDebugLog('[Hydrate] Branch B (document-backed result from documents[])', {
              docId: docIdRef,
              version: matched.version,
            });
            // Synthesize a snapshot whose currentResult has the body
            // fields populated — the rest of the hydration logic then
            // proceeds normally.
            const resolvedResult: ToolResult = {
              ...snapshotResultEarly,
              kind: 'document',
              documentId: docIdRef,
              draftHtml:  matched.html,
              draftTitle: matched.title,
              fileName:   `${(matched.title ?? 'Document').replace(/\.(docx|doc|html?)$/i, '') || 'Document'}.html`,
              mimeType:   'text/html',
              sizeBytes:  matched.html.length,
            };
            (snapshot as { currentResult: ToolResult | null }).currentResult = resolvedResult;
            (loaded as { latestResult: ToolResult | null }).latestResult = resolvedResult;
          } else if (!matched) {
            hydrationDebugLog('[Hydrate] Branch B unresolved — documentId missing from documents[]', {
              docIdRef,
              docsCount: allDocs.length,
              forceNocache: !!options?.forceNocache,
            });
            if (!options?.forceNocache) {
              hydrationDebugLog('[Hydrate] Branch B fallback: forcing nocache session reload');
              loaded = await getConversationSession(id, { nocache: true });
              if (isStaleHydration()) {
                hydrationDebugLog('[Hydrate] Stale nocache recovery response ignored', { id });
                return;
              }
              snapshot = loaded.sessionSnapshot;
              draftStatus = loaded.draftStatus ?? 'idle';
              draftStatusRef.current = draftStatus;
              pendingDraft = loaded.pendingDraft ?? null;
              allDocs = loaded.documents ?? [];
              snapshotResultEarly = snapshot?.currentResult ?? null;
              docIdRef =
                snapshotResultEarly?.kind === 'document' && typeof snapshotResultEarly.documentId === 'string'
                  ? snapshotResultEarly.documentId
                  : (
                    typeof snapshotResultEarly?.fileId === 'string'
                    && isConversationMongoDocumentFileId(snapshotResultEarly.fileId)
                  )
                    ? snapshotResultEarly.fileId.slice(4)
                    : undefined;
              const matchedAfterNocache = docIdRef
                ? allDocs.find((d) => d.id === docIdRef)
                : null;
              if (matchedAfterNocache && snapshot) {
                hydrationDebugLog('[Hydrate] Branch B recovered after nocache reload', {
                  docId: docIdRef,
                  version: matchedAfterNocache.version,
                });
                const resolvedAfterNocache: ToolResult = {
                  ...snapshotResultEarly,
                  kind: 'document',
                  documentId: docIdRef!,
                  draftHtml: matchedAfterNocache.html,
                  draftTitle: matchedAfterNocache.title,
                  fileName: `${(matchedAfterNocache.title ?? 'Document').replace(/\.(docx|doc|html?)$/i, '') || 'Document'}.html`,
                  mimeType: 'text/html',
                  sizeBytes: matchedAfterNocache.html.length,
                };
                (snapshot as { currentResult: ToolResult | null }).currentResult = resolvedAfterNocache;
                (loaded as { latestResult: ToolResult | null }).latestResult = resolvedAfterNocache;
                patchRouteHydrationState(id, {
                  nocacheAttempts: getRouteHydrationState(id).nocacheAttempts + 1,
                  lastNocacheAt: Date.now(),
                });
              } else {
                hydrationDebugLog('[Hydrate] Branch B fallback unresolved after nocache; continuing to Branch C resolution', {
                  docIdRef: docIdRef ?? null,
                  docsCount: allDocs.length,
                });
              }
            }
          }
        }
        // ───────────────────────────────────────────────────────────

        const restoredPrimaryDocument =
          snapshot?.primaryDocument ??
          snapshot?.sourceDocuments?.[0] ??
          null;
        const snapshotResult =
          snapshot?.currentResult ??
          snapshot?.lastConversionOutput ??
          null;
        const latestFromSession = loaded.latestResult ?? null;
        let restoredResult: ToolResult | null =
          snapshotResult && latestFromSession
            ? {
                ...latestFromSession,
                ...snapshotResult,
                editBatch: snapshotResult.editBatch ?? latestFromSession.editBatch,
              }
            : latestFromSession ?? snapshotResult ?? null;
        // If messages produced a real file but the persisted snapshot still has a draft-* shell id, prefer the file artifact.
        if (
          restoredResult &&
          latestFromSession &&
          snapshotResult &&
          typeof latestFromSession.fileId === 'string'
          && !latestFromSession.fileId.startsWith('draft-')
          && typeof snapshotResult.fileId === 'string'
          && snapshotResult.fileId.startsWith('draft-')
        ) {
          restoredResult = {
            ...restoredResult,
            fileId: latestFromSession.fileId,
            downloadUrl: latestFromSession.downloadUrl ?? restoredResult.downloadUrl,
            fileName: latestFromSession.fileName ?? restoredResult.fileName,
            mimeType: latestFromSession.mimeType ?? restoredResult.mimeType,
            sizeBytes: latestFromSession.sizeBytes ?? restoredResult.sizeBytes,
            ...(latestFromSession.sourceFileId
              ? { sourceFileId: latestFromSession.sourceFileId }
              : {}),
          };
        }
        const convDocDlForResult = conversationDocumentDownloadPath(restoredResult?.fileId, id);
        const restoredResultDocument =
          restoredResult?.fileId && convDocDlForResult
            ? {
                fileId: restoredResult.fileId,
                fileName: restoredResult.fileName,
                mimeType: restoredResult.mimeType,
                sizeBytes: restoredResult.sizeBytes,
                downloadUrl: convDocDlForResult,
                source: 'result' as const,
              }
            : restoredResult?.fileId && isFileStorageBackedFileId(restoredResult.fileId, restoredResult.downloadUrl)
            ? {
                fileId: restoredResult.fileId,
                fileName: restoredResult.fileName,
                mimeType: restoredResult.mimeType,
                sizeBytes: restoredResult.sizeBytes,
                downloadUrl: restoredResult.downloadUrl ?? `/api/file/${restoredResult.fileId}`,
                source: 'result' as const,
              }
            : null;
        const sourceDocumentFromResult =
          restoredResult?.sourceFileId
            ? (
                snapshot?.sourceDocuments?.find((doc) => doc.fileId === restoredResult.sourceFileId) ??
                {
                  fileId: restoredResult.sourceFileId,
                  fileName: restoredPrimaryDocument?.fileName ?? restoredResult.fileName,
                  mimeType: restoredPrimaryDocument?.mimeType ?? restoredResult.mimeType,
                  sizeBytes: restoredPrimaryDocument?.sizeBytes ?? restoredResult.sizeBytes,
                  downloadUrl:
                    conversationDocumentDownloadPath(restoredResult.sourceFileId, id)
                    ?? `/api/file/${restoredResult.sourceFileId}`,
                  source: 'source_file' as const,
                }
              )
            : null;
        const restoredPrimaryDocumentSafe =
          restoredPrimaryDocument && (
            isFileStorageBackedFileId(restoredPrimaryDocument.fileId, restoredPrimaryDocument.downloadUrl) ||
            isConversationDocumentRef(restoredPrimaryDocument) ||
            isConversationMongoDocumentFileId(restoredPrimaryDocument.fileId)
          )
            ? restoredPrimaryDocument
            : null;
        const previewDocument =
          restoredResultDocument ??
          restoredPrimaryDocumentSafe ??
          sourceDocumentFromResult;

        // If this chat had a signing envelope that finished, show the flattened signed
        // PDF — same artifact as Download from Workflows completed — instead of the
        // unsigned package still pointed at by the session snapshot.
        let displayPreviewDoc = previewDocument;
        let displayResult = restoredResult ?? null;
        if (!displayResult && allDocs.length > 0) {
          const newestDoc = allDocs[allDocs.length - 1];
          displayResult = {
            fileId: `doc-${newestDoc.id}`,
            downloadUrl: conversationDocumentDownloadPath(`doc-${newestDoc.id}`, id) ?? `draft://${id}`,
            fileName: `${(newestDoc.title ?? 'Document').replace(/\.(docx|doc|html?)$/i, '') || 'Document'}.html`,
            mimeType: 'text/html',
            sizeBytes: newestDoc.html.length,
            kind: 'document',
            documentId: newestDoc.id,
            documentVersion: newestDoc.version,
            draftHtml: newestDoc.html,
            draftTitle: newestDoc.title,
          };
          displayPreviewDoc = {
            fileId: `doc-${newestDoc.id}`,
            fileName: displayResult.fileName,
            mimeType: 'text/html',
            sizeBytes: newestDoc.html.length,
            downloadUrl: conversationDocumentDownloadPath(`doc-${newestDoc.id}`, id) ?? `/api/conversations/${id}/documents/${newestDoc.id}`,
            source: 'result' as const,
          };
          hydrationDebugLog('[Hydrate] Branch C fallback: selected newest documents[] entry as current result', {
            documentId: newestDoc.id,
            version: newestDoc.version,
          });
        }
        let hydratedSigningSummary: import('@corner/shared').SigningRequestSummary | null =
          null;
        /** When we match a completed envelope, realign `lastConversionOutput` to the signed PDF (see setLastConversionOutput below). */
        let signingHydrateLastOutput: ToolResult | null = null;
        const hydrateToken = authGetToken();
        if (
          hydrateToken &&
          displayResult &&
          (displayResult.mimeType === 'application/pdf' ||
            (displayResult.fileName ?? '').toLowerCase().endsWith('.pdf'))
        ) {
          try {
            const { data } = await axios.get('/api/signing', {
              headers: { Authorization: `Bearer ${hydrateToken}` },
            });
            if (isStaleHydration()) {
              hydrationDebugLog('[Hydrate] Stale signing lookup ignored', { id });
              return;
            }
            const rows = Array.isArray(data) ? data : [];
            const resultFid = displayResult.fileId;
            const resultSrc = displayResult.sourceFileId;
            const hit = rows.find((r) => {
              if (
                r.status !== 'completed'
                || !r.signedDocumentFileId
                || typeof r.documentFileId !== 'string'
              ) {
                return false;
              }
              const { documentFileId: docId, signedDocumentFileId: outId } = r;
              return (
                (resultFid && docId === resultFid)
                || (resultSrc && docId === resultSrc)
                || (resultFid && outId === resultFid)
                || (resultSrc && outId === resultSrc)
              );
            });
            if (hit?.signedDocumentFileId) {
              hydratedSigningSummary = hit;
              const sid = hit.signedDocumentFileId;
              const rawName =
                hit.documentName ?? displayResult.fileName ?? 'document.pdf';
              const signedFilename = /^signed_/i.test(rawName.trim())
                ? rawName.endsWith('.pdf')
                  ? rawName
                  : `${rawName}.pdf`
                : rawName.toLowerCase().endsWith('.pdf')
                  ? `signed_${rawName}`
                  : `signed_${rawName}.pdf`;
              displayResult = {
                ...displayResult,
                fileId: sid,
                sourceFileId: displayResult.sourceFileId ?? displayResult.fileId,
                downloadUrl: `/api/file/${sid}`,
                fileName: signedFilename,
                mimeType: 'application/pdf',
              };
              displayPreviewDoc = {
                fileId: sid,
                fileName: signedFilename,
                mimeType: 'application/pdf',
                sizeBytes: displayResult.sizeBytes,
                downloadUrl: `/api/file/${sid}`,
                source: 'result' as const,
              };
              const snapshotLco = snapshot?.lastConversionOutput ?? null;
              if (snapshotLco && typeof snapshotLco === 'object') {
                signingHydrateLastOutput = {
                  ...snapshotLco,
                  fileId: sid,
                  downloadUrl: `/api/file/${sid}`,
                  fileName: signedFilename,
                  mimeType: 'application/pdf',
                  sourceFileId: snapshotLco.sourceFileId ?? hit.documentFileId ?? resultSrc ?? resultFid,
                };
              } else {
                signingHydrateLastOutput = { ...displayResult } as ToolResult;
              }
            }
          } catch {
            /* best-effort — fall back to snapshot PDF */
          }
        }

        const recoverDraftFromMessages = (): { html: string; title?: string } | null => {
          const msgs = loaded.messages ?? [];
          for (let i = msgs.length - 1; i >= 0; i--) {
            const r = msgs[i]?.result;
            if (r && typeof r.draftHtml === 'string' && r.draftHtml.trim()) {
              return {
                html: r.draftHtml,
                title: typeof r.draftTitle === 'string' ? r.draftTitle : undefined,
              };
            }
          }
          return null;
        };
        const draftFromMessages = recoverDraftFromMessages();
        if (draftFromMessages && !String(displayResult?.draftHtml ?? '').trim()) {
          const draftConvId = id;
          const shell: ToolResult = {
            fileId: `draft-${draftConvId}`,
            downloadUrl: `draft://${draftConvId}`,
            fileName: `${(draftFromMessages.title ?? 'Document').replace(/\.(docx|doc)$/i, '') || 'Document'}.docx`,
            mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            sizeBytes: displayResult?.sizeBytes ?? 0,
          };
          displayResult = {
            ...(displayResult ?? shell),
            draftHtml: draftFromMessages.html,
            ...(draftFromMessages.title ? { draftTitle: draftFromMessages.title } : {}),
          };
        }

        let previewUrl = resolveSessionDocumentUrl(displayPreviewDoc);
        const draftHtmlTrimmed =
          typeof displayResult?.draftHtml === 'string' ? displayResult.draftHtml.trim() : '';
        const hasRealFileId = Boolean(
          displayResult?.fileId &&
            typeof displayResult.fileId === 'string' &&
            (isFileStorageBackedFileId(displayResult.fileId, displayResult.downloadUrl) ||
              isConversationMongoDocumentFileId(displayResult.fileId)),
        );

        // Detect a "broken draft snapshot" — the server returned a `draft-*`
        // shell currentResult but no recoverable draftHtml (recovery from
        // messages already ran above and produced nothing). This means the
        // last persistSnapshot before the user closed/reloaded was either
        // dropped on the floor or saved before the draft body was populated.
        // If we proceed normally we'd render an empty TipTap editor with a
        // file URL pointing at a non-existent `draft-*` artifact (400/404).
        // Instead, treat the workspace as "no artifact" and surface a soft
        // recovery message — the user's chat history is still preserved.
        const previewDocApiFileId = extractApiFileIdFromUrl((displayPreviewDoc as any)?.downloadUrl);
        const looksLikeSyntheticDraftPreview =
          (
            isSyntheticDraftFileId((displayPreviewDoc as any)?.fileId) &&
            !isFileStorageBackedFileId((displayPreviewDoc as any)?.fileId, (displayPreviewDoc as any)?.downloadUrl)
          ) ||
          (
            isSyntheticDraftFileId(previewDocApiFileId) &&
            !isFileStorageBackedFileId(previewDocApiFileId, (displayPreviewDoc as any)?.downloadUrl)
          );
        const looksLikeOrphanDraftShell =
          !hasRealFileId
          && draftHtmlTrimmed.length === 0
          && (
            isSyntheticDraftFileId(displayResult?.fileId) ||
            isSyntheticDraftFileId(extractApiFileIdFromUrl(displayResult?.downloadUrl)) ||
            looksLikeSyntheticDraftPreview
          );

        if (looksLikeOrphanDraftShell) {
          hydrationDebugLog('[Hydrate] Detected orphan draft-* shell with no recoverable HTML; falling back to empty mode');
          // Strip the draft-* shell currentResult and the bogus preview URL so
          // downstream rendering doesn't try to load `/api/file/draft-…`.
          displayResult = null;
          displayPreviewDoc = null;
          previewUrl = null;
        }

        const wantsDraft =
          !looksLikeOrphanDraftShell
          && (snapshot?.mode === 'drafting' || draftHtmlTrimmed.length > 0);
        const hasWorkspaceArtifact = !looksLikeOrphanDraftShell && Boolean(
          previewUrl || hasRealFileId || draftHtmlTrimmed.length > 0,
        );
        const hasChat = (loaded.messages?.length ?? 0) > 0;
        const explicitTextOnly = deriveExplicitTextOnly({
          hasWorkspaceArtifact,
          looksLikeOrphanDraftShell,
          documentsCount: allDocs.length,
          hasPendingDraft: !!pendingDraft,
          draftStatus,
          hasChat,
        });
        const hasContent = hasWorkspaceArtifact || hasChat;
        const nextMode =
          snapshot?.mode === 'clarifying'
            ? 'clarifying'
            : wantsDraft
              ? 'drafting'
              : looksLikeOrphanDraftShell
                ? 'empty'
                : snapshot?.mode === 'esign' ||
                    snapshot?.mode === 'processing' ||
                    snapshot?.mode === 'review'
                  ? snapshot.mode
                  : hasWorkspaceArtifact
                    ? 'result'
                    : 'empty';
        hydrationDebugLog('[Hydrate] Branch C (snapshot/latestResult merge resolution)', {
          hasWorkspaceArtifact,
          explicitTextOnly,
          docsCount: allDocs.length,
          nextMode,
          draftStatus,
        });


        hydrationDebugLog('[Hydrate] Resolution', {
          previewUrl: previewUrl ?? null,
          nextMode,
          hasContent,
          restoredResultUrl: displayResult?.downloadUrl ?? restoredResult?.downloadUrl ?? null,
          previewDocFileId: (displayPreviewDoc as any)?.fileId ?? null,
          previewDocDownloadUrl: (displayPreviewDoc as any)?.downloadUrl ?? null,
          signedHydrate: !!hydratedSigningSummary,
        });
        hydrationDebugLog('[Hydrate] Resolution details', {
          previewDoc: displayPreviewDoc ? { fileId: (displayPreviewDoc as any).fileId, downloadUrl: (displayPreviewDoc as any).downloadUrl, source: (displayPreviewDoc as any).source } : null,
          previewUrl,
          hasContent,
          nextMode,
          restoredResultUrl: displayResult?.downloadUrl ?? restoredResult?.downloadUrl ?? 'none',
        });

        if (isStaleHydration()) {
          hydrationDebugLog('[Hydrate] Stale resolved session ignored', { id });
          return;
        }
        const pendingVisibleBeforeApply = pendingDocEditVisibleFileRef.current;
        if (
          pendingVisibleBeforeApply &&
          (!pendingVisibleBeforeApply.conversationId || String(pendingVisibleBeforeApply.conversationId) === String(id)) &&
          Date.now() - pendingVisibleBeforeApply.createdAt < PENDING_DOC_EDIT_VISIBLE_TTL_MS
        ) {
          const resolvedFileId =
            displayResult?.fileId ??
            (displayPreviewDoc as { fileId?: string } | null)?.fileId ??
            null;
          console.log('[HydrationEffect] overwrite-doc', {
            fromFileId: resolvedFileId,
            toFileId: pendingVisibleBeforeApply.fileId,
            reason: 'stale-hydration-result-after-doc-edit',
          });
          patchRouteHydrationState(id, {
            attempted: true,
            succeeded: true,
            explicitTextOnly,
            lastFailure: null,
          });
          return;
        }

        const hydratedCotSteps = buildPersistedCotSteps((snapshot as any)?.cotSteps);
        clearWorkspaceSessionState();
        skipNextWorkspaceSnapshotPersistRef.current = true;
        currentConversationIdRef.current = id;
        setCurrentConversationId(id);
        setMessages(Array.isArray(loaded.messages) ? loaded.messages : []);
        setMessagesHasMore(loaded.hasMore);
        setConversationDocuments(allDocs);
        cot.applySnapshot({
          status: hydratedCotSteps.length > 0 ? 'done' : 'idle',
          steps: hydratedCotSteps,
        });
        console.log('[CoT] hydrate-steps', {
          conversationId: id,
          stepCount: hydratedCotSteps.length,
          editBatchCount: editBatchCountFromCotSteps(hydratedCotSteps),
        });
        setCurrentResult(displayResult);
        setLastConversionOutput(signingHydrateLastOutput ?? snapshot?.lastConversionOutput ?? null);
        setMode(nextMode);
        if (wantsDraft) {
          setDraftContent(typeof displayResult?.draftHtml === 'string' ? displayResult.draftHtml : '');
          if (typeof displayResult?.draftTitle === 'string' && displayResult.draftTitle.trim()) {
            setDraftTitle(displayResult.draftTitle);
          }
        }
        if (looksLikeOrphanDraftShell) {
          // Soft signal — chat history is intact, the draft body just isn't.
          // This affects drafts that completed before the dual-persist fix
          // (`toolCall.params.draftHtml`) shipped. Their HTML was only ever
          // held in the session snapshot, and that snapshot was overwritten
          // or never saved due to a network blip during the debounced PATCH.
          // No backend backup exists. New drafts going forward write the
          // body into both the snapshot AND the chat message, so this branch
          // should not fire for anything created from now on.
          toast({
            title: 'This draft can\'t be reopened',
            description:
              'It was created before we strengthened draft saving, and the body wasn\'t fully captured. Your chat history is intact — send the prompt again to regenerate it.',
          });
        }
        setSessionPrimaryDocument(displayPreviewDoc);
        setSessionSourceDocuments(
          snapshot?.sourceDocuments?.length
            ? snapshot.sourceDocuments
            : displayPreviewDoc
              ? [displayPreviewDoc]
              : [],
        );
        setActiveSigningRequest(hydratedSigningSummary);
        const ps = snapshot?.panelState;
        setFocusedDocumentView(ps?.focusedDocumentView ?? false);
        setCanvasViewMode(ps?.canvasViewMode ?? false);
        setChatCollapsed(ps?.chatCollapsed ?? false);
        setRightOpen(ps?.rightPanelOpen ?? false);
        setToolPanelExpanded(ps?.toolPanelExpanded ?? false);
        setPanelSettings((ps?.previewMode || ps?.zoom || ps?.documentPreviewVariant)
          ? {
              ...(ps?.previewMode ? { previewMode: ps.previewMode } : {}),
              ...(ps?.zoom != null ? { zoom: ps.zoom } : {}),
            }
          : {});
        setDocumentPreviewVariant(ps?.documentPreviewVariant ?? 'original');
        setLastTool(
          snapshot?.taskContext?.lastToolName
            ? (snapshot.taskContext.lastToolName as ToolName)
            : loaded.conversation.toolsUsed.length
              ? (loaded.conversation.toolsUsed[loaded.conversation.toolsUsed.length - 1] as ToolName)
              : null,
        );
        // Resolve the best available preview URL:
        // 1. Primary document from snapshot (has fileId + downloadUrl)
        // 2. Latest result download URL (tool output — always has a URL)
        const resultDownloadUrl =
          displayResult?.downloadUrl &&
          (
            isFileStorageBackedFileId(displayResult?.fileId, displayResult.downloadUrl) ||
            isFileStorageBackedFileId(extractApiFileIdFromUrl(displayResult.downloadUrl), displayResult.downloadUrl)
          ) &&
          !isTransientDocumentUrl(displayResult.downloadUrl)
            ? displayResult.downloadUrl
            : null;
        const effectivePreviewUrl = previewUrl
          ?? (resultDownloadUrl
              ? (resultDownloadUrl.startsWith('http')
                  ? resultDownloadUrl
                  : `${conversationApiBase}${resultDownloadUrl}`)
              : null);

        // Append auth token for API file URLs so iframes can authenticate.
        // Iframes can't send Authorization headers — the backend supports ?token= as fallback.
        let authedPreviewUrl = effectivePreviewUrl;
        if (authedPreviewUrl?.includes('/api/file/')) {
          const tk = authGetToken();
          hydrationDebugLog('[Hydrate] Auth token for file URL', {
            tokenPresent: !!tk,
            tokenLength: tk?.length ?? 0,
          });
          if (tk) {
            const sep = authedPreviewUrl.includes('?') ? '&' : '?';
            authedPreviewUrl = `${authedPreviewUrl}${sep}token=${encodeURIComponent(tk)}`;
          }
        }

        if (authedPreviewUrl) {
          setFilePreviewUrl(authedPreviewUrl);
          setFilePreviewSource('restored');
        }

        // Only warn when we expected a file-backed preview (PDF/image blob) but
        // could not resolve a URL. Conversation HTML (`doc-{mongo24}`) is edited
        // in TipTap from `documents[]`; `resolveSessionDocumentUrl` returns null
        // for those ids on purpose — chat + draft still hydrated correctly.
        const previewPrimaryFileId =
          displayPreviewDoc && typeof (displayPreviewDoc as { fileId?: unknown }).fileId === 'string'
            ? String((displayPreviewDoc as { fileId: string }).fileId).trim()
            : '';
        const editorBackedConversationDoc =
          isConversationMongoDocumentFileId(previewPrimaryFileId);
        if (
          !effectivePreviewUrl
          && displayPreviewDoc
          && !editorBackedConversationDoc
          && !wantsDraft
        ) {
          showAppError(
            'Document unavailable',
            'We restored the conversation, but the original working document is not available anymore.',
            6000,
          );
        }
        patchRouteHydrationState(id, {
          succeeded: true,
          explicitTextOnly,
          lastFailure: null,
        });
      } catch (err) {
        if (isStaleHydration()) {
          hydrationDebugLog('[Hydrate] Stale hydrate error ignored', {
            id,
            error: err instanceof Error ? err.message : String(err),
          });
          return;
        }
        patchRouteHydrationState(id, {
          succeeded: false,
          explicitTextOnly: false,
          lastFailure: err instanceof Error ? err.message : String(err),
        });
        console.error('[hydrateFromSessionSnapshot] Failed to restore conversation:', err);
        showAppError(
          'Could not load conversation',
          'Something went wrong restoring this session. Please try again.',
          5000,
        );
      } finally {
        setRestoringConversationId((current) =>
          current != null && String(current) === String(id) ? null : current,
        );
        setWorkspaceRouteBootstrapId((current) =>
          current != null && String(current) === String(id) ? null : current,
        );
        if (hydratingConversationIdRef.current === id && hydrationRequestSeqRef.current === requestSeq) {
          hydratingConversationIdRef.current = null;
        }
      }
    },
    [
      authGetToken,
      clearWorkspaceSessionState,
      conversationApiBase,
      getConversationSession,
      getRouteHydrationState,
      hasWorkspaceDocArtifactForConversation,
      patchRouteHydrationState,
      resolveSessionDocumentUrl,
      resumeDraftStream,
    ],
  );

  /** Build left panel list from project workspaces first, then loose chats. */
  const projectConversationIds = new Set(documentProjects.map((project) => project.primaryConversationId));
  const versionHistoryForPanel: LeftPanelVersionNode[] = [
    ...documentProjects.map((project) => {
      const currentVersion = project.currentVersion ?? null;
      const displayVersionFileName = displayFileNameForProjectVersion(project.title, currentVersion);
      const versionDownloadUrl = documentVersionDownloadUrl(currentVersion, project.primaryConversationId);
      const currentSourceLabel = currentVersion
        ? (currentVersion.source === 'signed' ? 'Signed' : currentVersion.source.replace('_', ' '))
        : null;
      return {
        id: `project:${project.id}`,
        kind: 'project',
        projectId: project.id,
        conversationId: project.primaryConversationId,
        fileName: project.title,
        fileType: 'project',
        operation: currentVersion ? `v${currentVersion.versionNumber} · ${currentSourceLabel}` : 'Project',
        timestamp: new Date(project.lastActivityAt),
        isActive: project.id === currentProjectId || project.primaryConversationId === currentConversationId,
        pinned: false,
        folderId: project.folderId ?? undefined,
        mode: 'result',
        contextLabel: displayVersionFileName,
        versionCount: project.versionCount,
        historyLocked: false,
        statusBadge: currentVersion?.source === 'signed' ? 'signed' : undefined,
        primaryDocument: currentVersion && versionDownloadUrl ? {
          fileId: currentVersion.fileId,
          fileName: displayVersionFileName ?? currentVersion.fileName,
          mimeType: currentVersion.mimeType,
          sizeBytes: 0,
          downloadUrl: versionDownloadUrl,
          source: 'result',
        } : null,
      };
    }),
    ...conversationList
    .filter((c: ConversationListItem) => !c.inTrash && !c.archived && !c.projectId && !projectConversationIds.has(c.id))
    .map((c: ConversationListItem) => ({
      id: c.id,
      kind: 'chat',
      conversationId: c.id,
      fileName: c.title,
      fileType: inferFileTypeFromFileName(c.sessionSnapshot?.primaryDocument?.fileName ?? c.latestResultFileName ?? c.title),
      operation: c.toolsUsed?.length
        ? toolToFriendlyOperation(c.toolsUsed[c.toolsUsed.length - 1] as ToolName)
        : 'Processed',
      timestamp: c.lastMessageAt,
      isActive: c.id === currentConversationId,
      pinned: c.pinned,
      folderId: c.folderId,
      mode: c.sessionSnapshot?.mode ?? null,
      contextLabel: c.sessionSnapshot?.primaryDocument?.fileName ?? c.latestResultFileName ?? undefined,
      historyLocked: c.historyLocked,
      primaryDocument: c.sessionSnapshot?.primaryDocument ?? null,
    })),
  ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  const activeNodeId = currentConversationId;

  const handleRestoreConversation = useCallback(
    async (id: string) => {
      if (currentConversationIdRef.current && String(currentConversationIdRef.current) === String(id)) {
        setRestoringConversationId(null);
      } else {
        setRestoringConversationId(id);
      }
      navigate(conversationWorkspacePath(id));
    },
    [navigate]
  );

  /**
   * When the URL points at conversation B but workspace state still reflects conversation A
   * (common after accept-invite redirect), document refs can block hydration / show the wrong
   * file until a slow fetch completes. Clear immediately so the next hydrate runs clean.
   */
  useEffect(() => {
    if (!routeConversationId) return;
    const showing = currentConversationIdRef.current;
    if (showing != null && String(showing) !== String(routeConversationId)) {
      console.log('[RouteConversationSync] URL conversation changed — clearing stale workspace', {
        routeConversationId,
        wasShowing: showing,
      });
      setRestoringConversationId(routeConversationId);
      hydratingConversationIdRef.current = null;
      mainHydrateAttemptedForRouteRef.current = null;
      hydrateDeferredForRouteRef.current = null;
      cot.reset();
      setNarrowChatForInitialDraftStream(false);
      clearWorkspaceSessionState();
      filePreviewUrlRef.current = null;
      currentResultRef.current = null;
      sessionPrimaryDocumentRef.current = null;
      currentConversationIdRef.current = null;
      setCurrentConversationId(null);
    }
  }, [routeConversationId, clearWorkspaceSessionState, cot]);

  useEffect(() => {
    if (routeConversationId) return;
    if (processingRef.current != null || modeRef.current === 'drafting' || draftStreamAbortRef.current) return;
    if (cot.state.status === 'idle') return;
    cot.reset();
  }, [routeConversationId, cot]);

  useEffect(() => {
    if (
      routeConversationId != null
      && mainHydrateAttemptedForRouteRef.current != null
      && String(mainHydrateAttemptedForRouteRef.current) !== String(routeConversationId)
    ) {
      mainHydrateAttemptedForRouteRef.current = null;
    }
    if (
      routeConversationId != null
      && hydrateDeferredForRouteRef.current != null
      && String(hydrateDeferredForRouteRef.current) !== String(routeConversationId)
    ) {
      hydrateDeferredForRouteRef.current = null;
    }
    const sameRoute =
      currentConversationIdRef.current != null
      && String(currentConversationIdRef.current) === String(routeConversationId);
    const draftStreamInFlightForRoute =
      draftStreamAbortRef.current != null
      && !draftStreamAbortRef.current.signal.aborted
      && draftStatusRef.current !== 'idle'
      && draftStatusRef.current !== 'failed'
      && draftStatusRef.current !== 'complete'
      && (
        draftStreamingConversationIdRef.current != null
          ? String(draftStreamingConversationIdRef.current) === String(routeConversationId)
          : sameRoute
      );
    const hasArtifact = hasWorkspaceDocArtifactForConversation(routeConversationId);
    const routeState = routeConversationId
      ? getRouteHydrationState(routeConversationId)
      : null;
    const pendingDocEditVisible = pendingDocEditVisibleFileRef.current;
    const selectedFileId = activeVisibleDocFileId ?? activeDocFileId ?? currentResultRef.current?.fileId ?? sessionPrimaryDocumentRef.current?.fileId ?? null;
    console.log('[HydrationEffect] Checking:', {
      routeConversationId,
      authLoading: auth.loading,
      currentConvId: currentConversationIdRef.current,
      pathname: location.pathname,
      mode: modeRef.current,
      sameRoute,
      hasArtifact,
      draftStreamInFlightForRoute,
      deferred: hydrateDeferredForRouteRef.current,
      mainAttempted: mainHydrateAttemptedForRouteRef.current,
      routeHydrationState: routeState,
    });
    console.log('[HydrationEffect] ownership-check', {
      routeConversationId,
      currentConversationId: currentConversationIdRef.current,
      currentDocumentId: currentResultRef.current?.fileId ?? sessionPrimaryDocumentRef.current?.fileId ?? null,
      selectedFileId,
      routeOwnsSurface: sameRoute && hasArtifact,
      pendingEditFileId: pendingDocEditVisible?.fileId ?? null,
    });
    if (!routeConversationId) { console.log('[HydrationEffect] Skip: no routeConversationId'); return; }
    if (auth.loading) { console.log('[HydrationEffect] Skip: auth still loading'); return; }
    if (hydratingConversationIdRef.current === routeConversationId) { console.log('[HydrationEffect] Skip: already hydrating this conversation'); return; }
    if (
      pendingDocEditVisible &&
      (!pendingDocEditVisible.conversationId || String(pendingDocEditVisible.conversationId) === String(routeConversationId)) &&
      Date.now() - pendingDocEditVisible.createdAt < PENDING_DOC_EDIT_VISIBLE_TTL_MS
    ) {
      console.log('[HydrationEffect] overwrite-doc', {
        fromFileId: selectedFileId,
        toFileId: pendingDocEditVisible.fileId,
        reason: 'pending-doc-edit-visible-file',
      });
      patchRouteHydrationState(routeConversationId, {
        attempted: true,
        succeeded: true,
        explicitTextOnly: false,
        lastFailure: null,
      });
      return;
    }
    if (sameRoute && draftStreamInFlightForRoute) {
      hydrationDebugLog('[HydrationEffect] Skip: draft stream in-flight for route', {
        routeConversationId,
        draftStatus: draftStatusRef.current,
        draftStreamingConversationId: draftStreamingConversationIdRef.current,
      });
      return;
    }
    if (shouldSkipHydrationForOwnedArtifact({
      sameRoute,
      hasOwnedArtifact: hasArtifact,
      routeState,
    })) {
      console.log('[HydrationEffect] Skip: route already hydrated and owns current document surface');
      return;
    }
    if (shouldSkipHydrationForExplicitTextOnly({
      sameRoute,
      hasOwnedArtifact: hasArtifact,
      messageCount: messagesRef.current.length,
      routeState,
    })) {
      console.log('[HydrationEffect] Skip: explicit text-only conversation already hydrated');
      return;
    }
    if (!sameRoute) setRestoringConversationId(routeConversationId);
    patchRouteHydrationState(routeConversationId, { attempted: true });
    mainHydrateAttemptedForRouteRef.current = routeConversationId;
    console.log('[HydrationEffect] Hydrating conversation:', routeConversationId);
    hydrateFromSessionSnapshot(routeConversationId);
  }, [
    auth.loading,
    activeDocFileId,
    activeVisibleDocFileId,
    getRouteHydrationState,
    hasWorkspaceDocArtifactForConversation,
    hydrateFromSessionSnapshot,
    patchRouteHydrationState,
    routeConversationId,
  ]);

  /** If hydrate was deferred while a query ran on the same conversation, retry once the workspace is idle. */
  useEffect(() => {
    if (!routeConversationId || auth.loading) return;
    if (processing != null || activeQueryRunId != null) return;
    const deferred = hydrateDeferredForRouteRef.current;
    if (deferred == null || String(deferred) !== String(routeConversationId)) return;
    hydrateDeferredForRouteRef.current = null;
    if (hydratingConversationIdRef.current === routeConversationId) return;
    if (hasWorkspaceDocArtifactForConversation(routeConversationId)) return;
    hydrateFromSessionSnapshot(routeConversationId);
  }, [processing, activeQueryRunId, routeConversationId, auth.loading, hydrateFromSessionSnapshot, hasWorkspaceDocArtifactForConversation]);

  useEffect(() => {
    if (!currentConversationId) return;
    if (hydratingConversationIdRef.current) return;
    const streamingBlocksSnapshot =
      draftStreamingConversationId != null
      && draftStreamingConversationId === currentConversationId;
    if (streamingBlocksSnapshot) return;
    if (skipNextWorkspaceSnapshotPersistRef.current) {
      skipNextWorkspaceSnapshotPersistRef.current = false;
      return;
    }
    const snapshot = buildWorkspaceSessionSnapshot();
    const timer = window.setTimeout(() => {
      persistConversationSnapshot(currentConversationId, snapshot).catch(() => {});
    }, 400);
    return () => window.clearTimeout(timer);
  }, [buildWorkspaceSessionSnapshot, currentConversationId, draftStreamingConversationId, persistConversationSnapshot]);

  /**
   * Editor-driven persistence for document-backed drafts.
   *
   * When `currentResult.kind === 'document'`, the canonical body lives
   * in `Conversation.documents[]` on the server. This effect debounces
   * a PATCH to that document so user edits land in the canonical store
   * and not just in the snapshot blob. Without this, editing a draft
   * would update the snapshot's `draftHtml` but leave the document
   * body stale — meaning a reload would render the older version.
   *
   * Runs in parallel to the snapshot-persist effect above; cheap
   * because the BE is the same Conversation document either way.
   */
  const updateConversationDocument = conversations.updateConversationDocument;
  useEffect(() => {
    if (!currentConversationId) return;
    if (hydratingConversationIdRef.current) return;
    if (mode !== 'drafting') return;
    if (currentResult?.kind !== 'document') return;
    const docId = currentResult.documentId;
    if (!docId) return;
    const streamingBlocksDocPersist =
      draftStreamingConversationId != null
      && draftStreamingConversationId === currentConversationId;
    if (streamingBlocksDocPersist) return; // BE owns persistence during the stream
    const html = draftContent;
    if (typeof html !== 'string' || !html.length) return;
    const timer = window.setTimeout(() => {
      void updateConversationDocument(currentConversationId, docId, {
        html,
        ...(typeof draftTitle === 'string' && draftTitle.trim() ? { title: draftTitle.trim() } : {}),
      });
    }, 600);
    return () => window.clearTimeout(timer);
  }, [
    currentConversationId,
    currentResult?.kind,
    currentResult?.documentId,
    draftContent,
    draftTitle,
    draftStreamingConversationId,
    mode,
    updateConversationDocument,
  ]);

  const handleLoadEarlierMessages = useCallback(async () => {
    const cid = currentConversationIdRef.current;
    if (!cid || loadingEarlierRef.current) return;
    const oldestId = messagesRef.current[0]?.id;
    if (!oldestId) return;
    loadingEarlierRef.current = true;
    setLoadingEarlierMessages(true);
    try {
      const { messages: older, hasMore } = await getConversationMessages(cid, {
        before: oldestId,
        limit: 50,
      });
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        const prefix = older.filter((m) => !seen.has(m.id));
        return [...prefix, ...prev];
      });
      setMessagesHasMore(hasMore);
    } catch {
      /* Global axios interceptor shows the error banner */
    } finally {
      loadingEarlierRef.current = false;
      setLoadingEarlierMessages(false);
    }
  }, [getConversationMessages]);

  const handleClearHistory = useCallback(() => {
    clearStoredConversations();
    setCurrentConversationId(null);
    clearWorkspaceSessionState();
    setMode('empty');
    clearHistory();
    navigate(APP_WORKSPACE_PATH, { replace: true });
  }, [clearStoredConversations, clearWorkspaceSessionState, clearHistory, navigate]);

  const handleDeleteConversation = useCallback(async (id: string) => {
    await deleteConversation(id).catch(() => {});
    if (id === currentConversationId) {
      setCurrentConversationId(null);
      clearWorkspaceSessionState();
      setMode('empty');
      navigate(APP_WORKSPACE_PATH, { replace: true });
    }
  }, [clearWorkspaceSessionState, currentConversationId, deleteConversation, navigate]);

  const handleStarConversation = useCallback(async (id: string, pinned: boolean) => {
    await updateConversation(id, { pinned }).catch(() => {});
  }, [updateConversation]);

  const handleRenameConversation = useCallback(
    async (id: string) => {
      const c = conversationList.find((x) => x.id === id);
      const current = c?.title ?? '';
      const next = window.prompt('Rename conversation', current);
      if (next == null) return;
      const trimmed = next.trim();
      if (!trimmed) return;
      await updateConversation(id, { title: trimmed }).catch(() => {});
    },
    [conversationList, updateConversation],
  );


  const handleArchiveConversation = useCallback(async (id: string, archived: boolean) => {
    await updateConversation(id, { archived }).catch(() => {});
    if (archived && id === currentConversationId) {
      setCurrentConversationId(null);
      clearWorkspaceSessionState();
      setMode('empty');
      navigate(APP_WORKSPACE_PATH, { replace: true });
    }
  }, [clearWorkspaceSessionState, currentConversationId, navigate, updateConversation]);

  const handleESignConfirm = useCallback(
    async (placedFields: SignatureField[]) => {
      await executeESignFields(placedFields);
    },
    [executeESignFields]
  );

  const advanceSignerFieldQueue = useCallback((updated: SignatureField[], completedIndex: number) => {
    const nextIdx = updated.findIndex((f, i) => i > completedIndex && f.signer !== 'other' && !f.placed);
    setEsignActiveFieldIndex(nextIdx >= 0 ? nextIdx : -1);
    const yourFields = updated.filter((f) => f.signer !== 'other');
    const allSigned = yourFields.length > 0 && yourFields.every((f) => f.placed);
    if (allSigned) {
      setTimeout(() => {
        void handleESignConfirm(updated);
      }, 600);
    }
  }, [handleESignConfirm]);

  const hasSelfSigningMarkers = useMemo(
    () => esignFields.some((field) => field.party !== 'other' && field.signer !== 'other'),
    [esignFields],
  );

  const handleApplyReviewedSignature = useCallback(() => {
    const fields = esignFieldsRef.current;
    if (!fields.some((field) => field.party !== 'other' && field.signer !== 'other')) {
      toast({
        title: 'Place a signature marker first',
        description: 'Use the Signature chip below, then click the document where your signature belongs.',
      });
      return;
    }
    if (tryOpenSelfTitleModal(fields)) {
      toast({
        title: 'Title or role needed',
        description:
          'Enter how this line should read on the PDF (for example CEO), then Save. Your changes will apply right after.',
      });
      return;
    }
    setMessages((prev) => [...prev, {
      id: crypto.randomUUID(),
      role: 'user' as const,
      content: 'Apply changes',
      timestamp: Date.now(),
    }]);
    void executeESignFields(fields);
  }, [tryOpenSelfTitleModal, executeESignFields, toast]);

  const normalizeEsignSignerIndex = useCallback((signerIndex: number) => {
    if (signerIndex <= 0) return 0;
    return Math.max(1, Math.min(signerIndex, Math.max(1, esignSigners.length - 1)));
  }, [esignSigners.length]);

  const handleSigningGuideSelectSigner = useCallback((signerIndex: number) => {
    setEsignActiveSignerIndex(normalizeEsignSignerIndex(signerIndex));
  }, [normalizeEsignSignerIndex]);

  const handleSigningGuidePlaceField = useCallback((type: PlacementType, signerIndex: number) => {
    const normalizedSignerIndex = normalizeEsignSignerIndex(signerIndex);
    setMode('esign');
    setEsignActiveSignerIndex(normalizedSignerIndex);
    setEsignPlacementMode(type);
  }, [normalizeEsignSignerIndex]);

  const selfSigningMarkerCount = useMemo(
    () => esignFields.filter((field) => field.party !== 'other' && field.signer !== 'other').length,
    [esignFields],
  );

  const selfSignatureApplied = useMemo(
    () => esignFields.some((field) => field.party !== 'other' && field.signer !== 'other' && (field.placed || field.signedAt)),
    [esignFields],
  );

  const recipientSigningMarkerCount = useMemo(
    () => esignFields.filter(isRecipientPlacementField).length,
    [esignFields],
  );
  const currentResultSignableFileId = isFileStorageBackedFileId(currentResult?.fileId, currentResult?.downloadUrl)
    ? currentResult?.fileId ?? null
    : null;
  const signableFileIdForSending =
    currentResultSignableFileId
    ?? activeVisibleDocFileId
    ?? activeDocFileId
    ?? extractApiFileIdFromUrl(filePreviewUrl);

  const handleAddSigningRecipientEmail = useCallback((email: string) => {
    const normalized = email.trim().toLowerCase();
    if (!normalized || !normalized.includes('@')) return;
    setEsignRecipientDrafts((prev) =>
      prev.some((item) => item.toLowerCase() === normalized) ? prev : [...prev, normalized],
    );
    setEsignActiveSignerIndex((current) => (current > 0 ? current : 1));
  }, []);

  const handleRemoveSigningRecipientEmail = useCallback((email: string) => {
    const normalized = email.trim().toLowerCase();
    setEsignRecipientDrafts((prev) => prev.filter((item) => item.toLowerCase() !== normalized));
    setEsignFields((prev) =>
      prev.filter((field) => (field.signedByEmail ?? '').trim().toLowerCase() !== normalized),
    );
    setEsignActiveSignerIndex(0);
    setEsignPlacementMode(null);
  }, []);

  const signingGuideNode = useMemo(() => {
    if (mode !== 'esign' || activeSigningRequest) return null;
    return (
      <SigningGuideCard
        signers={esignSigners}
        activeSignerIndex={esignActiveSignerIndex}
        placementMode={esignPlacementMode}
        selfMarkerCount={selfSigningMarkerCount}
        selfApplied={selfSignatureApplied}
        recipientMarkerCount={recipientSigningMarkerCount}
        sendDisabled={!signableFileIdForSending}
        onSelectSigner={handleSigningGuideSelectSigner}
        onPlaceField={handleSigningGuidePlaceField}
        onCancelPlacement={() => setEsignPlacementMode(null)}
        onAddRecipientEmail={handleAddSigningRecipientEmail}
        onRemoveRecipientEmail={handleRemoveSigningRecipientEmail}
        onSendForSignature={openSendForSignatureModal}
      />
    );
  }, [
    activeSigningRequest,
    currentResultSignableFileId,
    esignActiveSignerIndex,
    esignPlacementMode,
    esignSigners,
    handleAddSigningRecipientEmail,
    handleRemoveSigningRecipientEmail,
    handleSigningGuidePlaceField,
    handleSigningGuideSelectSigner,
    mode,
    openSendForSignatureModal,
    recipientSigningMarkerCount,
    signableFileIdForSending,
    selfSignatureApplied,
    selfSigningMarkerCount,
  ]);

  const esignCompactHeaderTrailing = useMemo(() => {
    if (mode !== 'esign' || activeSigningRequest) return undefined;
    if (selfSigningMarkerCount === 0 || selfSignatureApplied) return undefined;
    return (
      <button
        type="button"
        onClick={() => void handleApplyReviewedSignature()}
        title="Apply your saved signature to the markers you placed"
        style={{
          border: 'none',
          borderRadius: 6,
          padding: '5px 12px',
          fontSize: 11,
          fontWeight: 700,
          fontFamily: 'inherit',
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          flexShrink: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          backgroundColor: '#1B7A4B',
          color: '#FFFFFF',
        }}
      >
        Apply changes
      </button>
    );
  }, [activeSigningRequest, handleApplyReviewedSignature, mode, selfSignatureApplied, selfSigningMarkerCount]);

  // ─── Multi-party signing handlers ───────────────────────────────
  const handleSendForSignature = useCallback(async (data: SendForSignatureData) => {
    if (!signableFileIdForSending) return;
    setSendForSignatureSending(true);
    try {
      setEsignRecipientDrafts(data.signerEmails.map((email) => email.trim().toLowerCase()).filter(Boolean));
      const recipientFields = esignFields
        .filter(isRecipientPlacementField)
        .map((field) => {
          let idx = field.assignedSignerIndex;
          if (typeof idx === 'number' && idx >= 1) {
            idx = idx - 1;
          }
          return {
            ...field,
            party: 'other' as const,
            signer: 'other' as const,
            placed: false,
            signedAt: undefined,
            assignedSignerIndex: idx,
          };
        });
      if (recipientFields.length === 0) {
        toast({
          variant: 'destructive',
          title: 'Add recipient signing spots first',
          description:
            'Use the signing guide to place at least one field for the recipient, then send the invite.',
        });
        setSendForSignatureSending(false);
        return;
      }
      const token = auth.getToken();
      const signerSet = new Set(data.signerEmails.map((e) => e.toLowerCase()));
      const inviteCcFiltered = data.ccEmails.filter((e) => !signerSet.has(e.toLowerCase()));
      const resolveSignerName = (email: string) => {
        const key = email.trim().toLowerCase();
        const raw = data.signerDisplayNamesByEmail?.[key]?.trim();
        return raw && raw.length > 0 ? raw : guessNameFromEmail(email);
      };
      const signersPayload = data.signerEmails.map((email) => ({
        email,
        name: resolveSignerName(email),
        role: data.signerRole,
      }));
      const res = await axios.post('/api/signing', {
        documentFileId: signableFileIdForSending,
        signers: signersPayload,
        fields: recipientFields,
        message: data.message,
        expiresInDays: data.expiresInDays,
        requireEmailVerification: data.requireVerification,
        ...(inviteCcFiltered.length > 0 ? { inviteCc: inviteCcFiltered } : {}),
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setActiveSigningRequest(res.data);
      setSendForSignatureOpen(false);
      // Capture the placeholder values the user filled (typed or AI-suggested)
      // so the resulting workflow can replay the same fill step on future
      // recipients. The store keys are fieldIds; the workflow factory needs
      // CapturedPlaceholderValue[].
      const baselineSnapshot = signableFileIdForSending
        ? snapshotForBackend(signableFileIdForSending)
        : {};
      const capturedBaseline: import('@corner/shared').CapturedPlaceholderValue[] =
        Object.entries(baselineSnapshot).map(([fieldId, value]) => ({
          fieldId,
          value,
          source: 'user',
        }));
      const recipientSummary =
        data.signerEmails.length === 1
          ? `${resolveSignerName(data.signerEmails[0])} (${data.signerEmails[0]})`
          : `${data.signerEmails.length} recipients (${data.signerEmails.join(', ')})`;
      const failedInviteEmails = Array.isArray(res.data?.signers)
        ? res.data.signers
            .filter((signer: { email?: string; emailStatus?: string }) => signer.emailStatus === 'failed' && signer.email)
            .map((signer: { email: string }) => signer.email)
        : [];
      const inviteMessage = failedInviteEmails.length > 0
        ? `Signing request created, but invite delivery failed for ${failedInviteEmails.join(', ')}. Use Resend signing link from the status panel.`
        : `Signing invite sent to ${recipientSummary}. You'll be notified when they sign.`;
      setMessages((prev) => [...prev, {
        id: crypto.randomUUID(), role: 'corner' as const,
        content: inviteMessage,
        timestamp: Date.now(),
        contractSendCapture: {
          signingRequestId: res.data?.requestId,
          documentName: res.data?.documentName,
          recipientCount: data.signerEmails.length,
          capturedBaseline,
          signerRole: data.signerRole,
        },
      }]);
      if (failedInviteEmails.length > 0) {
        toast({
          variant: 'destructive',
          title: 'Some invites were not delivered',
          description: failedInviteEmails.join(', '),
        });
      }
    } catch (err) {
      const e = getActionError('send signing request', err);
      toast({ variant: 'destructive', title: e.title, description: e.description });
    } finally {
      setSendForSignatureSending(false);
    }
  }, [auth, esignFields, signableFileIdForSending, toast]);

  const handleRefreshSigningRequest = useCallback(async () => {
    if (!activeSigningRequest) return;
    try {
      const token = auth.getToken();
      const res = await axios.get(`/api/signing/${activeSigningRequest.requestId}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      ingestSigningRequestDetail(res.data);
    } catch { /* ignore */ }
  }, [activeSigningRequest, auth, ingestSigningRequestDetail]);

  const handleSendReminder = useCallback(async (signerEmail: string) => {
    if (!activeSigningRequest) return;
    try {
      const token = auth.getToken();
      await axios.post(`/api/signing/${activeSigningRequest.requestId}/reminder/${signerEmail}`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      toast({ title: 'Reminder sent', description: `Sent to ${signerEmail}` });
    } catch (err) {
      const e = getActionError('send reminder', err);
      toast({ variant: 'destructive', title: e.title, description: e.description });
    }
  }, [activeSigningRequest, auth]);

  const handleResendLink = useCallback(async (signerEmail: string) => {
    if (!activeSigningRequest) return;
    try {
      const token = auth.getToken();
      await axios.post(`/api/signing/${activeSigningRequest.requestId}/resend/${signerEmail}`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      toast({ title: 'Link resent', description: `New signing link sent to ${signerEmail}` });
    } catch (err) {
      const e = getActionError('resend signing link', err);
      toast({ variant: 'destructive', title: e.title, description: e.description });
    }
  }, [activeSigningRequest, auth]);

  const handleRevokeSigningRequest = useCallback(async () => {
    if (!activeSigningRequest) return;
    try {
      const token = auth.getToken();
      await axios.post(`/api/signing/${activeSigningRequest.requestId}/revoke`, {}, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      setActiveSigningRequest((prev) => prev ? { ...prev, status: 'revoked' } : null);
      toast({ title: 'Signing request revoked', description: 'Signers can no longer access this document.' });
    } catch (err) {
      const e = getActionError('revoke signing request', err);
      toast({ variant: 'destructive', title: e.title, description: e.description });
    }
  }, [activeSigningRequest, auth]);

  const handleDownloadSignedCopy = useCallback(async () => {
    if (!activeSigningRequest) return;
    try {
      const token = auth.getToken();
      const res = await axios.get(`/api/signing/${activeSigningRequest.requestId}/download`, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `signed_${activeSigningRequest.documentName}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      const e = getActionError('download signed document', err);
      toast({ variant: 'destructive', title: e.title, description: e.description });
    }
  }, [activeSigningRequest, auth]);

  const handleDownloadSignedCopyDocx = useCallback(async () => {
    if (!activeSigningRequest) return;
    try {
      const token = auth.getToken();
      const res = await axios.get(`/api/signing/${activeSigningRequest.requestId}/download-docx`, {
        responseType: 'blob',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const base =
        activeSigningRequest.documentName.replace(/\.(pdf|docx)$/i, '').trim() || 'document';
      a.download = `signed_${base}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      const e = getActionError('download Word copy', err);
      toast({ variant: 'destructive', title: e.title, description: e.description });
    }
  }, [activeSigningRequest, auth]);

  // E-sign field-by-field: use the saved account signature, never a fresh client payload.
  const openStoredSignatureVerification = useCallback((fieldIndex: number) => {
    if (!auth.getToken()) {
      toast({ variant: 'destructive', title: 'Sign in required', description: 'Sign in to use your saved signature.' });
      return;
    }
    if (!auth.user?.signatureId) {
      toast({ variant: 'destructive', title: 'No signature saved', description: 'Add a signature in Settings or finish onboarding first.' });
      return;
    }

    const targetField = esignFields[fieldIndex];
    if (targetField?.party === 'other' || targetField?.signer === 'other') {
      toast({
        title: 'Not your marker',
        description: "Those markers are not for you — they're for the recipient.",
      });
      return;
    }
    if (targetField && isTitleMarkerField(targetField)) {
      if (!String(targetField.printedText ?? '').trim()) {
        setPrintedTitleModal({
          fieldId: targetField.id ?? '',
          initial: '',
          label: targetField.label,
          markPlacedOnSave: true,
        });
        return;
      }
      const updated = esignFields.map((f, i) =>
        i === fieldIndex ? { ...f, placed: true, signedAt: Date.now() } : f,
      );
      setEsignFields(updated);
      advanceSignerFieldQueue(updated, fieldIndex);
      return;
    }

    // Pre-sign warning: if any inline-placeholder text fields remain unfilled
    // (no value in the live overlay store), prompt the user before locking in
    // the signature. They can fill them now, assign to a recipient, or sign
    // as-is.
    const docKey =
      lastConversionOutput?.sourceFileId
      ?? lastConversionOutput?.fileId
      ?? sessionPrimaryDocument?.fileId
      ?? null;
    if (docKey) {
      const filledMap = getPlaceholderValues(docKey);
      const unfilled = esignFields
        .filter((f) => f.type === 'text' && f.placeholderStyle && f.placeholderStyle !== 'underline')
        .filter((f) => !filledMap[f.id ?? ''])
        .map((f) => ({
          fieldId: f.id ?? '',
          label: f.label,
          page: f.page,
          placeholderRaw: f.placeholderRaw,
        }));
      if (unfilled.length > 0) {
        pendingSignFieldIndexRef.current = fieldIndex;
        setUnfilledModalEntries(unfilled);
        setUnfilledModalOpen(true);
        return;
      }
    }

    setEsignActiveFieldIndex(fieldIndex);
    setEsignVerifyOpen(true);
  }, [auth, esignFields, lastConversionOutput, sessionPrimaryDocument, advanceSignerFieldQueue]);

  const handleUnfilledFillNow = useCallback((firstFieldId: string) => {
    setUnfilledModalOpen(false);
    pendingSignFieldIndexRef.current = null;
    // The viewer's FieldMarker opens its inline editor on click; we just need
    // to scroll to + activate the first unfilled placeholder. Reuse the existing
    // active-field machinery — the field-marker's own click handler will open
    // the popover when the user clicks it.
    const idx = esignFields.findIndex((f) => f.id === firstFieldId);
    if (idx >= 0) {
      setEsignActiveFieldIndex(idx);
    }
  }, [esignFields]);

  const handleUnfilledAssign = useCallback(() => {
    setUnfilledModalOpen(false);
    pendingSignFieldIndexRef.current = null;
    // Stub: open the configure flow so the user can reassign placeholder
    // ownership. Today we just toast — multi-party placeholder assignment is
    // a follow-up from the live-fill milestone.
    toast({
      title: 'Not yet supported',
      description: 'Per-placeholder recipient assignment is coming soon. For now, fill them yourself or sign as-is.',
    });
  }, []);

  const handleUnfilledSignAnyway = useCallback(() => {
    const idx = pendingSignFieldIndexRef.current;
    pendingSignFieldIndexRef.current = null;
    setUnfilledModalOpen(false);
    if (idx != null && idx >= 0) {
      setEsignActiveFieldIndex(idx);
      setEsignVerifyOpen(true);
    }
  }, []);

  const handleEsignFieldConfirm = useCallback(() => {
    setEsignVerifyOpen(false);
    if (esignActiveFieldIndex >= 0) {
      const updated = esignFields.map((f, i) =>
        i === esignActiveFieldIndex ? { ...f, placed: true, signedAt: Date.now() } : f,
      );
      setEsignFields(updated);
      advanceSignerFieldQueue(updated, esignActiveFieldIndex);
    }
  }, [esignActiveFieldIndex, esignFields, advanceSignerFieldQueue]);

  const handleUndo = useCallback(() => {
    // nodes is newest-last; undo goes to second-to-last
    if (nodes.length > 1) {
      handleVersionRestore(nodes[nodes.length - 2]);
    } else {
      setMode('empty');
      setCurrentResult(null);
    }
  }, [nodes, handleVersionRestore]);

  // Keep center workspace view in sync with Preview tab view mode
  useEffect(() => {
    const mode = panelSettings.previewMode;
    if (!mode) return;
    if (mode === 'frames') {
      setCanvasViewMode(true);
    } else if (mode === 'page' || mode === 'text') {
      setCanvasViewMode(false);
    }
  }, [panelSettings.previewMode]);

  /** Paper 5A / 2A5: topbar shows active document name (preview, output, or saved result) */
  const topbarFileName = useMemo(() => {
    // Draft mode: show draft title
    if (mode === 'drafting' && draftTitle) return draftTitle;
    if (documentPreviewVariant === 'output' && lastConversionOutput?.fileName) {
      return lastConversionOutput.fileName;
    }
    // QR: show the payload as the topbar name (Paper 64J-0)
    if (currentResult?.qrMeta?.payload) return currentResult.qrMeta.payload;
    if (currentResult?.fileName) return currentResult.fileName;
    if (currentFile?.name) return currentFile.name;
    return undefined;
  }, [
    mode,
    draftTitle,
    documentPreviewVariant,
    lastConversionOutput?.fileName,
    currentResult?.qrMeta?.payload,
    currentResult?.fileName,
    currentFile?.name,
  ]);

  // Keep this below topbarFileName. In production builds, referencing a later
  // const from a hook dependency array can trigger a TDZ crash after login.
  const handleLaunchClauseReview = useCallback(async (lane: ClauseFlagLane, jurisdiction?: string) => {
    const pending = clauseReviewPendingSourceRef.current;
    const fileId =
      pending?.fileId
      ?? currentResult?.sourceFileId
      ?? currentResult?.fileId
      ?? sessionPrimaryDocument?.fileId;
    const sourceKind = pending?.sourceKind ?? (fileId ? clauseReviewSourceKindForId(fileId) : 'file');
    clauseReviewPendingSourceRef.current = null;
    const titleForReview = pending?.title ?? topbarFileName ?? currentFile?.name ?? 'Document';
    clauseFlagsLog('run-start', {
      conversationId: currentConversationId,
      documentId: fileId,
      versionId: effectiveActiveVersionId,
      persona: lane,
      state: jurisdiction ?? null,
      source: pending ? 'pending-ref' : currentResult?.sourceFileId ? 'sourceFileId' : currentResult?.fileId ? 'resultFileId' : sessionPrimaryDocument?.fileId ? 'primaryDoc' : 'none',
    });
    if (!auth.getToken()) {
      toast({ variant: 'destructive', title: 'Sign in required', description: 'Sign in to run a clause review.' });
      return;
    }
    if (!fileId || !isClauseReviewRunnableSourceId(fileId)) {
      toast({ variant: 'destructive', title: 'No document loaded', description: 'Open a PDF or Word version of this project document, then run clause review.' });
      return;
    }
    setClauseReviewLoading(true);
    setClauseReviewSetupOpen(false);
    setProjectWorkspaceTab('documents');
    setMode('review');
    review.startGenerating([], () => {});

    try {
      const result = await clauseReviewApi.createClauseReview({
        sourceKind,
        sourceId: fileId,
        title: titleForReview,
        lane,
        jurisdiction: jurisdiction || undefined,
      });
      setClauseReviewResult(result);
      // Refresh risk summary so the AnalysisCard badge updates
      clauseReviewApi.getRiskSummary(fileId)
        .then((s) => { setRiskSummary(s ?? null); riskSummaryFetchedRef.current = fileId; })
        .catch(() => {});

      const suggestions = result.flags.map(clauseFlagToReviewSuggestion);
      review.loadSuggestions(suggestions);
      clauseFlagsLog('parsed', {
        clauseReviewId: result.clauseReviewId,
        flagCount: suggestions.length,
        persona: result.lane,
        state: result.jurisdiction,
      });
    } catch (err) {
      const e = getActionError('run clause review', err);
      toast({ variant: 'destructive', title: e.title, description: e.description });
      review.triggerGeneratingError(e.description, 'parse');
    } finally {
      setClauseReviewLoading(false);
    }
  }, [currentConversationId, currentResult, effectiveActiveVersionId, auth, topbarFileName, currentFile, review, sessionPrimaryDocument, toast]);

  const handleApplyClauseReviewToWorkspace = useCallback(async () => {
    const acceptedEdits = review.suggestions.filter(
      (s) => s.status === 'accepted' && !s.appliedToDocument,
    );
    if (acceptedEdits.length === 0) {
      toast({
        variant: 'destructive',
        title: 'Nothing to apply',
        description: 'Accept at least one unapplied suggestion first.',
      });
      return;
    }
    const token = auth.getToken();
    if (!token || !currentConversationId) {
      toast({ variant: 'destructive', title: 'Sign in required', description: 'Sign in to save revisions to your workspace.' });
      return;
    }

    const conversationDocumentId =
      currentResult?.documentId ??
      (isConversationMongoDocumentFileId(currentResult?.fileId) ? currentResult.fileId.slice(4) : null) ??
      (isConversationMongoDocumentFileId(sessionPrimaryDocument?.fileId) ? sessionPrimaryDocument?.fileId?.slice(4) ?? null : null);
    if (conversationDocumentId && currentResult?.draftHtml?.trim()) {
      setClauseReviewApplyBusy(true);
      try {
        const actions = acceptedEdits
          .map(buildHtmlEditActionFromReviewSuggestion)
          .filter((action) => (
            (action.kind === 'replace' && action.originalText.trim() && action.newText.trim()) ||
            (action.kind === 'delete' && action.originalText.trim()) ||
            ((action.kind === 'insertBefore' || action.kind === 'insertAfter') && action.anchorText.trim() && action.newText.trim())
          ));
        if (actions.length === 0) {
          throw new Error('Accepted suggestions do not include enough exact text to apply automatically.');
        }
        const batchRes = await axios.post<{ editBatch: EditBatch }>(
          '/api/document-edits/html/batches',
          {
            conversationId: currentConversationId,
            documentId: conversationDocumentId,
            ...(currentResult?.documentVersion != null ? { documentVersion: currentResult.documentVersion } : {}),
            draftTitle: currentResult?.draftTitle || draftTitle || topbarFileName || 'Document',
            actions,
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const applied = await axios.post<HtmlEditApplyResult>(
          `/api/document-edits/html/batches/${batchRes.data.editBatch.id}/apply`,
          {
            conversationId: currentConversationId,
            documentId: conversationDocumentId,
            ...(currentResult?.documentVersion != null ? { documentVersion: currentResult.documentVersion } : {}),
            acceptedEditIds: actions.map((action) => action.id),
          },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        const failed = new Set(applied.data.failedIds ?? []);
        for (const suggestion of acceptedEdits) {
          if (failed.has(suggestion.id)) review.markSuggestionApplyFailed(suggestion.id, 'Matching text was not found in the current document.');
        }
        review.markSuggestionsAppliedToDocument(applied.data.appliedIds ?? []);
        handleEditBatchVersionReady(applied.data.editBatch, applied.data);
        toast({
          title: failed.size > 0 ? 'Some suggestions applied' : 'Suggestions applied',
          description: failed.size > 0
            ? `${acceptedEdits.length - failed.size} suggestion(s) were saved in a new version. ${failed.size} need manual review.`
            : 'New document version created and opened.',
        });
        if (failed.size === 0) {
          review.reset();
          setClauseReviewResult(null);
          setFlagGroundingStatuses({});
        }
      } catch (err) {
        const e = getActionError('apply clause revisions', err);
        toast({ variant: 'destructive', title: e.title, description: e.description });
      } finally {
        setClauseReviewApplyBusy(false);
      }
      return;
    }

    const docText = currentResult?.textContent;
    if (!docText?.trim()) {
      toast({
        variant: 'destructive',
        title: 'No document text',
        description: 'Open reader view or use a document with extracted text before applying revisions.',
      });
      return;
    }

    const patches = acceptedEdits.map(buildClausePatchFromSuggestion);
    clauseFlagsLog('apply-all', {
      suggestionIds: patches.map((patch) => patch.suggestionId),
      documentId: currentResult?.fileId ?? sessionPrimaryDocument?.fileId ?? null,
      versionId: effectiveActiveVersionId,
    });
    const patchResult = applyClausePatches(docText, patches);
    for (const failure of patchResult.failures) {
      review.markSuggestionApplyFailed(failure.suggestionId, failure.reason);
      clauseFlagsLog('apply-failed', {
        suggestionId: failure.suggestionId,
        reason: failure.reason,
        selectorInfo: {
          kind: failure.patch.kind,
          hasOriginalText: Boolean(failure.patch.originalText),
          hasAnchorText: Boolean(failure.patch.anchorText),
        },
        documentId: currentResult?.fileId ?? sessionPrimaryDocument?.fileId ?? null,
        versionId: effectiveActiveVersionId,
      });
    }
    if (patchResult.appliedIds.length === 0) {
      toast({
        variant: 'destructive',
        title: 'No suggestions applied',
        description: 'Corner could not find the matching text in the current document. Failed cards are marked in the review panel.',
      });
      return;
    }
    const revised = patchResult.text;
    const baseName = (currentResult?.fileName ?? currentFile?.name ?? 'Document').replace(/\.[^.]+$/, '');
    const outLabel = `${baseName} (Clause revisions)`;
    setClauseReviewApplyBusy(true);
    try {
      const res = await axios.post(
        '/api/export-text',
        { text: revised, format: 'docx', fileName: outLabel },
        { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } },
      );
      const file = new File([res.data], `${outLabel}.docx`, {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      });
      const uploaded = await projectApi.uploadProjectSeedFile(file, auth.getToken);

      let serverProject =
        activeDocumentProject && !String(activeDocumentProject.id).startsWith('local:')
          ? activeDocumentProject
          : null;

      const baseFileId = currentResult?.fileId ?? sessionPrimaryDocument?.fileId;
      if (!serverProject && baseFileId) {
        const summary = await projectApi.ensureProject(
          {
            conversationId: currentConversationId,
            title: topbarFileName ?? activeDocumentProject?.title ?? 'Document',
            folderId: currentConversationFolderId ?? null,
            file: {
              fileId: baseFileId,
              fileName: currentResult?.fileName ?? sessionPrimaryDocument?.fileName ?? 'Document',
              mimeType: currentResult?.mimeType ?? sessionPrimaryDocument?.mimeType ?? 'application/pdf',
            },
            source: 'original',
            changeSummary: 'Original document.',
          },
          auth.getToken,
        );
        serverProject = await projectApi.getProject(summary.id, auth.getToken);
      }

      if (!serverProject) {
        toast({
          variant: 'destructive',
          title: 'No project to attach',
          description: 'Load a document in this conversation first.',
        });
        return;
      }

      const nextProject = await projectApi.createProjectVersion(
        serverProject.id,
        {
          fileId: uploaded.fileId,
          fileName: uploaded.fileName,
          mimeType: uploaded.mimeType,
          source: 'ai_edit',
          changeSummary: `Clause review: ${acceptedEdits.length} revision(s) applied as Word document.`,
          conversationId: currentConversationId,
          createdFromVersionId: serverProject.currentVersionId ?? null,
        },
        auth.getToken,
      );

      setActiveDocumentProject(nextProject);
      setProjectVersionTreeRevision((revision) => revision + 1);
      void refreshDocumentProjects();

      const newVersion =
        nextProject.currentVersion ?? nextProject.versions.find((v) => v.fileId === uploaded.fileId) ?? null;

      review.markSuggestionsAppliedToDocument(patchResult.appliedIds);
      const hadFailures = patchResult.failures.length > 0;
      if (!hadFailures) {
        review.reset();
        setClauseReviewResult(null);
        setFlagGroundingStatuses({});
        setMode('result');
      } else {
        setMode('review');
      }
      setProjectWorkspaceTab('documents');
      setClauseReviewSetupOpen(false);

      if (newVersion) {
        handleSelectDocumentVersion(newVersion);
      } else {
        const downloadUrl = uploaded.downloadUrl ?? `/api/file/${uploaded.fileId}`;
        setLastConversionOutput(null);
        setDocumentPreviewVariant('original');
        setFilePreviewUrl(appendTokenToUrl(downloadUrl, auth.getToken));
        setFilePreviewSource('restored');
        setCurrentResult({
          fileId: uploaded.fileId,
          downloadUrl,
          fileName: uploaded.fileName,
          mimeType: uploaded.mimeType,
          sizeBytes: uploaded.sizeBytes ?? 0,
        });
        setSessionPrimaryDocument({
          fileId: uploaded.fileId,
          fileName: uploaded.fileName,
          mimeType: uploaded.mimeType,
          sizeBytes: uploaded.sizeBytes ?? 0,
          downloadUrl,
          source: 'result',
        });
      }
      if (hadFailures) setMode('review');

      addNode(uploaded.fileName, undefined, uploaded.downloadUrl ?? `/api/file/${uploaded.fileId}`, 'Clause review — revised Word');

      toast({
        title: hadFailures ? 'Some suggestions applied' : 'Suggestions applied',
        description: hadFailures
          ? `${patchResult.appliedIds.length} suggestion(s) were saved in a new Word version. ${patchResult.failures.length} need manual review.`
          : 'New Word version added and opened in the document viewer.',
      });
    } catch (err) {
      const e = getActionError('apply clause revisions', err);
      toast({ variant: 'destructive', title: e.title, description: e.description });
    } finally {
      setClauseReviewApplyBusy(false);
    }
  }, [
    activeDocumentProject,
    addNode,
    auth,
    currentConversationFolderId,
    currentConversationId,
    currentFile?.name,
    currentResult?.documentId,
    currentResult?.documentVersion,
    currentResult?.draftHtml,
    currentResult?.draftTitle,
    currentResult?.fileId,
    currentResult?.fileName,
    currentResult?.mimeType,
    currentResult?.textContent,
    draftTitle,
    effectiveActiveVersionId,
    handleEditBatchVersionReady,
    handleSelectDocumentVersion,
    refreshDocumentProjects,
    review,
    sessionPrimaryDocument,
    toast,
    topbarFileName,
  ]);

  const downloadToolFile = useCallback((url: string | undefined, fileName: string | undefined) => {
    if (!url || !fileName) return;
    void downloadUrlWithAuthIfNeeded(url, fileName, auth.getToken).catch((err) => {
      const e = getActionError('download file', err);
      toast({ variant: 'destructive', title: e.title, description: e.description });
    });
  }, [auth.getToken, toast]);

  const downloadCurrentAsDocx = useCallback(() => {
    if (mode === 'drafting' && draftContent) {
      api.exportText(draftContent, 'docx', draftTitle || 'Document').then(() => {
        toast({ title: 'Word document saved' });
      }).catch((err) => {
        const e = getActionError('export Word document', err);
        toast({ title: e.title, description: e.description, variant: 'destructive' });
      });
      return;
    }

    const fileId = currentResult?.fileId ?? sessionPrimaryDocument?.fileId ?? activeDocumentProject?.currentVersion?.fileId;
    const baseName = currentResult?.fileName
      ?? sessionPrimaryDocument?.fileName
      ?? displayFileNameForProjectVersion(activeDocumentProject?.title ?? 'Document', activeDocumentProject?.currentVersion)
      ?? topbarFileName
      ?? 'document';
    if (!fileId) return;

    const docxName = `${baseName.replace(/\.[^.]+$/, '') || 'document'}.docx`;
    downloadToolFile(projectApi.fileDocxExportUrl(fileId), docxName);
  }, [
    activeDocumentProject,
    currentResult,
    downloadToolFile,
    draftContent,
    draftTitle,
    mode,
    sessionPrimaryDocument,
    toast,
    topbarFileName,
  ]);

  /** Enrich ChatMessage[] → ChatPanelMessage[] with imageResult / transcription cards */
  const chatMessages: ChatPanelMessage[] = useMemo(() =>
    (Array.isArray(messages) ? messages : []).map((m) => {
      const msg = m as ChatMessage & { result?: ToolResult; toolName?: string };

      // E-sign entry message → add action chips
      if (msg.systemKind === 'esign_entry' && msg.role === 'corner') {
        return {
          ...m,
          chips: [
            { label: 'Sign now', variant: 'initial' as const },
            { label: 'Review first', variant: 'initial' as const },
            { label: 'Add initials', variant: 'initial' as const },
          ],
        } as ChatPanelMessage;
      }

      // Multi-file loaded → add merge/summarize/compare chips
      if (msg.systemKind === 'multi_files_loaded' && msg.role === 'corner') {
        return {
          ...m,
          chips: [
            { label: 'Merge all', variant: 'initial' as const },
            { label: 'Summarize all', variant: 'initial' as const },
            { label: 'Compare docs', variant: 'initial' as const },
          ],
        } as ChatPanelMessage;
      }

      // Single PDF loaded → add action chips (Paper 5A-0 normal, Paper 5XP scanned)
      if (msg.systemKind === 'pdf_single_loaded' && msg.role === 'corner') {
        if (likelyScannedPdf) {
          return {
            ...m,
            chips: [
              { label: 'Extract text', variant: 'initial' as const },
              { label: 'Summarize', variant: 'initial' as const },
              { label: 'Convert to PDF', variant: 'initial' as const },
            ],
          } as ChatPanelMessage;
        }
        return {
          ...m,
          chips: [
            { label: 'Summarize', variant: 'initial' as const },
            { label: 'Study questions', variant: 'initial' as const },
            { label: 'Key terms', variant: 'initial' as const },
            { label: 'Get citation', variant: 'initial' as const },
            { label: 'Review contract', variant: 'initial' as const },
            { label: 'Draft email', variant: 'initial' as const },
            { label: 'Convert to PDF', variant: 'initial' as const },
            { label: 'Extract text', variant: 'initial' as const },
          ],
        } as ChatPanelMessage;
      }

      // QR generator intro → add chips (Paper 64J-0)
      if (msg.systemKind === 'qr_generator_intro' && msg.role === 'corner') {
        return {
          ...m,
          chips: [
            { label: 'URL or link', variant: 'initial' as const, sendAs: 'Generate a QR code for ' },
            { label: 'Contact card', variant: 'initial' as const, sendAs: 'Generate a QR code for a contact card (vCard): ' },
            { label: 'Plain text', variant: 'initial' as const, sendAs: 'Generate a QR code containing this text: ' },
          ],
        } as ChatPanelMessage;
      }

      if (!msg.result || msg.role !== 'corner') return m as ChatPanelMessage;

      // Image tool → ImageResultCard
      if (msg.toolName && IMAGE_TOOL_NAMES.has(msg.toolName)) {
        const r = msg.result;
        const imageResult: ImageResultData = {
          processingTime: '',
          actionLabel: toolToFriendlyOperation(msg.toolName as ToolName),
          previewUrl: r.previewUrl ?? r.downloadUrl,
          downloads: [
            {
              format: getFileFormatInfo(r.fileName).format || 'PNG',
              primary: true,
              onDownload: () => { downloadToolFile(r.downloadUrl, r.fileName); },
            },
          ],
          caption: `${r.fileName}${r.sizeBytes ? ` · ${(r.sizeBytes / (1024 * 1024)).toFixed(1)} MB` : ''}`,
        };
        return { ...m, imageResult } as ChatPanelMessage;
      }

      // Audio transcription → TranscriptionCard
      if (msg.toolName === 'transcribe_audio' && msg.result.transcriptionResult) {
        const tr = msg.result.transcriptionResult;
        const transcription: TranscriptionData = {
          processingTime: msg.result.durationLabel ?? `${tr.duration}s`,
          heading: 'Transcript',
          moments: tr.transcript
            ? [{ time: '0:00', text: tr.transcript }]
            : [],
          onDownload: () => { downloadToolFile(msg.result?.downloadUrl, msg.result?.fileName); },
        };
        return { ...m, transcription } as ChatPanelMessage;
      }

      // Merge PDF → MergeResultCard
      if (msg.toolName === 'merge_pdf' && msg.result.mergeManifest && msg.result.mergeManifest.length > 0) {
        const r = msg.result;
        const totalPages = r.mergeManifest!.reduce((sum, e) => sum + e.pageCount, 0);
        const durationSec = r.mergeDurationMs != null ? (r.mergeDurationMs / 1000).toFixed(1) : null;
        const mergeResult: MergeResultData = {
          headerTitle: durationSec ? `Merged in ${durationSec}s` : 'Merged',
          files: r.mergeManifest!.map((e) => ({ name: e.fileName, pageCount: e.pageCount })),
          onDownload: () => { downloadToolFile(r.downloadUrl, r.fileName); },
          fileMetaLine: `${r.fileName} · ${totalPages} pages${r.sizeBytes ? ` · ${(r.sizeBytes / (1024 * 1024)).toFixed(1)} MB` : ''}`,
        };
        return { ...m, mergeResult } as ChatPanelMessage;
      }

      // Split PDF → SplitResultCard
      if (msg.toolName === 'split_pdf' && msg.result && typeof msg.result.splitPageCount === 'number' && msg.result.splitPageCount > 0) {
        const r = msg.result;
        const fileCount = r.splitPageCount!;
        const durationSec = r.splitDurationMs != null ? (r.splitDurationMs / 1000).toFixed(1) : null;
        const splitResult: SplitResultData = {
          headerTitle: durationSec ? `Split in ${durationSec}s — ${fileCount} files` : `Split — ${fileCount} files`,
          getToken: auth.getToken,
          files: r.splitFiles
            ? r.splitFiles.map((f: { fileName: string; downloadUrl: string }) => ({ name: f.fileName, downloadUrl: f.downloadUrl }))
            : Array.from({ length: fileCount }, (_, i) => ({ name: `Page ${i + 1}`, downloadUrl: '' })),
          onDownloadZip: () => { downloadToolFile(r.downloadUrl, r.fileName); },
        };
        return { ...m, splitResult } as ChatPanelMessage;
      }

      // OCR → OcrResultCard
      if (msg.toolName === 'ocr' && msg.result?.ocrMeta) {
        const r = msg.result;
        const meta = r.ocrMeta!;
        const text = r.textContent ?? '';
        const wordCount = text.split(/\s+/).filter(Boolean).length;
        const durationSec = (meta.durationMs / 1000).toFixed(1);
        const preview = text.length > 300 ? text.slice(0, 300).trimEnd() + '…' : text;
        const ocrResult: OcrResultData = {
          headerTitle: `OCR complete · ${meta.confidence}% confidence`,
          textPreview: preview || '(no text extracted)',
          onDownload: () => { downloadToolFile(r.downloadUrl, r.fileName); },
          onCopy: () => {
            navigator.clipboard.writeText(text).catch(() => {});
          },
          footerLine: `${wordCount} words · extracted in ${durationSec}s`,
        };
        return { ...m, ocrResult } as ChatPanelMessage;
      }

      // QR code → QrResultCard
      if (msg.toolName === 'generate_qr' && msg.result?.qrMeta) {
        const r = msg.result;
        const meta = r.qrMeta!;
        const fmt = meta.format.toUpperCase();
        const qrResult: QrResultData = {
          headerTitle: 'QR code generated',
          subtitle: `${meta.payload} · ${fmt} ${meta.sizePx}×${meta.sizePx}px`,
          onDownloadPrimary: () => { downloadToolFile(r.downloadUrl, r.fileName); },
          primaryLabel: `Download ${fmt}`,
          onDownloadAlternate: () => {
            const altFmt = meta.format === 'png' ? 'svg' : 'png';
            if (altFmt === 'svg' && meta.svgContent) {
              const blob = new Blob([meta.svgContent], { type: 'image/svg+xml' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = 'corner_qr.svg';
              a.click();
              URL.revokeObjectURL(url);
            } else {
              runQrAlternateFormat(altFmt, meta.payload);
            }
          },
          alternateLabel: meta.format === 'png' ? 'SVG' : 'PNG',
        };
        return { ...m, qrResult } as ChatPanelMessage;
      }

      // Watermark → WatermarkResultCard
      if (msg.toolName === 'add_watermark_pdf' && msg.result?.watermarkMeta) {
        const r = msg.result;
        const meta = r.watermarkMeta!;
        const sizeMB = r.sizeBytes ? (r.sizeBytes / (1024 * 1024)).toFixed(1) : null;
        const watermarkResult: WatermarkResultData = {
          headerTitle: `Watermark applied to all ${meta.pageCount} page${meta.pageCount === 1 ? '' : 's'}`,
          watermarkText: meta.text,
          onDownload: () => { downloadToolFile(r.downloadUrl, r.fileName); },
          footerLine: `${r.fileName}${sizeMB ? ` · ${sizeMB} MB` : ''}`,
        };
        return { ...m, watermarkResult } as ChatPanelMessage;
      }

      // PDF Utility (add_page_numbers) → PdfUtilityResultCard
      if (msg.toolName === 'add_page_numbers' && msg.result?.pageNumbersMeta) {
        const r = msg.result;
        const meta = r.pageNumbersMeta!;
        const sizeMB = r.sizeBytes ? (r.sizeBytes / (1024 * 1024)).toFixed(1) : null;
        const posLabel = meta.position.replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase());
        const pdfUtilityResult: PdfUtilityResultData = {
          headerTitle: `Page numbers added · ${meta.pageCount} page${meta.pageCount === 1 ? '' : 's'}`,
          detailLabel: 'Position:',
          detailValue: posLabel,
          onDownload: () => { downloadToolFile(r.downloadUrl, r.fileName); },
          footerLine: `${r.fileName}${sizeMB ? ` · ${sizeMB} MB` : ''}`,
        };
        return { ...m, pdfUtilityResult } as ChatPanelMessage;
      }

      // Analysis result → AnalysisCard
      if (msg.type === 'analysis' && msg.content) {
        const analysisLabels: Record<string, string> = {
          summarize: 'Summary',
          study_questions: 'Study Questions',
          key_terms: 'Key Terms & Citations',
          citation_generator: 'Citations',
          contract_review: 'Contract Review',
          action_items: 'Action Items',
          email_draft: 'Email Draft',
          sensitive_data: 'Sensitive Data Review',
        };
        const analysisChips: Record<string, string[]> = {
          summarize: ['Explain', 'Flag risk', 'Rewrite'],
          study_questions: ['Explain', 'More questions', 'Simplify'],
          key_terms: ['Explain', 'Define terms', 'Expand'],
          citation_generator: ['Explain', 'Reformat'],
          contract_review: ['Explain', 'Flag risk', 'Suggest edits', 'Run clause review'],
          action_items: ['Explain', 'Prioritize', 'Expand'],
          email_draft: ['Explain', 'Make formal', 'Shorten'],
          sensitive_data: ['Explain', 'Flag risk', 'Suggest redactions'],
        };
        const typeKey = msg.analysisType ?? 'summarize';
        const docName = currentFiles[0]?.name ?? topbarFileName ?? 'Document';
        const baseTitle = docName.replace(/\.[^.]+$/, '').replace(/[_-]/g, ' ');
        const label = analysisLabels[typeKey] ?? 'Analysis';

        // Split content: first line may be a title, rest is body
        const lines = msg.content.split('\n');
        let title = `${baseTitle} — ${label}`;
        let body = msg.content;
        // If the first line looks like a heading (short, no period), use it as title
        if (lines.length > 1 && lines[0].length < 80 && !lines[0].endsWith('.')) {
          title = lines[0].replace(/^#+\s*/, '');
          body = lines.slice(1).join('\n').trim();
        }

        const chips = (analysisChips[typeKey] ?? ['Explain']).map((c) => ({
          label: c,
          variant: 'followup' as const,
          sendAs: `${c} this ${label.toLowerCase()}`,
        }));

        const analysisResult: AnalysisResultData = {
          title,
          body,
          chips,
          exportable: !!msg.exportable,
          riskSummary: typeKey === 'contract_review' ? riskSummary : undefined,
        };
        return { ...m, analysisResult } as ChatPanelMessage;
      }

      return m as ChatPanelMessage;
    }),
    [messages, currentFiles, topbarFileName, likelyScannedPdf, runQrAlternateFormat, riskSummary, downloadToolFile, auth.getToken]
  );

  // ─── Build sidebar data ──────────────────────────
  const sidebarUser: SidebarUser | null = auth.user
    ? {
        name: auth.user.displayName,
        email: auth.user.email,
        plan: auth.user.plan as 'free' | 'pro' | 'enterprise',
        avatarUrl: auth.user.avatarUrl,
        signatureId: auth.user.signatureId,
        initialsId: auth.user.initialsId,
        phone: auth.user.phone,
        address: auth.user.address,
        company: auth.user.company,
        jobTitle: auth.user.jobTitle,
        updatedAt: auth.user.updatedAt,
        usage: auth.user.usage as { aiRequestsThisMonth: number; pagesThisMonth: number } | undefined,
      }
    : null;

  const sidebarHistoryItems: HistoryItem[] = versionHistoryForPanel.map((v: any) => {
    const srcFileId = v.primaryDocument?.fileId ?? null;
    return {
      id: v.id,
      conversationId: v.conversationId,
      projectId: v.projectId,
      kind: v.kind,
      fileName: v.fileName,
      fileType: v.fileType ?? 'pdf',
      operation: v.operation ?? 'Processed',
      timestamp: v.timestamp,
      isActive: v.isActive,
      versionCount: v.versionCount,
      contextLabel: v.contextLabel,
      historyLocked: v.historyLocked,
      statusBadge: v.statusBadge,
      sourceFileId: srcFileId,
      riskNudge: (srcFileId && historyRiskData[srcFileId]?.nudge) || null,
    };
  });

  const handleSaveAsWorkflow = useCallback(async (messageId: string) => {
    const msg = messages.find((m) => m.id === messageId);
    if (!msg) return;
    const analysisType = (msg as any).analysisType ?? 'summarize';
    const docName = currentFiles[0]?.name ?? topbarFileName ?? 'Document';
    const analysisLabels: Record<string, string> = {
      summarize: 'Summary', study_questions: 'Study Questions', key_terms: 'Key Terms',
      citation_generator: 'Citations', contract_review: 'Contract Review',
      action_items: 'Action Items', email_draft: 'Email Draft', sensitive_data: 'Sensitive Data',
    };
    const label = analysisLabels[analysisType] ?? 'Analysis';
    const title = `${docName.replace(/\.[^.]+$/, '')} — ${label}`;
    const goal = `Analyze a document and produce a ${label.toLowerCase()}. Follow the same approach used for "${docName}".`;
    try {
      const definition = buildWorkflowFromResult({ analysisType, title, goal, fileName: docName });
      const created = await userWorkflows.create({ name: title });
      if (created) {
        await userWorkflows.update(created.id, { definition });
        toast({ title: 'Workflow saved', description: `"${title}" was saved to your templates — start from Signatures → Guided send when you're ready.` });
      }
    } catch (err) {
      const e = getActionError('save workflow', err);
      toast({ title: e.title, description: e.description, variant: 'destructive' });
    }
  }, [messages, currentFiles, topbarFileName, userWorkflows, toast]);

  // V6: post-success save entry point.
  //   - If the user pre-picked a destination via the chat composer
  //     button, consume it silently and call the save API directly.
  //   - Otherwise open the picker (handleSaveDestination handles the
  //     actual save when the user picks).
  const handleSaveContractSendAsWorkflow = useCallback(async (messageId: string) => {
    const msg = messages.find((m) => m.id === messageId);
    const capture = msg?.contractSendCapture;
    if (!capture?.signingRequestId) return;
    const docName = capture.documentName ?? currentFiles[0]?.name ?? 'Contract';
    const cleanName = docName.replace(/\.[^.]+$/, '');

    // Silent path: pre-set destination wins over the picker. consume()
    // clears it so the next message starts unset (intentional — pre-sets
    // shouldn't sticky-apply across context shifts).
    const presetDestination = workflowDestination.consume();
    if (presetDestination) {
      try {
        const baseInput = {
          signingRequestId: capture.signingRequestId,
          baselineValues: capture.capturedBaseline,
          signerRole: capture.signerRole,
          templateName: presetDestination.templateName ?? cleanName,
        };
        if (presetDestination.mode === 'existing') {
          await saveContractSendAsWorkflow({
            ...baseInput,
            addToExistingWorkflowId: presetDestination.workflowId,
          });
          toast({
            title: 'Saved as template',
            description: `Added to "${presetDestination.workflowName}" under ${presetDestination.categoryName}.`,
          });
        } else {
          const created = await saveContractSendAsWorkflow({
            ...baseInput,
            name: presetDestination.newWorkflowName,
            userCategory: presetDestination.newCategory,
            icon: presetDestination.icon,
          });
          toast({
            title: 'Workflow saved',
            description: `"${created.name}" created under ${presetDestination.newCategory}.`,
          });
        }
        await userWorkflows.refresh?.();
      } catch (err) {
        const e = getActionError('save workflow', err);
        toast({ title: e.title, description: e.description, variant: 'destructive' });
      }
      return;
    }

    // Reactive path: no pre-set, open the picker.
    setSavePickerOpen({ messageId, defaultName: cleanName });
  }, [messages, currentFiles, workflowDestination, userWorkflows, toast]);

  // V6: invoked when the user picks a destination + clicks Save. Three
  // modes are dispatched server-side via saveContractSendAsWorkflow's
  // upgraded payload:
  //   existing → addToExistingWorkflowId set, no workflow create
  //   newWorkflow → workflow + first template created together
  const handleSaveDestination = useCallback(async (destination: WorkflowDestination & object) => {
    if (!savePickerOpen) return;
    const msg = messages.find((m) => m.id === savePickerOpen.messageId);
    const capture = msg?.contractSendCapture;
    if (!capture?.signingRequestId) {
      setSavePickerOpen(null);
      return;
    }
    try {
      const baseInput = {
        signingRequestId: capture.signingRequestId,
        baselineValues: capture.capturedBaseline,
        signerRole: capture.signerRole,
        templateName: destination.templateName,
      };
      if (destination.mode === 'existing') {
        await saveContractSendAsWorkflow({
          ...baseInput,
          addToExistingWorkflowId: destination.workflowId,
        });
        toast({
          title: 'Saved as template',
          description: `Added to "${destination.workflowName}" under ${destination.categoryName}.`,
        });
      } else {
        const created = await saveContractSendAsWorkflow({
          ...baseInput,
          name: destination.newWorkflowName,
          userCategory: destination.newCategory,
          icon: destination.icon,
        });
        toast({
          title: 'Workflow saved',
          description: `"${created.name}" created under ${destination.newCategory}. Open Signatures → Guided send to use this template again.`,
        });
      }
      await userWorkflows.refresh?.();
      setSavePickerOpen(null);
    } catch (err) {
      const e = getActionError('save workflow', err);
      toast({ title: e.title, description: e.description, variant: 'destructive' });
      // Keep the picker open so the user can retry.
    }
  }, [savePickerOpen, messages, userWorkflows, toast]);

  const handleNewFlow = useCallback(() => {
    setNewFlowActive(true);
    hydrationRequestSeqRef.current += 1;
    hydratingConversationIdRef.current = null;
    hydrateDeferredForRouteRef.current = null;
    mainHydrateAttemptedForRouteRef.current = null;
    hydrationRouteStateRef.current = {};
    setWorkspaceRouteBootstrapId(null);
    activeWorkspaceRunRef.current = null;
    draftStreamAbortRef.current?.abort();
    draftStreamAbortRef.current = null;
    setDraftStreamingConversationId(null);
    setRestoringConversationId(null);
    currentConversationIdRef.current = null;
    currentResultRef.current = null;
    sessionPrimaryDocumentRef.current = null;
    filePreviewUrlRef.current = null;
    clearWorkspaceSessionState();
    setCurrentConversationId(null);
    setMode('empty');
    navigate(APP_WORKSPACE_PATH, { replace: true });
  }, [clearWorkspaceSessionState, setCurrentConversationId, navigate]);

  const appendWorkflowThreadToChat = useCallback((summary: WorkflowThreadSummary) => {
    const content = summary.summaryText ?? `Workflow "${summary.workflowLabel}" completed.`;
    setMessages((prev) => [...prev, {
      id: crypto.randomUUID(),
      role: 'corner' as const,
      content,
      timestamp: Date.now(),
      workflowThreadSummary: summary,
    }]);
    const cid = currentConversationIdRef.current;
    if (cid) {
      addConversationMessage(cid, { role: 'corner', content }).catch(() => {});
    }
  }, [addConversationMessage]);

  const isWorkspaceChromeRoute =
    !showIntegrations && !showSettings && !showFolders && !showSignatures && activeNav === null && !versionTimelineOverlay;
  const routeConversationPendingHydration = Boolean(
    routeConversationId
      && (
        currentConversationId == null
        || String(currentConversationId) !== String(routeConversationId)
      ),
  );
  const showWorkspaceRestoreShell = Boolean(
    isWorkspaceChromeRoute
      && routeConversationId
      && (
        routeConversationPendingHydration
        || hydratingConversationIdRef.current === routeConversationId
        || (workspaceRouteBootstrapId != null && String(workspaceRouteBootstrapId) === String(routeConversationId))
        || (restoringConversationId != null && String(restoringConversationId) === String(routeConversationId))
      ),
  );

  // Don't render the app until auth is resolved, and block if not authenticated
  if (auth.loading) return null;
  if (!auth.user) return null; // useEffect above will redirect to /auth/login

  return (
    <div {...getRootProps()} style={{ width: '100vw', maxWidth: '100vw', height: '100vh', overflow: 'hidden' }}>
      <input {...getInputProps()} />
      <AppLayout
        isMobile={isMobile}
        splitView={isWorkspaceChromeRoute && (mode !== 'empty' || showProjectShell || showWorkspaceRestoreShell)}
        inputBarInline={mode === 'empty' && !showProjectShell && !showWorkspaceRestoreShell}
        sidebar={
          <Sidebar
            open={leftPanelOpen}
            onToggle={() => setLeftPanelOpen((prev) => !prev)}
            activeNav={activeNav}
            onNavChange={(nav) => { setNewFlowActive(false); navigate(`/app/${nav}`); }}
            onNewFlow={handleNewFlow}
            onSignatures={() => navigate('/app/signatures')}
            signaturesActive={showSignatures}
            foldersActive={showFolders}
            settingsActive={showSettings}
            onFolders={() => navigate('/app/folders')}
            historyItems={sidebarHistoryItems}
            onHistoryItemClick={(id) => {
              const item = sidebarHistoryItems.find((h) => h.id === id);
              if (item?.historyLocked) {
                toast({ title: 'Upgrade to Pro', description: 'Upgrade to Pro to access conversations older than 7 days.', variant: 'default' });
                setPaywallOpen(true);
                return;
              }
              setNewFlowActive(false);
              handleRestoreConversation(item?.conversationId ?? id);
            }}
            user={sidebarUser}
            onUpgrade={() => setPaywallOpen(true)}
            onSettings={() => navigate('/app/settings')}
            onSignOut={handleSignOut}
            onReportBug={() => window.open('mailto:nick@trycorner.co?subject=Bug%20Report', '_blank')}
            onBrowseSource={(name) => setFileBrowserSource(name)}
            integrationStatuses={integrationStatuses}
            onConnectSource={(name) => {
              const providerSlug = Object.entries(PROVIDER_NAME_MAP).find(([, v]) => v === name)?.[0];
              if (providerSlug) window.location.href = `/api/auth/${providerSlug}`;
            }}
            onDisconnectSource={async (name) => {
              const token = auth.getToken();
              const providerSlug = Object.entries(PROVIDER_NAME_MAP).find(([, v]) => v === name)?.[0];
              if (!token || !providerSlug) return;
              try {
                await axios.post(`/api/integrations/${providerSlug}/disconnect`, {}, { headers: { Authorization: `Bearer ${token}` } });
                await fetchIntegrationStatuses();
              } catch { /* silent */ }
            }}
          />
        }
        topBar={
          (mode === 'empty' && !showProjectShell && !showWorkspaceRestoreShell && !versionTimelineOverlay && !showSettings && !showIntegrations && !showFolders && !showSignatures && activeNav === null) ? (
          <TopBar
            title={topbarFileName ?? 'What are you working on?'}
            onMenu={isMobile ? () => setMobileSidebarOpen(true) : undefined}
            folders={foldersApi.folders}
            currentFolderId={currentConversationFolderId}
            onAddToFolder={(folderId) => {
              if (currentConversationId) {
                foldersApi.assignConversationToFolder(currentConversationId, folderId);
              }
            }}
            onCreateFolder={(name) => foldersApi.createFolder(name)}
            onVersionHistory={() => setVersionTimelineOverlay(true)}
            onDownload={() => { downloadToolFile(currentResult?.downloadUrl, currentResult?.fileName); }}
          />
        ) : (isMobile && (versionTimelineOverlay || showSettings || showIntegrations || showFolders || showSignatures || activeNav !== null)) ? (
          <TopBar
            title={
              showSignatures ? 'Signatures'
              : showFolders ? 'Projects'
              : showSettings ? 'Settings'
              : showIntegrations ? 'Sources'
              : activeNav === 'archived' ? 'Archived'
              : 'Corner'
            }
            onMenu={() => setMobileSidebarOpen(true)}
            showActions={false}
          />
        ) : undefined}
        inputBar={mode === 'empty' && !showProjectShell && !showWorkspaceRestoreShell && !versionTimelineOverlay && !showSettings && !showIntegrations && !showFolders && !showSignatures && activeNav === null ? (
          <InputBar
            onSend={(message) => { handleSend(message); }}
            onFileDrop={handleFilesReceived}
            attachedFiles={currentFiles}
            onRemoveFile={handleRemoveQueuedFile}
            placeholder={chatComposerPlaceholderHint ?? 'Ask anything or drop a file…'}
            disabled={!!processing || !!activeQueryRunId}
            onStop={(processing || activeQueryRunId) ? handleStopProcessing : undefined}
            prefill={chatPrefill}
            onPrefillConsumed={handleChatPrefillConsumed}
            selectedSnippet={pendingSelectedSnippet}
            onRemoveSnippet={() => setPendingSelectedSnippet(null)}
            destinationSlot={auth.user ? (
              <FoldersButton
                getToken={auth.getToken}
                onPickFile={(file) => handleFilesReceived([...currentFiles, file])}
                enabled={!!auth.user}
              />
            ) : undefined}
          />
        ) : undefined}
      >
        {showIntegrations ? (
          <IntegrationsPanel
            onBack={() => { setShowIntegrations(false); navigate('/app/settings'); }}
            isSignedIn={!!auth.user}
            getToken={auth.getToken}
          />
        ) : showSettings ? (
          <SettingsPanel
            user={sidebarUser}
            onClose={() => navigate('/app')}
            onSignOut={handleSignOut}
            onUpgrade={() => setPaywallOpen(true)}
            onHelpSupport={() => window.open('mailto:nick@trycorner.co?subject=Help%20%26%20Support', '_blank')}
            onSaveProfile={async (fields) => {
              try {
                await auth.updateProfile(fields);
              } catch {
                // toast or silent — profile refresh happens inside updateProfile
              }
            }}
            onManageBilling={async () => {
              const token = auth.getToken();
              if (!token) return;
              try {
                const res = await axios.post<{ url: string }>('/api/billing/portal', {}, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                window.open(res.data.url, '_blank');
              } catch {
                window.open('mailto:nick@trycorner.co?subject=Billing', '_blank');
              }
            }}
            onCancelPlan={async () => {
              const token = auth.getToken();
              if (!token) return;
              try {
                await axios.post('/api/billing/cancel', {}, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                await auth.refreshUser();
                toast({ title: 'Subscription canceled', description: billingInfo?.status === 'trialing' ? 'Your trial has been canceled.' : 'You\'ll have access until the end of your billing period.' });
                // Refresh billing info
                const statusRes = await axios.get<{ subscription: any }>('/api/billing/status', { headers: { Authorization: `Bearer ${token}` } });
                const sub = statusRes.data.subscription;
                setBillingInfo(sub ? { status: sub.status, billingCycle: sub.billingCycle, currentPeriodEnd: sub.currentPeriodEnd, trialEnd: sub.trialEnd, cancelAtPeriodEnd: sub.cancelAtPeriodEnd } : null);
              } catch (err) {
                const e = getActionError('cancel subscription', err);
                toast({ title: e.title, description: e.description, variant: 'destructive' });
              }
            }}
            onReactivatePlan={async () => {
              const token = auth.getToken();
              if (!token) return;
              try {
                await axios.post('/api/billing/reactivate', {}, {
                  headers: { Authorization: `Bearer ${token}` },
                });
                toast({ title: 'Subscription reactivated', description: 'Your subscription will continue as normal.' });
                const statusRes = await axios.get<{ subscription: any }>('/api/billing/status', { headers: { Authorization: `Bearer ${token}` } });
                const sub = statusRes.data.subscription;
                setBillingInfo(sub ? { status: sub.status, billingCycle: sub.billingCycle, currentPeriodEnd: sub.currentPeriodEnd, trialEnd: sub.trialEnd, cancelAtPeriodEnd: sub.cancelAtPeriodEnd } : null);
              } catch (err) {
                const e = getActionError('reactivate subscription', err);
                toast({ title: e.title, description: e.description, variant: 'destructive' });
              }
            }}
            onIntegrations={() => { navigate('/app'); setShowIntegrations(true); }}
            onEditSignature={() => {
              navigate('/auth/signature-setup', { state: { from: 'settings' } });
            }}
            onDeleteSignature={async () => {
              const token = auth.getToken();
              if (!token) return;
              try {
                await axios.delete('/api/auth/me/signature', {
                  headers: { Authorization: `Bearer ${token}` },
                });
                await auth.refreshUser();
                toast({ title: 'Signature removed' });
              } catch (err) {
                const e = getActionError('delete signature', err);
                toast({ title: e.title, description: e.description, variant: 'destructive' });
              }
            }}
            onUploadAvatar={async (file) => {
              // Client-side validation before uploading
              const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
              if (!allowedTypes.includes(file.type)) {
                toast({ variant: 'destructive', title: 'Invalid file type', description: 'Only JPEG, PNG, GIF, and WebP images are allowed.' });
                return null;
              }
              if (file.size > 2 * 1024 * 1024) {
                toast({ variant: 'destructive', title: 'File too large', description: 'Avatar must be under 2 MB.' });
                return null;
              }

              // Resize client-side to 256x256 JPEG before uploading — keeps the
              // base64 data URL stored in MongoDB small (~15-30 KB instead of MBs).
              const resized = await new Promise<Blob>((resolve, reject) => {
                const img = new Image();
                img.onload = () => {
                  const size = 256;
                  const canvas = document.createElement('canvas');
                  canvas.width = size;
                  canvas.height = size;
                  const ctx = canvas.getContext('2d')!;
                  // Cover-crop: draw the largest centered square from the source
                  const srcSize = Math.min(img.width, img.height);
                  const sx = (img.width - srcSize) / 2;
                  const sy = (img.height - srcSize) / 2;
                  ctx.drawImage(img, sx, sy, srcSize, srcSize, 0, 0, size, size);
                  canvas.toBlob(
                    (blob) => (blob ? resolve(blob) : reject(new Error('Canvas toBlob failed'))),
                    'image/jpeg',
                    0.85,
                  );
                };
                img.onerror = () => reject(new Error('Failed to load image'));
                img.src = URL.createObjectURL(file);
              });

              const token = auth.getToken();
              if (!token) return null;
              const form = new FormData();
              form.append('avatar', new File([resized], 'avatar.jpg', { type: 'image/jpeg' }));
              try {
                const res = await axios.post<{ avatarUrl: string }>('/api/auth/me/avatar', form, {
                  headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
                });
                await auth.refreshUser();
                toast({ title: 'Avatar updated' });
                return res.data.avatarUrl;
              } catch (err) {
                const e = getActionError('update avatar', err);
                toast({ title: e.title, description: e.description, variant: 'destructive' });
                return null;
              }
            }}
            onDeleteAvatar={async () => {
              const token = auth.getToken();
              if (!token) return;
              try {
                await axios.delete('/api/auth/me/avatar', {
                  headers: { Authorization: `Bearer ${token}` },
                });
                await auth.refreshUser();
                toast({ title: 'Avatar removed' });
              } catch (err) {
                const e = getActionError('remove avatar', err);
                toast({ title: e.title, description: e.description, variant: 'destructive' });
              }
            }}
            billingInfo={billingInfo}
          />
        ) : showFolders && isFolderDetailRoute ? (
          (() => {
            const folder = foldersApi.folders.find((f) => f.id === folderIdFromPath);
            const childFolders = foldersWithCounts.filter((f) => f.parentId === folderIdFromPath);
            const folderProjects = documentProjects.filter((project) => project.folderId === folderIdFromPath);
            const folderConversations = conversationList.filter((c) => c.folderId === folderIdFromPath && !c.inTrash);
            const itemCount = childFolders.length + folderProjects.length + folderConversations.length;
            return (
              <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1, height: '100%', fontFamily: APP.fontFamily, overflow: 'hidden' }}>
                <div style={{ height: 52, display: 'flex', alignItems: 'center', flexShrink: 0, paddingInline: 24, gap: 12, borderBottom: `1px solid ${APP.border}` }}>
                  <button type="button" onClick={() => navigate('/app/folders')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: APP.textMuted, padding: 0, fontFamily: APP.fontFamily }}>
                    ← Projects
                  </button>
                  <span style={{ fontSize: 16, fontWeight: 600, color: APP.textDark, lineHeight: '20px' }}>
                    {folder?.name ?? 'Project'}
                  </span>
                  <span style={{ fontSize: 12, color: APP.textMuted }}>
                    {itemCount} {itemCount === 1 ? 'item' : 'items'}
                  </span>
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button
                      type="button"
                      onClick={async () => {
                        const name = window.prompt('Subproject name');
                        if (!name?.trim() || !folderIdFromPath) return;
                        await foldersApi.createFolder(name, folder?.kind ?? 'general', folderIdFromPath);
                      }}
                      style={{ borderRadius: 8, border: `1px solid ${APP.border}`, backgroundColor: APP.bgCardBody, color: APP.textDark, padding: '8px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: APP.fontFamily }}
                    >
                      New subproject
                    </button>
                    {folderIdFromPath && (
                      <button
                        type="button"
                        onClick={() => handleDownloadFolderZip(folderIdFromPath)}
                        style={{ borderRadius: 8, border: 'none', backgroundColor: APP.textDark, color: '#fff', padding: '8px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: APP.fontFamily }}
                      >
                        Download project
                      </button>
                    )}
                  </div>
                </div>
                <div
                  style={{ flexGrow: 1, overflowY: 'auto', padding: 24 }}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={(event) => {
                    event.preventDefault();
                    if (event.dataTransfer.files.length > 0 && folderIdFromPath) {
                      void handleDropFilesIntoFolder(event.dataTransfer.files, folderIdFromPath);
                      return;
                    }
                    const projectId = event.dataTransfer.getData('application/x-corner-project');
                    if (projectId && folderIdFromPath) void handleMoveProjectToFolder(projectId, folderIdFromPath);
                  }}
                >
                  {itemCount === 0 ? (
                    <div style={{ textAlign: 'center', paddingBlock: 60, fontSize: 13, color: APP.textMuted }}>
                      This project is empty. Create a subproject or drag documents here.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
                      {childFolders.length > 0 && (
                        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: APP.textMuted, textTransform: 'uppercase' }}>Subprojects</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                            {childFolders.map((child) => (
                              <button
                                key={child.id}
                                type="button"
                                onClick={() => navigate(`/app/folders/${child.id}`)}
                                onDragOver={(event) => event.preventDefault()}
                                onDrop={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  if (event.dataTransfer.files.length > 0) {
                                    void handleDropFilesIntoFolder(event.dataTransfer.files, child.id);
                                    return;
                                  }
                                  const projectId = event.dataTransfer.getData('application/x-corner-project');
                                  if (projectId) void handleMoveProjectToFolder(projectId, child.id);
                                }}
                                style={{ display: 'flex', alignItems: 'center', gap: 10, borderRadius: 12, border: `1px solid ${APP.border}`, backgroundColor: APP.bgCardBody, padding: 14, cursor: 'pointer', textAlign: 'left', fontFamily: APP.fontFamily }}
                              >
                                <AppFolderIcon />
                                <span style={{ flex: 1, minWidth: 0 }}>
                                  <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: APP.textDark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{child.name}</span>
                                  <span style={{ display: 'block', marginTop: 2, fontSize: 11, color: APP.textMuted }}>Drop documents here</span>
                                </span>
                              </button>
                            ))}
                          </div>
                        </section>
                      )}

                      {folderProjects.length > 0 && (
                        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: APP.textMuted, textTransform: 'uppercase' }}>Documents</div>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 10 }}>
                            {folderProjects.map((project) => {
                              const version = project.currentVersion;
                              const versionFileName = displayFileNameForProjectVersion(project.title, version) ?? version?.fileName;
                              const hasDisplayVersion =
                                Boolean(version?.fileId) && !isLegacyDraftShellFileId(version?.fileId);
                              const hasDraggableVersion =
                                Boolean(version?.fileId) && !isSyntheticDraftFileId(version?.fileId);
                              const isSignedVersion = version?.source === 'signed';
                              return (
                                <div
                                  key={project.id}
                                  draggable
                                  onDragStart={(event) => {
                                    event.dataTransfer.setData('application/x-corner-project', project.id);
                                    if (hasDraggableVersion && version?.fileId) {
                                      event.dataTransfer.setData('application/x-corner-file', JSON.stringify({
                                        fileId: version.fileId,
                                        fileName: versionFileName ?? version.fileName,
                                        mimeType: version.mimeType,
                                        projectId: project.id,
                                      }));
                                    }
                                    event.dataTransfer.effectAllowed = hasDraggableVersion ? 'copyMove' : 'move';
                                  }}
                                  style={{ borderRadius: 12, border: `1px solid ${APP.border}`, backgroundColor: APP.bgCardBody, padding: 14, cursor: 'grab', boxShadow: '0 1px 2px rgba(26,23,20,0.04)' }}
                                >
                                  <button
                                    type="button"
                                    onClick={() => handleRestoreConversation(project.primaryConversationId)}
                                    style={{ display: 'block', width: '100%', border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', textAlign: 'left', fontFamily: APP.fontFamily }}
                                  >
                                    <div style={{ fontSize: 13, fontWeight: 700, color: APP.textDark, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{project.title}</div>
                                    <div style={{ marginTop: 5, display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                                      <span style={{ minWidth: 0, flex: 1, fontSize: 12, color: APP.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {hasDisplayVersion ? versionFileName : 'No saved document yet'} · {project.versionCount} {project.versionCount === 1 ? 'version' : 'versions'}
                                      </span>
                                      {isSignedVersion && (
                                        <span
                                          title="Signed version saved"
                                          style={{
                                            flexShrink: 0,
                                            borderRadius: 999,
                                            backgroundColor: 'rgba(27, 122, 75, 0.10)',
                                            color: '#1B7A4B',
                                            border: '1px solid rgba(27, 122, 75, 0.20)',
                                            padding: '1px 6px',
                                            fontSize: 9,
                                            fontWeight: 700,
                                            lineHeight: '13px',
                                          }}
                                        >
                                          Signed
                                        </span>
                                      )}
                                    </div>
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </section>
                      )}

                      {folderConversations.length > 0 && (
                        <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: APP.textMuted, textTransform: 'uppercase' }}>Chats</div>
                          <RecentPanel
                            items={folderConversations.map((c) => ({
                              id: c.id,
                              title: c.title,
                              lastMessageAt: c.lastMessageAt,
                              fileType: (() => { const ft = inferFileTypeFromFileName(c.sessionSnapshot?.primaryDocument?.fileName ?? c.latestResultFileName ?? c.title); return ft === 'word' ? 'doc' : ft === 'image' ? 'img' : ft; })(),
                              operation: c.toolsUsed?.length ? toolToFriendlyOperation(c.toolsUsed[c.toolsUsed.length - 1] as ToolName) : undefined,
                              pinned: c.pinned,
                              folderId: c.folderId,
                            }))}
                            folders={foldersApi.folders}
                            onOpen={(id) => { handleRestoreConversation(id); }}
                            onRename={handleRenameConversation}
                            onStar={handleStarConversation}
                            onAddToFolder={(id, folderId) => { foldersApi.assignConversationToFolder(id, folderId); }}
                            onDelete={handleDeleteConversation}
                          />
                        </section>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })()
        ) : showFolders ? (
          <FoldersPanel
            folders={foldersWithCounts}
            onCreateFolder={(name) => foldersApi.createFolder(name)}
            onCreateProjectFolder={handleCreateProjectFolder}
            recentProjectSources={projectFolderRecentSources}
            onRenameFolder={(id, name) => foldersApi.renameFolder(id, name)}
            onDeleteFolder={(id) => foldersApi.deleteFolder(id)}
            onDownloadFolder={handleDownloadFolderZip}
            onOpenFolder={(id) => {
              navigate(`/app/folders/${id}`);
            }}
            onBack={() => navigate(-1)}
          />
        ) : showSignatures ? (
          <SignaturesDashboard
            getToken={auth.getToken}
            refreshKey={signingRequestsRefreshKey}
            userDisplayName={auth.user?.displayName ?? undefined}
            onOpenRequest={handleOpenSigningRequestFromDashboard}
            onSendForSignature={handleSignaturesDashboardSendForSignature}
            contractSendBusy={guidedContractSendBusy}
            onOpenProjects={() => navigate('/app/folders')}
          />
        ) : activeNav === 'archived' ? (
          <ArchivedPanel
            items={archivedItems}
            onUnarchive={(id) => handleArchiveConversation(id, false)}
          />
        ) : versionTimelineOverlay ? (
          <VersionTimeline
            fileName={topbarFileName}
            nodes={nodes}
            entries={versionTimelineEntries}
            onSelectEntry={handleSelectVersionTimelineEntry}
            onClose={() => setVersionTimelineOverlay(false)}
          />
        ) : showWorkspaceRestoreShell ? (
          <div style={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: 0, backgroundColor: APP.bgContent, fontFamily: APP.fontFamily }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: APP.textMuted, fontSize: 13, fontWeight: 500 }}>
              <span style={{ width: 8, height: 8, borderRadius: 999, backgroundColor: APP.cornerGradientStart, boxShadow: '0 0 0 4px rgba(212, 106, 58, 0.10)' }} />
              <span>Opening document...</span>
            </div>
          </div>
        ) : mode === 'empty' && !showProjectWorkspaceHeader ? (
          <EmptyState
            onQuickAction={(action) => {
              if (action === 'view_signatures') {
                navigate('/app/signatures');
                return;
              }
              const prefillMap: Record<string, string> = {
                send_signature: 'Help me send this document for electronic signature',
                multi_party_sign: 'I need multiple signers on this document — walk me through collecting signatures in order',
                place_sign_fields: 'Place signature and date fields on this PDF so I can send it for signing',
              };
              setChatPrefill(prefillMap[action] ?? action);
            }}
            onStarterTemplateClick={(title) => {
              const promptByTitle: Record<string, string> = {
                'Mutual NDA':
                  'Help me prepare a mutual NDA for e-signature: what to double-check before I send it for signing',
                'Offer letter':
                  'Help me prepare an offer letter package for e-signature for a new hire',
                'Vendor agreement':
                  'Help me prepare a vendor or services agreement to send for counterparty signature',
              };
              const prompt = promptByTitle[title] ?? `Help me use the ${title} template for signing`;
              setChatPrefill(prompt);
            }}
            onViewSignatures={() => { navigate('/app/signatures'); }}
            queuedFileCount={currentFiles.length}
            primaryFileName={currentFiles[0]?.name ?? null}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0, width: '100%', height: '100%' }}>
            {showProjectWorkspaceHeader && (
              <ProjectWorkspaceTabs
                projectTitle={
                  activeDocumentProject?.title
                  ?? topbarFileName
                  ?? activeDocumentSurface?.ref.fileName
                  ?? currentFile?.name
                  ?? draftTitle
                  ?? 'Document'
                }
                activeTab={showProjectShell ? projectWorkspaceTab : 'documents'}
                onTabChange={(tab) => {
                  if (!showProjectShell && tab === 'issues') {
                    clauseReviewPendingSourceRef.current = null;
                    openClauseReviewSetupScreen();
                    return;
                  }
                  handleProjectWorkspaceTabChange(tab);
                }}
                onRenameProject={
                  activeDocumentProject && !String(activeDocumentProject.id).startsWith('local:')
                    ? handleRenameActiveProject
                    : undefined
                }
                issuesCount={issuesData?.totalCount ?? 0}
                issuesHasUrgent={issuesData?.hasCriticalOrHigh ?? false}
              />
            )}
            <div style={{ display: 'flex', flexDirection: 'row', flex: 1, minHeight: 0, minWidth: 0, width: '100%', alignItems: 'stretch' }}>
            <>
            {currentFile && isAudioLikeFile(currentFile) ? (
              <AudioPlayer
                fileName={currentFile.name}
                meta={[
                  audioDurationLabel ?? '',
                  currentFile.size ? `${(currentFile.size / (1024 * 1024)).toFixed(1)} MB` : '',
                  getFileFormatInfo(currentFile.name).format,
                ].filter(Boolean).join(' · ')}
                src={filePreviewUrl ?? undefined}
                onActionClick={(label) => {
                  setChatPrefill(label);
                }}
                folders={foldersApi.folders}
                currentFolderId={currentConversationFolderId}
                onAddToFolder={(folderId) => {
                  if (currentConversationId) foldersApi.assignConversationToFolder(currentConversationId, folderId);
                }}
                onCreateFolder={(name) => foldersApi.createFolder(name)}
                onDownload={() => { downloadToolFile(currentResult?.downloadUrl, currentResult?.fileName); }}
              />
            ) : (
              clauseReviewSetupOpen && (!showProjectShell || projectWorkspaceTab === 'issues') ? (
                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    minHeight: 0,
                    backgroundColor: APP.bgDocViewer,
                    overflow: 'auto',
                    padding: 24,
                  }}
                >
                  <ClauseReviewSetupStrip
                    variant="panel"
                    lane={clauseReviewLane}
                    jurisdiction={clauseReviewJurisdiction}
                    loading={clauseReviewLoading}
                    onLaneChange={setClauseReviewLane}
                    onJurisdictionChange={setClauseReviewJurisdiction}
                    onRun={() => {
                      if (!clauseReviewLane) {
                        toast({ variant: 'destructive', title: 'Select a persona', description: 'Choose Real Estate or Business / Founder.' });
                        return;
                      }
                      if (!clauseReviewJurisdiction) {
                        toast({ variant: 'destructive', title: 'Select a state', description: 'Choose a state so Corner can apply state-specific clause flags.' });
                        return;
                      }
                      handleLaunchClauseReview(clauseReviewLane, clauseReviewJurisdiction || undefined);
                    }}
                    onDismiss={dismissClauseReviewSetup}
                  />
                </div>
              ) : showProjectShell && projectWorkspaceTab === 'issues' && issuesProjectId ? (
                // Issues tab: the doc column slot is replaced with
                // the project-level Issues panel. Chat panel stays
                // pinned to the right at its normal width — the
                // user keeps their conversation in view while
                // browsing flags.
                <IssuesPanel
                  data={issuesData}
                  loading={issuesLoading}
                  error={issuesError}
                  onRetry={refreshProjectIssues}
                  activeVersionId={effectiveActiveVersionId}
                  onOpenFlag={handleOpenIssueFlag}
                  onRunClauseReview={handleRunClauseReviewFromIssues}
                  onLaunchSetup={() => {
                    clauseReviewPendingSourceRef.current = null;
                    openClauseReviewSetupScreen();
                  }}
                />
              ) : (
              <DocumentColumn
                isMobile={isMobile}
                onMobileMenu={() => setMobileSidebarOpen(true)}
                fileName={topbarFileName ?? activeDocumentSurface?.ref.fileName}
                filePreviewUrl={documentPreviewVariant === 'output' && lastConversionOutput?.downloadUrl ? appendTokenToUrl(lastConversionOutput.downloadUrl, auth.getToken) : filePreviewUrl}
                documentId={activeVisibleDocFileId ?? activeDocFileId ?? activeDocumentSurface?.currentDocumentId ?? undefined}
                presentingMode={mode === 'esign' || documentPreviewVariant === 'output'}
                emptyStateTitle={showProjectShell ? 'Your files will appear here' : undefined}
                emptyStateDescription={showProjectShell ? 'Upload a document, pull one from history, or ask Corner to generate something for this project.' : undefined}
                documentVersions={displayDocumentVersions}
                versionWorkspaceTitle={
                  activeDocumentProject?.title
                  ?? conversationList.find((conversation) => conversation.id === currentConversationId)?.title
                  ?? topbarFileName
                }
                onRenameWorkspace={
                  // Only enable the rail's rename when an editable
                  // project actually backs the workspace. Without an
                  // active server-side project there's nowhere to
                  // persist the new name to, so we leave the pencil
                  // hidden rather than letting the user "rename" into
                  // a void.
                  activeDocumentProject ? handleRenameActiveProject : undefined
                }
                activeLogicalDocumentId={currentLogicalDocumentId}
                activeVersionId={effectiveActiveVersionId}
                versionRailPeekKey={versionRailPeekKey}
                versionVerifyResult={versionVerifyResult}
                onSelectDocumentVersion={handleSelectDocumentVersion}
                onVerifyDocumentVersion={handleVerifyDocumentVersion}
                onVersionHistory={() => setVersionTimelineOverlay(true)}
                folders={foldersApi.folders}
                currentFolderId={currentConversationFolderId}
                onAddToFolder={(folderId) => {
                  if (currentConversationId) foldersApi.assignConversationToFolder(currentConversationId, folderId);
                }}
                onCreateFolder={(name) => foldersApi.createFolder(name)}
                onToggleFolderContractsKind={(folderId, nextKind) => {
                  void foldersApi.setFolderKind(folderId, nextKind);
                }}
                onDownload={() => {
                  if (mode === 'drafting' && draftContent) {
                    // For drafts we open the browser's print dialog rather
                    // than going through the server. The PaginatedDocument
                    // component owns a `@media print` stylesheet (scoped to
                    // its rootId) that hides app chrome and renders the
                    // pages at full letter size. The user picks "Save as
                    // PDF" as the destination → the resulting PDF is a
                    // pixel-perfect match of the on-screen document with
                    // real text (selectable, not rasterized), proper
                    // headings, lists, links, etc.
                    //
                    // The optional document title becomes the default file
                    // name in the print dialog; we set document.title
                    // briefly so it's pre-filled, then restore it.
                    const previousTitle = document.title;
                    if (draftTitle) document.title = draftTitle;
                    try {
                      window.print();
                    } finally {
                      document.title = previousTitle;
                    }
                  } else if (currentResult?.downloadUrl) {
                    downloadToolFile(currentResult.downloadUrl, currentResult.fileName);
                  }
                }}
                onDownloadDocx={downloadCurrentAsDocx}
                imageMode={!!(currentFile && isImageLikeFile(currentFile))}
                imageDimensions={currentFile && isImageLikeFile(currentFile) && imageChatMetaLabel
                  ? imageChatMetaLabel.replace(/\s+\S+$/, '').replace('×', ' × ')
                  : undefined}
                imageFileSize={currentFile ? `${(currentFile.size / (1024 * 1024)).toFixed(1)} MB` : undefined}
                imageResult={currentFile && isImageLikeFile(currentFile) && currentResult ? {
                  url: currentResult.downloadUrl ?? '',
                  fileName: currentResult.fileName,
                  fileSize: currentResult.sizeBytes ? `${(currentResult.sizeBytes / (1024 * 1024)).toFixed(1)} MB` : '',
                  format: getFileFormatInfo(currentResult.fileName).format,
                  transformLabel: currentResult.message?.split(' ').slice(0, 2).join(' ') ?? 'Processed',
                } : undefined}
                onImageActionClick={(label) => {
                  setChatPrefill(label);
                }}
                variantLabel={lastConversionOutput ? (documentPreviewVariant === 'original' ? `Original ${getFileFormatInfo(currentFile?.name ?? 'file.pdf').label}` : lastConversionOutput.fileName) : undefined}
                onVariantAction={lastConversionOutput ? (action) => {
                  if (action === 'retrieve_original') {
                    setDocumentPreviewVariant('original');
                  } else if (action === 'retrieve_with_prompt') {
                    setChatPrefill('Convert this file');
                  }
                } : undefined}
                processing={processing}
                cotState={cot.state}
                esignFields={mode === 'esign' || esignFields.length > 0 ? esignFields : undefined}
                esignPageCount={mode === 'esign' || esignFields.length > 0 ? esignPdfPageCount : undefined}
                esignSignerName={auth.user?.displayName}
                onSignFieldClick={(field) => {
                  if (field.party === 'other' || field.signer === 'other') {
                    toast({
                      title: 'Not your marker',
                      description: "Those markers are not for you — they're for the recipient.",
                    });
                    return;
                  }
                  const idx = esignFields.indexOf(field);
                  if (idx < 0) return;
                  if (mode === 'esign') {
                    if (isTitleMarkerField(field)) {
                      setPrintedTitleModal({
                        fieldId: field.id ?? '',
                        initial: field.printedText ?? '',
                        label: field.label,
                        markPlacedOnSave: false,
                        configureOnly: true,
                      });
                      return;
                    }
                    setEsignFields((prev) =>
                      prev.map((f, i) => (i === idx ? { ...f, state: 'confirmed' as const } : f)),
                    );
                    return;
                  }
                  if (isTitleMarkerField(field)) {
                    setPrintedTitleModal({
                      fieldId: field.id ?? '',
                      initial: field.printedText ?? '',
                      label: field.label,
                      markPlacedOnSave: true,
                    });
                    return;
                  }
                  // Name / printed-text fields don't need the signature
                  // modal — they auto-fill with the user's display name.
                  // Without this branch the modal opened for every field
                  // type and the backend stamped the signature image
                  // even on name fields. The backend embed loop now
                  // renders text for type==='text' (see esign.ts).
                  const labelLower = field.label?.toLowerCase() ?? '';
                  const isDateOrPrintedName =
                    field.type === 'date' ||
                    /\bname\b|printed\s*name|full\s*name/.test(labelLower) ||
                    (field.type === 'text' && !isTitleMarkerField(field));
                  if (isDateOrPrintedName) {
                    setEsignFields((prev) =>
                      prev.map((f, i) => (i === idx ? { ...f, placed: true, signedAt: Date.now() } : f)),
                    );
                    return;
                  }
                  openStoredSignatureVerification(idx);
                }}
                esignActiveFieldIndex={esignActiveFieldIndex >= 0 ? esignActiveFieldIndex : undefined}
                onEsignNextField={() => {
                  if (mode === 'esign') return;
                  const nextIdx = esignFields.findIndex((f, i) => i > esignActiveFieldIndex && f.signer !== 'other' && !f.placed);
                  if (nextIdx >= 0) {
                    openStoredSignatureVerification(nextIdx);
                  }
                }}
                // V5: template-memory props. When the active doc matches
                // a saved template, the viewer surfaces the
                // SaveToTemplatePrompt after each field drag.
                linkedTemplateName={templateLink?.templateName}
                onSaveFieldToTemplate={templateLink?.onSaveFieldToTemplate}
                // v1 placement-redesign: the placement UX (color-coded
                // markers, AddFieldToolbar pills, RecipientLegend, ×
                // delete) is ALWAYS available for the sender. The
                // recipient-signing surface is a separate page
                // (SigningWorkspacePage) that passes mode='sign'
                // directly and never wires these callbacks, so the
                // recipient flow is unaffected. The pills serve as
                // the entry point — clicking one arms placement mode.
                esignViewerMode="configure"
                esignSigners={esignSigners}
                esignActiveSignerIndex={esignActiveSignerIndex}
                onSelectSigner={handleSigningGuideSelectSigner}
                esignPlacementMode={esignPlacementMode}
                onEsignPlacementModeChange={setEsignPlacementMode}
                onAddField={handleAddField}
                onDeleteField={handleDeleteField}
                onConfirmField={handleConfirmField}
                onMoveField={handleMoveField}
                onApplySignature={handleApplyReviewedSignature}
                applySignatureDisabled={!hasSelfSigningMarkers}
                onSendForSignature={openSendForSignatureModal}
                sendForSignatureDisabled={!signableFileIdForSending}
                onAddSigningRecipientEmail={handleAddSigningRecipientEmail}
                esignInlinePlacementToolbar={!(mode === 'esign' && !activeSigningRequest)}
                onLaunchSigning={handleLaunchSigning}
                signingButtonState={signingButtonState}
                splitPageCount={lastTool === 'split_pdf' && currentResult?.splitPageCount ? currentResult.splitPageCount : undefined}
                ocrOverlay={lastTool === 'ocr' && currentResult?.ocrMeta ? {
                  confidence: currentResult.ocrMeta.confidence,
                  wordCount: (currentResult.textContent ?? '').split(/\s+/).filter(Boolean).length,
                } : undefined}
                qrPreview={lastTool === 'generate_qr' && currentResult?.qrMeta ? {
                  imageUrl: currentResult.downloadUrl ?? currentResult.previewUrl ?? '',
                  payload: currentResult.qrMeta.payload,
                  format: currentResult.qrMeta.format,
                  sizePx: currentResult.qrMeta.sizePx,
                } : undefined}
                reviewMode={mode === 'review'}
                reviewSuggestions={mode === 'review' ? review.suggestions : undefined}
                reviewActiveSuggestionId={mode === 'review' ? review.activeSuggestionId : undefined}
                reviewStats={mode === 'review' ? { total: review.stats.total, remaining: review.stats.remaining } : undefined}
                onExitReview={() => { review.reset(); setClauseReviewResult(null); setFlagGroundingStatuses({}); setMode('result'); }}
                onReviewAnnotationClick={(id) => review.setActiveSuggestionId(id)}
                reviewDocumentView={review.documentView}
                onReviewDocumentViewChange={review.setDocumentView}
                reviewUndoEntry={review.undoEntry ? { sectionRef: review.undoEntry.sectionRef, action: review.undoEntry.action } : null}
                onReviewUndo={review.undoLast}
                reviewGenerating={review.generating}
                reviewGeneratingError={review.generatingError}
                riskSnapshot={riskSummary && riskSummary.nudgeLevel !== 'silent' ? {
                  nudgeLevel: riskSummary.nudgeLevel,
                  label: riskSummary.label,
                  laneKey: riskSummary.lane,
                  jurisdiction: riskSummary.jurisdiction,
                } : null}
                onRiskSnapshotClick={() => { clauseReviewPendingSourceRef.current = null; openClauseReviewSetupScreen(); }}
                draftMode={documentPreviewVariant !== 'output' && mode !== 'esign' && (mode === 'drafting' || Boolean(activeDocumentSurface?.draftHtml))}
                draftContent={draftContent || activeDocumentSurface?.draftHtml || currentResult?.draftHtml || ''}
                draftIsStreaming={
                  draftStreamingConversationId != null
                  && draftStreamingConversationId === currentConversationId
                }
                onShare={auth.user ? () => setShareModalOpen(true) : undefined}
                presenceUsers={collab.presenceUsers.filter((u) => u.userId !== auth.user?.id)}
                remoteCursors={collab.remoteCursors}
                onCursorMove={collab.sendCursorPosition}
                commentCount={commentPins.filter((p) => p.status === 'open').length}
                commentPins={commentPins}
                activeCommentPinId={activeCommentPinId}
                onCommentPinClick={(pinId) => { setActiveCommentPinId(pinId); setCollabTab('comments'); }}
                onCommentPinMove={auth.user ? (pinId, xPercent, yPercent) => {
                  if (!currentConversationId) return;
                  const cToken = auth.getToken();
                  if (!cToken) return;
                  setCommentPins((prev) => prev.map((p) => p.pinId === pinId ? { ...p, xPercent, yPercent } : p));
                  axios.patch(`/api/comments/${currentConversationId}/${pinId}/move`, { xPercent, yPercent }, { headers: { Authorization: `Bearer ${cToken}` } })
                    .catch((err: unknown) => { const e = getActionError('move comment', err); toast({ title: e.title, description: e.description, variant: 'destructive' }); });
                } : undefined}
                onDocumentClick={auth.user ? (xPercent, yPercent, pageNumber) => {
                  setPendingComment({ xPercent, yPercent, pageNumber });
                } : undefined}
                commentMode={commentMode}
                onCommentModeChange={auth.user ? setCommentMode : undefined}
                readerView={readerView}
                onReaderViewChange={auth.user ? setReaderView : undefined}
                documentText={documentText}
                editHighlightAnchor={editHighlightAnchor}
                onSelectionComment={auth.user ? (quote, yPercent) => {
                  setPendingComment({ xPercent: 0.5, yPercent, pageNumber: 1, quote });
                } : undefined}
                onAskCornerAboutSelection={(snippet) => {
                  setPendingSelectedSnippet(snippet);
                  setChatCollapsed(false);
                  setCollabTab('analysis');
                }}
                pendingComment={pendingComment}
                onCommentSubmit={auth.user ? (body) => {
                  console.log('[App] onCommentSubmit fired', { body: body.slice(0, 50), pendingComment, conversationId: currentConversationId });
                  if (!pendingComment || !currentConversationId) return;
                  const cToken = auth.getToken();
                  if (!cToken) return;
                  axios.post(`/api/comments/${currentConversationId}`, {
                    pageNumber: pendingComment.pageNumber,
                    xPercent: pendingComment.xPercent,
                    yPercent: pendingComment.yPercent,
                    body,
                    quote: pendingComment.quote,
                  }, { headers: { Authorization: `Bearer ${cToken}` } })
                    .then((res) => {
                      // Dedupe: socket handler may have already inserted this pin
                      if (!insertedPinIds.current.has(res.data.pinId)) {
                        insertedPinIds.current.add(res.data.pinId);
                        setCommentPins((prev) => [res.data, ...prev]);
                      }
                    })
                    .catch((err: unknown) => { const e = getActionError('add comment', err); toast({ title: e.title, description: e.description, variant: 'destructive' }); });
                  setPendingComment(null);
                } : undefined}
                onCommentCancel={() => setPendingComment(null)}
              />
              )
            )}
            {!chatCollapsed && mode === 'esign' && activeSigningRequest && (
              <SigningStatusPanel
                signingRequest={activeSigningRequest}
                currentUserEmail={auth.user?.email}
                onSendReminder={handleSendReminder}
                onResendLink={handleResendLink}
                onRevoke={handleRevokeSigningRequest}
                onDownload={handleDownloadSignedCopy}
                onDownloadDocx={handleDownloadSignedCopyDocx}
                onRefresh={handleRefreshSigningRequest}
                onBack={() => {
                  setActiveSigningRequest(null);
                  setEsignFields([]);
                  setEsignRecipientDrafts([]);
                  setEsignPlacementMode(null);
                  setMode('empty');
                  navigate('/app/signatures');
                }}
                onExportToDrive={() => {
                  setExportModal({
                    destination: 'drive',
                    fileName: activeSigningRequest?.documentName ?? 'Signed Document',
                    fileId: currentResult?.fileId,
                  });
                }}
                onArchive={async () => {
                  if (currentConversationId) {
                    await deleteConversation(currentConversationId).catch(() => {});
                    toast({ title: 'Archived', description: 'Signing request moved to trash.' });
                    setActiveSigningRequest(null);
                    setMode('empty');
                    navigate('/');
                  }
                }}
              />
            )}
            {!chatCollapsed && mode === 'review' && (
              <>
              {/* Clause review header — shown only for clause reviews */}
              {isClauseReview && clauseReviewResult && (
                <div style={{
                  padding: '10px 16px', borderBottom: '1px solid #E8DED4',
                  display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0,
                  fontFamily: 'Geist, system-ui, sans-serif',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#D97645' }}>
                      Clause Review
                    </span>
                    <span style={{ fontSize: 11, color: '#9E9189' }}>
                      {clauseReviewResult.lane === 'real_estate' ? 'Real Estate' : 'Business / Founder'}
                      {clauseReviewResult.jurisdiction ? ` · ${clauseReviewResult.jurisdiction}` : ''}
                    </span>
                    {clauseReviewResult.triggerSource === 'auto' && (
                      <span
                        title="This review was automatically created after import"
                        style={{
                          fontSize: 9, fontWeight: 500, letterSpacing: '0.04em',
                          color: '#B5AA9C', backgroundColor: '#F5F2EC',
                          padding: '2px 6px', borderRadius: 3,
                        }}
                      >
                        Auto scan
                      </span>
                    )}
                  </div>
                  {clauseReviewResult.executiveSummary && (
                    <p style={{ margin: 0, fontSize: 12, color: '#6B5E52', lineHeight: '18px' }}>
                      {clauseReviewResult.executiveSummary}
                    </p>
                  )}
                  {clauseReviewResult.coverage.truncated && (
                    <p style={{ margin: 0, fontSize: 11, color: '#B5AA9C', lineHeight: '16px', fontStyle: 'italic' }}>
                      {getCoverageNote(clauseReviewResult.coverage)}
                    </p>
                  )}
                  {/* Grounding completeness indicator — mirrors CoT partial completion */}
                  {(() => {
                    const gs = computeGroundingSummary(flagGroundingStatuses, clauseReviewResult.flags.length);
                    const msg = getGroundingMessage(gs);
                    if (!msg) return null;
                    const dotColor = msg.level === 'success' ? '#4A9E6B' : msg.level === 'info' ? '#D4A843' : '#C04A2A';
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 }}>
                        <div style={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: dotColor, flexShrink: 0 }} />
                        <span style={{ fontSize: 10, color: '#9E9189', lineHeight: '14px', fontFamily: 'Geist, system-ui, sans-serif' }}>
                          {msg.text}
                        </span>
                      </div>
                    );
                  })()}
                </div>
              )}
              <ReviewPanel
                suggestions={review.suggestions}
                activeSuggestionId={review.activeSuggestionId}
                stats={review.stats}
                onSelectSuggestion={review.setActiveSuggestionId}
                onAccept={review.acceptSuggestion}
                onDismiss={review.dismissSuggestion}
                onAcceptAll={review.acceptAllRemaining}
                onDismissAll={review.dismissAllRemaining}
                onSend={(msg) => handleSend(msg)}
                onClose={() => { review.reset(); setClauseReviewResult(null); setFlagGroundingStatuses({}); setMode('result'); }}
                generating={review.generating}
                generatingSteps={review.generatingSteps}
                foundItems={review.foundItems}
                generatingProgress={review.generatingProgress}
                generatingError={review.generatingError}
                onRetry={async () => {
                  const token = auth.getToken();
                  const fileId = currentResult?.sourceFileId ?? currentResult?.fileId;
                  if (!token || !fileId) return;
                  review.startGenerating([], async () => {});
                  try {
                    let rid = review.reviewId;
                    if (!rid) {
                      const created = await reviewApi.createReview({
                        sourceKind: 'file',
                        sourceId: fileId,
                        title: topbarFileName ?? currentFile?.name ?? 'Document Review',
                      });
                      rid = created.reviewId;
                      review.setReviewId(rid);
                    }
                    const generated = await reviewApi.generateReview(rid);
                    const suggestions = generated.suggestions.map((s) => ({
                      id: s.id,
                      sectionRef: s.sectionRef,
                      sectionTitle: s.sectionTitle,
                      priority: s.priority,
                      category: s.category,
                      summary: s.summary,
                      originalText: s.originalText,
                      proposedText: s.proposedText,
                      rationale: s.rationale,
                      tags: s.tags,
                      shortLabel: s.shortLabel,
                      status: s.status,
                    }));
                    review.loadSuggestions(suggestions);
                  } catch (err) {
                    review.triggerGeneratingError(
                      err instanceof Error ? err.message : 'Failed to generate review.',
                      'draft',
                    );
                  }
                }}
                onRunOCR={() => { handleSend('Run OCR on this document'); }}
                onSaveRevised={async () => {
                  const docText = currentResult?.textContent;
                  if (!docText) {
                    toast({ title: 'Nothing to save', description: 'No document text available.', variant: 'destructive' });
                    return;
                  }
                  let revised = docText;
                  if (isClauseReview) {
                    const acceptedEdits = review.suggestions.filter((s) => s.status === 'accepted');
                    const patchResult = applyClausePatches(docText, acceptedEdits.map(buildClausePatchFromSuggestion));
                    for (const failure of patchResult.failures) {
                      review.markSuggestionApplyFailed(failure.suggestionId, failure.reason);
                      clauseFlagsLog('apply-failed', {
                        suggestionId: failure.suggestionId,
                        reason: failure.reason,
                        selectorInfo: {
                          kind: failure.patch.kind,
                          hasOriginalText: Boolean(failure.patch.originalText),
                          hasAnchorText: Boolean(failure.patch.anchorText),
                        },
                        documentId: currentResult?.fileId ?? sessionPrimaryDocument?.fileId ?? null,
                        versionId: effectiveActiveVersionId,
                        destination: 'download',
                      });
                    }
                    if (patchResult.failures.length > 0) {
                      toast({
                        title: 'Some accepted suggestions could not be applied',
                        description: 'Failed cards are marked in the review panel. Fix or dismiss those before exporting a revised document.',
                        variant: 'destructive',
                      });
                      return;
                    }
                    revised = patchResult.text;
                  } else {
                    // Legacy review mode still uses exact text replacement.
                    for (const s of review.suggestions) {
                      if (s.status === 'accepted' && s.originalText && s.proposedText) {
                        revised = revised.replace(s.originalText, s.proposedText);
                      }
                    }
                  }
                  const baseName = (currentResult?.fileName ?? currentFile?.name ?? 'Document').replace(/\.[^.]+$/, '');
                  try {
                    const exportToken = auth.getToken();
                    const res = await axios.post('/api/export-text', { text: revised, format: 'docx', fileName: `${baseName} (Revised)` }, { responseType: 'blob', headers: exportToken ? { Authorization: `Bearer ${exportToken}` } : {} });
                    const url = URL.createObjectURL(res.data);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${baseName} (Revised).docx`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast({ title: 'Saved', description: 'Revised Word document (.docx) downloaded.' });
                  } catch (err) {
                    const e = getActionError('export revised document', err);
                    toast({ title: e.title, description: e.description, variant: 'destructive' });
                  }
                }}
                onApplyRevisedToWorkspace={handleApplyClauseReviewToWorkspace}
                showApplyRevisedToWorkspace={Boolean(isClauseReview && currentConversationId)}
                applyRevisedToWorkspaceDisabled={
                  review.stats.remaining > 0
                  || !currentResult?.textContent?.trim()
                  || !review.suggestions.some((s) => (
                    s.status === 'accepted' && !s.appliedToDocument
                  ))
                }
                applyRevisedToWorkspaceBusy={clauseReviewApplyBusy}
                onSendForSignature={openSendForSignatureModal}
                onExportToGmail={() => setExportModal({ destination: 'gmail', fileName: currentResult?.fileName ?? currentFile?.name ?? 'Document', fileId: currentResult?.fileId })}
                onViewOriginal={() => setDocumentPreviewVariant('original')}
                clauseReviewMode={isClauseReview}
                onLocateInDocument={isClauseReview && clauseReviewResult ? (flagId: string, quote: string, pageHint: number | null, sectionRef: string, sectionTitle: string) => {
                  // Navigate iframe to page immediately (fast feedback)
                  if (pageHint != null) {
                    const iframe = document.querySelector('iframe[title="Document preview"]') as HTMLIFrameElement | null;
                    if (iframe?.src) {
                      const base = iframe.src.replace(/#.*$/, '');
                      iframe.src = `${base}#page=${pageHint}`;
                    }
                  }
                  // Copy quote to clipboard as fallback
                  navigator.clipboard.writeText(quote).catch(() => {});

                  // Call locate API to get span-level match + open drawer
                  setSourcePreviewLoading(true);
                  setSourcePreview({
                    result: { matched: false, page: pageHint, before: '', snippet: quote, after: '', pageText: null, matchStartInPage: null, matchEndInPage: null },
                    sectionRef,
                    sectionTitle,
                  });
                  clauseReviewApi.locateClauseFlag(clauseReviewResult.clauseReviewId, flagId)
                    .then((locateResult) => {
                      setSourcePreview({ result: locateResult, sectionRef, sectionTitle });
                      // Record grounding status for this flag
                      setFlagGroundingStatuses((prev) => ({
                        ...prev,
                        [flagId]: groundingStatusFromLocateResult(locateResult),
                      }));
                      // Update iframe page if locate found a different page
                      if (locateResult.page != null && locateResult.page !== pageHint) {
                        const iframe = document.querySelector('iframe[title="Document preview"]') as HTMLIFrameElement | null;
                        if (iframe?.src) {
                          const base = iframe.src.replace(/#.*$/, '');
                          iframe.src = `${base}#page=${locateResult.page}`;
                        }
                      }
                    })
                    .catch(() => {
                      // Record as missing on error
                      setFlagGroundingStatuses((prev) => ({ ...prev, [flagId]: 'missing' }));
                    })
                    .finally(() => setSourcePreviewLoading(false));
                } : undefined}
                pageHints={isClauseReview && clauseReviewResult ? Object.fromEntries(
                  clauseReviewResult.flags.map((f) => [f.flagId, f.pageHint ?? null])
                ) : undefined}
                flagGroundingStatuses={isClauseReview ? flagGroundingStatuses : undefined}
              />
              </>
            )}
            {/* Resize handle between document/issues column and chat panel */}
            {!isMobile && !chatCollapsed && mode !== 'review' && !(mode === 'esign' && activeSigningRequest) && (
              <div
                style={{
                  width: 4,
                  flexShrink: 0,
                  cursor: 'col-resize',
                  backgroundColor: 'transparent',
                  borderLeft: `1px solid ${APP.border}`,
                  transition: 'background-color 0.1s',
                  zIndex: 5,
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = '#D9764530'; }}
                onMouseLeave={(e) => { if (!chatResizing.current) (e.currentTarget as HTMLElement).style.backgroundColor = 'transparent'; }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  chatResizing.current = true;
                  const startX = e.clientX;
                  const startW = chatPanelWidth;
                  const handle = e.currentTarget as HTMLElement;
                  handle.style.backgroundColor = '#D9764540';
                  document.body.style.cursor = 'col-resize';
                  document.body.style.userSelect = 'none';

                  const onMove = (ev: MouseEvent) => {
                    const delta = startX - ev.clientX;
                    const next = Math.max(320, Math.min(maxChatPanelWidth, startW + delta));
                    setChatPanelWidth(next);
                  };
                  const onUp = () => {
                    chatResizing.current = false;
                    handle.style.backgroundColor = 'transparent';
                    document.body.style.cursor = '';
                    document.body.style.userSelect = '';
                    window.removeEventListener('mousemove', onMove);
                    window.removeEventListener('mouseup', onUp);
                    setChatPanelWidth((w) => {
                      try { localStorage.setItem('corner.chatPanelWidth', String(w)); } catch {}
                      return w;
                    });
                  };
                  window.addEventListener('mousemove', onMove);
                  window.addEventListener('mouseup', onUp);
                }}
                role="separator"
                aria-orientation="vertical"
              />
            )}
            {!isMobile && chatCollapsed && mode !== 'review' && !(mode === 'esign' && activeSigningRequest) && (
              <div
                style={{
                  width: 44,
                  flexShrink: 0,
                  borderLeft: `1px solid ${APP.border}`,
                  backgroundColor: APP.bgChatPanel,
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'center',
                  paddingTop: 12,
                }}
              >
                <button
                  type="button"
                  aria-label="Open chat"
                  title="Open chat"
                  onClick={() => {
                    setChatCollapsed(false);
                    setCollabTab('analysis');
                  }}
                  style={{
                    width: 30,
                    height: 30,
                    borderRadius: 8,
                    border: `1px solid ${APP.border}`,
                    backgroundColor: APP.bgContent,
                    color: APP.textMuted,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: 0,
                    boxShadow: '0 1px 2px rgba(36, 22, 12, 0.08)',
                  }}
                >
                  <svg width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true">
                    <path d="M3 3.25h9v6.5H6.25L3 11.75v-8.5Z" stroke="currentColor" strokeWidth="1.35" strokeLinejoin="round" />
                    <path d="M5 5.5h5M5 7.5h3.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>
            )}
            {!chatCollapsed && mode !== 'review' && !(mode === 'esign' && activeSigningRequest) && (
              <div
                onDragOver={(event) => {
                  if (event.dataTransfer.types.includes('application/x-corner-file')) {
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'copy';
                  }
                }}
                onDrop={handleChatProjectFileDrop}
                style={{ display: 'contents' }}
              >
              <MobileBottomSheet
                disabled={!isMobile}
                open={mobileChatOpen}
                onOpenChange={setMobileChatOpen}
                title={mode === 'esign' ? 'Fill' : 'Corner chat'}
              >
              <ChatPanel
                title={mode === 'esign' ? 'Fill' : 'Analysis'}
                subtitle={topbarFileName ? `Reviewing: ${topbarFileName}` : undefined}
                compactHeader={mode === 'esign' || !!(currentFile && isAudioLikeFile(currentFile))}
                compactHeaderTrailing={esignCompactHeaderTrailing}
                messages={chatMessages}
                onSend={handleSend}
                signingGuide={signingGuideNode}
                onChipClick={(label) => {
                  if (mode === 'esign' && label === 'Set up signature') {
                    navigate('/auth/signature-setup', { state: { from: 'settings' } });
                  } else if (mode === 'esign' && label === 'Cancel') {
                    setMode('empty');
                    setProcessing(null);
                    setEsignFields([]);
                    setEsignRecipientDrafts([]);
                    setEsignActiveSignerIndex(0);
                    setEsignPlacementMode(null);
                  } else if (mode === 'esign' && label === 'Add Signature Field') {
                    addManualField('signature', 'Manual signature field');
                    setMessages((prev) => [...prev, {
                      id: crypto.randomUUID(),
                      role: 'corner' as const,
                      content: 'I added a signature marker. Drag it to the correct line, or use the signing guide to add more markers, then use **Apply changes** in the guide.',
                      timestamp: Date.now(),
                    }]);
                  } else if (
                    (mode === 'esign' || (mode === 'result' && esignFields.length > 0))
                    && (label === 'Apply changes' || label === 'Apply my signature' || label === 'Apply your signature' || label === 'Sign now')
                  ) {
                    void handleApplyReviewedSignature();
                  } else if (mode === 'esign' && label === 'Add initials') {
                    addManualField('initials', 'Manual initials field');
                    setMessages((prev) => [...prev, {
                      id: crypto.randomUUID(),
                      role: 'corner' as const,
                      content: 'I added an initials marker. Drag it to the correct line, add any other markers you need from the signing guide, then use **Apply changes** once.',
                      timestamp: Date.now(),
                    }]);
                  } else if (mode === 'esign' && label === 'Review first') {
                    setMessages((prev) => [...prev, {
                      id: crypto.randomUUID(), role: 'user' as const,
                      content: 'Review first', timestamp: Date.now(),
                    }, {
                      id: crypto.randomUUID(), role: 'corner' as const,
                      content: 'Review the document on the left. When you\'re ready, click a signature field or use **Sign now** to begin signing.',
                      timestamp: Date.now(),
                    }]);
                  } else if (mode === 'esign' && label === 'Add signers') {
                    openSendForSignatureModal();
                  } else if (mode === 'esign' && label === 'Add recipients') {
                    openSendForSignatureModal();
                  } else if (mode === 'result' && label === 'Add recipients') {
                    openSendForSignatureModal();
                  } else if (label === 'Run clause review') {
                    clauseReviewPendingSourceRef.current = null;
                    openClauseReviewSetupScreen();
                  } else if (['Summarize', 'Study questions', 'Key terms', 'Get citation', 'Review contract', 'Draft email', 'Convert to PDF', 'Extract text', 'Split by pages', 'Summarize sections'].includes(label)) {
                    handleSend(label);
                  } else if (label === 'Merge all' && currentFiles.length >= 2) {
                    handleSend(`Merge all ${currentFiles.length} into one PDF`);
                  } else if (label === 'Summarize all' && currentFiles.length >= 2) {
                    handleSend('Summarize all documents');
                  } else if (label === 'Compare docs' && currentFiles.length >= 2) {
                    handleSend('Compare these documents and highlight key differences');
                  } else if (label.startsWith('sign as the ')) {
                    // Role selection chip from role_selection_needed — auto-send to trigger signing
                    handleSend(label);
                  } else {
                    setChatPrefill(label);
                  }
                }}
                attachedFiles={currentFiles.map((f) => ({
                  name: f.name,
                  type: getFileFormatInfo(f.name).format,
                }))}
                onRemoveFile={(index) => {
                  setCurrentFiles((prev) => {
                    const next = prev.filter((_, i) => i !== (index ?? 0));
                    if (next.length === 0 && filePreviewUrl) {
                      URL.revokeObjectURL(filePreviewUrl);
                      setFilePreviewUrl(null);
                    }
                    return next;
                  });
                }}
                onClose={() => setChatCollapsed(true)}
                disabled={!!processing || !!activeQueryRunId}
                onStop={(processing || activeQueryRunId) ? handleStopProcessing : undefined}
                wide={toolPanelExpanded}
                processing={!!processing}
                cotState={cot.state}
                onConfirmStep={handleConfirmStep}
                onCancelStep={handleCancelStep}
                onClarifyAnswer={handleClarifyAnswer}
                onClarifyCancel={handleClarifyCancel}
                onEditBatchChange={handleEditBatchChange}
                onEditBatchVersionReady={handleEditBatchVersionReady}
                onEditBatchRecoveryVersionReady={handleEditBatchRecoveryVersionReady}
                documentVersions={displayDocumentVersions}
                onSelectDocumentVersion={handleSelectDocumentVersion}
                editArtifactFormat="pdf"
                onToggleActivityPhase={cot.toggleActivityPhase}
                onToggleActivityStep={cot.toggleActivityStep}
                cotErrorRenderContext={{
                  userPlan: auth.user?.plan,
                  usage: auth.user?.usage,
                  failureCount: cot.state.failureCount,
                  fileName: currentFile?.name ?? currentResult?.fileName,
                }}
                queryRun={activeQueryRunId ? queryRun : undefined}
                queryRunBootContext={activeQueryRunId ? queryRunBootContext : undefined}
                onExportClick={(destination, messageId) => {
                  const exportMsg = chatMessages.find((m) => m.id === messageId);
                  const exportText = exportMsg?.analysisResult
                    ? `${exportMsg.analysisResult.title}\n\n${exportMsg.analysisResult.body}`
                    : exportMsg?.content ?? '';
                  const exportFileName = exportMsg?.analysisResult?.title.split('—')[0].trim() || topbarFileName || 'export';

                  if (destination === 'save') {
                    if (exportText) {
                      api.exportText(exportText, 'pdf', exportFileName).then(() => {
                        toast({ title: 'PDF saved' });
                      }).catch((err) => {
                        const e = getActionError('export document', err);
                        toast({ title: e.title, description: e.description, variant: 'destructive' });
                      });
                    }
                    return;
                  }
                  setExportModal({ destination, text: exportText, fileName: exportFileName, fileId: currentResult?.fileId });
                }}
                hasMoreMessages={messagesHasMore}
                loadingEarlierMessages={loadingEarlierMessages}
                onLoadEarlierMessages={handleLoadEarlierMessages}
                rateLimitInfo={rateLimitInfo}
                onSaveAsWorkflow={auth.user ? handleSaveAsWorkflow : undefined}
                onSaveContractSendAsWorkflow={auth.user ? handleSaveContractSendAsWorkflow : undefined}
                onEditLinearIssue={(issueId) => setLinearEditIssueId(issueId)}
                activeTab={collabTab}
                onTabChange={(tab) => {
                  setCollabTab(tab);
                  if (tab === 'comments') setCommentMode(true);
                  else setCommentMode(false);
                }}
                commentCount={commentPins.filter((p) => p.status === 'open').length}
                commentPins={commentPins}
                activeCommentPinId={activeCommentPinId}
                onCommentPinClick={(pinId) => setActiveCommentPinId(pinId)}
                widthOverride={
                  isMobile
                    ? undefined
                    : desktopChatPanelRenderWidth
                }
                mobileSheet={isMobile}
                getToken={auth.getToken}
                composerAccessory={
                  mode === 'result' && lastTool === 'esign' && auth.user ? (
                    <FoldersButton
                      getToken={auth.getToken}
                      onPickFile={(file) => handleFilesReceived([...currentFiles, file])}
                      enabled={!!auth.user}
                    />
                  ) : undefined
                }
                selectedSnippet={pendingSelectedSnippet}
                onRemoveSnippet={() => setPendingSelectedSnippet(null)}
              />
              </MobileBottomSheet>
              </div>
            )}
            </>
            {!isMobile && <ToolPanel
              expanded={toolPanelExpanded}
              activeIndex={activeToolIndex}
              onToolClick={(i) => { setActiveToolIndex(i); setToolPanelExpanded(true); }}
              onExpand={() => setToolPanelExpanded(true)}
              onCollapse={() => setToolPanelExpanded(false)}
              onToolSelect={(toolId, toolSettings) => {
                const msg = toolSettings
                  ? `Run ${toolId} with custom settings`
                  : toolId.replace(/_/g, ' ');
                handleSend(msg);
              }}
              settings={panelSettings}
              onSettingsChange={(patch) => setPanelSettings((prev) => ({ ...prev, ...patch }))}
            />}
            </div>
          </div>
        )}
      </AppLayout>
      {isMobile && (
        <MobileSidebarDrawer open={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)}>
          <Sidebar
            open
            onToggle={() => setMobileSidebarOpen(false)}
            activeNav={activeNav}
            onNavChange={(nav) => { setMobileSidebarOpen(false); setNewFlowActive(false); navigate(`/app/${nav}`); }}
            onNewFlow={() => { setMobileSidebarOpen(false); handleNewFlow(); }}
            onSignatures={() => { setMobileSidebarOpen(false); navigate('/app/signatures'); }}
            signaturesActive={showSignatures}
            foldersActive={showFolders}
            settingsActive={showSettings}
            onFolders={() => { setMobileSidebarOpen(false); navigate('/app/folders'); }}
            historyItems={sidebarHistoryItems}
            onHistoryItemClick={(id) => {
              setMobileSidebarOpen(false);
              const item = sidebarHistoryItems.find((h) => h.id === id);
              if (item?.historyLocked) {
                toast({ title: 'Upgrade to Pro', description: 'Upgrade to Pro to access conversations older than 7 days.', variant: 'default' });
                setPaywallOpen(true);
                return;
              }
              setNewFlowActive(false);
              handleRestoreConversation(item?.conversationId ?? id);
            }}
            user={sidebarUser}
            onUpgrade={() => { setMobileSidebarOpen(false); setPaywallOpen(true); }}
            onSettings={() => { setMobileSidebarOpen(false); navigate('/app/settings'); }}
            onSignOut={handleSignOut}
            onReportBug={() => window.open('mailto:nick@trycorner.co?subject=Bug%20Report', '_blank')}
            onBrowseSource={(name) => { setMobileSidebarOpen(false); setFileBrowserSource(name); }}
            integrationStatuses={integrationStatuses}
            onConnectSource={(name) => {
              const providerSlug = Object.entries(PROVIDER_NAME_MAP).find(([, v]) => v === name)?.[0];
              if (providerSlug) window.location.href = `/api/auth/${providerSlug}`;
            }}
            onDisconnectSource={async (name) => {
              const token = auth.getToken();
              const providerSlug = Object.entries(PROVIDER_NAME_MAP).find(([, v]) => v === name)?.[0];
              if (!token || !providerSlug) return;
              try {
                await axios.post(`/api/integrations/${providerSlug}/disconnect`, {}, { headers: { Authorization: `Bearer ${token}` } });
                await fetchIntegrationStatuses();
              } catch { /* silent */ }
            }}
          />
        </MobileSidebarDrawer>
      )}
      {/* Export modals overlay */}
      {exportModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0,0,0,0.2)',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setExportModal(null); }}
        >
          {(exportModal.destination === 'dropbox' || exportModal.destination === 'drive') && (
            <FolderBrowserModal
              service={exportModal.destination}
              onSave={(folderId, folderName) => {
                toast({ title: `Saved to ${folderName}`, description: `File exported to ${exportModal.destination === 'dropbox' ? 'Dropbox' : 'Google Drive'}.` });
                setExportModal(null);
              }}
              onClose={() => setExportModal(null)}
              getToken={auth.getToken}
              fileId={exportModal.fileId}
            />
          )}
          {exportModal.destination === 'slack' && (
            <SlackExportModal
              onSend={(channelId, channelName) => {
                toast({ title: `Sent to #${channelName}`, description: 'Message shared to Slack.' });
                setExportModal(null);
              }}
              onClose={() => setExportModal(null)}
              getToken={auth.getToken}
            />
          )}
          {exportModal.destination === 'gmail' && (
            <GmailExportModal
              fileName={exportModal.fileName ?? 'export.pdf'}
              onSend={(to, subject) => {
                toast({ title: 'Email sent', description: `Sent to ${to.join(', ')}.` });
                setExportModal(null);
              }}
              onClose={() => setExportModal(null)}
              getToken={auth.getToken}
              fileId={exportModal.fileId}
            />
          )}
          {exportModal.destination === 'notion' && (
            <NotionDatabaseEntryModal
              onSave={(entryTitle, dbName) => {
                toast({ title: `Saved to ${dbName}`, description: `"${entryTitle}" added to Notion database.` });
                setExportModal(null);
              }}
              onClose={() => setExportModal(null)}
              getToken={auth.getToken}
              title={exportModal.fileName}
              content={exportModal.text}
            />
          )}
          {exportModal.destination === 'linear' && (
            <LinearCreateIssueModal
              onSave={(issueTitle, teamName) => {
                toast({ title: 'Issue created', description: `"${issueTitle}" added to ${teamName}.` });
                setExportModal(null);
              }}
              onClose={() => setExportModal(null)}
              getToken={auth.getToken}
              title={exportModal.fileName}
              description={exportModal.text}
            />
          )}
        </div>
      )}
      <IntegrationFileBrowserModal
        open={!!fileBrowserSource}
        onClose={() => setFileBrowserSource(null)}
        integrationName={fileBrowserSource === 'Gmail' ? 'Gmail' : fileBrowserSource === 'Drive' ? 'Google Drive' : fileBrowserSource === 'Dropbox' ? 'Dropbox' : fileBrowserSource === 'Slack' ? 'Slack' : fileBrowserSource ?? ''}
        integrationColor={fileBrowserSource === 'Gmail' ? '#EA4335' : fileBrowserSource === 'Drive' ? '#4285F4' : fileBrowserSource === 'Dropbox' ? '#0061FF' : fileBrowserSource === 'Slack' ? '#4A154B' : '#6B5E52'}
        integrationIcon={
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2.7 14L5.3 9H1L8.7 2L6.3 7H11L2.7 14Z" fill="#FFFFFF" />
          </svg>
        }
        breadcrumb={fileBrowserBreadcrumb.length > 0 ? fileBrowserBreadcrumb : ['Files']}
        files={fileBrowserLoading ? [] : fileBrowserFiles}
        onBreadcrumbClick={(index) => {
          // Navigate back to breadcrumb level
          const newBreadcrumb = fileBrowserBreadcrumb.slice(0, index + 1);
          setFileBrowserBreadcrumb(newBreadcrumb);
          // Reset path — for simplicity go back to root for index 0
          if (index === 0) {
            setFileBrowserPath('/');
          }
        }}
        onFolderOpen={(name) => {
          const newPath = fileBrowserSource === 'Dropbox'
            ? `${fileBrowserPath === '/' ? '' : fileBrowserPath}/${name}`
            : name; // For Drive, folder name might need to be folder ID
          setFileBrowserPath(newPath);
          setFileBrowserBreadcrumb((prev) => [...prev, name]);
        }}
        onImport={async (selectedFiles) => {
          const token = auth.getToken();
          const providerSlug = fileBrowserSource === 'Dropbox' ? 'dropbox' : fileBrowserSource === 'Drive' ? 'google-drive' : null;
          if (!token || !providerSlug) {
            toast({ title: `Imported ${selectedFiles.length} file${selectedFiles.length !== 1 ? 's' : ''}`, description: `From ${fileBrowserSource}` });
            setFileBrowserSource(null);
            return;
          }
          try {
            const body = providerSlug === 'dropbox'
              ? { paths: selectedFiles }
              : { fileIds: selectedFiles };
            await axios.post(`/api/integrations/${providerSlug}/import`, body, { headers: { Authorization: `Bearer ${token}` } });
            toast({ title: `Imported ${selectedFiles.length} file${selectedFiles.length !== 1 ? 's' : ''}`, description: `From ${fileBrowserSource}` });
          } catch (err) {
            const e = getActionError('import files', err);
            toast({ title: e.title, description: e.description, variant: 'destructive' });
          }
          setFileBrowserSource(null);
        }}
      />
      {/* CORNER_V2: Permission confirmation now handled inline in CoT panel (CoTConfirmCard) */}

      {/* V6: workflow destination picker — opens when user clicks "Save as Workflow"
          on a contract-send confirmation. Replaces the old auto-name behaviour with
          a category × workflow chooser. */}
      <WorkflowDestinationPicker
        open={!!savePickerOpen}
        defaultTemplateName={savePickerOpen?.defaultName}
        onClose={() => setSavePickerOpen(null)}
        onSelect={handleSaveDestination}
      />

      <SendForSignatureModal
        key={sendForSignatureModalKey}
        open={sendForSignatureOpen}
        onClose={() => setSendForSignatureOpen(false)}
        fromDisplayName={auth.user?.displayName ?? 'You'}
        fromEmail={auth.user?.email ?? ''}
        documentName={currentResult?.fileName ?? currentFile?.name ?? 'Document'}
        pageCount={esignPdfPageCount || 1}
        fieldCount={esignFields.filter(isRecipientPlacementField).length}
        initialSignerEmails={esignRecipientDrafts}
        onSend={handleSendForSignature}
        sending={sendForSignatureSending}
      />
      <VerifySignatureModal
        open={esignVerifyOpen}
        signatureImageSrc={esignSignaturePreviewSrc}
        onConfirm={handleEsignFieldConfirm}
        onRedo={() => {
          setEsignVerifyOpen(false);
          navigate('/auth/signature-setup', { state: { from: 'settings' } });
        }}
        onClose={() => { setEsignVerifyOpen(false); }}
      />
      <UnfilledPlaceholderModal
        open={unfilledModalOpen}
        unfilled={unfilledModalEntries}
        onFillNow={handleUnfilledFillNow}
        onAssignToRecipient={handleUnfilledAssign}
        onSignAnyway={handleUnfilledSignAnyway}
        onClose={() => { setUnfilledModalOpen(false); pendingSignFieldIndexRef.current = null; }}
      />
      <PrintedTitleModal
        open={!!printedTitleModal}
        initialValue={printedTitleModal?.initial ?? ''}
        fieldLabel={printedTitleModal?.label}
        onClose={() => setPrintedTitleModal(null)}
        onSave={(value) => {
          if (!printedTitleModal) return;
          const { fieldId, markPlacedOnSave, configureOnly } = printedTitleModal;
          setPrintedTitleModal(null);
          if (configureOnly) {
            setEsignFields((prev) =>
              prev.map((f) =>
                f.id === fieldId
                  ? {
                      ...f,
                      printedText: value,
                      state: value.trim() ? ('confirmed' as const) : ('suggested' as const),
                    }
                  : f,
              ),
            );
            return;
          }
          setEsignFields((prev) => {
            const next = prev.map((f) =>
              f.id === fieldId ? { ...f, printedText: value } : f,
            );
            if (markPlacedOnSave) {
              const idx = next.findIndex((f) => f.id === fieldId);
              if (idx >= 0) {
                const withPlaced = next.map((f, i) =>
                  i === idx ? { ...f, placed: true, signedAt: Date.now() } : f,
                );
                queueMicrotask(() => { advanceSignerFieldQueue(withPlaced, idx); });
                return withPlaced;
              }
            } else {
              queueMicrotask(() => { flushTitlesThenApply(next); });
            }
            return next;
          });
        }}
      />
      {(workflowRunId || hudWorkflow) && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9200,
            backgroundColor: '#F5F2EC',
            display: 'flex',
          }}
        >
          <WorkflowRunPanel
            runId={workflowRunId}
            getToken={auth.getToken}
            onClose={() => {
              const shouldReturnToSignatures = Boolean(hudWorkflow);
              setWorkflowRunId(null);
              setHudWorkflow(null);
              if (shouldReturnToSignatures) {
                navigate(APP_SIGNATURES_PATH);
              }
            }}
            onGoBack={() => setWorkflowRunId(null)}
            onAppendThreadSummary={appendWorkflowThreadToChat}
            viewMode={hudWorkflow ? 'wizard' : undefined}
            // Forward the workflow definition so WorkflowRunPanel can auto-pick
            // the contract velocity HUD view when applicable.
            definition={hudWorkflow?.definition}
            workflowId={hudWorkflow?.id}
            onRun={hudWorkflow ? async ({ workflowId, initialVariables, documentIds }) => {
              try {
                const token = auth.getToken();
                const res = await axios.post<{ runId: string }>(
                  '/api/workflow-runs',
                  { workflowId, initialVariables, documentIds },
                  { headers: token ? { Authorization: `Bearer ${token}` } : {} },
                );
                setWorkflowRunId(res.data.runId);
              } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to start workflow run';
                showAppError('Failed to start run', message);
              }
            } : undefined}
          />
        </div>
      )}
      <PaywallModal
        open={paywallOpen}
        plan="pro"
        onClose={() => setPaywallOpen(false)}
        onUpgrade={async (planTier, cycle) => {
          const token = auth.getToken();
          if (!token) { toast({ title: 'Please sign in first' }); return; }
          try {
            const res = await axios.post<{ url: string }>('/api/billing/checkout', {
              plan: planTier === 'business' ? 'enterprise' : planTier,
              cycle,
            }, { headers: { Authorization: `Bearer ${token}` } });
            window.location.href = res.data.url;
          } catch (err) {
            const e = getActionError('start checkout', err);
            toast({ title: e.title, description: e.description, variant: 'destructive' });
          }
        }}
      />
      {proSuccessOpen && (
        <ProUpgradeSuccessModal
          onStart={() => { setProSuccessOpen(false); }}
          onDismiss={() => { setProSuccessOpen(false); }}
        />
      )}
      {linearEditIssueId && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.2)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setLinearEditIssueId(null); }}
        >
          <LinearEditIssueModal
            issueId={linearEditIssueId}
            onClose={() => setLinearEditIssueId(null)}
            onUpdated={() => { /* could refresh chat */ }}
            onDeleted={() => { /* could refresh chat */ }}
            getToken={auth.getToken}
          />
        </div>
      )}
      {shareModalOpen && currentConversationId && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.2)' }}
          onClick={(e) => { if (e.target === e.currentTarget) setShareModalOpen(false); }}
        >
          <ShareDocumentModal
            conversationId={currentConversationId}
            onClose={() => setShareModalOpen(false)}
            getToken={auth.getToken}
            currentUser={auth.user ? { name: auth.user.displayName, email: auth.user.email, avatarUrl: auth.user.avatarUrl } : undefined}
          />
        </div>
      )}
      <AppErrorBanner />
      {sourcePreview && (
        <SourcePreviewDrawer
          result={sourcePreview.result}
          sectionRef={sourcePreview.sectionRef}
          sectionTitle={sourcePreview.sectionTitle}
          onClose={() => setSourcePreview(null)}
          loading={sourcePreviewLoading}
        />
      )}
      {inlineClauseFlagPopover && (
        <InlineClauseFlagPopover
          flag={inlineClauseFlagPopover.flag}
          page={
            inlineClauseFlagPopover.located?.page
            ?? inlineClauseFlagPopover.flag.pageHint
            ?? null
          }
          busy={inlineClauseFlagBusy}
          onAccept={() => handleInlinePopoverPatch('accepted')}
          onDismiss={() => handleInlinePopoverPatch('dismissed')}
          onLocate={handleInlinePopoverLocate}
          onClose={handleInlinePopoverClose}
        />
      )}
      <Toaster />
    </div>
  );
}
